import Phaser from 'phaser';
import {getHexOutlineMarkerColor} from "./markers/getHexOutlineMarkerColor";
import {getUnitTypeConfig, UNIT_TYPES} from "../configs/units";
import {isUnitSizeRecruitmentMarkersShown} from "./markers/isUnitSizeRecruitmentMarkersShown";
import {isNewUnitRecruitmentMarkersShown} from "./markers/isNewUnitRecruitmentMarkersShown";
import {isUnitMovementMarkersShown} from "./markers/isUnitMovementMarkersShown";
import {
  COORDS_TEXT_STYLE,
  INCOME_TEXT_FRACTIONAL_STYLE,
  UNIT_LABEL_DAMAGE_STYLE,
  UNIT_LABEL_SEDUCTION_STYLE,
  UNIT_LABEL_TEN_AND_MORE_TEXT_STYLE,
  UNIT_LABEL_TEXT_STYLE
} from "../styles/HexGrid.style";
import {isConstructionMarkersShown} from "./markers/isConstructionMarkersShown";
import {PHASES, WAR_PHASE_STAGES} from "../constants/phases";
import {calculateAvailableRecruitmentCells} from "./gameState/calculateAvailableRecruitmentCells";
import {calculateCitiesCache} from "./gameState/calculateCitiesCache";
import {
  selectBuildingsDamage,
  selectCitiesCache,
  selectCurrentPlayer,
  selectFortressesUnderConstruction,
  selectGameResults,
  selectGiantsSelectedUnitId,
  selectGoblinsAbilityActions,
  selectGoblinsTrapDeathHexKeys,
  selectGoblinsTraps,
  selectGoblinsTrapStageTriggers,
  selectHexes,
  selectIsGameStarted,
  selectLeprechaunsStealCoinsActions,
  selectPendingManeuverActions,
  selectPendingMeleeDamage,
  selectPendingMovementActions,
  selectPendingRecruitmentActions,
  selectPendingShootingActions,
  selectPendingSupply,
  selectPhase,
  selectPlayers,
  selectRound,
  selectSelectedFarmsForConversion,
  selectSelectedUnit,
  selectSharedGameState,
  selectSpecialBuildings,
  selectStartingQuarters,
  selectSuccubesSeductionActions,
  selectUnits,
  selectWarPhaseStage
} from "../redux/reducers/sharedStateReducer/selectors";
import {
  selectBlockedCellsVisible,
  selectCityOwnershipVisible,
  selectFortressVisible,
  selectGoblinsAbilityMode,
  selectGuildVisible,
  selectInspectedHexKey,
  selectKeyPointIncomeVisible,
  selectQuarterZoneVisible,
  selectSelectedBarracksHexKey,
  selectSelectedFarmHexKey,
  selectSelectedToken,
  selectSelectedUnitId,
  selectShowMeleeTargets,
  selectTempleVisible,
  selectUnitsVisible
} from "../redux/reducers/privateStateReducer/selectors";
import {calculateAvailableMovementCells} from "./gameState/calculateAvailableMovementCells";
import {calculateAvailableManeuverCells} from "./gameState/calculateAvailableManeuverCells";
import {checkIfUnitCouldManeuverFromHex} from "./gameState/maneuver/checkIfUnitCouldManeuverFromHex";
import {coordsToKey} from "./coords/coordsToKey";
import {calculateAvailableSupplyCells} from "./gameState/calculateAvailableSupplyCells";
import {
  calculatePlannedTokenImage,
  calculateTokenImage,
  getSupplyPreviewPlayers,
  selectSupplyPlayerPendingSupply
} from "./hexes/calculateTokenImage";
import {checkIfParallelSupplyPhase, getNotCompletedPlayers} from "./gameState/supply/parallelSupply";
import {checkIfSupplyShownOnField} from "./gameState/supply/checkIfSupplyShownOnField";
import {selectSupplyActingPlayer, selectSupplyActingPlayerCities} from "./gameState/supply/parallelSupplySelectors";
import {getTutorialChapter} from "../configs/tutorial";
import {generateOutlineTexture} from "./sprites/generateOutlineTexture";
import {checkIfHexBlockedByEnemyUnit} from "./gameState/checkIfHexBlockedByEnemyUnit";
import {calcCellsBlockedByUnitMove} from "./gameState/war/calcCellsBlockedByUnitMove";
import {checkIfUnitHasPendingAdditionalSupply} from "./gameState/checkIfUnitHasPendingAdditionalSupply";
import {calcUnitUpdateBasedOnPendingSupply} from "./gameState/supply/calcUnitUpdateBasedOnPendingSupply";
import {calcUnitUpdateBasedOnPendingRecruitment} from "./gameState/recruitment/calcUnitUpdateBasedOnPendingRecruitment";
import {MINUS_SIGN} from "../constants/text";
import {UNIT_MOVEMENT_DURATION_MS, UNIT_MOVEMENT_WAIT_MARGIN_MS} from "../constants/animation";
import {
  CENTRAL_QUARTER_PREFIX,
  COMMON_BUILDING_TOKENS,
  FLAG_PREFIX,
  KEY_POINT_TOKENS,
  SPECIAL_BUILDING_TOKENS,
  TERRAIN_TOKENS,
  TOKENS,
  UNDESTRUCTIBLE_TOKENS
} from "../constants/tokens";
import {getAllQuartersOfCity} from "./gameState/getAllQuartersOfCity";
import {createUnitSizeRecruitmentMarker, UnitSizeRecruitmentMarkerPulse} from "../scenes/unitSizeRecruitmentMarker";
import {createIncomeMarker} from "../scenes/incomeMarker";
import GlowingThreads from "../scenes/GlowingThreads";
import LiveFieldMarkers from "../scenes/LiveFieldMarkers";
import {CONSTRUCTION_MARKER, CONSTRUCTION_MARKER_KINDS} from "../configs/liveFieldMarkers";
import FieldCellOutlines from "../scenes/FieldCellOutlines";
import FieldRangeZone from "../scenes/FieldRangeZone";
import {getHexDistance} from "./coords/getHexDistance";
import {COMBAT_MARKER_KINDS} from "../configs/liveFieldMarkers";
import {CELL_OUTLINE_PALETTES} from "../configs/fieldCellOutlines";
import {UNIT_SIZE_DELTA_BADGE, UNIT_SIZE_DELTA_KINDS} from "../configs/unitBadges";
import {
  ensureUnitBadgeTexture,
  ensureUnitSizeDeltaTexture,
  resolveUnitBadgeLayout,
  resolveUnitBadgeTextureKey,
  resolveUnitSizeDeltaTextureKey,
} from "./effects/unitBadgeTextures";
import {INCOME_MARKER} from "../configs/incomeMarker";
import {GLOW_THREAD_PALETTES, SUPPLY_BLOCKED_NODE_OVERLAYS} from "../configs/glowingThreads";
import {buildThreadGeometry, resolveThreadRevealMs} from "./effects/glowThreadGeometry";
import {
  buildIncomeRevealCastPlan,
  buildSupplyBreakCastPlan,
  buildSupplyTransferCastPlan,
} from "./effects/buildAbilityCast";
import {filterArrivedRecruitmentActions} from "./effects/abilityResultHolds";
import {
  WAR_COLOR_0x,
} from "../styles/constants";
import {findOwner} from "./gameState/findOwner";
import {getNeutralPlayerMock} from "./players/getNeutralPlayerMock";
import {
  FACTION_UNIT_SCALE_MULTIPLIER, GOBLINS_TRAP_DAMAGE_ICON_SCALE,
  MARKER_FADING_DURATION_MS,
  MARKER_FADING_HOLD_MS,
  NEUTRAL_UNIT_ICON_SCALE_MULTIPLIER,
  NEUTRAL_UNIT_SHADOW_SCALE_MULTIPLIER,
  STAT_BACKGROUND_MODES,
  COMBAT_INDICATOR_FADING_ENABLED,
  UNIT_FADING_OUTLINE_MIN_ALPHA,
  STAT_ICON_SCALE,
  SUPPLY_ANIMATION_DURATION_MS,
  SUPPLY_ANIMATION_HOLD_MS,
  TOKEN_SCALE,
  UNIT_SCALE_MULTIPLIER
} from "../constants/visuals";
import {INCOME_TYPE, INCOME_TYPE_MAPPING} from "../constants/income";
import {calcGuildIncome} from "./gameState/income/calcGuildIncome";
import {getEnemyCitiesForPlayer} from "./gameState/getEnemyCitiesForPlayer";
import {calcTempleIncome} from "./gameState/income/calcTempleIncome";
import {calculateInfluencePathIncome} from "./gameState/calculateInfluencePathIncome";
import {calcCoinsRecruitmentSize} from "./gameState/recruitment/calcCoinsRecruitmentSize";
import {calculateConstructionCells} from "./gameState/construction/calculateConstructionCells";
import {
  calculateBaseQuarterAllowedCells,
  calculateQuarterZoneCells
} from "./gameState/construction/calculateQuarterZoneCells";
import {calculateAvailableShootingCells} from "./gameState/shooting/calculateAvailableShootingCells";
import {calculateAvailableSeductionCells} from "./factions/succubes/calculateAvailableSeductionCells";
import {calculateShootingReachableCells} from "./gameState/shooting/calculateShootingReachableCells";
import {getPlayer0xColor} from "./players/getPlayer0xColor";
import {checkIfUnitTurnSkipShown} from "./gameState/progress/unitTurnSkips";
import {UNIT_SKIP_MARK_STYLE} from "../styles/HexGrid.style";
import {getUnitSkipMarkAlphas} from "./effects/getUnitSkipMarkAlphas";
import {getAbilitySparkleStates} from "./effects/getAbilitySparkleStates";
import {ABILITY_SPARKLES} from "../configs/abilitySparkleAnimation";
import {checkIfSuccubesUnitCouldSeduce} from "./factions/checkIfSuccubesUnitCouldSeduce";
import {getCityHexKeys} from "./gameState/getCityHexKeys";
import {getInfluencedEnemyCities} from "./gameState/income/getInfluencedEnemyCities";
import {getPlayerFlagToken} from "./players/getPlayerFlagToken";
import {calcUnitMeleeDamageWithFortress} from "./damage/calcUnitReceivedDamage";
import {getNeighborEnemyUnits} from "./unit/getNeighborEnemyUnits";
import {D4_SHOOTING_DAMAGE_FOR_1_BUILDING_DAMAGE} from "../configs/war";
import {FORTRESS_MAX_DAMAGE} from "../configs/specialBuildings";
import {getActingPlayerFromState} from "./players/getActingPlayer";
import {checkIfSupplyRecruitHiddenFromViewer} from "./unit/checkIfSupplyRecruitHiddenFromViewer";
import {isUnitManeuverMarkersShown} from "./markers/isUnitManeuverMarkersShown";
import {FORTRESS_MELEE_DAMAGE_RATION} from "../constants/combat";
import {isUnitFaction} from "./factions/isUnitFaction";
import {FACTIONS} from "../constants/factions";
import {checkIfUnitTypeIsFlyingForFaction} from "../configs/factions";
import {WINGED_FLIGHT_TEXTURE_KEYS} from "../configs/wingedFlightAnimation";
import {buildWingedFlightCastPlan} from "./effects/buildWingedFlightCast";
import UnitFootprints from "../scenes/UnitFootprints";
import {SUCCUBES_SEDUCTION_RANGE} from "../configs/factions/succubes";
import {GOBLINS_ABILITY_MODES, GOBLINS_ABILITY_RADIUS} from "../configs/factions/goblins";
import {calculateAvailableTrapSetupCells} from "./factions/goblins/calculateAvailableTrapSetupCells";
import {calculateAvailableTrapRemovalCells} from "./factions/goblins/calculateAvailableTrapRemovalCells";
import {calculateAvailableBuildingDamageCells} from "./factions/goblins/calculateAvailableBuildingDamageCells";
import {checkIfGoblinsUnitCouldUseAbility} from "./factions/checkIfGoblinsUnitCouldUseAbility";
import {getCapturedUnitOriginalColor} from "./factions/succubes/getCapturedUnitOriginalColor";
import {GIANTS_UNIT_ICON_SCALE_MULTIPLIER} from "../configs/factions/giants";
import {checkIfUnitMeleeElf} from "./factions/elves/checkIfUnitMeleeElf";
import {calculateAvailableShootingCellsForElves} from "./factions/elves/calculateAvailableShootingCellsForElves";
import {ELVES_SHOOTING_RANGE} from "../configs/factions/elves";
import {UNIT_D4_MAX_SHOOTING_RANGE} from "../constants/units";
import {getUnitById} from "./unit/getUnitById";
import {calculateOrcLoss} from "./factions/orcs/calculateOrcLoss";
import {isUnitInsectoid} from "./factions/insectoids/isUnitInsectoid";
import {getSizeRecruitmentPrice} from "./recruitment/getSizeRecruitmentPrice";
import {calcFarmRemainingSupply} from "./gameState/calcFarmRemainingSupply";
import {isPlayerPolicy} from "./factions/isPlayerPolicy";
import {POLICIES} from "../constants/policies";
import {isHegemonismBonusHexKey} from "./policy/hegemonism/isHegemonismBonusHexKey";
import {POLICY_HEGEMONISM_QUARTER_WIN_POINTS_INCOME_BONUS} from "../configs/policy/hegemonism";
import {checkIfAnyQuarter} from "./buildings/checkIfAnyQuarter";
import {checkIfAdjacentToAnyQuarter} from "./buildings/checkIfAdjacentToAnyQuarter";
import {checkIfAdjacentToCentralQuarter} from "./buildings/checkIfAdjacentToCentralQuarter";
import {calcMineIncome} from "./gameState/income/calcMineIncome";
import {calcRuinsIncome} from "./gameState/income/calcRuinsIncome";
import {
  checkIfRoundWithIncreasedRuinsIncome,
  selectRuinsIncomeRoundLimit,
} from "./gameState/income/checkIfRoundWithIncreasedRuinsIncome";
import {isPlayerCentralQuarter} from "./gameState/isPlayerCentralQuarter";
import {checkIfCentralQuarter} from "./buildings/checkIfCentralQuarter";
import {NEUTRAL_PLAYER_COLOR, NEUTRAL_PLAYER_ID} from "../constants/players";
import {checkIfFlag} from "./buildings/checkIfFlag";
import {checkIfSpecialBuilding} from "./buildings/checkIfSpecialBuilding";
import {getNeighborHexKeys} from "./coords/getNeighborHexKeys";
import {mixColor0x} from "./colors/mixColor0x";
import {
  checkIfPlanEntryCoversHex,
  selectConstructionPlanEntriesOnField,
  selectFieldWithConstructionPlan
} from "./gameState/construction/planning/selectFieldWithConstructionPlan";
import {
  selectConstructionPlanEntries,
  selectConstructionPlanInvalidHexKeys
} from "../redux/reducers/privateStateReducer/selectors";
import {getConstructionMarkersState} from "./gameState/construction/planning/buildConstructionPlanningViewState";
import {resolveOwnStartingQuarter} from "./gameState/setup/resolveOwnStartingQuarter";
import {
  getEmptyHexAlpha,
  getHexGridColor,
  getQuarterZoneMarkerAlpha,
  getQuarterZoneMarkerFillColor,
} from "../configs/groundBackgrounds";
import {checkIfUnitStandsOnBuilding} from "./hexes/checkIfUnitStandsOnBuilding";
import {checkIfHexClickable} from "./hexes/checkIfHexClickable";
import FieldFlags from "../scenes/FieldFlags";
import ActiveUnitGlows from "../scenes/ActiveUnitGlows";
import {ACTIVE_GLOW, UNIT_GLOW_TEXTURE} from "../configs/activeGlow";
import {generateGlowTexture} from "./sprites/generateGlowTexture";
import {checkIfMatchOver} from "./gameState/checkIfMatchOver";
import {
  BUILDING_DUST,
  BUILDING_DUST_TEXTURES,
  BUILDING_DUST_TINT,
  DUST_TEXTURE_SIZE,
} from "../configs/buildingDustAnimation";
import {buildBuildingDustBurst} from "./effects/buildBuildingDustBurst";
import {BUILDING_DROP, BUILDING_DROP_COVER_MS} from "../configs/buildingDropAnimation";
import {
  BUILDING_IMPACT_LIMITS,
  BUILDING_IMPACTS,
  QUARTER_ZONE_BURN,
  resolveBuildingDropCoverExtraMs,
} from "../configs/buildingConstructionAnimation";
import {buildBuildingApproachCastPlan, buildBuildingImpactCastPlan} from "./effects/buildBuildingImpact";
import {ensureBuildingImpactTextures} from "./effects/buildingImpactTextures";
import QuarterZoneBurn from "../scenes/QuarterZoneBurn";
import {
  SHOT_ANIMATION,
  SHOT_KINDS,
  SHOT_PROJECTILE_IMAGE_SIZE,
  SHOT_PROJECTILE_IMAGES,
  SHOT_SHARDS_SHEET,
  SHOT_TEXTURE_KEYS,
  SHOT_TRAJECTORY,
} from "../configs/shotAnimation";
import {buildShotDebris, resolveShardAppearance} from "./effects/buildShotDebris";
import {checkIfAnyShotInFlight, markShotInFlight, markShotLanded} from "./effects/shotFlightTracker";
import {
  buildShotVolley,
  resolveShotProjectilePosition,
  resolveShotTimeFactor,
} from "./effects/buildShotVolley";
import {ensureShotTextures, SHOT_TEXTURE_SIZES} from "./effects/shotProjectileTextures";
import {MELEE_ANIMATION, MELEE_TEXTURE_KEYS, MELEE_WEAPONS_SHEET} from "../configs/meleeAnimation";
import {
  buildMeleeStrikeGeometry,
  resolveMeleeSlashState,
} from "./effects/buildMeleeTimeline";
import {ensureMeleeTextures, MELEE_TEXTURE_SIZES} from "./effects/meleeEffectTextures";
import {buildBloodSplatter, resolveBloodDropletPosition} from "./effects/buildBloodSplatter";
import {BLOOD_SPLATTER, resolveBloodColors, resolveBloodIntensity} from "../configs/bloodSplatter";
import {holdMeleeResult, releaseMeleeResult} from "./effects/meleeAnimationTracker";
import {
  markAwaitedAnimationFinished,
  markAwaitedAnimationPlaying,
  markAwaitedAnimationResultShown,
} from "./replay/replayAwaitedAnimations";
import {checkIfUnitHasFactionSkin} from "./factions/checkIfUnitHasFactionSkin";
import {COINS_SPENT_NUMBER} from "../configs/coinsSpentAnimation";
import {
  COINS_SPENT_TEXT_SCALE,
  formatCoinsSpentText,
  resolveCoinsSpentAmount,
  resolveCoinsSpentIconOutlineThickness,
  resolveCoinsSpentLayout,
  resolveCoinsSpentLift,
  resolveCoinsSpentPose,
  resolveCoinsSpentSizes,
  resolveCoinsSpentStackPush,
} from "./effects/buildCoinsSpentNumber";
import {
  COMBAT_CELL_PULSE,
  RECRUITMENT_PULSE,
  RECRUITMENT_PULSE_COUNT,
} from "../configs/unitRecruitmentAnimation";
import {ensureHexWaveTextures, resolveHexWaveScale} from "./effects/hexWaveTextures";
import {ARROW_CAST, BOULDER_CAST, COMBAT_CAST_COLORS, MELEE_SLASH_CAST, resolveMeleeImpactTier} from "../configs/combatCastAnimation";
import {
  buildArrowHitCastPlan,
  buildArrowLaunchCastPlan,
  buildArrowVolleyCastPlan,
  buildBoulderImpactCastPlan,
  buildBoulderLaunchCastPlan,
  buildBoulderTargetCastPlan,
  buildBoulderVolleyCastPlan,
  buildMeleeImpactCastPlan,
  buildMeleeSlashCastPlan,
} from "./effects/buildCombatCast";
import {ABILITY_CAST_TEXTURE_SIZES, ensureAbilityCastTextures} from "./effects/abilityCastTextures";
import {ABILITY_CAST_TEXTURE_KEYS} from "../configs/abilityCastAnimation";
import {getTokenGlintStyle} from "../configs/tokenGlintAnimation";
import TokenGlints from "../scenes/TokenGlints";
import {checkIfRuinsIncomeGlowToken} from "../configs/ruinsIncomeGlow";
import {selectIsRuinsIncomeAnimationAwaited} from "../redux/reducers/privateStateReducer/selectors";
import RuinsIncomeGlow from "../scenes/RuinsIncomeGlow";
import AbilityCastAnimation from "../scenes/AbilityCastAnimation";
import {shakeFieldCamera} from "./effects/shakeFieldCamera";
import {ABILITY_CAST_UNIT_BODY_LIFT_FACTOR} from "../configs/abilityCastAnimation";

/**
 * Конфигурация типов жетонов
 */

// Глубины слоёв рендеринга (z-index)
const LAYER_DEPTH = {
  HEX_BASE: 0,      // Базовый слой - сетка гексов
  TOKEN: 1,        // Слой токенов - здания, ключевые точки
  TOKEN_SPECIAL_BUILDING: 1.05,        // Слой токенов - здания, ключевые точки
  CITY_OWNERSHIP: 1.5, // Слой принадлежности городов
  HIGHLIGHT: 2,     // Слой подсветки ячеек
  SHOOTING_TRAJECTORY: 2.6, // Пунктир выстрела: ПОД отрядами (иконка 3,
                           // контур 2.9, тень 2.8) — линия шла поверх них и
                           // перечеркивала и стрелка, и цель, — но выше
                           // жетонов строений, подсветок ячеек и волны боя,
                           // чтобы сам выстрел читался
  COMBAT_CELL_PULSE: 2.5,  // Волна по ячейке в такт боевым иконкам (ближний
                           // бой и стрельба): выше жетонов строений и подсветок
                           // ячеек, но ниже ВСЕХ слоев отряда — не только
                           // иконки (UNIT), но и тени (UNIT - 0.2) с контуром
                           // (UNIT - 0.1). На 2.95 волна закрашивала контур, и
                           // отряд выглядел перечеркнутым; у нейтралов контура
                           // нет, поэтому у них дефект был не виден. У
                           // подсветки найма слой свой, поверх всего поля
                           // (RECRUITMENT_PULSE)
  UNIT: 3,         // Слой отрядов - выше токенов
  UNIT_MOVEMENT_FLIGHT: 3.4, // Двойник идущего отряда (чужой ход, запись): выше
                           // стоящих отрядов и их подписей, но ниже индикаторов
                           // и маркеров — отряд идет ПО полю, а не падает на
                           // него сверху
  INFO: 5,     // Слой информации
  MARKER: 6,       // Слой маркеров
  WINGED_FLIGHT_WINGS: 7.5, // Крылья и след отряда в полете: над маркерами и
                           // другими отрядами, но за его поднятой иконкой (см.
                           // WINGED_UNIT_DEPTH_RAISE)
  WINGED_FLIGHT_LANDED: 2.78, // Они же после посадки: настоящий отряд уже на
                           // своем слое, и складываются они у него за спиной
  UNIT_FOOTPRINT_GROUND: 2.73, // След за отрядом (см. UnitFootprints): свет и
                           // трещины, отпечатки, пыль и знаки фракций. Выше
                           // жетонов и пыли построек, ниже всех слоев отрядов
  UNIT_FOOTPRINT_PRINTS: 2.74,
  UNIT_FOOTPRINT_AIR: 2.76,
  RECRUITMENT_PULSE: 8.4,  // Световая волна при найме цветом игрока: поверх
                           // всего поля, иначе ее перекрывают маркеры и кнопки
                           // соседних ячеек. Из-под казармы она выходит не
                           // слоем, а маской по своему гексу (см.
                           // showUnitRecruitmentPulse) — так ничего на поле
                           // прятать не приходится
  BUILDING_DROP: 8.25,     // Двойник прилетающего жетона: ВЫШЕ пыли (чтобы она
                           // выбивалась из-под жетона, но накрывала соседние
                           // жетоны), а заодно выше отрядов, индикаторов,
                           // подписей и кнопок ячеек — жетон летит сверху и
                           // ничем не перекрывается. Выше и ABILITY_CAST: лучи,
                           // искры и шлейф постройки идут из-за жетона
  BUILDING_DUST: 2.72,     // Клубы пыли от поставленного жетона: выше жетонов, зон
                           // кварталов, подсветки городов и прочих слоев
                           // переключателей вида, но ниже отрядов и ниже волн и
                           // трещин удара (ABILITY_CAST_GROUND): иначе пыль их
                           // скрывает. На глубине TOKEN облако срезали бы жетоны
                           // ячеек, нарисованных позже (см. getHexKeysTopToBottom)
  INFO_ANIMATION: 7,       // Слой анимации информации (короткие неповторяющиеся анимации)
  RANGE_ZONE_GROUND: 2.2,  // Зона действия отряда: свет, тень снаружи и штриховка. Под
                           // значками строений и всеми слоями отрядов: те остаются яркими
  RANGE_ZONE_CONTOUR: 2.35, // Ее контур: под обводками целей и доступных ячеек (UNIT - 0.5)
  SHOT_SHADOW: 2.7,        // Тень летящего валуна: лежит на земле — выше линии
                           // выстрела и волны боя, но под отрядами
  BLOOD_STAIN: 2.92,       // Пятна крови на земле: над пылью построек и жетонами,
                           // но под отрядами (см. showBloodSplatter)
  BLOOD_DROP: 4.62,        // Летящие капли крови: поверх отряда-цели и его значков
  SHOT_IMPACT: 4.6,        // Искры попаданий и осколки валуна: поверх отряда-цели
                           // и его значков, но ниже маркеров ячеек
  SHOT_PROJECTILE: 8.2,    // Снаряд в полете (валун, стрелы): над полем — выше
                           // отрядов и всех маркеров
  MELEE_EFFECT: 8.22,      // Удар ближнего боя (оружие, световой след, вспышка,
                           // искры): над отрядами, их значками и маркерами ячеек
  MELEE_NUMBER: 8.24,      // Всплывающее «−N» удара — поверх самого удара
  ABILITY_CAST: 8.23,      // Анимация способности (сердце соблазна, бомба и
                           // ловушка гоблинов): над отрядами, значками и
                           // маркерами, как удар ближнего боя
  ABILITY_CAST_GROUND: 2.75, // Ее свечение и волны по земле: выше жетонов,
                           // подсветок и линии выстрела, но под тенью отряда
  COINS_SPENT_NUMBER: 8.45, // Всплывающее «−N» потраченных монет: выше подсветки
                           // найма (RECRUITMENT_PULSE) — та расходится из-под
                           // отряда как раз тогда, когда над ним всплывает
                           // число, — и выше прилетающих жетонов строений
  TUTORIAL_ARROW: 8.5,     // Стрелка обучения — поверх всего: подсказка не должна гаснуть
};

// Прозрачность оверлея принадлежности городов — общая для постоянного слоя
// (иконка bust в VisualToggles) и для разовой подсветки городов, до которых
// дотянулись пути влияния гильдии/храма
const CITY_OWNERSHIP_FILL_ALPHA = 0.7;
const UNOWNED_INCOME_MARKER_ALPHA = 0.8;

// Маркер найма размера на квартале, который уже ничего не даст: отряд
// максимального размера. Клик по нему по-прежнему обрабатывается (тост с
// причиной), поэтому маркер не убирается, а показывается полупрозрачным

// Разовый пульс иконки блокировки над ячейкой сразу после хода, которым она
// заблокирована (см. updateBlockedCellsPulse) — избыточен теперь, когда слой
// заблокированных ячеек отображается постоянно (addBlockedCellsMarkers,
// blockedCellsVisible). Чтобы вернуть анимацию — просто поставить true
const BLOCKED_CELLS_PULSE_ANIMATION_ENABLED = false;

// Толщина обводки-маркера гекса. Маркеры доступности действия (куда отряд
// может пойти, где можно поставить выбранное строение) и ВЫБРАННАЯ игроком
// ячейка рисуются ОДНИМ белым цветом, поэтому различает их только толщина:
// у выбранной она втрое больше.
// Выбранная ячейка — это не только ячейка под осмотром (inspectedHexKey), но и
// собственная ячейка выбранного отряда, выбранная казарма найма и выбранная
// ферма снабжения: игрок ткнул в них сам, и они не должны сливаться с
// подсказками доступности вокруг
const HEX_OUTLINE_MARKER_LINE_WIDTH = 4;

// Иконка доступной для перемещения ячейки. У иконки полета (крылатая ступня)
// при том же размере файла ступня ниже в 511/418 ≈ 1,22 раза (крыло занимает
// верх картинки) — увеличиваем ее, чтобы ступня выглядела как на обычной иконке
const MOVEMENT_ICON_SCALE = 0.08;
const FLYING_MOVEMENT_ICON_SCALE = 0.098;
const SELECTED_HEX_OUTLINE_MARKER_LINE_WIDTH = HEX_OUTLINE_MARKER_LINE_WIDTH * 2;

const UNIT_STAT_SHIFT_Y = 35;
const UNIT_BADGE_PIXELS_PER_UNIT = 4;
const UNIT_BADGE_GOLD_ICON_TINT = 0xffd97a;
const UNIT_LABEL_TEXT_SHIFT_Y = UNIT_STAT_SHIFT_Y - 0.3;
const UNIT_INDICATOR_SHIFT_X = 30;
const UNIT_COMBAT_INDICATOR_SHIFT_Y = -10;

const MOUNTAIN_SHIFT_Y = -2;
const OTHER_TOKEN_SHIFT_Y = 0;
const COMBAT_TOKEN_SHIFT_Y = 0;
/**
 * Насколько выше центра ячейки рисуется спецстроение (крепость, гильдия, храм,
 * арсенал и прочие) вместе со своими накладками — здоровьем и повреждениями
 * крепости.
 *
 * Смещение у них СВОЕ, отдельно от OTHER_TOKEN_SHIFT_Y: картинки спецстроений
 * не перерисовывались под псевдо-3D, они по-прежнему ложатся поверх базового
 * квартала и выше не стали. Значит и подниматься им нужно на другую величину,
 * чем подросшим жетонам.
 *
 * Значение подбирается глазами: нижний край строения должен садиться на ту же
 * линию сетки, что и раньше.
 */
const SPECIAL_BUILDING_SHIFT_Y = 0;
/**
 * Насколько выше центра ячейки рисуются накладки-подсказки поверх жетона:
 * значок повреждения строения, плашка дохода и значок блокировки. Едут на ту
 * же величину, что и спецстроения, но своим именем — чтобы подстраивать их
 * можно было отдельно, не трогая сами строения.
 */
const MARKER_SHIFT_Y = SPECIAL_BUILDING_SHIFT_Y;

// Жетон повреждения строения стоит слева от центра ячейки. По нему же
// выравниваются боевые иконки (ближний бой и выстрел): в центре они закрывали
// собой отряд (см. addMeleeIndicator, addShootingIndicator)
// Живой маркер меньше прежнего, а его внешний край остался на месте (-62 от центра):
// так маркер отступает от отряда, стоящего посередине ячейки
const BUILDING_DAMAGE_MARKER_SHIFT_X = -42.2;
// Живые маркеры по бокам ячейки (повреждение, блокировка, ближний бой, выстрел):
// над обводками ячеек, чтобы контур их не перечеркивал, но под отрядом, чтобы не
// перекрывать его иконку
const BUILDING_STATE_MARKER_DEPTH = LAYER_DEPTH.HIGHLIGHT + 0.3;
// Глубина контура ловушки Гоблинов: должна перекрывать ТОЛЬКО сам жетон
// строения (TOKEN_SPECIAL_BUILDING) - но не его накладки, которые обязаны
// оставаться поверх ловушки (индикатор повреждения обычного строения (+0.1),
// здоровье/повреждения крепости (+0.2/+0.25, см. renderFortressDamage) и
// всё остальное на этой глубине). Поэтому смещение маленькое - строго между
// 0 (сам жетон строения) и +0.1 (первая накладка над ним)
const GOBLINS_TRAP_MARKER_DEPTH_OFFSET = 0.05;
// Ловушка Гоблинов рисуется не иконкой, а прямо по контуру гекса (по
// просьбе пользователя после ручного тестирования - готовая иконка в центре
// перекрывала жетон строения) - толстая линия по границе шестиугольника с
// треугольными зубьями внутрь, как капкан. Цвет и прозрачность подобраны под
// технику, которой уже нарисованы тени символов на baked-иконках строений
// (barracks_3d.png/farm_3d.png, public/img/hexes/3d) - там тень символа это
// не отдельный цвет, а плоский чёрный поверх заливки гекса примерно на 25%
// непрозрачности (замерено по пикселям: 151/200 и 161/214/104/138/68/90 -
// везде отношение ~0.75, то есть чёрный с alpha~0.245). Та же формула черный+
// альфа сама подстраивается под ЛЮБОЙ цвет гекса под ней, включая ещё не
// нарисованные типы местности - конкретный цвет фона гекса поэтому не важен
const GOBLINS_TRAP_COLOR_0x = 0x000000;
const GOBLINS_TRAP_ALPHA = 0.32;
const GOBLINS_TRAP_OUTER_RADIUS_RATIO = 0.96; // отношение радиуса ловушки к радиусу гекса
const GOBLINS_TRAP_BAND_WIDTH_RATIO = 0.10; // толщина линии по контуру
const GOBLINS_TRAP_TOOTH_LENGTH_RATIO = 0.19; // на сколько зуб уходит глубже внутренней границы линии
const GOBLINS_TRAP_TEETH_PER_EDGE = 4;
const GOBLINS_TRAP_TOOTH_HALF_WIDTH_T = 0.07;
// Граница владений игрока (addCityBorderMarkers): «неоновая трубка» цвета
// игрока вдоль тех сторон гекса, за которыми уже чужая/ничья ячейка, - яркая
// линия со светлой сердцевиной и мягким свечением в обе стороны: внутрь своей
// ячейки и ореолом наружу; между ячейками одного игрока - тонкая тусклая
// линия того же цвета. Центр поднят на тот же сдвиг, что у капкана
// Гоблинов на застроенной ячейке. Сдвиг один на всю границу, а не по типу
// жетона: иначе стыки соседних ячеек разъехались бы (ячейки городов все
// застроены, пустых среди них нет)
const CITY_BORDER_SHIFT_Y = MARKER_SHIFT_Y - 0.45;
// Ниже капкана Гоблинов (GOBLINS_TRAP_MARKER_DEPTH_OFFSET), но выше жетона:
// ниже жетона свечение пряталось бы под картинками жетонов соседних ячеек и
// контур рвался бы на застроенных соседях; отряды и их числа все равно выше
// (LAYER_DEPTH.UNIT)
const CITY_BORDER_DEPTH_OFFSET = 0.04;
// Легкий тон цвета владельца на ячейках внутри границы владений (жетоны
// чуть окрашены в цвет игрока). Рисуется в той же графике, что граница
// ячейки, и раньше нее — поверх жетонов, но под трубкой и свечением; отряды
// выше (LAYER_DEPTH.UNIT) и не тонируются. Ничейные (нейтральные) города —
// без тона
export const CITY_TERRITORY_TINT_ALPHA = 0.12;
// Слои свечения границы (drawCityBorderGlow) - вложенные трапеции от
// стороны сетки, каждая с альфой от своей на стороне до 0 на дальнем краю;
// ширина - в долях радиуса гекса. Слои складываются: ореол наружу (на ничьи
// ячейки) шире и ярче, внутрь своей ячейки - короче и слабее, чтобы не
// превращаться в заливку жетона
const CITY_BORDER_HALO_LAYERS = [
  {widthRatio: 0.08, alpha: 0.45},
  {widthRatio: 0.18, alpha: 0.28},
  {widthRatio: 0.32, alpha: 0.15},
];
const CITY_BORDER_INNER_GLOW_LAYERS = [
  {widthRatio: 0.2, alpha: 0.3},
  {widthRatio: 0.32, alpha: 0.12},
];
// Профиль трубки поперек стороны, от стороны сетки внутрь ячейки: расстояние
// (в долях радиуса гекса), доля белого в цвете игрока и альфа. Между точками
// цвет и альфа меняются линейно. Трубка целиком внутри своей ячейки: на
// стороне между двумя игроками их трубки ложатся рядом, не накладываясь.
// Большую часть ширины занимает насыщенный цвет игрока, а сердцевина -
// узкий блик с небольшой долей белого: с широкой и почти белой
// сердцевиной трубки разных игроков казались одинаково светлыми
const CITY_BORDER_TUBE_PROFILE = [
  {distanceRatio: 0, whiteShare: 0, alpha: 1},
  {distanceRatio: 0.04, whiteShare: 0, alpha: 1},
  {distanceRatio: 0.058, whiteShare: 0.35, alpha: 1},
  {distanceRatio: 0.076, whiteShare: 0, alpha: 1},
  {distanceRatio: 0.115, whiteShare: 0, alpha: 1},
  {distanceRatio: 0.13, whiteShare: 0, alpha: 0},
];
// Линия между ячейками одного игрока (в том числе разных его городов):
// каждая ячейка рисует свою половину, вместе - тонкая линия по стороне сетки
// с легкой дымкой по краям
const CITY_BORDER_INNER_LINE_PROFILE = [
  {distanceRatio: 0, whiteShare: 0, alpha: 0.6},
  {distanceRatio: 0.012, whiteShare: 0, alpha: 0.6},
  {distanceRatio: 0.05, whiteShare: 0, alpha: 0},
];
// Гора — единственный жетон, чья картинка выше своей ячейки: вершина заходит
// на ячейку над ней. Поэтому сама гора (без тени) рисуется чуть ВЫШЕ границы
// владений — иначе свечение границы города, стоящего над горой, ложилось бы
// поверх вершины. Но НИЖЕ всего остального, что есть на ячейке над горой:
// капкана Гоблинов, накладок строения (повреждения, здоровье крепости), слоя
// принадлежности городов, подсветок ячеек и всех слоев отряда — шевроны
// отряда висят у нижнего края его ячейки, как раз там, куда заходит вершина.
// Строения под горой она при этом не закрывает: вниз и в стороны за свою
// ячейку картинка горы не свешивается. Глубина остается в пределах слоев
// жетона (checkIfHexTokenSprite): на время прилета двойника гора прячется,
// как любой жетон
const MOUNTAIN_DEPTH_OFFSET = CITY_BORDER_DEPTH_OFFSET + 0.005;
/**
 * Слои облета захваченных городов (см. scenes/CityCaptureAnimation). Заливка
 * ячеек города — над жетонами, но под границей: трубка границы поверх
 * заливки остается яркой. Обе версии границы (до и после захвата) — на
 * глубине обычной границы, которую они на время анимации заменяют. Огонь
 * фронта — над границей, но под горой: ее вершина заходит на ячейку выше и
 * гореть не должна. Искры и иконки владельцев — над отрядами
 */
export const CITY_CAPTURE_LAYER_DEPTHS = {
  fill: LAYER_DEPTH.TOKEN_SPECIAL_BUILDING + CITY_BORDER_DEPTH_OFFSET - 0.005,
  border: LAYER_DEPTH.TOKEN_SPECIAL_BUILDING + CITY_BORDER_DEPTH_OFFSET,
  fire: LAYER_DEPTH.TOKEN_SPECIAL_BUILDING + CITY_BORDER_DEPTH_OFFSET + 0.003,
  sparks: LAYER_DEPTH.INFO - 0.1,
  icon: LAYER_DEPTH.INFO,
};
// Где висят искры способности у отрядов Гоблинов и Суккубов, которые ещё
// могут применить способность на этапе ABILITIES (addAbilityAvailableIndicator):
// центр группы — в правом верхнем углу квадрата иконки отряда (~85 px при
// hexSize 60), сдвиг от центра ячейки. Масштаб — иконки этапа способностей
// целиком (по нему расставлены искры): группа ~31 px целиком в этом углу
const ABILITY_AVAILABLE_ICON_SHIFT_X = 33;
const ABILITY_AVAILABLE_ICON_SHIFT_Y = -36;
const ABILITY_AVAILABLE_ICON_SCALE = 0.06;
// Сторона картинки иконки этапа способностей (img/icons/ability_white.png)
const ABILITY_ICON_SOURCE_SIZE_PX = 512;
// Отметка пропуска хода «zzZ» (addUnitSkipMark): правее значка способности
// (его правый край ~16 px от центра при hexSize 60) и на его высоте
const UNIT_SKIP_MARK_SHIFT_X = 18;
const UNIT_SKIP_MARK_SHIFT_Y = -32;
// Буквы «zzZ» от маленькой к большой, каждая правее и выше предыдущей:
// сдвиг левого нижнего угла буквы от точки отметки
const UNIT_SKIP_MARK_LETTERS = [
  {char: 'z', fontSize: 13, dx: 0, dy: 6},
  {char: 'z', fontSize: 17, dx: 8, dy: 2},
  {char: 'Z', fontSize: 22, dx: 18, dy: -3},
];
// Силуэт пропустившего ход — чуть тусклее, но узнаваем
const UNIT_SKIP_SPRITE_ALPHA = 0.8;
// Размах покачивания отряда (renderUnit) — на столько же качается и «zzZ» над ним
const UNIT_BOB_AMPLITUDE_PX = 5;
// Списки подсветки ячеек, доступных выбранному отряду на этапах войны (см.
// getUnitActionCellsBounds): по ним автовыбор вписывает ход отряда в кадр
const UNIT_ACTION_CELL_MARKER_LISTS = [
  'movementMarkers',
  'maneuverMarkers',
  'shootingMarkers',
  'seductionMarkers',
  'goblinsAbilityMarkers',
];
// Тень летящего валуна — та же мягкая текстура, что у тени отряда (500 px в
// ширину, см. загрузку в GameScene)
const SHOT_SHADOW_TEXTURE_KEY = 'unit_shadow';
const SHOT_SHADOW_TEXTURE_WIDTH = 500;

// Тень жетона на ячейке: мягкий ореол под жетоном, шире самой ячейки. Одна
// картинка на все жетоны, построенные и запланированные
const TOKEN_SHADOW_TEXTURE_KEY = 'hex_shadow';
// У горы тень чуть меньше, чем у остальных жетонов. Основание у картинок гор
// разной формы (у каждой подложки гора своя), и из-под некоторых выглядывал
// край тени — а тень непрозрачная, и рядом с горой она читалась как темная
// кайма. Значение подобрано на глаз
const MOUNTAIN_SHADOW_SCALE_FACTOR = 0.96;
// Запланированные строения (план строительства в чужой ход, см.
// renderPlannedBuildings): обычные жетоны чуть выше настоящих на той же
// ячейке. От построенных их отличает иконка плана (updatePlannedBuildingMarkers)
const PLANNED_BUILDING_DEPTH_SHIFT = 0.005;
/**
 * Насколько выше центра ячейки рисуется заливка принадлежности города цветом
 * владельца — и постоянный слой (иконка bust в VisualToggles), и разовые
 * анимации: рост города после постройки, подсветка чужих городов на путях
 * влияния гильдий и храмов.
 *
 * Тоже отдельным именем: заливка повторяет форму гекса, а не картинку жетона,
 * и подобранная глазами величина у нее может оказаться своя.
 */
const CITY_OWNERSHIP_SHIFT_Y = SPECIAL_BUILDING_SHIFT_Y;
/**
 * Насколько выше центра ячейки проходят ломаные путей: пути влияния гильдий и
 * храмов и путь снабжения от фермы. Поднимаются целиком — и изломы ломаной, и
 * кружки на ее концах и на промежуточных фермах, — чтобы линия шла через центр
 * псевдо-3D плато жетона, а не через центр ячейки под ним.
 */
const PATH_SHIFT_Y = SPECIAL_BUILDING_SHIFT_Y;
/**
 * Насколько поднимается отряд, стоящий на жетоне (на пустой ячейке — нет), а
 * вместе с ним и весь его обвес: тень, контур, размер, шевроны, метки
 * ближнего боя, стрельбы, хода, снабжения. Жетоны в псевдо-3D подняты над
 * центром ячейки, и без этого отряд проваливался бы в строение под ним.
 * Своим именем, как и остальные смещения этого блока, — подбирается глазами.
 */
const UNIT_ON_BUILDING_SHIFT_Y = SPECIAL_BUILDING_SHIFT_Y;
/**
 * Оверлей зоны влияния кварталов лежит на ячейке двух разных видов, и ведет
 * себя в них по-разному.
 *
 * На ДОРОГЕ он ложится поверх ее жетона, поэтому едет вместе с ним — иначе
 * оверлей остался бы на старой линии сетки, а плато дороги ушло бы выше.
 *
 * На ПУСТОЙ ячейке подниматься нечему: там оверлей рисуется по центру ячейки,
 * зато уходит ПОД жетоны строений. Жетоны стали выше и свешиваются на ячейку
 * ниже, и без этого край оверлея перекрывал бы им низ.
 */
const QUARTER_ZONE_ON_ROAD_SHIFT_Y = 0;
// Слои жетона ячейки: сам жетон (TOKEN), его обводка (±0.01), флаг
// центрального квартала (+0.02) и спецстроение (TOKEN_SPECIAL_BUILDING). По
// глубине отличаем их от прочих маркеров ячейки — подписей дохода, подсветок,
// зоны кварталов (TOKEN - 0.1)
const checkIfHexTokenSprite = (marker) => typeof marker?.depth === 'number'
  && marker.depth >= LAYER_DEPTH.TOKEN - 0.05
  && marker.depth <= LAYER_DEPTH.TOKEN_SPECIAL_BUILDING + 0.05;

// Слои отряда: тень (UNIT - 0.2), контур (UNIT - 0.1), сам отряд (UNIT),
// подписи и индикаторы (до UNIT + 1). По глубине отличаем их от жетонов ячейки
// и маркеров поля
const checkIfUnitLayerSprite = (marker) => typeof marker?.depth === 'number'
  && marker.depth >= LAYER_DEPTH.UNIT - 0.5
  && marker.depth <= LAYER_DEPTH.UNIT + 1;

// Разгон и торможение отряда по пути: единственный сегмент — плавно с обоих
// концов, первый — плавный старт, последний — плавная остановка, середина пути
// равномерная. Общее для своего хода (animateUnitMovement) и для двойника
// чужого хода (showUnitMovementFlight), чтобы отряд соперника шел так же, как
// свой
const resolveMovementSegmentEase = (index, total) => {
  if (total === 1) {
    return 'Quad.easeInOut';
  }
  if (index === 0) {
    return 'Quad.easeIn';
  }
  if (index === total - 1) {
    return 'Quad.easeOut';
  }
  return 'Linear';
};

// На столько поднимаются слои летящего отряда (кроме тени, она остается на
// земле): он пролетает над маркерами и отрядами, а иконка — поверх своих крыльев
const WINGED_UNIT_DEPTH_RAISE = 4.8;

// Сколько ждем экшен, фиксирующий уже отыгранное рендерером перемещение: он
// приходит из колбэка самой анимации, то есть в той же синхронной задаче
const LOCAL_UNIT_MOVEMENT_ECHO_MS = 1000;

// Что именно прилетает на ячейку: от этого зависит, какие слои прятать на
// время полета (см. hideHexTokenSprites)
const DROP_KINDS = {
  TOKEN: 'token',
  SPECIAL: 'special',
  STARTING_QUARTER: 'startingQuarter',
};

const resolveDropKind = ({specialBuildingType, isStartingQuarter}) => {
  if (specialBuildingType) {
    return DROP_KINDS.SPECIAL;
  }
  return isStartingQuarter ? DROP_KINDS.STARTING_QUARTER : DROP_KINDS.TOKEN;
};

/**
 * Обрезать спрайт по контуру ячейки ИЗНУТРИ: внутри гекса он не рисуется, а
 * снаружи виден целиком. Так подсветка найма выходит из-под жетона, оставаясь
 * выше всего поля. Маски может не быть (старый рендерер, заглушки в тестах) —
 * тогда спрайт просто рисуется целиком
 */
const applyInvertedMask = (sprite, maskShape) => {
  const mask = maskShape?.createGeometryMask?.();
  if (!mask) {
    return;
  }

  mask.invertAlpha = true;
  sprite.setMask?.(mask);
};

const QUARTER_ZONE_EMPTY_CELL_DEPTH = LAYER_DEPTH.TOKEN - 0.1;
/**
 * Слой картинки пустой ячейки. Тоже НИЖЕ жетонов строений: подросшие
 * псевдо-3D жетоны свешиваются на ячейку ниже, и лежи картинка пустоты на
 * одном слое с ними — она бы срезала им низ (на равной глубине побеждает тот,
 * кого нарисовали позже, то есть порядок обхода ячеек).
 *
 * Прозрачность в саму картинку уже заложена (максимум альфы ~30%), поэтому
 * спрайту дополнительная не выставляется.
 */
const EMPTY_HEX_DEPTH = LAYER_DEPTH.TOKEN - 0.1;
// Толщина контура ячейки. Цвет контура зависит от подложки поля и берется из
// configs/groundBackgrounds (getHexGridColor)
const HEX_GRID_LINE_WIDTH = 4;

// Контур ячеек самой сетки. Выключен: поле читается по картинкам ячеек и
// жетонам, а сплошная сетка поверх них только дробила его. Обводки
// доступности, выбранной ячейки и прочих маркеров это не касается — они
// рисуются своими маркерами (drawHexOutlineMarker)
const HEX_GRID_OUTLINE_VISIBLE = false;

/**
 * Игроки, чей ход виден на поле по развевающимся флагам их центральных
 * кварталов. Обычно это один текущий игрок. В одновременной фазе снабжения
 * сетевой игры ходят все сразу (текущий игрок — лишь якорь хода), поэтому
 * машут флаги всех, кто еще не закончил свою часть фазы
 *
 * @param {Object} state
 * @returns {Object[]}
 */
export const getTurnFlagPlayers = (state) => {
  if (checkIfMatchOver(state)) {
    return [];
  }
  const sharedGameState = selectSharedGameState(state);
  if (checkIfParallelSupplyPhase(sharedGameState)) {
    return getNotCompletedPlayers(sharedGameState);
  }
  const currentPlayer = selectCurrentPlayer(state);
  return currentPlayer ? [currentPlayer] : [];
};

/**
 * Владельцы отрядов с белым контуром — отрядов, чья сейчас фаза. Обычно это
 * отряды текущего игрока. В одновременной фазе снабжения игроку, который еще
 * раздает снабжение, белым выделены только его отряды — те, которые он и
 * снабжает; закончившему игроку и зрителю — отряды всех, кто еще не
 * закончил (как флаги, см. getTurnFlagPlayers)
 *
 * @param {Object} state
 * @returns {string[]}
 */
export const getActiveUnitOwnerIds = (state) => {
  if (checkIfMatchOver(state)) {
    return [];
  }
  const sharedGameState = selectSharedGameState(state);
  if (checkIfParallelSupplyPhase(sharedGameState)) {
    const supplyPlayer = selectSupplyActingPlayer(state);
    return supplyPlayer
      ? [supplyPlayer.id]
      : getNotCompletedPlayers(sharedGameState).map((player) => player.id);
  }
  const currentPlayer = selectCurrentPlayer(state);
  return currentPlayer ? [currentPlayer.id] : [];
};

/**
 * Класс для отрисовки гексагональной сетки и жетонов
 */
export class HexGridRenderer {
  unitSpriteScales = {};

  constructor(scene, hexMath) {
    this.scene = scene;
    this.hexMath = hexMath;
    this.hexes = {};
    this.gridConfig = null;
    this.unitsLayer = null; // Слой для отрядов
    this.unitsSprites = {}; // Кэш спрайтов отрядов
    this.unitsVisible = true; // Видимость слоя отрядов
    this.isAnimatingMovement = false; // Флаг для отслеживания анимации перемещения
    this.currentMovementUnitId = null;
    this.currentMovementTargetHexKey = null;
    this.currentMovementOnComplete = null;
    // Твины цепочки перемещения хранятся отдельно от hex.animations (декоративных
    // твинов), которые clearTweens() безусловно останавливает на КАЖДОЕ обновление
    // стора — см. skipUnitMovementAnimation()/stopMovementTweens()
    this.movementTweens = [];
    // Позиции отрядов на прошлом store-апдейте (Map unitId -> hexKey) — дифф
    // для разовой анимации «ячейка заблокирована» (updateBlockedCellsPulse)
    this.prevUnitPositionsForBlockPulse = null;
    // Живые маркеры анимации роста города по ячейкам (см.
    // showCityGrowthAnimation): нужны, чтобы серия построек в один город не
    // копила полупрозрачные заливки друг на друге
    this.cityGrowthAnimationMarkers = new Map();
    // Подмена на время облета захваченных городов (см.
    // setCityCaptureOverrides) и настоящие владельцы ячеек с последней
    // перерисовки границы — по ним облет строит свои слои
    this.cityCaptureOverrides = null;
    this.cityBorderBaseOwners = new Map();
    // Живые анимации сети снабжения ферм по ключу (см.
    // showSupplyNetworkAnimation). У постройки и у постановки в план ключ —
    // ячейка фермы: у каждой своя анимация, друг друга они не гасят — при
    // применении плана ферм ставится сразу несколько. У осмотра ячейки ключ
    // один на все ячейки (см. playInspectedFarmSupplyNetworkAnimation)
    this.supplyNetworkAnimationGraphics = new Map();
    // Живые клубы пыли от размещения жетона по ячейкам (см.
    // showBuildingPlacementDust): как и соседние разовые анимации, они не
    // регистрируются на гексе — иначе их сносил бы clearMarkers() на первом же
    // обновлении стора
    this.buildingDustSprites = new Map();
    // Двойники прилетающих жетонов по ячейкам (см. showBuildingDropAnimation)
    this.buildingDropSprites = new Map();
    // Когда на ячейке закончится анимация размещения (полет жетона и разлет
    // пыли): до этого момента иконка действия строительства не показывается —
    // молоток поверх летящего жетона и клубов пыли только мешает
    this.placementAnimationEndAt = new Map();
    // Световые волны по ячейкам (см. showUnitRecruitmentPulse)
    this.recruitmentPulseSprites = new Map();
    // Всплывающие числа потраченных монет (см. showCoinsSpentNumber): в порядке
    // появления, над одной ячейкой их может идти несколько
    this.coinsSpentNumbers = new Set();
    // Спрайты брызг крови (см. showBloodSplatter): капли и пятна живут дольше
    // удара и анимацию боя не задерживают — убираются сами или при разборе поля
    this.bloodSprites = new Set();
    // Отряды, чьи спрайты спрятаны на время полета двойника: ключ — id отряда
    this.droppingUnitIds = new Map();
    // Идущие двойники отрядов по id отряда (см. showUnitMovementFlight): ход
    // соперника и запись матча меняют состояние ДО анимации, поэтому по полю
    // идет двойник, а настоящий отряд на это время спрятан. Полетов может идти
    // сразу несколько — по одному на отряд, они друг друга не гасят
    this.unitMovementFlights = new Map();
    // Идущие анимации выстрелов по id записи выстрела (см. showShotAnimation):
    // пока снаряд летит, итог этого выстрела на поле не показывается
    this.shotAnimations = new Map();
    // Идущие анимации ударов ближнего боя по своему id (см.
    // showMeleeAnimation): пока удары идут, итог боя на плашках отрядов
    // не показывается
    this.meleeAnimations = new Map();
    // До какого момента отряд занят толчком (рывок, отшатывание): ключ — id
    // отряда (см. pushUnitSprites)
    this.unitPushUntil = new Map();
    // Идущая красная вспышка спрайта отряда и его настоящий цвет (см.
    // flashUnitSprites). Ключ — сам спрайт: перерисовка поля пересоздает
    // спрайты, и запись о старом уходит вместе с ним
    this.unitFlashStates = new WeakMap();
    // Перемещение, которое рендерер только что отыграл сам (animateUnitMovement):
    // экшен из колбэка анимации не должен запустить тот же полет второй раз
    this.lastLocalUnitMovement = null;
    // Ячейки, чей настоящий жетон спрятан на время полета двойника: значение —
    // список спрятанных спрайтов, которым вернут видимость при приземлении.
    // Проверяется на КАЖДОЙ отрисовке жетона, иначе обновление стора посреди
    // полета показало бы жетон на месте раньше времени
    this.droppingHexKeys = new Map();
    // Блики на жетонах ключевых точек (см. TokenGlints): счет времени у
    // каждого жетона свой и живет там, а не в спрайте — спрайты пересоздаются
    // на каждой перерисовке. Здесь только собираем, где такие жетоны стоят
    // сейчас. Пока на ячейку летит двойник жетона, сам жетон спрятан — не блестит
    this.tokenGlints = new TokenGlints(scene, {
      checkIfTokenVisible: (hexKey) => !this.checkIfTokenHiddenByDrop(hexKey),
    });
    this.tokenGlintTargets = [];
    // Свечение руин в раунды с увеличенным доходом (см. RuinsIncomeGlow):
    // так же, как блики, живет вне спрайтов жетонов; здесь только собираем,
    // где стоят руины
    this.ruinsIncomeGlow = new RuinsIncomeGlow(scene);
    this.ruinsIncomeGlowTargets = [];
    // Руины, до которых уже дошла волна анимации роста дохода (см.
    // scenes/RuinsIncomeAnimation): до конца анимации светятся только они
    this.ruinsIncomeGlowRevealedKeys = new Set();
    this.isRuinsIncomeGlowAwaited = false;
    // Анимации способностей фракций (сердце соблазна, бомба и ловушка
    // гоблинов): живут поверх поля и переживают его перерисовки (см.
    // scenes/playAbilityCastEffects)
    this.unitSizeRecruitmentMarkerPulse = new UnitSizeRecruitmentMarkerPulse(scene);
    // Постоянные плашки дохода дышат в такт, переживая перерисовки поля
    this.incomeMarkerPulse = new UnitSizeRecruitmentMarkerPulse(scene, INCOME_MARKER.halo);
    this.incomeMarkerHalos = [];
    // Светящиеся нити путей влияния и снабжения
    this.glowingThreads = new GlowingThreads(scene, {linearFilter: Phaser.Textures?.FilterMode?.LINEAR});
    // Живые маркеры строений: повреждение, блокировка, шкала крепости
    this.liveFieldMarkers = new LiveFieldMarkers(scene, {linearFilter: Phaser.Textures?.FilterMode?.LINEAR});
    // Светящиеся обводки ячеек; волна света идет от выбранного отряда
    this.fieldCellOutlines = new FieldCellOutlines(scene, {linearFilter: Phaser.Textures?.FilterMode?.LINEAR});
    this.fieldRangeZone = new FieldRangeZone(scene, {
      linearFilter: Phaser.Textures?.FilterMode?.LINEAR,
      addBlendMode: Phaser.BlendModes?.ADD,
    });
    this.rangeZoneTargetHexKeys = [];
    // Флаги центральных кварталов: у ходящего игрока ткань живая
    this.fieldFlags = new FieldFlags(scene, {linearFilter: Phaser.Textures?.FilterMode?.LINEAR});
    // Контурное свечение отрядов активного игрока: дышит в такт его флагам
    this.activeUnitGlows = new ActiveUnitGlows(scene);
    this.outlineWaveOrigin = undefined;
    this.abilityCastAnimation = new AbilityCastAnimation(scene, {
      depths: {
        air: LAYER_DEPTH.ABILITY_CAST,
        ground: LAYER_DEPTH.ABILITY_CAST_GROUND,
        wings: LAYER_DEPTH.WINGED_FLIGHT_WINGS,
        wingsLanded: LAYER_DEPTH.WINGED_FLIGHT_LANDED,
      },
      linearFilter: Phaser.Textures?.FilterMode?.LINEAR,
      addBlendMode: Phaser.BlendModes?.ADD,
    });
    // Свой ход летающего отряда, который сейчас ведет план полета (см.
    // startWingedUnitMovement)
    this.wingedMovement = null;
    this.unitFootprints = new UnitFootprints(scene, {
      depths: {
        ground: LAYER_DEPTH.UNIT_FOOTPRINT_GROUND,
        prints: LAYER_DEPTH.UNIT_FOOTPRINT_PRINTS,
        air: LAYER_DEPTH.UNIT_FOOTPRINT_AIR,
      },
      addBlendMode: Phaser.BlendModes?.ADD,
      onQuake: (shake) => shakeFieldCamera(scene, shake),
    });
    this.buildingImpactEndTimes = [];
    this.quarterZoneBurn = new QuarterZoneBurn(scene, {
      emberDepth: LAYER_DEPTH.ABILITY_CAST_GROUND,
      linearFilter: Phaser.Textures?.FilterMode?.LINEAR,
      getCell: (cellKey) => this.hexes[cellKey],
      createHatch: (hex, spriteKey, isOnRoad) => this.createQuarterZoneSprite(hex, spriteKey, isOnRoad),
      returnCells: (cellKeys, state) => this.drawQuarterZoneCells(cellKeys, state),
    });
  }

  /**
   * Сгенерировать гексагональную сетку
   * @param {Object} gridConfig - Конфигурация сетки
   * @param hexCoordinatesVisible
   */
  generateHexGrid(gridConfig, hexCoordinatesVisible) {
    this.gridConfig = gridConfig;
    this.isHexClickabilityUnused = Boolean(window.matchMedia?.('(hover: none)').matches);
    // Сетка строится заново — порядок отрисовки надо пересчитать
    this.hexKeysTopToBottom = null;
    const {radius, hexSize, orientation} = gridConfig;

    this.hexMath.hexSize = hexSize;
    this.hexMath.orientation = orientation;

    for (let q = -radius; q <= radius; q++) {
      for (let r = -radius; r <= radius; r++) {
        const s = -q - r;

        if (Math.abs(s) <= radius) {
          const key = `${q},${r}`;

          const {x, y} = this.hexMath.hexToPixel(q, r);

          const graphics = this.scene.add.graphics();
          const points = this.hexMath.getHexPoints(x, y);

          // Отрисовка гексагона с улучшенным качеством
          // graphics.fillStyle(0x00ff00, 0.1);
          // graphics.lineStyle(0, 0x000000, 0); // Увеличиваем толщину линии

          // graphics.beginPath();
          // graphics.moveTo(points[0].x, points[0].y);
          // for (let i = 1; i < 6; i++) {
          //   graphics.lineTo(points[i].x, points[i].y);
          // }
          // graphics.closePath();
          // graphics.fillPath();
          // graphics.strokePath();

          // Включаем сглаживание для графики
          graphics.setPipeline('GraphicsPipeline');

          // Сам клик сцена находит по координатам (GameScene.handleClick), объект ячейки
          // ничего не рисует и нужен только ради курсора-руки. Поэтому ему нечего делать в
          // списке отрисовки: тысяча пустых объектов сортируется и обходится каждый кадр,
          // а любая постановка спрайта ищет в этом списке линейно
          graphics.removeFromDisplayList?.();
          // Без мыши нет и курсора: на телефоне иначе каждое движение пальца при
          // перетаскивании поля проверяло бы попадание в тысячу многоугольников
          if (!this.isHexClickabilityUnused) {
            graphics.setInteractive({
              hitArea: new Phaser.Geom.Polygon(points.map(p => [p.x, p.y]).flat()),
              hitAreaCallback: Phaser.Geom.Polygon.Contains,
              useHandCursor: true
            });
          }

          // Сохранение свойств гекса
          graphics.q = q;
          graphics.r = r;
          graphics.key = key;
          graphics.centerX = x;
          graphics.centerY = y;
          graphics.size = hexSize;
          graphics.points = points;

          // Координаты ячейки — только если они включены (см.
          // ensureHexCoordinateLabel)
          if (hexCoordinatesVisible) {
            this.ensureHexCoordinateLabel(graphics).forEach((label) => label.setVisible(true));
          }

          this.hexes[key] = graphics;
        }
      }
    }
  }

  /**
   * Подпись координат ячейки (подложка и текст) — создается при первом
   * включении координат, а не вместе с сеткой: координаты нужны только для
   * отладки, а у каждого текста Phaser свой canvas и своя текстура — на поле
   * в тысячу ячеек это тысяча скрытых текстур в видеопамяти
   * @param {Phaser.GameObjects.Graphics} hex
   * @returns {Array} подложка и текст
   */
  ensureHexCoordinateLabel(hex) {
    if (!hex.coordBg) {
      const coordBg = this.scene.add.sprite(hex.centerX, hex.centerY, 'size_rect');
      coordBg.setScale(0.15);
      coordBg.setDepth(9);
      coordBg.setAlpha(0.5);
      coordBg.setVisible(false);
      hex.coordBg = coordBg;
    }
    if (!hex.coordText) {
      const text = `${hex.q},${hex.r}`;
      const textStyle = {...COORDS_TEXT_STYLE};
      if (text.length > 5) {
        textStyle.fontSize = '50px';
      }
      const coordText = this.scene.add.text(hex.centerX, hex.centerY, text, textStyle);
      coordText.setScale(0.5);
      coordText.setOrigin(0.5, 0.5);
      coordText.setDepth(10); // Выше подложки
      coordText.setVisible(false);
      hex.coordText = coordText;
    }
    return [hex.coordBg, hex.coordText];
  }

  /**
   * Определяет, что отрисовывать для повреждённого строения
   */
  getBuildingRenderState(hexKey, tokenType, specialBuildingType, buildingsDamage, pendingShootingActions, phase, warPhaseStage, citiesCache, specialBuildings, hexes, pendingMeleeDamage = [], units = []) {
    if (!tokenType || tokenType === TOKENS.ROAD) {
      return {
        tokenType,
        specialBuildingType,
        showDamageOverlay: false,
        totalDamage: 0,
        appliedDamage: 0,
        pendingDamage: 0
      }
    }

    const isWarShootingStage = phase === PHASES.WAR && warPhaseStage === WAR_PHASE_STAGES.SHOOTING;
    const isWarCombatStage = phase === PHASES.WAR && warPhaseStage === WAR_PHASE_STAGES.COMBAT;

    let nextTokenType = tokenType
    let nextSpecialBuildingType = specialBuildingType
    let appliedDamage = buildingsDamage[hexKey] || 0;
    let pendingDamage = 0;
    let totalDamage = appliedDamage;
    let showDamageOverlay = false;

    const isDestructibleToken = !UNDESTRUCTIBLE_TOKENS.includes(tokenType)

    if (isWarCombatStage) {
      if (specialBuildingType === SPECIAL_BUILDING_TOKENS.FORTRESS) {
        const unitOnHex = units.find(u => u.hexKey === hexKey);
        // Пока по отряду идут удары, урона крепости под ним еще нет — как и
        // урона на его плашке (см. showMeleeAnimation)
        if (unitOnHex && !this.checkIfMeleeResultHeld(unitOnHex.id)) {
          const incomingDamage = (pendingMeleeDamage || []).reduce((sum, action) => {
            return sum + (action.damageDistribution?.[unitOnHex.id] || 0);
          }, 0);
          const fortressMeleeDamage = Math.floor(incomingDamage / FORTRESS_MELEE_DAMAGE_RATION);
          pendingDamage += fortressMeleeDamage;
        }

        const remainingFortressCapacity = FORTRESS_MAX_DAMAGE - appliedDamage;
        pendingDamage = Math.min(pendingDamage, remainingFortressCapacity)
      } else {
        showDamageOverlay = totalDamage > 0;
      }
    } else if (isWarShootingStage) {
      // const isCentralQuarter = checkIfCentralQuarter(tokenType)
      // if(isCentralQuarter && showDamageOverlay && !nextSpecialBuildingType) {
      //
      // }

      if (specialBuildingType === SPECIAL_BUILDING_TOKENS.FORTRESS) {
        let remainingFortressCapacity = FORTRESS_MAX_DAMAGE - appliedDamage;

        pendingShootingActions.forEach((action) => {
          if (checkIfUnitMeleeElf(getUnitById(units, action.shooterUnitId))) {
            return;
          }

          if (action.targetHexKey === hexKey) {
            if (remainingFortressCapacity > 0) {
              const fortressDamage = Math.min(action.damage, remainingFortressCapacity);
              pendingDamage += fortressDamage;
              remainingFortressCapacity -= fortressDamage
            } else if (!action.isCentralQuarterDamageIgnored) {
              pendingDamage += action.damage >= D4_SHOOTING_DAMAGE_FOR_1_BUILDING_DAMAGE ? 1 : 0;
            }
          }
        });

        totalDamage += pendingDamage;
        if (totalDamage >= FORTRESS_MAX_DAMAGE + 2) {
          nextSpecialBuildingType = null;
          nextTokenType = TOKENS.ROAD;
          showDamageOverlay = false;
        } else if (totalDamage >= FORTRESS_MAX_DAMAGE) {
          nextSpecialBuildingType = null;
          if (totalDamage > FORTRESS_MAX_DAMAGE) {
            if (isDestructibleToken) {
              showDamageOverlay = true;
            }
          }
        }
      } else if (specialBuildingType) {
        pendingDamage = pendingShootingActions.filter(
          action => action.targetHexKey === hexKey
            && action.damage >= D4_SHOOTING_DAMAGE_FOR_1_BUILDING_DAMAGE
            && !checkIfUnitMeleeElf(getUnitById(units, action.shooterUnitId))
        ).length;

        // Все специальные строения, кроме крепостей, строятся на базовых кварталах
        // Поэтому не проверяем дополнительно, что токен является разрушаемым
        totalDamage += pendingDamage;
        if (totalDamage >= 4) {
          nextSpecialBuildingType = null;
          nextTokenType = TOKENS.ROAD;
          showDamageOverlay = false;
        } else if (totalDamage >= 2) {
          nextSpecialBuildingType = null;
          if (totalDamage > 2) {
            showDamageOverlay = true;
          }
        } else {
          showDamageOverlay = totalDamage > 0;
        }
      } else if (isDestructibleToken) {
        pendingDamage = pendingShootingActions.filter(action =>
          action.targetHexKey === hexKey
          && action.damage >= D4_SHOOTING_DAMAGE_FOR_1_BUILDING_DAMAGE
          && !action.isCentralQuarterDamageIgnored
          && !checkIfUnitMeleeElf(getUnitById(units, action.shooterUnitId))
        ).length;

        totalDamage += pendingDamage;
        if (totalDamage >= 2) {
          nextTokenType = TOKENS.ROAD;
          showDamageOverlay = false;
        } else {
          showDamageOverlay = totalDamage > 0;
        }
      }
    } else {
      // Если на ячейке есть крепость, значит строение под крепостью еще не повреждено (вне этапа стрельбы)
      // Отдельная проверка для центрального квартала не нужна,
      // так как редьюсер APPLY_SHOOTING_ACTIONS гарантирует нанесение применение урона центральному кварталу
      // только после разрушения
      showDamageOverlay = specialBuildingType === SPECIAL_BUILDING_TOKENS.FORTRESS ? false : totalDamage > 0;
    }

    return {
      tokenType: nextTokenType,
      specialBuildingType: nextSpecialBuildingType,
      showDamageOverlay,
      totalDamage,
      appliedDamage,
      pendingDamage
    }
  }

  /**
   * Обновить отрисовку гексов на основе состояния
   * @param {Object} hexState - Состояние гексов из Redux store
   * @param state
   */
  updateHexes(hexState, state) {
    // Для getUnitShiftY — здесь, а не только в updateUnits: метка цели и линии
    // стрельбы рисуются в той же перерисовке РАНЬШЕ отрядов (см. порядок в
    // GameScene.handleStoreUpdate), и со старым состоянием ячеек построенное
    // или разрушенное только что здание подняло бы метку не на ту высоту
    this.unitHexState = hexState;
    // Жетоны с бликами и руины этой перерисовки (см. renderToken и конец метода)
    this.tokenGlintTargets = [];
    this.ruinsIncomeGlowTargets = [];
    this.incomeMarkerHalos = [];
    this.updateHexClickability(state);
    const isGameStarted = selectIsGameStarted(state);
    const incomeVisible = selectKeyPointIncomeVisible(state);
    const guildVisible = selectGuildVisible(state);
    const templeVisible = selectTempleVisible(state);
    const fortressVisible = selectFortressVisible(state);
    const fortressesUnderConstruction = selectFortressesUnderConstruction(state);
    const cityOwnershipVisible = selectCityOwnershipVisible(state);
    const blockedCellsVisible = selectBlockedCellsVisible(state);
    const specialBuildings = selectSpecialBuildings(state);
    const players = selectPlayers(state);
    const currentPlayer = selectCurrentPlayer(state);
    const goblinsTraps = selectGoblinsTraps(state);
    const hexes = selectHexes(state);
    // Постройку могли отменить, пока жетон летел: двойник снятого строения
    // висел бы над ячейкой до конца анимации
    this.dropCancelledBuildingFlights(
      hexes, specialBuildings, selectConstructionPlanEntries(state), this.getStartingQuarterOverlay(state)
    );
    // Store-кэш городов пересчитывается только на COMPLETE_PHASE — в течение
    // одного хода строительства он не видит города/кварталы, расширенные
    // более ранними действиями этого же хода (см.
    // playNearbyGuildsTemplesIncomeChangeAnimation). Считаем заново из
    // актуальных hexes/specialBuildings, а не читаем устаревший стор
    const citiesCache = calculateCitiesCache(hexes, specialBuildings);
    const units = selectUnits(state);
    // Перемещение могли отменить, пока отряд шел: двойник довел бы его до
    // ячейки, с которой отряд уже вернули
    this.dropCancelledUnitMovementFlights(units);
    const round = selectRound(state);
    const ruinsIncomeRoundLimit = selectRuinsIncomeRoundLimit(state);
    const buildingsDamage = selectBuildingsDamage(state);
    // Залп мог зависнуть (вкладка была скрыта): итог его выстрела пора
    // показать. А залпу из «будущего» (запись перемотали к началу) на этом
    // поле делать нечего
    this.dropStaleShotAnimations(round);
    // То же у ударов ближнего боя; а бой, которого в состоянии уже нет (этап
    // завершили, урон применили), доигрывать не на ком
    this.dropStaleMeleeAnimations(state, round);
    // Поврежденное выстрелом строение показывается с момента попадания: пока
    // снаряд летит, его выстрел в отрисовку не идет (см. showShotAnimation)
    const pendingShootingActions = this.filterLandedShootingActions(selectPendingShootingActions(state));
    const pendingMeleeDamage = selectPendingMeleeDamage(state);
    const phase = selectPhase(state);
    const warPhaseStage = selectWarPhaseStage(state);
    const isPhaseSetup = phase === PHASES.SETUP;

    // Предвычисляем hexKeys ферм и казарм текущего игрока для полупрозрачности крепостей.
    // Крепость мерцает над фермами, чей остаток снабжения показан на поле
    // (см. calculateTokenImage): в поочередной фазе — текущего игрока, в
    // одновременной — самого пользователя, а зрителю — всех игроков
    const playerFarmHexKeys = new Set();
    const playerBarracksHexKeys = new Set();
    if (currentPlayer && citiesCache) {
      const playerCities = citiesCache[currentPlayer.color] || [];
      playerCities.forEach(city => {
        (city[COMMON_BUILDING_TOKENS.BARRACKS] || []).forEach(hk => playerBarracksHexKeys.add(hk));
      });
    }
    // Во время облета захваченных городов снабжение на поле не показывается
    // (checkIfSupplyShownOnField) — и крепости на фермах не оживают
    if (checkIfSupplyShownOnField(state) && citiesCache) {
      getSupplyPreviewPlayers(state).forEach((farmsPlayer) => {
        (citiesCache[farmsPlayer.color] || []).forEach(city => {
          (city[COMMON_BUILDING_TOKENS.FARM] || []).forEach(hk => playerFarmHexKeys.add(hk));
        });
      });
    }

    // Цвет сетки подбирается под подложку поля — светлее на темном пепле и
    // пекле, темнее на зеленой равнине. Считается один раз на перерисовку, а
    // не на ячейку: подложка выбрана сценой при загрузке и в течение партии
    // не меняется
    const gridColor = getHexGridColor(this.scene.groundBackgroundId);

    // Запланированные строения игрока по ячейкам (план строительства в чужой
    // ход) — рисуются жетонами в общем обходе ниже
    const plannedEntriesByHexKey = this.getPlannedEntriesByHexKey(state);

    this.getHexKeysTopToBottom().forEach((key) => {
      const hex = this.hexes[key];
      // Захваченный город, до которого облет еще не дошел, — с флагом
      // прежнего владельца (см. setCityCaptureOverrides)
      const capturedTokenOverride = this.cityCaptureOverrides?.tokenByHexKey.get(key);
      const tokenType = capturedTokenOverride && checkIfCentralQuarter(hexState[key])
        ? capturedTokenOverride
        : hexState[key];

      // Очищаем спрайт специального строения
      if (hex.specialBuildingSprite) {
        hex.specialBuildingSprite.destroy();
        hex.specialBuildingSprite = null;
      }

      // Очищаем слой принадлежности городов
      if (hex.cityOwnershipGraphics) {
        hex.cityOwnershipGraphics.forEach(graphic => graphic.destroy());
        hex.cityOwnershipGraphics = [];
      }

      // Контур ячейки и картинка пустой ячейки — один раз, а не на каждой
      // перерисовке (см. syncHexGroundLayer)
      this.syncHexGroundLayer(hex, tokenType === TERRAIN_TOKENS.SEA, gridColor);

      // Определяем состояние отрисовки с учётом повреждений строений
      const specialBuildingType = specialBuildings && specialBuildings[key];
      const {
        tokenType: renderTokenType,
        specialBuildingType: renderSpecialBuildingType,
        showDamageOverlay,
        appliedDamage,
        pendingDamage,
        totalDamage
      } = this.getBuildingRenderState(key, tokenType, specialBuildingType, buildingsDamage, pendingShootingActions, phase, warPhaseStage, citiesCache, specialBuildings, hexes, pendingMeleeDamage, units);

      // Отрисовка жетона, если ячейка не пустая. Море не рисуется НИКОГДА:
      // оно лишь помечает неигровую часть сетки, а видно там подложку-пейзаж
      // (см. createGroundBackground в GameScene). Раньше с вероятностью
      // WAVE_CHANCE на морской ячейке выпадала волна — теперь не выпадает
      if (renderTokenType && renderTokenType !== TERRAIN_TOKENS.SEA) {
        this.renderToken(key, hex, renderTokenType, state, isGameStarted, isPhaseSetup);
      }

      // Отрисовка специального строения поверх основного токена
      const shouldRenderSpecialBuilding = renderSpecialBuildingType && !(renderSpecialBuildingType === SPECIAL_BUILDING_TOKENS.FORTRESS && !fortressVisible);
      if (shouldRenderSpecialBuilding) {
        let isAnimated = false;
        let spriteKey = renderSpecialBuildingType;
        if (renderSpecialBuildingType === SPECIAL_BUILDING_TOKENS.FORTRESS) {
          if (phase === PHASES.SUPPLY && playerFarmHexKeys.has(key)) {
            isAnimated = true;
          } else if (phase === PHASES.WAR && warPhaseStage === WAR_PHASE_STAGES.RECRUITMENT && playerBarracksHexKeys.has(key)) {
            isAnimated = true;
          }
          if (fortressesUnderConstruction?.includes(key)) {
            spriteKey = 'fortress_construction';
          }
        }
        this.renderSpecialBuilding(key, hex, spriteKey, isAnimated);

        if (incomeVisible && INCOME_TYPE_MAPPING[renderSpecialBuildingType]) {
          this.renderSpecialBuildingIncome(key, hex, renderSpecialBuildingType, players, citiesCache, units, hexes, specialBuildings, buildingsDamage, currentPlayer, false, false);
        }
        if (guildVisible && renderSpecialBuildingType === SPECIAL_BUILDING_TOKENS.GUILD) {
          this.renderSpecialBuildingIncome(key, hex, renderSpecialBuildingType, players, citiesCache, units, hexes, specialBuildings, buildingsDamage, currentPlayer, false, true);
        }
        if (templeVisible && renderSpecialBuildingType === SPECIAL_BUILDING_TOKENS.TEMPLE) {
          this.renderSpecialBuildingIncome(key, hex, renderSpecialBuildingType, players, citiesCache, units, hexes, specialBuildings, buildingsDamage, currentPlayer, false, true);
        }
      }

      // Запланированные строения — поверх настоящего содержимого ячейки и в
      // том же обходе сверху вниз: так жетоны перекрывают соседей в том же
      // порядке, что и построенные
      const plannedEntries = plannedEntriesByHexKey.get(key);
      if (plannedEntries) {
        this.renderPlannedBuildings(key, hex, plannedEntries, tokenType, specialBuildingType, {fortressVisible, state});
      }

      // Отрисовка повреждений строений
      if (renderSpecialBuildingType === SPECIAL_BUILDING_TOKENS.FORTRESS && fortressVisible && totalDamage > 0) {
        this.renderFortressDamage(hex, appliedDamage, pendingDamage);
      } else if (tokenType && showDamageOverlay) {
        this.renderBuildingDamage(hex, pendingDamage);
      }

      // Отрисовка дохода ключевых точек (рудники, руины)
      // Плашка руин показала бы новый доход раньше анимации его роста
      if (incomeVisible && INCOME_TYPE_MAPPING[renderTokenType]
        && !(checkIfRuinsIncomeGlowToken(renderTokenType) && selectIsRuinsIncomeAnimationAwaited(state))) {
        this.renderKeyPointIncome(key, hex, renderTokenType, players, citiesCache, hexes, units, round, specialBuildings, buildingsDamage, ruinsIncomeRoundLimit);
      }

      if (incomeVisible && (checkIfAnyQuarter(renderTokenType))) {
        const isBonusHegemosismHexKey = isHegemonismBonusHexKey(key);
        if (isBonusHegemosismHexKey) {
          this.renderHegemonismQuarterIncome(key, hex, renderTokenType, players, citiesCache, hexes, units, round, specialBuildings, buildingsDamage);
        }
      }

      if (incomeVisible
        && renderTokenType === TOKENS.BARRACKS
      ) {
        this.renderTotalitarismMilitaryBuildingIncome(key, hex, renderTokenType, players, citiesCache, hexes, units, round, specialBuildings, buildingsDamage);
      }
      if (incomeVisible
        && (renderSpecialBuildingType === SPECIAL_BUILDING_TOKENS.ARSENAL
          || renderSpecialBuildingType === SPECIAL_BUILDING_TOKENS.MILITARY_CAMP
        )
      ) {
        this.renderTotalitarismMilitaryBuildingIncome(key, hex, renderSpecialBuildingType, players, citiesCache, hexes, units, round, specialBuildings, buildingsDamage);
      }
    });

    // Отрисовка предварительно выбранного стартового квартала текущего игрока в фазе SETUP
    const startingQuarterOverlay = this.getStartingQuarterOverlay(state);
    if (startingQuarterOverlay) {
      const hex = this.hexes[startingQuarterOverlay.hexKey];
      if (hex) {
        // Отрисовываем токен центрального квартала поверх флага
        this.renderToken(
          startingQuarterOverlay.hexKey, hex, startingQuarterOverlay.tokenType, state, isGameStarted, isPhaseSetup,
          {isStartingQuarterOverlay: true}
        );
      }
    }

    // Отрисовка слоя заблокированных ячеек
    if (blockedCellsVisible) {
      this.addBlockedCellsMarkers(citiesCache, players, units, hexes, specialBuildings, buildingsDamage);
    }

    // Ловушки Гоблинов - видны ТОЛЬКО владельцу (см. add-goblins-faction
    // design.md Decision 1): проверка по actingPlayer (реальный локальный
    // зритель этого клиента), а не по currentPlayer (чей сейчас ход)
    if (goblinsTraps.length > 0) {
      const actingPlayerForTraps = getActingPlayerFromState(state);
      this.addGoblinsTrapMarkers(goblinsTraps, actingPlayerForTraps, hexes);
    }

    // Граница владений - по полю таким, каким оно нарисовано: строения
    // текущего хода строительства (pendingBuildings) уже стоят в hexes, а пока
    // план строительства на поле - и запланированные (renderPlannedBuildings)
    this.addCityBorderMarkers(selectFieldWithConstructionPlan(state).citiesCache || citiesCache);

    // Отрисовка слоя принадлежности городов
    if (cityOwnershipVisible) {
      this.addCityOwnershipMarkers(citiesCache);
    }

    this.incomeMarkerPulse.setHalos(this.incomeMarkerHalos);
    // Блики на жетонах: где жетоны стоят после этой перерисовки
    this.tokenGlints.sync(this.tokenGlintTargets);
    // Руины светятся, пока приносят увеличенный доход; вне этих раундов
    // пустой список гасит свечение
    this.isRuinsIncomeGlowAwaited = selectIsRuinsIncomeAnimationAwaited(state);
    this.ruinsIncomeGlow.sync(
      checkIfRoundWithIncreasedRuinsIncome(round, ruinsIncomeRoundLimit) ? this.getShownRuinsIncomeGlowTargets() : []
    );
  }

  /** Руины светятся все — или, пока ждем анимацию роста дохода, только те, что она уже зажгла */
  getShownRuinsIncomeGlowTargets() {
    return this.isRuinsIncomeGlowAwaited
      ? this.ruinsIncomeGlowTargets.filter(({hexKey}) => this.ruinsIncomeGlowRevealedKeys.has(hexKey))
      : this.ruinsIncomeGlowTargets;
  }

  /** Анимация роста дохода начинается: руины снова ждут ее волну */
  resetRuinsIncomeGlowReveal() {
    this.ruinsIncomeGlowRevealedKeys = new Set();
  }

  /**
   * Волна анимации роста дохода дошла до руин: они загораются на месте, поле
   * не пересобирается
   * @param {string} hexKey
   */
  revealRuinsIncomeGlow(hexKey) {
    this.ensureFieldCurrent();
    this.ruinsIncomeGlowRevealedKeys.add(hexKey);
    this.ruinsIncomeGlow.sync(this.getShownRuinsIncomeGlowTargets());
  }

  /**
   * Основа ячейки под жетонами: контур и картинка пустой ячейки. Зависят они
   * только от того, море ли ячейка, и от подложки сцены, а это не меняется,
   * пока не загрузят другое поле. Поэтому рисуются один раз и живут вне
   * маркеров ячейки: раньше перерисовка поля каждый раз заново обводила
   * контуры и пересоздавала сотни спрайтов пустых ячеек — заметная доля ее
   * времени (уничтожение объекта сцены обходится дороже создания).
   *
   * Только контур, без заливки: под жетоном ее все равно не видно, а на
   * пустых ячейках лежит картинка (empty_hex).
   *
   * ВАЖНО: заливки нет — значит и fillPath() звать НЕЛЬЗЯ. Phaser держит
   * текущий цвет заливки не в самом объекте Graphics, а в конвейере отрисовки
   * (GraphicsWebGLRenderer: fillTint = pipeline.fillTint), и команда
   * FILL_STYLE меняет его на общий для всех. Объект, который заливает путь,
   * ни разу не задав цвет, красится тем, что оставил предыдущий, — и пустые
   * ячейки перекрашивались то в цвет игрока (оверлей принадлежности города),
   * то в желтый и серый (пути влияния гильдии и храма), смотря что нарисовали
   * перед ними в этом кадре.
   *
   * Картинка пустой ячейки лежит под всеми ячейками, КРОМЕ моря: у него своя
   * картинка на всю ячейку. Слой у нее ниже жетонов (EMPTY_HEX_DEPTH), поэтому
   * на занятых ячейках ее и не видно, зато ячейка не проваливается в голый
   * фон, когда жетон над ней на миг скрыт: так бывает, пока жетон прилетает
   * сверху (см. showBuildingDropAnimation)
   * @param {Phaser.GameObjects.Graphics} hex
   * @param {boolean} isSea
   * @param {number} gridColor - цвет контура под подложку
   */
  syncHexGroundLayer(hex, isSea, gridColor) {
    const groundLayerKey = isSea ? 'sea' : `land|${gridColor}|${this.scene.groundBackgroundId}`;
    if (hex.groundLayerKey === groundLayerKey) {
      return;
    }
    hex.groundLayerKey = groundLayerKey;

    hex.clear();
    if (HEX_GRID_OUTLINE_VISIBLE && !isSea) {
      hex.lineStyle(HEX_GRID_LINE_WIDTH, gridColor);
      hex.beginPath();
      hex.moveTo(hex.points[0].x, hex.points[0].y);
      for (let i = 1; i < 6; i++) {
        hex.lineTo(hex.points[i].x, hex.points[i].y);
      }
      hex.closePath();
      hex.strokePath();
    }

    hex.emptyHexSprite?.destroy();
    hex.emptyHexSprite = null;
    if (!isSea) {
      const emptySprite = this.scene.add.sprite(hex.centerX, hex.centerY, 'empty_hex');
      emptySprite.setScale(TOKEN_SCALE);
      emptySprite.setDepth(EMPTY_HEX_DEPTH);
      emptySprite.setOrigin(0.5, 0.5);
      // На темных подложках картинка плотнее — иначе сливается с фоном
      emptySprite.setAlpha(getEmptyHexAlpha(this.scene.groundBackgroundId));
      hex.emptyHexSprite = emptySprite;
    }
  }

  renderBuildingDamage(hex, pendingDamage) {
    // Живой маркер (см. LiveFieldMarkers): медальон с пламенем, дымом и искрами
    const damageMarker = this.liveFieldMarkers.createDamageMarker({
      x: hex.centerX + BUILDING_DAMAGE_MARKER_SHIFT_X,
      y: hex.centerY + MARKER_SHIFT_Y,
      hexSize: hex.size,
      depth: BUILDING_STATE_MARKER_DEPTH,
    });
    this.registerMarker(hex, damageMarker);
    if (pendingDamage > 0) {
      this.addMarkerFadingTween(hex, damageMarker);
    }
  }

  renderFortressDamage(hex, appliedDamage = 0, pendingDamage = 0) {
    // Шкала едет вместе с самой крепостью: отстань она на несколько пикселей —
    // метки здоровья повисли бы ниже строения, к которому относятся
    const {container, pendingPips} = this.liveFieldMarkers.createFortressGauge({
      x: hex.centerX,
      y: hex.centerY + SPECIAL_BUILDING_SHIFT_Y,
      hexSize: hex.size,
      depth: BUILDING_STATE_MARKER_DEPTH,
      maxDamage: FORTRESS_MAX_DAMAGE,
      appliedDamage,
      pendingDamage,
    });
    this.registerMarker(hex, container);
    // Отложенный урон мигает в такт остальным отложенным маркерам
    pendingPips.forEach((pip) => this.addMarkerFadingTween(hex, pip));
  }

  /**
   * Заливка гекса по его вершинам (стиль заливки задается вызывающим кодом)
   * @param {Phaser.GameObjects.Graphics} graphics
   * @param {Phaser.GameObjects.Graphics} hex - объект гексагона
   */
  /**
   * Залить шестиугольник ячейки.
   * @param graphics
   * @param hex
   * @param {number} [shiftY] - на сколько поднять заливку относительно сетки
   */
  /**
   * Ключи ячеек в порядке отрисовки — сверху вниз по экрану.
   *
   * Жетоны нарисованы в псевдо-3D: картинка выше самой ячейки и вылезает за
   * ее верхнюю границу, то есть перекрытие бывает только по вертикали и
   * только с соседом СВЕРХУ. Глубина у всех жетонов одинаковая, поэтому
   * побеждает нарисованный позже — значит нижние ячейки должны идти после
   * верхних. Решать это глубиной по типу жетона нельзя: жетон перекрывал бы
   * строения снизу либо, наоборот, прятался под строениями сверху. Гора
   * поднята над остальными жетонами (см. MOUNTAIN_DEPTH_OFFSET) только потому,
   * что вниз за свою ячейку ее картинка не свешивается; порядок между горами
   * задает все тот же обход.
   *
   * Порядок считается один раз на сетку: она не меняется до пересоздания.
   */
  getHexKeysTopToBottom() {
    if (!this.hexKeysTopToBottom) {
      this.hexKeysTopToBottom = Object.keys(this.hexes).sort((leftKey, rightKey) => {
        const left = this.hexes[leftKey];
        const right = this.hexes[rightKey];
        // При равном Y порядок задается по X — лишь бы он был устойчивым:
        // соседи по одной строке друг друга не перекрывают
        return (left.centerY - right.centerY) || (left.centerX - right.centerX);
      });
    }

    return this.hexKeysTopToBottom;
  }

  fillHexShape(graphics, hex, shiftY = 0) {
    graphics.beginPath();
    graphics.moveTo(hex.points[0].x, hex.points[0].y + shiftY);
    for (let i = 1; i < 6; i++) {
      graphics.lineTo(hex.points[i].x, hex.points[i].y + shiftY);
    }
    graphics.closePath();
    graphics.fillPath();
  }

  /**
   * Постоянный слой принадлежности (иконка bust в VisualToggles): ячейки
   * городов заливаются цветом владельца.
   *
   * Заливаются только города ИГРОКОВ. Ничейные ячейки и нейтральные города
   * остаются как есть: белая заливка поверх первых и черная поверх вторых
   * ничего не сообщали — «не твое и не соперника» видно и без них, — зато
   * закрывали местность, рудники и застройку нейтральных городов.
   * @param {Object} citiesCache - города по цвету владельца
   */
  addCityOwnershipMarkers(citiesCache) {
    Object.entries(citiesCache).forEach(([playerColor, cities]) => {
      if (playerColor === NEUTRAL_PLAYER_COLOR) {
        return;
      }

      const playerColor0x = getPlayer0xColor(playerColor);
      cities.forEach(city => {
        getCityHexKeys(city).forEach(hexKey => {
          const hex = this.hexes[hexKey];
          if (!hex) return;

          // Рисуем цветной гекс принадлежности игрока
          const ownershipGraphics = this.scene.add.graphics();
          ownershipGraphics.fillStyle(playerColor0x, CITY_OWNERSHIP_FILL_ALPHA);
          this.fillHexShape(ownershipGraphics, hex, CITY_OWNERSHIP_SHIFT_Y);
          ownershipGraphics.setDepth(LAYER_DEPTH.CITY_OWNERSHIP);

          this.registerMarker(hex, ownershipGraphics);
        });
      })
    });
  }

  /**
   * Граница владений каждого игрока: замкнутое свечение цвета игрока по
   * периметру всех его городов. У каждой ячейки города свечение рисуется
   * только по тем сторонам, за которыми соседняя ячейка НЕ принадлежит тому
   * же игроку (ничья, чужая, море или край поля); между ячейками одного
   * игрока - даже разных его городов - границы нет.
   * @param {Object} citiesCache - города по цвету игрока на поле таким, каким
   *   оно нарисовано: с постройками текущего хода и запланированными
   */
  addCityBorderMarkers(citiesCache) {
    const ownerColorByHexKey = new Map();
    Object.entries(citiesCache || {}).forEach(([playerColor, cities]) => {
      cities.forEach((city) => {
        getCityHexKeys(city).forEach((hexKey) => ownerColorByHexKey.set(hexKey, playerColor));
      });
    });
    this.cityBorderBaseOwners = new Map(ownerColorByHexKey);

    // На время облета захваченных городов: еще не показанные города — у
    // прежних владельцев, а ячейки текущего города и их соседей рисуют слои
    // анимации (см. setCityCaptureOverrides)
    const overrides = this.cityCaptureOverrides;
    overrides?.ownerColorByHexKey.forEach((playerColor, hexKey) => ownerColorByHexKey.set(hexKey, playerColor));

    ownerColorByHexKey.forEach((playerColor, hexKey) => {
      const hex = this.hexes[hexKey];
      if (!hex || overrides?.hiddenBorderHexKeys.has(hexKey)) return;
      this.addCityBorderCellMarker(hex, playerColor, ownerColorByHexKey);
    });
  }

  /** Граница владений одной ячейки — маркер ячейки с пометкой (см. redrawCityBorderCells) */
  addCityBorderCellMarker(hex, playerColor, ownerColorByHexKey) {
    const borderGraphics = this.scene.add.graphics();
    borderGraphics.setDepth(CITY_CAPTURE_LAYER_DEPTHS.border);
    this.drawCityBorderCell(borderGraphics, hex, playerColor, ownerColorByHexKey);
    borderGraphics.cityBorderHexKey = hex.key;
    this.registerMarker(hex, borderGraphics);
    return borderGraphics;
  }

  /** Маркеры границы владений на ячейке (addCityBorderCellMarker) */
  getCityBorderCellMarkers(hex) {
    return (hex?.markers || []).filter((marker) => marker?.cityBorderHexKey === hex.key);
  }

  /**
   * Спрятать или показать границу владений на ячейках, не пересобирая поле:
   * облет захваченного города рисует их своими слоями (см.
   * setCityCaptureOverrides, hiddenBorderHexKeys)
   * @param {string[]} hexKeys
   * @param {boolean} isVisible
   */
  setCityBorderCellsVisible(hexKeys, isVisible) {
    hexKeys.forEach((hexKey) => {
      this.getCityBorderCellMarkers(this.hexes[hexKey]).forEach((marker) => marker.setVisible?.(isVisible));
    });
  }

  /**
   * Перерисовать границу владений на ячейках по владельцам последней
   * перерисовки поля и подмене облета, не пересобирая все поле: облет
   * показал город — его ячейки и соседей снова рисует обычная граница, уже
   * за нового владельца. Владельцы остальных ячеек от этого не меняются, а
   * граница ячейки зависит только от владельцев ее самой и соседей.
   * Новые маркеры встают в списке отрисовки на места прежних
   * @param {string[]} hexKeys
   */
  redrawCityBorderCells(hexKeys) {
    const ownerColorByHexKey = new Map(this.cityBorderBaseOwners || []);
    const overrides = this.cityCaptureOverrides;
    overrides?.ownerColorByHexKey.forEach((playerColor, hexKey) => ownerColorByHexKey.set(hexKey, playerColor));

    hexKeys.forEach((hexKey) => {
      const hex = this.hexes[hexKey];
      if (!hex) {
        return;
      }
      const oldMarkers = this.getCityBorderCellMarkers(hex);
      const playerColor = ownerColorByHexKey.get(hexKey);
      if (playerColor && !overrides?.hiddenBorderHexKeys.has(hexKey)) {
        const borderGraphics = this.addCityBorderCellMarker(hex, playerColor, ownerColorByHexKey);
        if (oldMarkers[0]) {
          borderGraphics.setAbove?.(oldMarkers[0]);
        }
      }
      const oldMarkersSet = new Set(oldMarkers);
      hex.markers = (hex.markers || []).filter((marker) => !oldMarkersSet.has(marker));
      oldMarkers.forEach((marker) => marker.destroy?.());
    });
  }

  /**
   * Граница владений одной ячейки игрока `playerColor` по владельцам всех
   * ячеек `ownerColorByHexKey` — общая для обычной границы и слоев облета
   * захваченных городов: после облета обычная граница встает на место слоя
   * анимации без скачка
   */
  drawCityBorderCell(graphics, hex, playerColor, ownerColorByHexKey) {
    const color0x = getPlayer0xColor(playerColor);
    // Тон владельца — первым, под трубкой границы. Та же графика, что у
    // границы: лишних объектов на поле нет, а слои облета захваченного города
    // (createCityBorderCellsGraphics) несут тон вместе с границей
    if (playerColor !== NEUTRAL_PLAYER_COLOR) {
      graphics.fillStyle(color0x, CITY_TERRITORY_TINT_ALPHA);
      graphics.fillPoints(hex.points.map((point) => ({x: point.x, y: point.y + CITY_BORDER_SHIFT_Y})), true);
    }

    const neighborOwners = this.getHexEdgeNeighborKeys(hex).map((neighborKey) => ownerColorByHexKey.get(neighborKey));
    const borderEdges = neighborOwners.map((owner) => owner !== playerColor);
    // Ореол наружу - только на ничьи ячейки и за край поля: на стороне
    // между двумя игроками каждый светит лишь внутрь своей ячейки, и цвета
    // встречаются встык, не смешиваясь
    const haloEdges = neighborOwners.map((owner) => owner === undefined);
    const innerLineEdges = borderEdges.map((isBorder) => !isBorder);

    this.drawCityBorderGlow(graphics, hex, {borderEdges, haloEdges, innerLineEdges}, color0x);
  }

  /**
   * Подмена на время облета захваченных городов (см. scenes/CityCaptureAnimation
   * и buildCityCaptureOverrides): состояние уже «после захвата», а на поле
   * города, до которых облет не дошел, показаны прежними владельцами
   * @param {{ownerColorByHexKey: Map, tokenByHexKey: Map, hiddenBorderHexKeys: Set}|null} overrides
   */
  setCityCaptureOverrides(overrides) {
    this.cityCaptureOverrides = overrides || null;
  }

  /** Настоящие владельцы ячеек с последней перерисовки границы (без подмены) */
  getCityBorderBaseOwners() {
    this.ensureFieldCurrent();
    return this.cityBorderBaseOwners;
  }

  /**
   * Граница владений набора ячеек одним объектом — слой облета захваченного
   * города (версия до или после захвата)
   * @param {string[]} hexKeys
   * @param {Map<string, string>} ownerColorByHexKey
   */
  createCityBorderCellsGraphics(hexKeys, ownerColorByHexKey) {
    const graphics = this.scene.add.graphics();
    graphics.setDepth(CITY_CAPTURE_LAYER_DEPTHS.border);
    hexKeys.forEach((hexKey) => {
      const hex = this.hexes[hexKey];
      const playerColor = ownerColorByHexKey.get(hexKey);
      if (hex && playerColor) {
        this.drawCityBorderCell(graphics, hex, playerColor, ownerColorByHexKey);
      }
    });
    return graphics;
  }

  /**
   * Заливка ячеек цветом владельца для облета захваченного города. Без
   * `graphics` — новый объект на глубине заливки; с ним — заливка в готовый
   * (форма маски огня). `scale` раздувает шестиугольник от центра ячейки
   * @param {string[]} hexKeys
   * @param {number} color0x
   * @param {number} alpha
   * @param {{graphics?: Object, scale?: number}} [options]
   */
  fillCityCaptureCells(hexKeys, color0x, alpha, {graphics = null, scale = 1} = {}) {
    const target = graphics || this.scene.add.graphics();
    if (!graphics) {
      target.setDepth(CITY_CAPTURE_LAYER_DEPTHS.fill);
    }
    target.fillStyle(color0x, alpha);
    hexKeys.forEach((hexKey) => {
      const hex = this.hexes[hexKey];
      if (!hex) {
        return;
      }
      const scaled = scale === 1 ? hex : {
        points: hex.points.map((point) => ({
          x: hex.centerX + (point.x - hex.centerX) * scale,
          y: hex.centerY + (point.y - hex.centerY) * scale,
        })),
      };
      this.fillHexShape(target, scaled, CITY_OWNERSHIP_SHIFT_Y);
    });
    return target;
  }

  /**
   * Ключи соседних ячеек по сторонам гекса: i-й элемент - сосед за стороной
   * hex.points[i] -> hex.points[i + 1] (null - за краем поля). Сосед
   * находится геометрически (его центр - отражение центра гекса через
   * середину стороны), а не по порядку HEX_DIRECTIONS: так соответствие не
   * зависит от ориентации сетки и порядка вершин. Сетка до пересоздания не
   * меняется - результат кэшируется на самом гексе.
   * @param {Object} hex
   * @returns {(string|null)[]}
   */
  getHexEdgeNeighborKeys(hex) {
    if (hex.edgeNeighborKeys) return hex.edgeNeighborKeys;

    const neighbors = getNeighborHexKeys(hex.key)
      .map((key) => this.hexes[key])
      .filter(Boolean);

    hex.edgeNeighborKeys = hex.points.map((point, i) => {
      const nextPoint = hex.points[(i + 1) % 6];
      const expectedX = point.x + nextPoint.x - hex.centerX;
      const expectedY = point.y + nextPoint.y - hex.centerY;
      const neighbor = neighbors.find((candidate) => (
        Math.hypot(candidate.centerX - expectedX, candidate.centerY - expectedY) < hex.size / 2
      ));
      return neighbor ? neighbor.key : null;
    });

    return hex.edgeNeighborKeys;
  }

  /**
   * Линии границы владений на сторонах гекса, отмеченных в `drawnEdges`, -
   * геометрия свечения (drawCityBorderGlow). Для каждой стороны возвращается
   * функция
   * `distance -> {start, end}`: отрезок, параллельный стороне, на расстоянии
   * `distance` от вершин сетки - положительное внутрь гекса, отрицательное
   * наружу.
   *
   * Концы отрезка режутся по-разному, чтобы граница была непрерывной и через
   * несколько ячеек:
   *  - соседняя сторона гекса тоже рисуется (выпуклый угол) - конец идёт по
   *    радиусу к центру, как у капкана (drawGoblinsTrapOutline): свечение
   *    двух сторон сходится встык;
   *  - соседняя сторона НЕ рисуется (например, за ней ячейка того же игрока,
   *    граница переходит на неё) - конец идёт по этой стороне сетки.
   *    Соседняя ячейка режет свое свечение по той же самой стороне с теми
   *    же отступами, и оно смыкается без щели и без наложения (по радиусу
   *    между ними остался бы вырез).
   * Наружу (отрицательное расстояние) те же линии реза продолжаются за
   * вершину, и кусок остается в пределах внешней ячейки: продолжение
   * радиуса идет ровно по стороне между двумя внешними ячейками, а
   * продолжение стороны сетки делит пополам угол внешней ячейки - по нему же
   * режет свой ореол соседняя ячейка за этой стороной (своя или другого
   * игрока). Поэтому и снаружи куски смыкаются «на ус», без щели и без
   * наложения.
   * Во всех случаях точка берётся на расстоянии |d| от вершины сетки: и
   * радиус, и соседняя сторона идут под 60° к стороне, так что это ровно
   * отступ |d| * sin(60°) от неё. Контуры построены по полным вершинам сетки
   * (hex.points), а не по уменьшенному шестиугольнику - иначе стыки соседних
   * ячеек разошлись бы.
   * @param {Object} hex
   * @param {boolean[]} drawnEdges - по сторонам hex.points[i] -> [i + 1]
   * @returns {function(number): {start: {x: number, y: number}, end: {x: number, y: number}}[]}
   */
  getCityBorderEdgeLines(hex, drawnEdges) {
    const points = hex.points.map((p) => ({x: p.x, y: p.y + CITY_BORDER_SHIFT_Y}));
    const center = {x: hex.centerX, y: hex.centerY + CITY_BORDER_SHIFT_Y};

    // Точка на расстоянии `distance` от вершины в сторону `toward` (центр или
    // соседняя вершина - обе ровно на расстоянии радиуса от вершины);
    // отрицательное расстояние - в обратную сторону, за вершину
    const stepFrom = (vertex, toward, distance) => ({
      x: vertex.x + (toward.x - vertex.x) * distance / hex.size,
      y: vertex.y + (toward.y - vertex.y) * distance / hex.size,
    });

    const lines = [];
    for (let i = 0; i < 6; i++) {
      if (!drawnEdges[i]) continue;

      const start = points[i];
      const end = points[(i + 1) % 6];
      const startToward = drawnEdges[(i + 5) % 6] ? center : points[(i + 5) % 6];
      const endToward = drawnEdges[(i + 1) % 6] ? center : points[(i + 2) % 6];

      lines.push((distance) => ({
        start: stepFrom(start, startToward, distance),
        end: stepFrom(end, endToward, distance),
      }));
    }
    return lines;
  }

  /**
   * Полоса между двумя линиями границы (getCityBorderEdgeLines): цвет и
   * непрозрачность меняются от `from` на линии `fromLine` до `to` на линии
   * `toLine` - строго перпендикулярно стороне: все вершины на одной линии
   * получают одни цвет и альфу, а внутри треугольников WebGL интерполирует
   * их линейно
   * @param {Phaser.GameObjects.Graphics} graphics
   * @param {{start: Object, end: Object}} fromLine
   * @param {{start: Object, end: Object}} toLine
   * @param {{color0x: number, alpha: number}} from
   * @param {{color0x: number, alpha: number}} to
   */
  fillCityBorderBand(graphics, fromLine, toLine, from, to) {
    // Вершины fillTriangle получают цвет и альфу TL, TR, BL по порядку
    graphics.fillGradientStyle(from.color0x, from.color0x, to.color0x, to.color0x, from.alpha, from.alpha, to.alpha, to.alpha);
    graphics.fillTriangle(fromLine.start.x, fromLine.start.y, fromLine.end.x, fromLine.end.y, toLine.end.x, toLine.end.y);
    graphics.fillGradientStyle(from.color0x, to.color0x, to.color0x, to.color0x, from.alpha, to.alpha, to.alpha, to.alpha);
    graphics.fillTriangle(fromLine.start.x, fromLine.start.y, toLine.end.x, toLine.end.y, toLine.start.x, toLine.start.y);
  }

  /**
   * Профиль поперек стороны (CITY_BORDER_TUBE_PROFILE и подобные) -
   * полосами между соседними точками профиля на каждой линии `lines`
   * @param {Phaser.GameObjects.Graphics} graphics
   * @param {Object} hex
   * @param {Function[]} lines - из getCityBorderEdgeLines
   * @param {Object[]} profile - точки {distanceRatio, whiteShare, alpha} от стороны внутрь
   * @param {number} color0x
   */
  fillCityBorderProfile(graphics, hex, lines, profile, color0x) {
    const stops = profile.map(({distanceRatio, whiteShare, alpha}) => ({
      distance: hex.size * distanceRatio,
      color0x: mixColor0x(color0x, 0xffffff, whiteShare),
      alpha,
    }));
    lines.forEach((lineAt) => {
      for (let i = 1; i < stops.length; i++) {
        this.fillCityBorderBand(graphics, lineAt(stops[i - 1].distance), lineAt(stops[i].distance), stops[i - 1], stops[i]);
      }
    });
  }

  /**
   * Граница владений одной ячейки - «неоновая трубка». Сначала свечение:
   * вложенные трапеции от стороны сетки внутрь ячейки (на граничных
   * сторонах) и ореолом наружу (на сторонах к ничьим ячейкам), каждая со
   * своей шириной и альфой, затухающей до 0. Сумма слоев дает мягкий спад
   * яркости - дешевая имитация размытия без шейдера (postFX работает только
   * в WebGL и светит весь объект, а не одну сторону). Поверх - тонкие линии
   * на сторонах к своим же ячейкам и сама трубка: насыщенный цвет игрока по
   * краям и светлая сердцевина. Все начинается от самой стороны сетки, а не
   * от края картинки жетона: так граница закрывает и просвет между жетонами
   * соседних ячеек
   * @param {Phaser.GameObjects.Graphics} graphics
   * @param {Object} hex
   * @param {Object} edges - маски сторон hex.points[i] -> [i + 1]
   * @param {boolean[]} edges.borderEdges - граница: за стороной не своя ячейка
   * @param {boolean[]} edges.haloEdges - ореол наружу: за стороной ничья ячейка
   * @param {boolean[]} edges.innerLineEdges - тонкая линия: за стороной своя ячейка
   * @param {number} color0x
   */
  drawCityBorderGlow(graphics, hex, {borderEdges, haloEdges, innerLineEdges}, color0x) {
    const borderLines = this.getCityBorderEdgeLines(hex, borderEdges);
    const haloLines = this.getCityBorderEdgeLines(hex, haloEdges);
    const innerLines = this.getCityBorderEdgeLines(hex, innerLineEdges);
    const transparent = {color0x, alpha: 0};

    const fillGlowLayers = (lines, layers, direction) => layers.forEach(({widthRatio, alpha}) => {
      const width = hex.size * widthRatio * direction;
      lines.forEach((lineAt) => {
        this.fillCityBorderBand(graphics, lineAt(0), lineAt(width), {color0x, alpha}, transparent);
      });
    });
    fillGlowLayers(haloLines, CITY_BORDER_HALO_LAYERS, -1);
    fillGlowLayers(borderLines, CITY_BORDER_INNER_GLOW_LAYERS, 1);

    this.fillCityBorderProfile(graphics, hex, innerLines, CITY_BORDER_INNER_LINE_PROFILE, color0x);
    this.fillCityBorderProfile(graphics, hex, borderLines, CITY_BORDER_TUBE_PROFILE, color0x);
  }

  /**
   * Разовая анимация «ячейка заблокирована» после перемещения отряда в фазе
   * войны (этапы перемещения и маневра): дифф позиций отрядов между
   * store-апдейтами ловит КАЖДОЕ перемещение — свое, чужое по сети, бота —
   * поэтому анимацию видят все участники и зрители. Для переместившегося
   * отряда calcCellsBlockedByUnitMove возвращает ячейки вражеских городов,
   * ставшие заблокированными именно им (с каскадом на строения заблокированных
   * кварталов), и над каждой проигрывается пульс символа блокировки:
   * быстрое появление -> 1с полной непрозрачности -> затухание -> destroy
   */
  updateBlockedCellsPulse(state) {
    const units = selectUnits(state);
    const nextPositions = new Map(units.map((unit) => [unit.id, unit.hexKey]));

    const prevPositions = this.prevUnitPositionsForBlockPulse;
    // Слежение за позициями отрядов обновляем безусловно (даже если сама
    // анимация выключена ниже) — иначе при возврате BLOCKED_CELLS_PULSE_ANIMATION_ENABLED
    // в true диффом окажется весь простой, накопленный, пока флаг был выключен
    this.prevUnitPositionsForBlockPulse = nextPositions;
    if (!prevPositions || !BLOCKED_CELLS_PULSE_ANIMATION_ENABLED) {
      return;
    }

    const phase = selectPhase(state);
    const warPhaseStage = selectWarPhaseStage(state);
    if (phase !== PHASES.WAR
      || (warPhaseStage !== WAR_PHASE_STAGES.MOVEMENT && warPhaseStage !== WAR_PHASE_STAGES.MANEUVER)
    ) {
      return;
    }

    const movedUnits = units.filter((unit) =>
      prevPositions.has(unit.id) && prevPositions.get(unit.id) !== unit.hexKey
    );
    if (movedUnits.length === 0) {
      return;
    }

    const hexes = selectHexes(state);
    const specialBuildings = selectSpecialBuildings(state);
    const buildingsDamage = selectBuildingsDamage(state);
    const players = selectPlayers(state);
    const citiesCache = selectCitiesCache(state);

    movedUnits.forEach((movedUnit) => {
      const blockedCells = calcCellsBlockedByUnitMove(
        movedUnit, units, hexes, specialBuildings, buildingsDamage, players, citiesCache
      );
      blockedCells.forEach((hexKey) => this.pulseBlockedCellMarker(hexKey));
    });
  }

  // Спрайт и твины пульса НЕ регистрируются в hex.markers/hex.animations:
  // clearMarkers()/clearTweens() убивают их содержимое на каждом store-апдейте,
  // а пульс должен дожить до конца (после чего сам уничтожает спрайт)
  pulseBlockedCellMarker(hexKey) {
    const hex = this.hexes[hexKey];
    if (!hex) return;

    const blockedSprite = this.scene.add.sprite(
      hex.centerX,
      hex.centerY + MARKER_SHIFT_Y,
      'block_red_outline'
    );
    blockedSprite.setScale(0.1);
    blockedSprite.setDepth(LAYER_DEPTH.MARKER);
    blockedSprite.setOrigin(0.5, 0.5);
    blockedSprite.setAlpha(0);

    this.scene.tweens.add({
      targets: blockedSprite,
      alpha: 1,
      duration: 250,
      ease: 'Quad.easeOut',
      onComplete: () => {
        this.scene.tweens.add({
          targets: blockedSprite,
          alpha: 0,
          delay: 1000,
          duration: 600,
          ease: 'Quad.easeIn',
          onComplete: () => blockedSprite.destroy(),
        });
      },
    });
  }

  addBlockedCellsMarkers(citiesCache, players, units, hexes, specialBuildings, buildingsDamage) {
    // Обходим всех игроков и их города
    Object.entries(citiesCache).forEach(([playerColor, cities]) => {
      // Находим игрока по цвету
      const cityOwner = players.find(p => p.color === playerColor);
      if (!cityOwner) return;

      cities.forEach(city => {
        const cityHexKeys = getCityHexKeys(city);

        // Для каждой ячейки города проверяем заблокирована ли она для владельца города
        cityHexKeys.forEach(hexKey => {
          const hex = this.hexes[hexKey];
          if (!hex) return;

          let isAnyTokenBlocked = false;
          // Проверяем блокировку относительно ВЛАДЕЛЬЦА города, а не текущего игрока
          if (checkIfHexBlockedByEnemyUnit(hexKey, hexes[hexKey], cityOwner.id, units, hexes, specialBuildings, buildingsDamage)) {
            isAnyTokenBlocked = true
          } else {
            // Необходимо выполнить отдельную проверку для спецстроений, так как они могут быть повреждены
            const specialBuilding = specialBuildings[hexKey];
            if (specialBuilding) {
              isAnyTokenBlocked = checkIfHexBlockedByEnemyUnit(hexKey, specialBuilding, cityOwner.id, units, hexes, specialBuildings, buildingsDamage)
            }
          }

          if (isAnyTokenBlocked) {
            // Живая печать блокировки на той же глубине, что и маркер
            // повреждения строений (renderBuildingDamage), но симметрично справа
            // от центра (тот слева на hex.centerX - 40)
            this.registerMarker(hex, this.liveFieldMarkers.createBlockedMarker({
              x: hex.centerX - BUILDING_DAMAGE_MARKER_SHIFT_X,
              y: hex.centerY + MARKER_SHIFT_Y,
              hexSize: hex.size,
              depth: BUILDING_STATE_MARKER_DEPTH,
            }));
          }
        });
      });
    });
  }

  /**
   * Ловушки Гоблинов - видны ТОЛЬКО их владельцу (см. add-goblins-faction
   * design.md Decision 1); для всех остальных клиентов (включая других
   * игроков-гоблинов) не рендерятся вовсе. НЕ иконка - вместо неё контур по
   * границе гекса (капкан): линия по всем 6 сторонам + треугольные зубья,
   * направленные внутрь (см. drawGoblinsTrapOutline). Так ловушка не
   * перекрывает изображение строения в центре гекса (по просьбе пользователя
   * после ручного тестирования - прежняя центрированная иконка это делала).
   * Глубина отрисовки (GOBLINS_TRAP_MARKER_DEPTH_OFFSET) поднимает контур
   * ТОЛЬКО над самим жетоном строения - все накладки НАД строением (индикатор
   * повреждения, здоровье/повреждения крепости и т.д.) по-прежнему рисуются
   * поверх ловушки, а не под ней.
   * @param {Array} goblinsTraps
   * @param {Object|null} actingPlayer
   * @param {Object} hexes - сырые типы токенов по ключу гекса (см.
   *   resolveGoblinsTrapShiftY - пустой гекс не должен сдвигаться по высоте)
   */
  addGoblinsTrapMarkers(goblinsTraps, actingPlayer, hexes) {
    if (!actingPlayer) return;

    goblinsTraps
      .filter(trap => trap.ownerId === actingPlayer.id)
      .forEach(trap => {
        const hex = this.hexes[trap.hexKey];
        if (!hex) return;

        const trapGraphics = this.scene.add.graphics();
        trapGraphics.setDepth(LAYER_DEPTH.TOKEN_SPECIAL_BUILDING + GOBLINS_TRAP_MARKER_DEPTH_OFFSET);
        this.drawGoblinsTrapOutline(trapGraphics, hex, this.resolveGoblinsTrapShiftY(hexes, trap.hexKey));

        this.registerMarker(hex, trapGraphics);
      });
  }

  /**
   * Вертикальный сдвиг контура ловушки для гекса `hexKey`: тот же
   * MARKER_SHIFT_Y, что и у остальных накладок над строением, но ТОЛЬКО если
   * на гексе действительно есть жетон/строение с псевдо-3D "стенкой" внизу
   * картинки - на пустом гексе (нет ни жетона, ни спецстроения) такой
   * картинки нет, и капкан должен рисоваться без подъёма, иначе он висит
   * заметно не по центру (замечание пользователя). "Пустота" определяется
   * ТЕМ ЖЕ способом, что и картинка `empty_hex` в updateHexes - по сырому
   * типу токена из `hexes`, без учёта возможных наложений вроде разрушенного
   * строения. Читается из `hexes` заново на каждый рендер-такт (тот же live
   * `state`, что и весь остальной рендер этого файла), поэтому как только на
   * пустой гекс ставят здание, контур автоматически подхватывает новый сдвиг
   * на следующей перерисовке - отдельного триггера/инвалидации не нужно.
   * @param {Object} hexes - сырые типы токенов по ключу гекса (selectHexes)
   * @param {string} hexKey
   * @returns {number}
   */
  resolveGoblinsTrapShiftY(hexes, hexKey) {
    const tokenType = hexes ? hexes[hexKey] : null;
    return (!tokenType || checkIfFlag(tokenType)) ? 0 : MARKER_SHIFT_Y - 0.3;
  }

  /**
   * Рисует капкан по контуру гекса `hex`: кольцо (линия) вдоль всех 6 сторон
   * плюс `GOBLINS_TRAP_TEETH_PER_EDGE` треугольных зубьев на каждую сторону,
   * направленных внутрь. Все размеры - доля от `hex.size` (радиуса), а не
   * от `hex.points` напрямую (см. GOBLINS_TRAP_OUTER_RADIUS_RATIO): точки
   * `hex.points` - это геометрические вершины, общие сразу с двумя соседними
   * гексами, и картинка объекта на гексе рендерится заметно МЕНЬШЕ них, так
   * что обводка по самим `hex.points` наезжала бы и на соседний гекс, и на
   * край его собственной картинки (найдено пользователем в ручном
   * тестировании).
   *
   * НЕ используется `lineStyle`/`strokePath` для кольца - у Phaser нет
   * настоящих стыков (miter/round join) между сегментами обводки замкнутого
   * пути, каждый сегмент рисуется как отдельный прямоугольник с выступом на
   * конце, из-за чего в вершинах два соседних сегмента перекрываются и
   * полупрозрачный цвет там становится заметно темнее (тоже найдено
   * пользователем). Вместо этого кольцо - это 6 отдельных ЗАЛИТЫХ трапеций
   * между внешним и внутренним шестиугольником, у каждой пары соседних
   * трапеций ОБЩИЕ вершины (не приблизительно смежные, а буквально одни и те
   * же точки) - оверлапа по площади нет вообще, значит и накопления альфы
   * нет. Зубья - треугольники, основание которых лежит РОВНО на внутренней
   * границе этих трапеций (общие точки, не пересечение), а по своей стороне
   * они не перекрываются друг с другом за счёт запаса
   * GOBLINS_TRAP_TOOTH_HALF_WIDTH_T (и не дотягиваются до общей с соседним
   * ребром вершины - см. её комментарий).
   *
   * Смещение по Y (`shiftY`, см. resolveGoblinsTrapShiftY) учитывается для
   * центра, вокруг которого строится вся геометрия, - у гексов со строением
   * визуальный центр картинки поднят из-за псевдо-3D "стенки" внизу картинки
   * (renderSpecialBuilding/renderFortressDamage), капкан должен
   * центрироваться на ту же точку. У ПУСТОГО гекса (нет ни жетона, ни
   * строения) такой картинки нет вообще, поднимать капкан там не за чем -
   * caller обязан передать 0 в этом случае, иначе контур на пустой ячейке
   * визуально повиснет не по центру (замечание пользователя).
   * @param {Phaser.GameObjects.Graphics} graphics
   * @param {Object} hex
   * @param {number} shiftY
   */
  drawGoblinsTrapOutline(graphics, hex, shiftY) {
    const points = hex.points;
    const cx = hex.centerX;
    const cy = hex.centerY + shiftY;

    const atRadius = (ratio) => points.map((p) => ({
      x: cx + (p.x - hex.centerX) * ratio,
      y: cy + (p.y - hex.centerY) * ratio,
    }));

    const outerPoints = atRadius(GOBLINS_TRAP_OUTER_RADIUS_RATIO);
    const innerRadiusRatio = GOBLINS_TRAP_OUTER_RADIUS_RATIO - GOBLINS_TRAP_BAND_WIDTH_RATIO;
    const innerPoints = atRadius(innerRadiusRatio);
    const toothLength = hex.size * GOBLINS_TRAP_TOOTH_LENGTH_RATIO;

    graphics.fillStyle(GOBLINS_TRAP_COLOR_0x, GOBLINS_TRAP_ALPHA);

    for (let i = 0; i < 6; i++) {
      const nextI = (i + 1) % 6;
      const outer1 = outerPoints[i];
      const outer2 = outerPoints[nextI];
      const inner1 = innerPoints[i];
      const inner2 = innerPoints[nextI];

      // Трапеция кольца на этой стороне - вершины общие с соседними
      // трапециями, оверлапа по площади нет
      graphics.beginPath();
      graphics.moveTo(outer1.x, outer1.y);
      graphics.lineTo(outer2.x, outer2.y);
      graphics.lineTo(inner2.x, inner2.y);
      graphics.lineTo(inner1.x, inner1.y);
      graphics.closePath();
      graphics.fillPath();

      const edgeDx = inner2.x - inner1.x;
      const edgeDy = inner2.y - inner1.y;
      const edgeLength = Math.hypot(edgeDx, edgeDy);
      const ux = edgeDx / edgeLength;
      const uy = edgeDy / edgeLength;

      let nx = -uy;
      let ny = ux;
      const midX = (inner1.x + inner2.x) / 2;
      const midY = (inner1.y + inner2.y) / 2;
      if (nx * (cx - midX) + ny * (cy - midY) < 0) {
        nx = -nx;
        ny = -ny;
      }

      for (let j = 0; j < GOBLINS_TRAP_TEETH_PER_EDGE; j++) {
        const t = (j + 0.5) / GOBLINS_TRAP_TEETH_PER_EDGE;
        const baseCenterX = inner1.x + edgeDx * t;
        const baseCenterY = inner1.y + edgeDy * t;
        const halfBase = edgeLength * GOBLINS_TRAP_TOOTH_HALF_WIDTH_T;

        graphics.fillTriangle(
          baseCenterX - ux * halfBase, baseCenterY - uy * halfBase,
          baseCenterX + ux * halfBase, baseCenterY + uy * halfBase,
          baseCenterX + nx * toothLength, baseCenterY + ny * toothLength
        );
      }
    }
  }

  /**
   * Индикаторы срабатывания ловушки(ек) на одном гексе - до 5 одновременных
   * (максимум игроков-гоблинов, враждебных одному отряду), расположены
   * СТОЛБИКОМ слева от центра. Каждая запись - плашка "число урона, иконка
   * урона" (композиция скопирована с addMeleeIndicator/addShootingIndicator),
   * всё белым на чёрном фоне в рамке цвета владельца сработавшей ловушки -
   * рамка нужна именно здесь (и не нужна в ближнем бою/стрельбе), т.к. на
   * одном гексе одновременно может быть несколько ловушек РАЗНЫХ игроков, и
   * без рамки было бы не различить, чья запись. Своей иконки ловушки в
   * плашке больше нет (пожелание пользователя) - сам гекс, на котором стояла
   * ловушка, теперь и так обведён контуром на время срабатывания (см.
   * revealTriggeredTrapOutline, design.md Decision 39), повторять иконку в
   * плашке избыточно. Ширина плашки менялась симметрично относительно
   * прежнего центра (BADGE_WIDTH 58 → 40 при отказе от иконки ловушки, затем
   * 40 → 46 под увеличенный шрифт урона; сам `centerX` не менялся) - было бы
   * заметно, если бы плашка "съехала" в сторону.
   *
   * Вызывается КАЖДЫЙ рендер-такт из updateGoblinsTrapTriggerDisplay, пока
   * показано состояние срабатывания (state.private.goblinsTrapKillNotice,
   * см. design.md Decision 21) - НЕ одноразовый эффект, в отличие от прежней
   * версии: та создавала спрайты один раз императивно, и любой следующий
   * диспатч (даже несвязанный - тост, снятие выбора) тут же стирал их
   * clearMarkers()'ом, из-за чего анимация иногда была не видна вовсе, а
   * иногда - что более странно - оставались висеть маркеры от уже
   * неактуального предыдущего срабатывания на другом гексе, наложенные на
   * новые (баг, найденный пользователем). Персистентный редрав из состояния
   * (тот же приём, что и pendingMeleeDamage/pendingShootingActions) устраняет
   * оба симптома разом: на каждый кадр ровно одно поколение маркеров.
   * @param {string} hexKey
   * @param {{ownerId: string, damage: number}[]} trapsByOwner
   * @param {Array} players - для цвета рамки владельца ловушки
   */
  playTrapTriggerAnimation(hexKey, trapsByOwner, players = []) {
    const hex = this.hexes[hexKey];
    if (!hex || !trapsByOwner || trapsByOwner.length === 0) return;

    const ROW_HEIGHT = 26;
    // Ширина/высота — под тот же размер шрифта, что у шеврона отряда (см. ниже)
    const BADGE_WIDTH = 35;
    const BADGE_HEIGHT = 22;
    const centerX = hex.centerX - 40;
    const startY = hex.centerY - ((trapsByOwner.length - 1) / 2) * ROW_HEIGHT;

    trapsByOwner.forEach((entry, index) => {
      const owner = players.find(p => p.id === entry.ownerId);
      const y = startY + index * ROW_HEIGHT;
      const ownerColor0x = owner ? getPlayer0xColor(owner.color) : WAR_COLOR_0x;

      const badge = this.scene.add.graphics();
      badge.fillStyle(0x000000, 1);
      badge.fillRoundedRect(centerX - BADGE_WIDTH / 2, y - BADGE_HEIGHT / 2, BADGE_WIDTH, BADGE_HEIGHT, 4);
      badge.lineStyle(2, ownerColor0x, 1);
      badge.strokeRoundedRect(centerX - BADGE_WIDTH / 2, y - BADGE_HEIGHT / 2, BADGE_WIDTH, BADGE_HEIGHT, 4);
      // Глубина — как у шеврона отряда (подложка размера: LAYER_DEPTH.UNIT +
      // 0.2), а не слоя строений: на слое строений плашку перекрывали белые
      // контуры доступных для перемещения ячеек (они на LAYER_DEPTH.UNIT - 0.5)
      badge.setDepth(LAYER_DEPTH.UNIT + 0.2);
      this.registerMarker(hex, badge);

      const damageIconX = centerX + BADGE_WIDTH / 2 - 11;
      // Урон ловушки читается как урон на шевроне отряда: тот же стиль
      // (UNIT_LABEL_DAMAGE_STYLE — цвет WAR_COLOR) и тот же масштаб 0.5, что у
      // размера отряда и урона ближнего боя. Мельче он выглядел чужеродно
      const damageLabel = this.scene.add.text(
        centerX - BADGE_WIDTH / 2 + 11, y - 1, entry.damage.toString(), UNIT_LABEL_DAMAGE_STYLE
      );
      damageLabel.setScale(0.6);
      damageLabel.setOrigin(0.5, 0.5);
      damageLabel.setDepth(LAYER_DEPTH.UNIT + 0.3);
      this.registerMarker(hex, damageLabel);

      const damageIcon = this.scene.add.sprite(damageIconX, y - 1, 'damage');
      damageIcon.setScale(GOBLINS_TRAP_DAMAGE_ICON_SCALE);
      damageIcon.setOrigin(0.5, 0.5);
      damageIcon.setDepth(LAYER_DEPTH.UNIT + 1);
      this.registerMarker(hex, damageIcon);
    });
  }

  /**
   * Пока показано срабатывание (notice.show), контур сработавшей ловушки
   * временно виден ВСЕМ игрокам на её гексе, а не только владельцу (обычное
   * правило видимости - addGoblinsTrapMarkers) - иначе остальные видят урон и
   * анимацию, но не понимают, на каком именно гексе стояла ловушка (пожелание
   * пользователя). Владельцу этой же ловушки повторно не рисуем - её контур и
   * так уже всегда на экране через addGoblinsTrapMarkers, второй проход по
   * той же геометрии дал бы удвоенную (значит заметно более тёмную) альфу на
   * то же самое время.
   *
   * Отдельного жизненного цикла у этого маркера нет - вызывается из
   * updateGoblinsTrapTriggerDisplay каждый рендер-такт, пока notice.show,
   * тем же clearMarkers()-циклом, что и остальные индикаторы срабатывания
   * (badge/trapIcon/damageLabel/damageIcon выше), поэтому пропадает вместе с
   * ними на следующем такте после клика по уведомлению
   * (hideGoblinsTrapKillNotice) - без отдельной логики скрытия.
   * @param {{hexKey: string, trapsByOwner: {ownerId: string, damage: number}[]}} event
   * @param {Object|null} actingPlayer - реальный локальный зритель этого клиента
   * @param {Object} hexes - сырые типы токенов по ключу гекса (см.
   *   resolveGoblinsTrapShiftY)
   */
  revealTriggeredTrapOutline(event, actingPlayer, hexes) {
    const hex = this.hexes[event.hexKey];
    if (!hex) return;

    const viewerOwnsTrapHere = actingPlayer
      && event.trapsByOwner.some(entry => entry.ownerId === actingPlayer.id);
    if (viewerOwnsTrapHere) return;

    const trapGraphics = this.scene.add.graphics();
    trapGraphics.setDepth(LAYER_DEPTH.TOKEN_SPECIAL_BUILDING + GOBLINS_TRAP_MARKER_DEPTH_OFFSET);
    this.drawGoblinsTrapOutline(trapGraphics, hex, this.resolveGoblinsTrapShiftY(hexes, event.hexKey));

    this.registerMarker(hex, trapGraphics);
  }

  /**
   * Упрощённый "призрак" отряда, погибшего от ловушки - держит его иконку на
   * поле и мигающей, пока показано состояние срабатывания (см.
   * updateGoblinsTrapTriggerDisplay, design.md Decision 21): к этому моменту
   * юнит уже удалён из state.units (winPoints начисляются немедленно), и
   * обычный renderUnit его не отрисует. Ключ текстуры/масштаб - через тот же
   * resolveUnitImageKey, что и у renderUnit (см. design.md, найденный
   * пользователем баг: фракционный скин пропадал на призраке), но без
   * остальных нюансов полного renderUnit (половина силуэта у захваченных
   * Суккубами и т.п.) - для короткого второстепенного отображения этого
   * достаточно; unit - это снимок отряда ДО гибели (полная копия, см.
   * GameScene.js/decideMovementStep.js), нужны только type/ownerId/factionId/
   * capturedFromOwnerId
   * @param {Object} unit - снимок погибшего отряда {type, ownerId, ...}
   * @param {string} hexKey - гекс, на котором отряд погиб
   * @param {Array} players
   */
  renderGoblinsTrapGhostUnit(unit, hexKey, players) {
    const hex = this.hexes[hexKey];
    if (!hex || !unit) return;

    // Тот же резолвер ключа текстуры, что и у обычного renderUnit - раньше
    // здесь стоял захардкоженный `${unit.type}_white`, из-за чего купленный
    // фракционный скин (например, у Гоблинов) на призраке пропадал, откатываясь
    // на общий силуэт (найдено пользователем в ручном тестировании)
    const {unitImageKey, usesNeutralIcon, useFactionUnitSkin, isGiantsUnitIcon} = this.resolveUnitImageKey(unit, players);
    if (!this.scene.textures.exists(unitImageKey)) return;

    const player = players.find(p => p.id === unit.ownerId);
    const playerColor = player?.color || getNeutralPlayerMock().color;
    const unitShiftY = this.getUnitShiftY(hex);

    const sprite = this.scene.add.sprite(hex.centerX, hex.centerY + unitShiftY, unitImageKey);
    sprite.texture.setFilter(Phaser.Textures.FilterMode.LINEAR);

    // Та же формула масштаба, что и в renderUnit (и тот же кэш
    // unitSpriteScales по ключу текстуры) - иначе отряд с фракционным
    // скином/иконкой великана "прыгнул" бы в размере в момент смерти
    let scale = this.unitSpriteScales[unitImageKey];
    if (!scale) {
      const bounds = this.hexMath.getHexBounds(hex.points);
      let scaleMultiplier = useFactionUnitSkin ? FACTION_UNIT_SCALE_MULTIPLIER : UNIT_SCALE_MULTIPLIER;
      if (isGiantsUnitIcon) {
        scaleMultiplier *= GIANTS_UNIT_ICON_SCALE_MULTIPLIER;
      }
      if (usesNeutralIcon) {
        scaleMultiplier *= NEUTRAL_UNIT_ICON_SCALE_MULTIPLIER;
      }
      scale = this.hexMath.calculateSpriteScale(bounds, {width: sprite.width, height: sprite.height})
        * scaleMultiplier;
      this.unitSpriteScales[unitImageKey] = scale;
    }
    sprite.setScale(scale);
    sprite.setDepth(LAYER_DEPTH.UNIT);
    sprite.setTint(getPlayer0xColor(playerColor));
    sprite.setAlpha(0.85);

    // Обводка - без неё силуэт сливается с фоном (замечание пользователя).
    // Белая, как у обычного отряда на своём ходу (renderUnit,
    // hasWhiteOutline) - отряд погиб во время хода СВОЕГО владельца, другого
    // случая для этого призрака не бывает. Тот же ключ текстуры, что и у
    // настоящих юнитов этого типа - generateOutlineTexture идемпотентна и
    // почти наверняка уже сгенерирована обычным рендером этого юнита раньше
    const outlineKey = `${unitImageKey}_outline`;
    generateOutlineTexture(this.scene, unitImageKey, outlineKey, 20, 0xFFFFFF);
    const outlineSprite = this.scene.add.sprite(hex.centerX, hex.centerY + unitShiftY, outlineKey);
    outlineSprite.setScale(scale);
    outlineSprite.setDepth(LAYER_DEPTH.UNIT - 0.1);
    outlineSprite.setAlpha(0.85);
    this.applyActiveContourTint(outlineSprite);

    this.addMarkerFadingTween(hex, sprite);
    this.addMarkerFadingTween(hex, outlineSprite);
    this.registerMarker(hex, sprite, outlineSprite);

    // Численность ДО урона - чтобы игрок видел, что нанесённый урон (индикатор
    // "-N" рядом, addUnitSizeUpdateIndicator) равен или превышает её, и отряд
    // поэтому погиб. Без этой метки численность отряда во время анимации
    // призрака не отображалась вовсе (замечание пользователя)
    this.addUnitSizeLabel(hex, unit.size);
  }

  /**
   * Точка входа, вызывается каждый рендер-такт из GameScene.handleStoreUpdate
   * (после updateUnits - см. ниже про мигание живого отряда). Рисует ВСЁ, что
   * должно быть видно всем игрокам при срабатывании ловушек: плашки урона на
   * гексах ловушек, контуры сработавших ловушек, "-N" под отрядом (точно тот
   * же индикатор, что у ближнего боя/стрельбы - addUnitSizeUpdateIndicator) и
   * мигание самого отряда - живого (мигают уже отрисованные updateUnits
   * иконка и тень, см. addUnitFadingTween) либо погибшего (упрощённый
   * призрак, см. renderGoblinsTrapGhostUnit).
   *
   * Источник - ОБЩЕЕ состояние этапа (selectGoblinsTrapStageTriggers), а не
   * приватное уведомление: срабатываний за этап может быть сколько угодно, и
   * следующее не должно стирать предыдущие. Раньше слот был один, и подрыв
   * следующего отряда убирал с поля уже погибшего вместе с его плашками
   * (найдено пользователем). Приватное состояние теперь отвечает только за
   * карточку у ходившего игрока (GoblinsTrapKillNotice)
   * @param {Object} state
   */
  updateGoblinsTrapTriggerDisplay(state) {
    const triggers = selectGoblinsTrapStageTriggers(state);
    if (triggers.length === 0) return;

    const players = selectPlayers(state);
    const actingPlayer = getActingPlayerFromState(state);
    const hexes = selectHexes(state);

    triggers.forEach(trigger => {
      (trigger.triggeredTrapEvents || []).forEach(event => {
        this.playTrapTriggerAnimation(event.hexKey, event.trapsByOwner, players);
        this.revealTriggeredTrapOutline(event, actingPlayer, hexes);
      });

      const hex = trigger.finalHexKey ? this.hexes[trigger.finalHexKey] : null;
      if (!hex || !trigger.unit) {
        return;
      }

      if (trigger.totalDamageTaken > 0) {
        this.addUnitSizeUpdateIndicator(hex, -trigger.totalDamageTaken, false, trigger.unit.size);
      }

      if (trigger.died) {
        this.renderGoblinsTrapGhostUnit(trigger.unit, trigger.finalHexKey, players);
      } else {
        // Иконка гаснет вместе с тенью, контур — наполовину: как у отряда,
        // обреченного в бою (см. addUnitFadingTween)
        this.addUnitFadingTween(hex, trigger.unit.id);
      }
    });
  }

  /**
   * Обновить маркеры снабжения
   * Подсвечивает выбранную ферму и отряды игрока, которые можно снабдить
   * @param {Object} state - Состояние store
   */
  updateSupplyMarkers(state) {
    // Очищаем предыдущие маркеры
    this.clearSupplyMarkers();

    // Проверяем, находимся ли мы в фазе снабжения и показывается ли оно на
    // поле: во время облета захваченных городов — нет
    if (!checkIfSupplyShownOnField(state)) {
      return;
    }

    // Защита от null scene
    if (!this.scene || !this.scene.add) {
      return;
    }

    // Игрок, за которого этот клиент раздает снабжение: в поочередной фазе —
    // текущий, в одновременной — сам пользователь, пока не закончил (у
    // зрителя и закончившего игрока подсвечивать нечего)
    const supplyPlayer = selectSupplyActingPlayer(state);
    if (!supplyPlayer) return;

    const markerColor = getHexOutlineMarkerColor();

    const selectedFarmHexKey = selectSelectedFarmHexKey(state);

    if (!selectedFarmHexKey) {
      // Великаны: подсветка отряда, выбранного для GiantsSupplyToolbar (счётчик
      // снабжения + кнопка конвертации) - раздача снабжения от этого выбора не
      // зависит, это чисто визуальное подтверждение клика. Проверка по факции
      // ЮНИТА, а не игрока - сохраняет способность и у Великана, захваченного
      // Суккубом (см. unit-scoped-faction-abilities)
      const selectedUnitId = selectGiantsSelectedUnitId(state, supplyPlayer.id);
      const selectedUnit = selectedUnitId && selectUnits(state).find(u => u.id === selectedUnitId);
      if (selectedUnit && isUnitFaction(selectedUnit, FACTIONS.GIANTS)) {
        const selectedUnitHex = this.hexes[selectedUnit.hexKey];
        if (selectedUnitHex) {
          this.drawSelectedHexOutlineMarker(selectedUnitHex, 'supplyMarkers');
        }
      }
      return;
    }

    // Подсвечиваем выбранную ферму
    const selectedFarmHex = this.hexes[selectedFarmHexKey];
    if (selectedFarmHex) {
      this.drawSelectedHexOutlineMarker(selectedFarmHex, 'supplyMarkers');
    }


    const hexes = selectHexes(state);
    const units = selectUnits(state);
    const supplyPlayerCities = selectSupplyActingPlayerCities(state);
    const buildingsDamage = selectBuildingsDamage(state);
    const specialBuildings = selectSpecialBuildings(state);
    const pendingSupply = selectSupplyPlayerPendingSupply(state, supplyPlayer);

    // Вычисляем доступные для снабжения ячейки с отрядами игрока
    const availableSupplyCells = calculateAvailableSupplyCells(
      this,
      hexes,
      specialBuildings,
      units,
      selectedFarmHexKey,
      supplyPlayer,
      supplyPlayerCities,
      buildingsDamage,
    );

    // Подсвечиваем только отряды, которые можно снабдить
    availableSupplyCells.forEach(hexKey => {
      const unitHex = this.hexes[hexKey];
      if (unitHex) {
        const unit = units.find(u => u.hexKey === hexKey);
        // Великаны получают базовое снабжение неограниченное число раз - флаг доп. снабжения
        // не должен снимать подсветку. Проверка по факции ЮНИТА, а не игрока - сохраняет
        // способность и у Великана, захваченного Суккубом (см. unit-scoped-faction-abilities)
        if (isUnitFaction(unit, FACTIONS.GIANTS)) {
          this.drawHexOutlineMarker(unitHex, markerColor, 'supplyMarkers');
          return;
        }
        // Не подсвечиваем отряд, если он уже получил дополнительное снабжение
        if (!checkIfUnitHasPendingAdditionalSupply(unit.id, pendingSupply)) {
          this.drawHexOutlineMarker(unitHex, markerColor, 'supplyMarkers');
        }
      }
    });
  }

  /**
   * Обновить маркеры для размещения стартовых кварталов в фазе SETUP
   * Подсвечивает ячейки с флагами цвета текущего игрока
   * @param {Object} state - Состояние store
   */
  updateSetupQuarterMarkers(state) {
    const phase = selectPhase(state);

    // Очищаем предыдущие маркеры
    this.clearSetupQuarterMarkers();

    // Проверяем, находимся ли мы в фазе размещения стартовых кварталов
    if (phase !== PHASES.SETUP) {
      return;
    }

    const actingPlayer = getActingPlayerFromState(state);
    // Маркеры размещения не показываются для ботов — их выбор скрыт до конца фазы SETUP
    if (!actingPlayer || actingPlayer.isBot) {
      return;
    }

    const hexes = selectHexes(state);
    const markerColor = getHexOutlineMarkerColor();

    const startingQuarters = selectStartingQuarters(state);
    const playerAlreadyHasQuarter = startingQuarters[actingPlayer.id];

    Object.entries(hexes).forEach(([hexKey, tokenType]) => {
      if (tokenType === getPlayerFlagToken(actingPlayer)) {
        const hex = this.hexes[hexKey];
        this.drawHexOutlineMarker(hex, markerColor, 'setupQuarterMarkers', undefined, undefined, undefined, !playerAlreadyHasQuarter);
      }
    });
  }

  /**
   * Очистить все маркеры размещения стартовых кварталов
   */
  clearSetupQuarterMarkers() {
    Object.values(this.hexes).forEach(hex => {
      if (hex.setupQuarterMarkers) {
        hex.setupQuarterMarkers.forEach(marker => marker.destroy());
        hex.setupQuarterMarkers = [];
      }
    });
  }

  /**
   * Очистить все маркеры снабжения
   */
  clearSupplyMarkers() {
    Object.values(this.hexes).forEach(hex => {
      if (hex.supplyMarkers) {
        hex.supplyMarkers.forEach(marker => marker.destroy());
        hex.supplyMarkers = [];
      }
    });
  }

  /**
   * Отрисовать жетон на гексагоне
   * @param key
   * @param {Phaser.GameObjects.Graphics} hex - Объект гексагона
   * @param {string} tokenType - Тип жетона
   * @param state
   * @param isGameStarted
   * @param isPhaseSetup
   */
  renderToken(key, hex, tokenType, state, isGameStarted, isPhaseSetup, {isStartingQuarterOverlay = false} = {}) {
    // Накладка стартового квартала в фазе setup лежит поверх флага той же
    // ячейки: ее слои помечаем, чтобы прилет квартала не утащил с собой флаг.
    // Расстановку могли закрыть, пока квартал летел: тогда он уже настоящий
    // жетон ячейки, а двойник все еще в воздухе. Настоящий жетон ждет
    // приземления так же, как ждала накладка, — иначе двойник падал бы на уже
    // стоящий квартал
    const isLandingStartingQuarter = isStartingQuarterOverlay
      || this.checkIfStartingQuarterIsLanding(key, tokenType);
    const dropKind = isLandingStartingQuarter ? DROP_KINDS.STARTING_QUARTER : DROP_KINDS.TOKEN;
    const markOverlay = (sprite) => {
      if (isStartingQuarterOverlay && sprite) {
        sprite.isStartingQuarterOverlay = true;
      }
      return sprite;
    };

    let tokenImage;
    let shouldRenderFlag = false;
    let playerColor;
    let shouldAnimate = false;
    let shouldWaveFlag = false;
    let hasFlagGlow = false;
    if ((!isGameStarted || isPhaseSetup) && checkIfFlag(tokenType)) {
      shouldRenderFlag = true;
      playerColor = tokenType.replace(FLAG_PREFIX, '');

      if (isPhaseSetup) {
        const actingPlayer = getActingPlayerFromState(state);

        // В setup стартовый квартал выбирают все сразу, поэтому машут флаги
        // всех игроков; свой от чужих отличает свечение
        shouldWaveFlag = true;
        const isOwnSetupFlag = Boolean(actingPlayer && tokenType === getPlayerFlagToken(actingPlayer));
        hasFlagGlow = isOwnSetupFlag;

        if (actingPlayer && !actingPlayer.isBot && isOwnSetupFlag) {
          const startingQuarters = selectStartingQuarters(state);
          const playerAlreadyHasQuarter = startingQuarters[actingPlayer.id];

          if (!playerAlreadyHasQuarter) {
            shouldAnimate = true;
          }
        }
      }
    } else if (checkIfCentralQuarter(tokenType)) {
      shouldRenderFlag = true;
      tokenImage = 'quarter_base';
      playerColor = tokenType.replace(CENTRAL_QUARTER_PREFIX, '');
      if (!isPlayerCentralQuarter(tokenType, NEUTRAL_PLAYER_COLOR)) {
        // Флаг игрока, чей сейчас ход, развевается на ветру — так на поле
        // видно, чья очередь ходить, не заглядывая в список игроков (в
        // одновременной фазе снабжения — всех, кто еще не закончил)
        if (isGameStarted && getTurnFlagPlayers(state).some((player) => player.color === playerColor)) {
          shouldWaveFlag = true;
          hasFlagGlow = true;
        }
      }
    } else {
      // Подложка — та, что показывает сцена: под нее загружены тайлы гор
      tokenImage = calculateTokenImage(key, tokenType, state, {groundBackgroundId: this.scene.groundBackgroundId});
    }

    // Гора поднята сильнее прочих жетонов: картинка у нее выше, и без этого
    // она сидела бы в ячейке глубже соседей.
    // ГЛУБИНА у жетонов ОДНА: кто кого перекрывает, решает порядок отрисовки
    // ячеек сверху вниз (см. getHexKeysTopToBottom). Исключение — сама гора:
    // она идет выше границы владений (см. MOUNTAIN_DEPTH_OFFSET); между собой
    // горы по-прежнему перекрываются по порядку отрисовки
    const spriteShiftY = tokenType === TERRAIN_TOKENS.MOUNTAIN ?
      MOUNTAIN_SHIFT_Y : OTHER_TOKEN_SHIFT_Y;
    const depth = LAYER_DEPTH.TOKEN;
    const tokenSpriteDepth = tokenType === TERRAIN_TOKENS.MOUNTAIN
      ? LAYER_DEPTH.TOKEN_SPECIAL_BUILDING + MOUNTAIN_DEPTH_OFFSET
      : depth;

    if (tokenImage) {
      if(!checkIfFlag(tokenType)) {
        const yShift = tokenType === TERRAIN_TOKENS.MOUNTAIN ? -MOUNTAIN_SHIFT_Y : 0;
        const shadowSprite = this.scene.add.sprite(
          hex.centerX,
          hex.centerY + spriteShiftY + yShift,
          TOKEN_SHADOW_TEXTURE_KEY
        );
        shadowSprite.setScale(
          tokenType === TERRAIN_TOKENS.MOUNTAIN ? TOKEN_SCALE * MOUNTAIN_SHADOW_SCALE_FACTOR : TOKEN_SCALE
        );
        shadowSprite.setDepth(depth);
        shadowSprite.setPipeline('TextureTintPipeline'); // Используем улучшенный пайплайн
        shadowSprite.setOrigin(0.5, 0.5);
        shadowSprite.setAlpha(1);
        // Тень — часть жетона: пока он летит на ячейку, она не стоит готовой,
        // а проявляется вместе с падением и на приземлении становится полной
        // (см. updateBuildingDropShadows)
        shadowSprite.isTokenShadow = true;
        this.hideSpriteWhileDropping(key, markOverlay(shadowSprite), dropKind);

        this.registerTokenPart(hex, key, shadowSprite);
      }
      const tokenSprite = this.scene.add.sprite(
        hex.centerX,
        hex.centerY + spriteShiftY,
        tokenImage
      );
      tokenSprite.setScale(TOKEN_SCALE);
      tokenSprite.setDepth(tokenSpriteDepth);
      tokenSprite.setPipeline('TextureTintPipeline'); // Используем улучшенный пайплайн
      tokenSprite.setOrigin(0.5, 0.5);
      tokenSprite.setAlpha(1);
      // По ячейке идет полет двойника — настоящий жетон ждет приземления
      this.hideSpriteWhileDropping(key, markOverlay(tokenSprite), dropKind);
      // По жетонам ключевых точек время от времени пробегает блик — у
      // металла и камня он свой. Ему нужны место, размер и слой спрайта жетона
      // (см. TokenGlints)
      const glintStyle = getTokenGlintStyle(tokenType);
      if (glintStyle) {
        this.tokenGlintTargets.push({
          hexKey: key,
          x: tokenSprite.x,
          y: tokenSprite.y,
          width: tokenSprite.displayWidth,
          height: tokenSprite.displayHeight,
          depth: tokenSpriteDepth,
          style: glintStyle,
        });
      }
      // Руины светятся в раунды с увеличенным доходом — светиться ли сейчас,
      // решает конец updateHexes (см. RuinsIncomeGlow). От типа жетона
      // зависит, вспыхивают ли на нем звездочки
      if (checkIfRuinsIncomeGlowToken(tokenType)) {
        this.ruinsIncomeGlowTargets.push({
          hexKey: key,
          tokenType,
          x: tokenSprite.x,
          y: tokenSprite.y,
          width: tokenSprite.displayWidth,
          height: tokenSprite.displayHeight,
          depth: tokenSpriteDepth,
        });
      }

      this.registerTokenPart(hex, key, tokenSprite);
    }

    // Флаг центрального квартала (и стартовый флаг расстановки): у ходящего игрока
    // ткань живая, см. scenes/FieldFlags
    if (shouldRenderFlag) {
      const flagParts = this.fieldFlags.createFlag({
        x: hex.centerX,
        y: hex.centerY + spriteShiftY,
        hexSize: hex.size,
        depth,
        color: getPlayer0xColor(playerColor),
        isWaving: shouldWaveFlag,
        hasGlow: hasFlagGlow,
      });
      flagParts.forEach((part) => {
        this.hideSpriteWhileDropping(key, markOverlay(part), dropKind);
        this.registerTokenPart(hex, key, part);
        // Анимируем свой флаг во время фазы setup, если стартовый квартал еще не установлен
        if (isPhaseSetup && shouldAnimate) {
          this.addMarkerFadingTween(hex, part);
        }
      });
    }
  }

  /**
   * Отрисовать специальное строение поверх основного токена
   * @param {string} key - Ключ гекса
   * @param {Phaser.GameObjects.Graphics} hex - Объект гексагона
   * @param {string} specialBuildingType - Тип специального строения
   * @param isAnimated
   */
  renderSpecialBuilding(key, hex, specialBuildingType, isAnimated) {
    // Использование изображения
    hex.specialBuildingSprite = this.scene.add.sprite(
      hex.centerX,
      hex.centerY + SPECIAL_BUILDING_SHIFT_Y,
      specialBuildingType
    );

    // Включаем линейную фильтрацию для более гладкого отображения при зуме
    hex.specialBuildingSprite.texture.setFilter(Phaser.Textures.FilterMode.LINEAR);

    // Улучшение качества спрайта
    hex.specialBuildingSprite.setScale(TOKEN_SCALE);
    hex.specialBuildingSprite.setDepth(LAYER_DEPTH.TOKEN_SPECIAL_BUILDING); // Выше основного токена
    hex.specialBuildingSprite.setPipeline('TextureTintPipeline');
    hex.specialBuildingSprite.setOrigin(0.5, 0.5);
    // По ячейке идет полет двойника — настоящее строение ждет приземления
    this.hideSpriteWhileDropping(key, hex.specialBuildingSprite, DROP_KINDS.SPECIAL);

    if (isAnimated) {
      this.addMarkerFadingTween(hex, hex.specialBuildingSprite, true)
    }
  }

  // План строительства по ячейкам — пока он актуален: при планировании в
  // чужой ход и в начале своего, пока план не применен (например, ждет
  // восстановления соединения), см. getConstructionPlanContext
  getPlannedEntriesByHexKey(state) {
    const entriesByHexKey = new Map();
    selectConstructionPlanEntriesOnField(state).forEach((entry) => {
      const entries = entriesByHexKey.get(entry.hexKey) || [];
      entries.push(entry);
      entriesByHexKey.set(entry.hexKey, entries);
    });
    return entriesByHexKey;
  }

  /**
   * Запланированные строения ячейки — жетоны с той же картинкой, что у
   * построенных (центральный квартал — основание и флаг цвета игрока), без
   * прозрачности: план отличает от построенного иконка плана на ячейке
   * (updatePlannedBuildingMarkers). Если ходящий игрок уже построил на
   * ячейке, план поверх настоящего не рисуется (checkIfPlanEntryCoversHex).
   *
   * state нужен картинке запланированной фермы: ее запас снабжения зависит от
   * склада по соседству (calculatePlannedTokenImage)
   */
  renderPlannedBuildings(key, hex, entries, realTokenType, realSpecialBuildingType, {fortressVisible = true, state} = {}) {
    entries.forEach((entry) => {
      const {token} = entry;
      if (!checkIfPlanEntryCoversHex(entry, realTokenType, realSpecialBuildingType)) {
        return;
      }

      if (checkIfSpecialBuilding(token)) {
        // Слой крепостей скрыт — запланированная крепость скрыта вместе с ним
        if (token === SPECIAL_BUILDING_TOKENS.FORTRESS && !fortressVisible) {
          return;
        }
        this.drawPlannedBuildingSprite(
          hex, token, SPECIAL_BUILDING_SHIFT_Y, LAYER_DEPTH.TOKEN_SPECIAL_BUILDING + PLANNED_BUILDING_DEPTH_SHIFT,
          null, DROP_KINDS.SPECIAL
        );
        return;
      }

      // Тень — как у построенного жетона (renderToken): без нее план на пустой
      // ячейке лежал бы плоско рядом с построенными соседями. На дороге тень
      // уже есть — самой дороги
      if (!realTokenType) {
        this.drawPlannedBuildingSprite(
          hex, TOKEN_SHADOW_TEXTURE_KEY, OTHER_TOKEN_SHIFT_Y, LAYER_DEPTH.TOKEN, null, DROP_KINDS.TOKEN,
          {isTokenShadow: true}
        );
      }

      // Слои те же, что у построенного центрального квартала (renderToken):
      // основание, под флагом черное кольцо обводки, сверху флаг цвета игрока
      if (checkIfCentralQuarter(token)) {
        const playerColor = token.replace(CENTRAL_QUARTER_PREFIX, '');
        this.drawPlannedBuildingSprite(
          hex, 'quarter_base', OTHER_TOKEN_SHIFT_Y, LAYER_DEPTH.TOKEN + PLANNED_BUILDING_DEPTH_SHIFT
        );
        this.fieldFlags.getRestLayers().forEach(({textureKey, isTinted}, index) => {
          this.drawPlannedBuildingSprite(
            hex, textureKey, OTHER_TOKEN_SHIFT_Y, LAYER_DEPTH.TOKEN + PLANNED_BUILDING_DEPTH_SHIFT * (index + 2),
            isTinted ? getPlayer0xColor(playerColor) : null
          );
        });
        return;
      }

      // Картинка — сам жетон, кроме фермы: рядом со складом, построенным или
      // запланированным, она рисуется с двумя снабжениями — как построенная
      this.drawPlannedBuildingSprite(
        hex, calculatePlannedTokenImage(key, token, state), OTHER_TOKEN_SHIFT_Y,
        LAYER_DEPTH.TOKEN + PLANNED_BUILDING_DEPTH_SHIFT
      );
    });
  }

  drawPlannedBuildingSprite(
    hex, textureKey, shiftY, depth, tint = null, dropKind = DROP_KINDS.TOKEN, {isTokenShadow = false} = {}
  ) {
    const sprite = this.scene.add.sprite(hex.centerX, hex.centerY + shiftY, textureKey);
    sprite.setScale(TOKEN_SCALE);
    sprite.setDepth(depth);
    sprite.setPipeline('TextureTintPipeline');
    sprite.setOrigin(0.5, 0.5);
    if (tint !== null) {
      sprite.setTint(tint);
    }
    // Тень в полете не прячется, а проявляется (см. hideSpriteWhileDropping)
    if (isTokenShadow) {
      sprite.isTokenShadow = true;
    }
    // Запланированный жетон прилетает при постановке так же, как настоящий
    // (showBuildingDropAnimation с isPlanned): по метке полет находит, что
    // прятать, а перерисовка во время полета рисует его уже спрятанным
    sprite.plannedDropKind = dropKind;
    this.hideSpriteWhileDropping(hex.key, sprite, dropKind, {isPlannedSprite: true});
    // Как и прочие спрайты ячейки — уничтожается на следующей перерисовке
    this.registerMarker(hex, sprite);
    return sprite;
  }

  renderHegemonismQuarterIncome(key, hex, tokenType, players, citiesCache, hexes, units, round, specialBuildings, buildingsDamage) {
    const owner = findOwner(key, players, citiesCache);
    if (!owner || !isPlayerPolicy(owner, POLICIES.HEGEMONISM)) {
      return;
    }
    const isBlocked = checkIfHexBlockedByEnemyUnit(key, tokenType, owner.id, units, hexes, specialBuildings, buildingsDamage)
    this.renderIncome(key, hex, POLICY_HEGEMONISM_QUARTER_WIN_POINTS_INCOME_BONUS, INCOME_TYPE.WIN_POINTS, isBlocked, {
      isAnyPlayerOwner: true,
      depth: LAYER_DEPTH.INFO - 0.1
    })
  }

  renderTotalitarismMilitaryBuildingIncome(key, hex, tokenType, players, citiesCache, hexes, units, round, specialBuildings, buildingsDamage) {
    const owner = findOwner(key, players, citiesCache);
    if (!owner || !isPlayerPolicy(owner, POLICIES.TOTALITARIANISM)) {
      return;
    }
    const isBlocked = checkIfHexBlockedByEnemyUnit(key, tokenType, owner.id, units, hexes, specialBuildings, buildingsDamage)
    this.renderIncome(key, hex, '½', INCOME_TYPE.WIN_POINTS, isBlocked, {
      isAnyPlayerOwner: true,
      depth: LAYER_DEPTH.INFO - 0.1,
      textStyle: INCOME_TEXT_FRACTIONAL_STYLE,
    });
  }

  /**
   * Отрисовать доход ключевой точки (рудник, руины)
   * @param {string} key - Ключ гекса
   * @param {Phaser.GameObjects.Graphics} hex - Объект гексагона
   * @param {string} tokenType - Тип токена ключевой точки
   * @param {Array} players
   * @param {Object} citiesCache
   * @param {Object} hexes
   * @param units
   * @param round
   * @param specialBuildings
   * @param buildingsDamage
   * @param ruinsIncomeRoundLimit - лимит раундов для окна увеличенного дохода
   *   руин (selectRuinsIncomeRoundLimit)
   */
  renderKeyPointIncome(key, hex, tokenType, players, citiesCache, hexes, units, round, specialBuildings, buildingsDamage, ruinsIncomeRoundLimit, options = {}) {
    const owner = findOwner(key, players, citiesCache);
    const isBlocked = !!owner && checkIfHexBlockedByEnemyUnit(key, tokenType, owner.id, units, hexes, specialBuildings, buildingsDamage)

    let incomeValue;
    switch (tokenType) {
      case KEY_POINT_TOKENS.RUINS:
        incomeValue = calcRuinsIncome(KEY_POINT_TOKENS.RUINS, owner, round, ruinsIncomeRoundLimit);
        break;
      case KEY_POINT_TOKENS.SUPER_RUINS:
        incomeValue = calcRuinsIncome(KEY_POINT_TOKENS.SUPER_RUINS, owner, round, ruinsIncomeRoundLimit);
        break;
      default:
        incomeValue = calcMineIncome(key, tokenType, owner, citiesCache, units, hexes, specialBuildings, buildingsDamage);
    }

    const incomeType = INCOME_TYPE_MAPPING[tokenType];

    return this.renderIncome(key, hex, incomeValue, incomeType, isBlocked, {
      isAnyPlayerOwner: !!owner,
      ...options,
    });
  }

  renderSpecialBuildingIncome(key, hex, specialBuilding, players, citiesCache, units, hexes, specialBuildings, buildingsDamage, currentPlayer, animated = true, showPaths = false) {
    // Если строения еще не в citiesCache, значит это строение, которое текущий игрок возвел в текущей фазе строительства
    const owner = findOwner(key, players, citiesCache) || currentPlayer;

    let isGuildOnHegemonismBonusQuarter = false;
    if (owner) {
      const isBlocked = !!owner && checkIfHexBlockedByEnemyUnit(key, specialBuilding, owner.id, units, hexes, specialBuildings, buildingsDamage)

      const enemyCities = getEnemyCitiesForPlayer(owner.color, citiesCache);
      let incomeValue
      let influencePaths = []
      let influencePathColor
      switch (specialBuilding) {
        case SPECIAL_BUILDING_TOKENS.GUILD:
          incomeValue = calcGuildIncome(key, owner, enemyCities, hexes);
          influencePaths = calculateInfluencePathIncome(key, enemyCities, hexes).paths;
          influencePathColor = GLOW_THREAD_PALETTES.GUILD;
          if (isPlayerPolicy(owner, POLICIES.HEGEMONISM)) {
            if (isHegemonismBonusHexKey(key)) {
              isGuildOnHegemonismBonusQuarter = true;
            }
          }
          break;
        case SPECIAL_BUILDING_TOKENS.TEMPLE:
          incomeValue = calcTempleIncome(key, owner, enemyCities, hexes);
          if (isPlayerPolicy(owner, POLICIES.HEGEMONISM)) {
            if (isHegemonismBonusHexKey(key)) {
              incomeValue += POLICY_HEGEMONISM_QUARTER_WIN_POINTS_INCOME_BONUS;
            }
          }
          influencePaths = calculateInfluencePathIncome(key, enemyCities, hexes).paths;
          influencePathColor = GLOW_THREAD_PALETTES.TEMPLE;
          break;
        default:
          throw new Error(`[renderSpecialBuildingIncome] Unknown special building ${specialBuilding}`);
      }

      const incomeType = INCOME_TYPE_MAPPING[specialBuilding];
      this.renderIncome(key, hex, incomeValue, incomeType, isBlocked, {
        isAnyPlayerOwner: !!owner,
        isAnimated: isGuildOnHegemonismBonusQuarter,
      })
      if (isGuildOnHegemonismBonusQuarter) {
        this.renderIncome(key, hex, POLICY_HEGEMONISM_QUARTER_WIN_POINTS_INCOME_BONUS, INCOME_TYPE.WIN_POINTS, isBlocked, {
          isAnyPlayerOwner: !!owner,
          isAnimated: true,
          isAnimationReversed: true,
        });
      }

      if (showPaths && influencePaths.length > 0) {
        this.renderInfluencePaths(hex, influencePaths, influencePathColor);
      }
    }
  }

  /**
   * Разовый показ маркера: проявление -> секунда полной непрозрачности ->
   * затухание -> destroy. Общие тайминги ВСЕХ разовых анимаций поверх поля
   * (доход гильдий/храмов и путей влияния, подсветка городов) — держать их в
   * одном месте важно: анимации часто идут вместе и обязаны совпадать по темпу
   * @param {Phaser.GameObjects.GameObject} marker - маркер с alpha 0
   * @param {Function} [onComplete] - вызывается после уничтожения маркера
   */
  animateOneShotMarker(marker, onComplete) {
    this.scene.tweens.add({
      targets: marker,
      alpha: 1,
      duration: MARKER_FADING_DURATION_MS,
      hold: 1000,
      ease: Phaser.Math.Easing.Sine.InOut,
      yoyo: true,
      repeat: 0,
      onComplete: () => {
        marker.destroy();
        if (onComplete) {
          onComplete();
        }
      }
    });
  }

  showSpecialBuildingIncomeAnimation(key, specialBuilding, players, citiesCache, units, hexes, specialBuildings, buildingsDamage, currentPlayer) {
    const hex = this.hexes[key];
    if (!hex) return;

    // Двойник падающего жетона лежит выше плашки дохода и уходит разом: начни показ под
    // ним, и плашка будто бы появится мгновенно. Поэтому показ ждет конца размещения
    clearTimeout(hex.deferredIncomeRevealTimer);
    const placementRemainingMs = this.getPlacementAnimationRemainingMs(key);
    if (placementRemainingMs > 0) {
      hex.deferredIncomeRevealTimer = setTimeout(() => {
        this.showSpecialBuildingIncomeAnimation(
          key, specialBuilding, players, citiesCache, units, hexes, specialBuildings, buildingsDamage, currentPlayer
        );
      }, placementRemainingMs);
      return;
    }

    if (hex.animatedIncomeMarkers) {
      hex.animatedIncomeMarkers.forEach(marker => marker.destroy());
      hex.animatedIncomeMarkers = [];
    }

    const owner = findOwner(key, players, citiesCache) || currentPlayer;
    if (!owner) return;

    const isBlocked = !!owner && checkIfHexBlockedByEnemyUnit(key, specialBuilding, owner.id, units, hexes, specialBuildings, buildingsDamage);
    const enemyCities = getEnemyCitiesForPlayer(owner.color, citiesCache);
    let incomeValue;
    let influencePaths = [];
    let influencePathColor;

    switch (specialBuilding) {
      case SPECIAL_BUILDING_TOKENS.GUILD:
        incomeValue = calcGuildIncome(key, owner, enemyCities, hexes);
        influencePaths = calculateInfluencePathIncome(key, enemyCities, hexes).paths;
        influencePathColor = GLOW_THREAD_PALETTES.GUILD;
        break;
      case SPECIAL_BUILDING_TOKENS.TEMPLE:
        incomeValue = calcTempleIncome(key, owner, enemyCities, hexes);
        if (isPlayerPolicy(owner, POLICIES.HEGEMONISM)) {
          if (isHegemonismBonusHexKey(key)) {
            incomeValue += POLICY_HEGEMONISM_QUARTER_WIN_POINTS_INCOME_BONUS;
          }
        }
        influencePaths = calculateInfluencePathIncome(key, enemyCities, hexes).paths;
        influencePathColor = GLOW_THREAD_PALETTES.TEMPLE;
        break;
      default:
        return;
    }

    const incomeType = INCOME_TYPE_MAPPING[specialBuilding];

    const markers = this.renderIncome(key, hex, incomeValue, incomeType, isBlocked, {
      alpha: 0, register: false, depth: LAYER_DEPTH.INFO_ANIMATION
    });

    const animateMarker = (marker) => this.animateOneShotMarker(marker);

    markers.forEach(animateMarker);
    this.playIncomeReveal(hex, incomeType, markers);

    if (influencePaths.length > 0) {
      const pathGraphics = this.renderInfluencePaths(hex, influencePaths, influencePathColor, {
        alpha: 0,
        register: false,
        isRevealed: true,
      });
      pathGraphics.forEach(graphics => {
        markers.push(graphics);
        animateMarker(graphics);
      });

      // Города противников, до которых дотянулись пути влияния, подсвечиваются
      // цветом владельца — заливкой того же вида, что у постоянного слоя
      // принадлежности городов, и с таймингами анимации самих путей. Состав
      // городов здесь свой: нейтральный город тоже подсвечивается (пути
      // влияния строятся до него наравне с городами соперников), хотя
      // постоянный слой его пропускает
      const influencedCities = getInfluencedEnemyCities(influencePaths, citiesCache, owner.color);
      this.renderCityOwnershipOverlay(influencedCities, {alpha: 0}).forEach(graphics => {
        markers.push(graphics);
        animateMarker(graphics);
      });
    }

    hex.animatedIncomeMarkers = markers;
  }

  /**
   * Разовая анимация «город вырос»: оверлей цветом владельца по ВСЕМ ячейкам
   * города плюс доход перечисленных ключевых точек (рудники, руины). Визуал и
   * тайминги — те же, что у подсветки вражеских городов и дохода в анимации
   * путей влияния гильдий/храмов (renderCityOwnershipOverlay +
   * animateOneShotMarker).
   *
   * Маркеры НЕ регистрируются на гексах: как и прочие разовые анимации, они
   * должны пережить перерисовки стора и доиграть поверх обновленной сцены.
   *
   * @param {Object} params
   * @param {string} params.playerColor - цвет владельца города
   * @param {string[]} params.cityHexKeys - все ячейки города
   * @param {Array<{hexKey: string, tokenType: string}>} params.keyPoints -
   *   ключевые точки, доход которых надо показать
   * @param {Object} params.incomeContext - данные для расчета дохода
   */
  showCityGrowthAnimation({playerColor, cityHexKeys, keyPoints = [], incomeContext}) {
    if (!playerColor || !cityHexKeys || cityHexKeys.length === 0) {
      return;
    }

    cityHexKeys.forEach((hexKey) => {
      this.startCityGrowthMarkers(
        `overlay:${hexKey}`,
        this.renderCityOwnershipOverlay([{playerColor, hexKeys: [hexKey]}], {alpha: 0})
      );
    });

    const {
      players, citiesCache, hexes, units, round, specialBuildings, buildingsDamage, ruinsIncomeRoundLimit,
    } = incomeContext || {};
    keyPoints.forEach(({hexKey, tokenType}) => {
      const hex = this.hexes[hexKey];
      if (!hex) {
        return;
      }

      const incomeMarkers = this.renderKeyPointIncome(
        hexKey, hex, tokenType, players, citiesCache, hexes, units, round, specialBuildings, buildingsDamage,
        ruinsIncomeRoundLimit, {alpha: 0, register: false, depth: LAYER_DEPTH.INFO_ANIMATION}
      );
      this.startCityGrowthMarkers(`income:${hexKey}`, incomeMarkers);
      this.playIncomeReveal(hex, INCOME_TYPE_MAPPING[tokenType], incomeMarkers);
    });
  }

  /**
   * Запускает разовые маркеры роста города, предварительно убрав недоигравшие
   * маркеры с той же ячейки. Без этого серия построек в один город (два-четыре
   * действия строительства за фазу, ход бота, ускоренный повтор матча) кладет
   * на ячейку несколько заливок по CITY_OWNERSHIP_FILL_ALPHA каждая: они
   * складываются (0.7 -> 0.91 -> 0.97) и город вместо подсветки превращается в
   * сплошное пятно, под которым не видно самих жетонов. Ячейка — правильная
   * единица отмены: рост РАЗНЫХ городов не перекрывается и гасить друг друга
   * не должен.
   * @param {string} trackingKey - ключ отслеживания (тип маркера + ячейка)
   * @param {Array<Phaser.GameObjects.GameObject>} markers
   */
  startCityGrowthMarkers(trackingKey, markers) {
    if (!markers || markers.length === 0) {
      return;
    }

    this.destroyCityGrowthMarkers(trackingKey);
    this.cityGrowthAnimationMarkers.set(trackingKey, markers);

    let remaining = markers.length;
    markers.forEach((marker) => this.animateOneShotMarker(marker, () => {
      remaining -= 1;
      // Своими считаем только маркеры, которые все еще числятся за ключом:
      // более новая анимация той же ячейки уже перезаписала запись
      if (remaining === 0 && this.cityGrowthAnimationMarkers.get(trackingKey) === markers) {
        this.cityGrowthAnimationMarkers.delete(trackingKey);
      }
    }));
  }

  /**
   * Разовая анимация СЕТИ снабжения между фермами — тот же стиль, что у пути
   * снабжения от фермы до отряда (showSupplyPathAnimation): светящиеся нити
   * тянутся от фермы к ферме, на фермах точки, проявление и затухание. Отличие в
   * том, что путей несколько и рисуются они одной анимацией.
   *
   * @param {string} trackingKey - ключ анимации (ячейка построенной или
   *   запланированной фермы либо общий ключ осмотра): повторный вызов с тем же
   *   ключом гасит предыдущую
   * @param {string[][]} paths - пути [ферма, ..., ферма]
   * @param {Object} [options]
   * @param {string[]} [options.nodeHexKeys] - фермы сети (точки на линиях)
   */
  showSupplyNetworkAnimation(trackingKey, paths, {nodeHexKeys = []} = {}) {
    const validPaths = (paths || []).filter((path) => path && path.length >= 2 && this.hexes[path[0]]);
    if (validPaths.length === 0) {
      return;
    }

    this.destroySupplyNetworkAnimation(trackingKey);

    // Та же глубина, что у валидной части пути снабжения
    const depth = LAYER_DEPTH.UNIT - 0.5;
    const graphicsToAnimate = [];
    let maxRevealMs = 0;

    for (const path of validPaths) {
      const startHex = this.hexes[path[0]];
      const revealMs = resolveThreadRevealMs(buildThreadGeometry(this.getPathPoints(path)).length, startHex.size);
      maxRevealMs = Math.max(maxRevealMs, revealMs);
      // Точки на фермах рисуются отдельно: конец связи — тоже ферма сети
      const segmentGraphics = this.renderInfluencePaths(startHex, [path], GLOW_THREAD_PALETTES.SUPPLY, {
        alpha: 0,
        register: false,
        depth,
        revealMs,
        hasEndNode: false,
      });
      graphicsToAnimate.push(...segmentGraphics);
    }

    // Точки на фермах сети — того же размера, что точки промежуточных ферм
    // пути снабжения
    const nodeGraphics = this.renderSupplyNodes(nodeHexKeys, depth);
    if (nodeGraphics) {
      graphicsToAnimate.push(nodeGraphics);
    }

    this.supplyNetworkAnimationGraphics.set(trackingKey, graphicsToAnimate);

    let remaining = graphicsToAnimate.length;
    graphicsToAnimate.forEach((graphics) => {
      this.scene.tweens.add({
        targets: graphics,
        alpha: 1,
        duration: SUPPLY_ANIMATION_DURATION_MS,
        // Сеть успевает дотянуться и еще побыть целиком
        hold: SUPPLY_ANIMATION_HOLD_MS + maxRevealMs,
        ease: Phaser.Math.Easing.Sine.InOut,
        yoyo: true,
        repeat: 0,
        onComplete: () => {
          graphics.destroy();
          remaining -= 1;
          // Своей считаем только ту анимацию, что все еще числится за ключом:
          // более новая постройка на той же ячейке уже перезаписала запись
          if (remaining === 0 && this.supplyNetworkAnimationGraphics.get(trackingKey) === graphicsToAnimate) {
            this.supplyNetworkAnimationGraphics.delete(trackingKey);
          }
        },
      });
    });
  }

  destroySupplyNetworkAnimation(trackingKey) {
    const graphicsList = this.supplyNetworkAnimationGraphics.get(trackingKey);
    if (!graphicsList) {
      return;
    }

    graphicsList.forEach((graphics) => {
      this.scene.tweens.killTweensOf(graphics);
      graphics.destroy();
    });
    this.supplyNetworkAnimationGraphics.delete(trackingKey);
  }

  /**
   * Клубы пыли от только что поставленного жетона: тяжелый объект упал на
   * пыльную дорогу. Состав залпа каждый раз свой (см. buildBuildingDustBurst),
   * поэтому две одинаковые постройки подряд выглядят по-разному.
   *
   * Спрайты живут вне hex.markers и сами себя уничтожают в конце твина —
   * clearMarkers()/clearTweens() на каждом обновлении стора их не трогают.
   *
   * @param {string} hexKey
   * @param {Object} [options]
   * @param {number} [options.intensity] — «тяжесть» жетона
   */
  showBuildingPlacementDust(hexKey, {intensity = 1} = {}) {
    const hex = this.hexes[hexKey];
    if (!hex) {
      return;
    }

    // Текстуры могли не загрузиться (сцена без preload в тестах, битый файл) —
    // тогда эффекта просто нет, а партия идет дальше
    const textureKeys = BUILDING_DUST_TEXTURES
      .map((texture) => texture.key)
      .filter((key) => this.scene.textures?.exists?.(key));
    if (textureKeys.length === 0) {
      return;
    }

    // Повторная постройка на той же ячейке начинает залп заново
    this.destroyBuildingPlacementDust(hexKey);
    // Применение плана ставит пачку жетонов подряд: самые старые залпы гасим,
    // чтобы поле не утонуло в пыли
    while (this.buildingDustSprites.size >= BUILDING_DUST.maxLiveBursts) {
      this.destroyBuildingPlacementDust(this.buildingDustSprites.keys().next().value);
    }

    const {puffs} = buildBuildingDustBurst({hexSize: hex.size, intensity, textureKeys});
    if (puffs.length === 0) {
      return;
    }
    this.markPlacementAnimation(hexKey, BUILDING_DUST.delay.max + BUILDING_DUST.duration.max);

    const sprites = puffs.map((puff) => {
      const sprite = this.scene.add.sprite(hex.centerX + puff.offsetX, hex.centerY + puff.offsetY, puff.textureKey);
      sprite.setDepth(LAYER_DEPTH.BUILDING_DUST);
      sprite.setScale(puff.startScale);
      // Клуб виден сразу: пыль выбивает ударом, а не проявляется
      sprite.setAlpha(puff.startAlpha);
      sprite.setAngle(puff.angleDeg);
      sprite.setFlipX(puff.flipX);
      // Песочный тон: серые текстуры рядом с теплой подложкой читаются как дым
      sprite.setTint?.(BUILDING_DUST_TINT);
      return sprite;
    });
    this.buildingDustSprites.set(hexKey, sprites);

    let remaining = sprites.length;
    sprites.forEach((sprite, index) => {
      const puff = puffs[index];
      this.scene.tweens.add({
        targets: sprite,
        x: sprite.x + puff.driftX,
        y: sprite.y + puff.driftY,
        scale: puff.endScale,
        // Затухание с отдельным разгоном: на общем ease клуб гас за первые
        // 200 мс, не успев разлететься. Здесь он держит плотность на лету и
        // тает к концу
        alpha: {value: 0, ease: Phaser.Math.Easing.Quadratic.In},
        delay: puff.delay,
        duration: puff.duration,
        // Резкий старт с торможением: пыль выбивает ударом, дальше она оседает.
        // Поворота в твине нет намеренно — вращение превращало залп в торнадо
        ease: Phaser.Math.Easing.Quartic.Out,
        onComplete: () => {
          sprite.destroy();
          remaining -= 1;
          // Своим считаем только залп, который все еще числится за ячейкой:
          // более новая постройка уже перезаписала запись
          if (remaining === 0 && this.buildingDustSprites.get(hexKey) === sprites) {
            this.buildingDustSprites.delete(hexKey);
          }
        },
      });
    });
  }

  destroyBuildingPlacementDust(hexKey) {
    const sprites = this.buildingDustSprites.get(hexKey);
    if (!sprites) {
      return;
    }

    sprites.forEach((sprite) => {
      this.scene.tweens.killTweensOf(sprite);
      sprite.destroy();
    });
    this.buildingDustSprites.delete(hexKey);
  }

  /**
   * Анимация выстрела: катапульта бросает валуны по высокой дуге (тень на
   * земле, осколки и пыль при ударе), эльфы дают залп стрел. Снарядов в
   * залпе — по размеру отряда. Последний снаряд — это попадание: вспышка на
   * цели, цель вздрагивает.
   *
   * ИТОГ ВЫСТРЕЛА ПРИДЕРЖИВАЕТСЯ ДО ПОПАДАНИЯ. Экшен выстрела меняет состояние
   * сразу, и поле по нему уже перерисовано: на цели стоит «−N», обреченный
   * отряд мигает, строение показано поврежденным, линия выстрела проведена.
   * Пока снаряд летит, такой выстрел в отрисовку не идет
   * (filterLandedShootingActions), а в момент попадания поле перерисовывается
   * — и итог появляется вместе с ударом.
   *
   * Спрайты живут в своем реестре (shotAnimations), а не в hex.markers: поле
   * пересобирается на каждое обновление стора.
   *
   * ВЫСТРЕЛ ПРИМЕНИЛИ, ПОКА СНАРЯД ЛЕТЕЛ (этап завершили): залп не
   * обрывается, снаряд долетает и бьет в цель — пропавший в воздухе валун
   * выглядел бы поломкой. Прятать уже нечего: итог выстрела к этому моменту
   * применен и виден в самом состоянии. Обрывается только зависший залп
   * (dropStaleShotAnimations).
   *
   * ЧУЖОЙ ВЫСТРЕЛ В ЖИВОЙ ПАРТИИ УКЛАДЫВАЕТСЯ В ШАГ ТЕМПА (paceMs): боты
   * делают следующее действие через известную паузу, и им может оказаться
   * завершение этапа. Залп сжимается по времени, чтобы попасть раньше и
   * оставить итогу время побыть на поле; если не уложиться — анимации нет
   * (см. resolveShotTimeFactor).
   *
   * @param {Object} action — запись из pendingShootingActions
   * @param {Object} [options]
   * @param {string} [options.kind] — SHOT_KINDS: валун или стрелы
   * @param {Array} [options.units] — отряды партии
   * @param {number} [options.delayMs] — пауза до пуска (переброс костей: сначала
   *   звучат кости)
   * @param {number} [options.paceMs] — шаг темпа чужих действий; не задан —
   *   стреляет сам игрок, залп идет с обычной длительностью
   * @param {number} [options.round] — раунд выстрела: по нему узнается залп,
   *   оставшийся от состояния, которое переписали на более раннее (см.
   *   dropStaleShotAnimations)
   * @returns {boolean} пошла ли анимация
   */
  showShotAnimation(action, {kind = SHOT_KINDS.BOULDER, units = [], delayMs = 0, paceMs, round, isMiss = false, color = 0xFFFFFF} = {}) {
    if (!action?.id || !this.shotAnimations || !this.scene?.tweens?.add) {
      return false;
    }

    const shooterUnit = units.find((unit) => unit.id === action.shooterUnitId);
    const targetUnit = action.targetUnitId ? units.find((unit) => unit.id === action.targetUnitId) : null;
    const targetHexKey = targetUnit ? targetUnit.hexKey : action.targetHexKey;
    const shooterHex = shooterUnit && this.hexes[shooterUnit.hexKey];
    const targetHex = targetHexKey && this.hexes[targetHexKey];
    if (!shooterHex || !targetHex) {
      return false;
    }
    // Текстур снарядов нет (сцена без WebGL-текстур в тестах) — эффекта просто
    // нет, а итог выстрела виден сразу, как раньше
    if (!ensureShotTextures(this.scene, {linearFilter: Phaser.Textures?.FilterMode?.LINEAR})) {
      return false;
    }

    // Концы дуги — на высоте отрядов, как у линии выстрела (см. getUnitShiftY)
    const from = {x: shooterHex.centerX, y: shooterHex.centerY + this.getUnitShiftY(shooterHex)};
    const to = {x: targetHex.centerX, y: targetHex.centerY + this.getUnitShiftY(targetHex)};
    const volley = buildShotVolley({kind, from, to, hexSize: shooterHex.size, unitSize: shooterUnit.size});
    if (volley.projectiles.length === 0) {
      return false;
    }
    // Чужой выстрел должен попасть в цель до следующего действия. Не
    // укладывается — анимации нет, итог виден сразу
    const timeFactor = resolveShotTimeFactor({impactAtMs: delayMs + volley.impactAtMs, paceMs});
    if (!timeFactor) {
      return false;
    }

    // Переброс костей повторяет залп: прежний, если еще не отыграл, снимаем.
    // Только теперь, когда новый залп точно пойдет: иначе прежний остается —
    // он долетит и сам покажет итог, который прятал
    this.destroyShotAnimation(action.id);

    const animation = {
      id: action.id,
      kind,
      isLanded: false,
      // Промах: урона по цели не будет, и камера от падения камней не дрожит
      isMiss,
      // Цвет света залпа: цвет игрока-стрелка
      color,
      // Сумеем ли показать след снаряда: текстура считается кодом
      hasTrails: ensureAbilityCastTextures(this.scene, {linearFilter: Phaser.Textures?.FilterMode?.LINEAR}),
      targetHexRotation: this.getHexRotation(targetHex),
      // Множитель длительностей до попадания (пауза, вылет, полет): меньше 1 —
      // залп сжат под шаг темпа. Эффекты удара идут с обычной длительностью
      timeFactor,
      // Выстрел чужой: идет в темпе, который задает не зритель (см. paceMs)
      isPaced: Number.isFinite(paceMs) && paceMs > 0,
      round,
      // Сколько снарядов в залпе: от этого зависит, сколько осколков дает
      // каждый валун
      projectileCount: volley.projectiles.length,
      // Сколько снарядов еще не долетело: попадание залпа — когда упал
      // последний, в каком бы порядке они ни падали
      projectilesInFlight: 0,
      shooterUnitId: shooterUnit.id,
      shooterHexKey: shooterUnit.hexKey,
      targetUnitId: targetUnit ? targetUnit.id : null,
      targetFactionId: targetUnit ? targetUnit.factionId : null,
      // Урон по отряду-цели: есть урон — при попадании брызги крови
      targetDamage: targetUnit ? action.damage || 0 : 0,
      targetHexKey,
      from,
      to,
      hexSize: shooterHex.size,
      // Все, что надо убрать при обрыве: спрайты и объекты-счетчики полета
      sprites: new Set(),
      flights: new Set(),
      // Брызги крови от попадания: конца залпа не держат, обрыв убирает и их
      bloodSprites: new Set(),
      // Твины идут по часам сцены, а в скрытой вкладке она стоит: залп,
      // не долетевший к этому сроку, считается зависшим. Срок — по обычной
      // длительности: сжатый залп уложится тем более
      staleAt: Date.now() + delayMs + volley.impactAtMs + SHOT_ANIMATION.staleMarginMs,
    };
    this.shotAnimations.set(action.id, animation);
    // Интерфейсу тоже нужно знать, что итог выстрела придержан: панель с
    // результатом стрельбы в футере открывается после попадания залпа
    markShotInFlight(action.id);
    // Проигрыватель записи матча ждет, пока залп не уйдет с поля (попал, и
    // осколки погасли) — но не дольше срока, после которого он считается
    // зависшим: эффекты удара короче запаса этого срока
    markAwaitedAnimationPlaying(`shot:${action.id}`, animation.staleAt);

    try {
      volley.projectiles.forEach((projectile) => this.launchShotProjectile(animation, projectile, delayMs));
    } catch (error) {
      // Залп не запустился: без уборки его итог остался бы придержанным — и на
      // поле, и в футере — до срока зависания
      this.destroyShotAnimation(action.id);
      throw error;
    }

    // Поле уже нарисовано с итогом выстрела — перерисовываем, чтобы спрятать
    // его до попадания. Кадр между двумя перерисовками не успевает выйти
    this.requestFieldRedraw();
    this.playShotLaunchCast(animation, {
      delayMs: Math.round(delayMs * timeFactor),
      landAtMs: Math.round((delayMs + volley.impactAtMs) * timeFactor),
    });
    // Отдача стрелка — уже на свежих спрайтах
    const direction = to.x < from.x ? 1 : -1;
    this.nudgeUnitSprites(
      shooterUnit.id, shooterUnit.hexKey,
      direction * shooterHex.size * SHOT_ANIMATION.recoil.distanceFactor[kind],
      SHOT_ANIMATION.recoil.ms, Math.round(delayMs * timeFactor)
    );

    return true;
  }

  /** Свет пуска залпа, а у катапульты еще и прицел на цели (см. configs/combatCastAnimation) */
  playShotLaunchCast(animation, {delayMs, landAtMs}) {
    const {from, to, hexSize, color} = animation;
    if (animation.kind === SHOT_KINDS.ARROWS) {
      this.playFieldCast(() => buildArrowLaunchCastPlan({
        at: from, direction: Math.atan2(to.y - from.y, to.x - from.x), hexSize, color, delayMs,
      }));
      return;
    }
    this.playFieldCast(() => buildBoulderLaunchCastPlan({at: from, hexSize, delayMs}));
    this.playFieldCast(() => buildBoulderTargetCastPlan({at: to, hexSize, landAtMs, hexRotation: animation.targetHexRotation}));
  }

  /** Спрайт эффекта, летящий вместе со снарядом: убирается вместе с залпом */
  addShotFlightSprite(animation, textureKey, depth, tint, alpha = 0) {
    const sprite = this.scene.add.sprite(0, 0, textureKey);
    sprite.setOrigin(0.5, 0.5);
    sprite.setDepth(depth);
    sprite.setTint?.(tint);
    sprite.setAlpha(alpha);
    animation.sprites.add(sprite);
    return sprite;
  }

  /**
   * След снаряда: у стрелы — светящаяся черточка цветом игрока с ярким кончиком, у валуна
   * — пламя вокруг камня и хвост огня за ним. Лежит под самим снарядом
   */
  createShotTrail(animation) {
    if (!animation.hasTrails) {
      return [];
    }
    const below = (share) => LAYER_DEPTH.SHOT_PROJECTILE - share;
    const glow = MELEE_TEXTURE_KEYS.GLOW;
    const streak = ABILITY_CAST_TEXTURE_KEYS.STREAK;
    const {hexSize, color} = animation;
    const streakLayer = (tint, depthShare, lengthPx, thicknessPx, alpha) => {
      const width = ABILITY_CAST_TEXTURE_SIZES[streak].width;
      const height = ABILITY_CAST_TEXTURE_SIZES[streak].height;
      const sprite = this.addShotFlightSprite(animation, streak, below(depthShare), tint);
      sprite.setOrigin(1, 0.5);
      return {sprite, isTail: true, scaleX: lengthPx / width, scaleY: thicknessPx / height, alpha};
    };
    const glowLayer = (tint, depthShare, sizePx, alpha) => {
      const sprite = this.addShotFlightSprite(animation, glow, below(depthShare), tint);
      const scale = sizePx / MELEE_TEXTURE_SIZES[glow].width;
      sprite.setScale(scale);
      return {sprite, alpha, glowScale: scale};
    };

    if (animation.kind === SHOT_KINDS.ARROWS) {
      const {trail} = ARROW_CAST;
      const length = hexSize * trail.lengthFactor;
      const thickness = hexSize * trail.thicknessFactor;
      return [
        streakLayer(color, 0.004, length, thickness, trail.alpha),
        streakLayer(0xFFFFFF, 0.003, length * 0.8, thickness * trail.coreThicknessShare, trail.alpha),
        glowLayer(color, 0.002, hexSize * trail.tipSizeFactor, trail.tipAlpha),
      ];
    }

    const {fire} = BOULDER_CAST;
    const boulder = hexSize * SHOT_ANIMATION.boulder.sizeFactor;
    return [
      streakLayer(COMBAT_CAST_COLORS.fire, 0.005, boulder * fire.tailLengthFactor * 2, boulder * fire.tailThicknessFactor * 2.4, fire.tailAlpha),
      streakLayer(COMBAT_CAST_COLORS.fireLight, 0.004, boulder * fire.tailLengthFactor * 1.3, boulder * fire.tailThicknessFactor * 2.4 * fire.tailCoreShare, fire.tailAlpha),
      glowLayer(COMBAT_CAST_COLORS.fire, 0.003, boulder * fire.glowSizeFactor * 2, fire.glowAlpha),
      glowLayer(COMBAT_CAST_COLORS.fireLight, 0.002, boulder * fire.coreSizeFactor * 2, fire.coreAlpha),
    ];
  }

  /** Поставить след снаряда за ним: хвост смотрит против движения */
  placeShotTrail(item, position, progress) {
    // Камень в верхней точке крупнее: огонь растет вместе с ним
    const liftScale = item.projectile.spinTurns === undefined
      ? 1
      : 1 + (SHOT_ANIMATION.boulder.peakScaleMultiplier - 1) * position.lift;
    (item.trail || []).forEach(({sprite, isTail, scaleX, scaleY, alpha, glowScale}) => {
      sprite.x = position.x;
      sprite.y = position.y;
      if (isTail) {
        sprite.rotation = position.angleRad;
        // Хвост вытягивается за первые мгновения полета: снаряд только что вылетел
        sprite.setScale?.(scaleX * liftScale * Math.min(1, 0.15 + progress / 0.22), scaleY * liftScale);
      } else {
        sprite.setScale?.(glowScale * liftScale);
      }
      sprite.setAlpha(alpha * Math.min(1, progress * 12));
    });
  }

  /**
   * Что остается в воздухе за горящим валуном: дым и угольки. Они гаснут сами. Шаг зависит
   * от числа камней: большой залп не должен превращаться в сотни спрайтов
   */
  emitBoulderTrail(animation, item, position, elapsedMs) {
    if (!animation.hasTrails) {
      return;
    }
    const {trail} = BOULDER_CAST;
    const gapMs = trail.gapMs + trail.gapPerBoulderMs * Math.max(0, animation.projectileCount - 1);
    if (elapsedMs - (item.lastPuffAtMs ?? -Infinity) < gapMs) {
      return;
    }
    item.lastPuffAtMs = elapsedMs;
    item.puffIndex = (item.puffIndex || 0) + 1;
    const {hexSize} = animation;
    const addFading = (textureKey, tint, sizePx, alpha, depth, end) => {
      const sprite = this.addShotFlightSprite(animation, textureKey, depth, tint, alpha);
      sprite.x = position.x + (end.jitterX || 0);
      sprite.y = position.y + (end.jitterY || 0);
      const baseScale = sizePx / (sprite.width || 1);
      sprite.setScale(baseScale);
      this.scene.tweens.add({
        targets: sprite,
        scale: baseScale * end.growth,
        y: sprite.y + end.driftY,
        alpha: {value: 0, ease: Phaser.Math.Easing.Quadratic.In},
        duration: end.lifeMs,
        ease: Phaser.Math.Easing.Quadratic.Out,
        onComplete: () => {
          this.destroyShotSprite(animation, sprite);
          this.finishShotAnimationIfDone(animation);
        },
      });
    };
    addFading(MELEE_TEXTURE_KEYS.GLOW, COMBAT_CAST_COLORS.fire, hexSize * trail.flame.sizeFactor * (1 + position.lift * 0.4), trail.flame.alpha, LAYER_DEPTH.SHOT_PROJECTILE - 0.008, {
      growth: trail.flame.shrink, driftY: 0, lifeMs: trail.flame.lifeMs,
    });
    if (item.puffIndex % trail.smoke.everyNth === 0) {
      addFading(ABILITY_CAST_TEXTURE_KEYS.PUFF, COMBAT_CAST_COLORS.smoke, hexSize * trail.smoke.sizeFactor, trail.smoke.alpha, LAYER_DEPTH.SHOT_PROJECTILE - 0.01, {
        growth: trail.smoke.growth, driftY: -hexSize * trail.smoke.riseFactor, lifeMs: trail.smoke.lifeMs,
      });
    }
    if (item.puffIndex % trail.ember.everyNth === 0) {
      const spread = hexSize * trail.ember.spreadFactor;
      addFading(MELEE_TEXTURE_KEYS.BURST, COMBAT_CAST_COLORS.fireLight, hexSize * trail.ember.sizeFactor, trail.ember.alpha, LAYER_DEPTH.SHOT_PROJECTILE - 0.006, {
        growth: 0.4, driftY: hexSize * trail.ember.fallFactor, lifeMs: trail.ember.lifeMs,
        jitterX: (Math.random() - 0.5) * 2 * spread, jitterY: (Math.random() - 0.5) * 2 * spread,
      });
    }
  }

  /** Один снаряд залпа: спрайт (у валуна еще и тень на земле) и его полет */
  launchShotProjectile(animation, projectile, delayMs) {
    const isBoulder = animation.kind !== SHOT_KINDS.ARROWS;
    // У каждого камня залпа своя картинка и свой размер
    const sizeMultiplier = isBoulder ? projectile.sizeMultiplier ?? 1 : 1;
    const {key: textureKey, width: textureWidth} = isBoulder
      ? this.resolveBoulderTexture(projectile.variantSeed)
      : {key: SHOT_TEXTURE_KEYS.ARROW, width: SHOT_TEXTURE_SIZES[SHOT_TEXTURE_KEYS.ARROW].width};
    const sizeFactor = isBoulder ? SHOT_ANIMATION.boulder.sizeFactor : SHOT_ANIMATION.arrows.lengthFactor;
    const baseScale = animation.hexSize * sizeFactor * sizeMultiplier / textureWidth;

    const sprite = this.scene.add.sprite(projectile.startX, projectile.startY, textureKey);
    sprite.setDepth(LAYER_DEPTH.SHOT_PROJECTILE);
    sprite.setOrigin(0.5, 0.5);
    sprite.setScale(baseScale);
    // До своего вылета снаряда не видно: стрелы уходят одна за другой
    sprite.setAlpha(0);
    animation.sprites.add(sprite);

    let shadow = null;
    let shadowBaseScale = 0;
    if (isBoulder && this.scene.textures?.exists?.(SHOT_SHADOW_TEXTURE_KEY)) {
      shadowBaseScale = animation.hexSize * SHOT_ANIMATION.boulder.shadow.sizeFactor * sizeMultiplier
        / SHOT_SHADOW_TEXTURE_WIDTH;
      shadow = this.scene.add.sprite(projectile.startX, projectile.startY, SHOT_SHADOW_TEXTURE_KEY);
      shadow.setDepth(LAYER_DEPTH.SHOT_SHADOW);
      shadow.setOrigin(0.5, 0.5);
      shadow.setScale(shadowBaseScale);
      shadow.setAlpha(0);
      animation.sprites.add(shadow);
    }

    if (isBoulder && animation.hasTrails) {
      // Камень раскален: огонь вокруг него только подчеркивает, что он горит
      sprite.setTint?.(BOULDER_CAST.fire.boulderTint);
    }
    const trail = this.createShotTrail(animation);
    const item = {projectile, sprite, shadow, baseScale, shadowBaseScale, isLaunched: false, trail};
    // Твин ведет не спрайт, а часы снаряда: положение на дуге считается по ним.
    //
    // ЧАСЫ У ВСЕХ СНАРЯДОВ ЗАЛПА ИДУТ С ОДНОГО ШАГА. Пауза твина у них общая
    // (переброс костей), а свой вылет каждого (launchAtMs) отсчитывается уже
    // внутри. Отдать шаг между снарядами паузе твина нельзя: досчитав паузу,
    // Phaser отбрасывает остаток шага, а выстрел всегда начинается с заминки
    // кадра — поле перерисовывается. Все снаряды с паузой короче заминки
    // вылетали бы разом, и залп слипался бы в ком
    const launchAtMs = Math.round(projectile.delay * animation.timeFactor);
    const flightMs = Math.max(1, Math.round(projectile.duration * animation.timeFactor));
    const flight = {elapsed: 0, launchAtMs, flightMs};
    animation.flights.add(flight);
    animation.projectilesInFlight += 1;
    const isCurrent = () => this.shotAnimations.get(animation.id) === animation;

    this.scene.tweens.add({
      targets: flight,
      elapsed: launchAtMs + flightMs,
      delay: Math.round(delayMs * animation.timeFactor),
      duration: launchAtMs + flightMs,
      // Вдоль земли снаряд идет ровно — тогда дуга получается параболой
      ease: 'Linear',
      onUpdate: () => {
        if (!isCurrent() || flight.elapsed < launchAtMs) {
          return;
        }
        if (!item.isLaunched) {
          item.isLaunched = true;
          sprite.setAlpha(1);
          if (isBoulder && !projectile.index) {
            // Катапульта выбивает пыль в момент броска — один раз на залп, с
            // первым камнем
            this.showBuildingPlacementDust(
              animation.shooterHexKey, {intensity: SHOT_ANIMATION.boulder.launchDustIntensity}
            );
          }
        }
        const progress = (flight.elapsed - launchAtMs) / flightMs;
        const position = this.placeShotProjectile(animation, item, progress);
        this.placeShotTrail(item, position, progress);
        if (isBoulder) {
          this.emitBoulderTrail(animation, item, position, flight.elapsed);
        }
      },
      onComplete: () => this.landShotProjectile(animation, item, flight),
    });
  }

  /** Поставить снаряд (и тень валуна) в точку дуги по ходу полета 0..1 */
  placeShotProjectile(animation, item, progress) {
    const position = resolveShotProjectilePosition(item.projectile, progress);
    const {sprite, shadow} = item;
    sprite.x = position.x;
    sprite.y = position.y;

    if (animation.kind === SHOT_KINDS.ARROWS) {
      // Стрела смотрит по касательной к дуге: вверх на взлете, вниз на спуске
      sprite.rotation = position.angleRad;
      return position;
    }

    const config = SHOT_ANIMATION.boulder;
    // В верхней точке валун ближе к зрителю — крупнее
    sprite.setScale(item.baseScale * (1 + (config.peakScaleMultiplier - 1) * position.lift));
    const spinDirection = item.projectile.endX < item.projectile.startX ? -1 : 1;
    const spinTurns = item.projectile.spinTurns ?? config.spinTurns.min;
    sprite.rotation = spinDirection * progress * spinTurns * Math.PI * 2;

    if (shadow) {
      // Тень идет по земле по прямой; чем выше валун, тем она мельче и бледнее
      shadow.x = position.groundX;
      shadow.y = position.groundY;
      shadow.setScale(item.shadowBaseScale * (1 + (config.shadow.peakScaleMultiplier - 1) * position.lift));
      shadow.setAlpha(config.shadow.alpha + (config.shadow.peakAlpha - config.shadow.alpha) * position.lift);
    }
    return position;
  }

  /** Снаряд долетел: убираем его; когда упал последний — это попадание залпа */
  landShotProjectile(animation, item, flight) {
    // Колбэк твина: исключение здесь остановило бы цикл кадров Phaser насовсем
    try {
      animation.flights.delete(flight);
      (item.trail || []).forEach(({sprite}) => this.destroyShotSprite(animation, sprite));
      const isCurrent = this.shotAnimations.get(animation.id) === animation;
      if (isCurrent && animation.kind === SHOT_KINDS.ARROWS && animation.hasTrails) {
        this.stickShotArrow(animation, item);
      } else {
        this.destroyShotSprite(animation, item.sprite);
      }
      this.destroyShotSprite(animation, item.shadow);
      if (!isCurrent) {
        return;
      }

      // Эффект удара — украшение: его сбой не должен сорвать учет залпа ниже,
      // иначе счетчик не дойдет до нуля и итог выстрела останется спрятанным
      try {
        if (animation.kind === SHOT_KINDS.ARROWS) {
          this.showShotSpark(animation, item.projectile);
          this.playFieldCast(() => buildArrowHitCastPlan({
            at: {x: item.projectile.endX, y: item.projectile.endY},
            angle: resolveShotProjectilePosition(item.projectile, 1).angleRad,
            hexSize: animation.hexSize,
            color: animation.color,
            isMiss: animation.isMiss,
          }));
        } else {
          this.playFieldCast(() => buildBoulderImpactCastPlan({
            at: {x: item.projectile.endX, y: item.projectile.endY},
            hexSize: animation.hexSize,
            boulderCount: animation.projectileCount,
            isMiss: animation.isMiss,
          }));
          // Камень раскалывается о землю: осколки и клуб пыли — у каждого валуна
          this.showShotDebris(animation, item.projectile);
          // Камера вздрагивает от каждого упавшего валуна — кроме промаха
          if (!animation.isMiss) {
            shakeFieldCamera(this.scene, SHOT_ANIMATION.boulder.impactShake);
          }
        }
      } catch (error) {
        console.warn('[Shot] не удалось показать удар снаряда', error);
      }
      animation.projectilesInFlight -= 1;
      if (animation.projectilesInFlight <= 0 && !animation.isLanded) {
        this.landShotAnimation(animation);
      }
      this.finishShotAnimationIfDone(animation);
    } catch (error) {
      console.warn('[Shot] не удалось довести снаряд до цели', error);
    }
  }

  /**
   * Попадание залпа (последний снаряд): итог выстрела появляется на поле
   * (урон на цели, поврежденное строение, линия выстрела), у валунов — пыль
   * по всей ячейке, отряд-цель вздрагивает. Значка урона над целью нет — ни
   * над отрядом, ни над строением: он закрывал бы цель. Панель результата в
   * футере открывается чуть позже поля — итог на поле сначала виден целиком
   */
  landShotAnimation(animation) {
    animation.isLanded = true;
    // Запись матча дает итогу выстрела побыть на поле (см. ReplayRunner)
    markAwaitedAnimationResultShown();
    // Пауза перед панелью результата — у своего выстрела. Чужой идет в
    // чужом темпе: до следующего действия (а им может быть конец этапа)
    // остается доля шага, и с паузой панель не успела бы показаться
    markShotLanded(animation.id, {revealDelayMs: animation.isPaced ? 0 : SHOT_ANIMATION.resultPanelDelayMs});
    this.requestFieldRedraw();

    const isBoulder = animation.kind !== SHOT_KINDS.ARROWS;
    if (isBoulder) {
      this.showBuildingPlacementDust(
        animation.targetHexKey, {intensity: SHOT_ANIMATION.boulder.impactDustIntensity}
      );
    }
    // Промах сил не показывает: только свои пыль и искры у каждого снаряда
    if (!animation.isMiss) {
      this.playFieldCast(() => (isBoulder
        ? buildBoulderVolleyCastPlan({at: animation.to, hexSize: animation.hexSize, hexRotation: animation.targetHexRotation})
        : buildArrowVolleyCastPlan({
          at: animation.to,
          hexSize: animation.hexSize,
          color: animation.color,
          arrowCount: animation.projectileCount,
          hexRotation: animation.targetHexRotation,
        })));
    }

    // Попадание по отряду — брызги крови от стрелка
    if (animation.targetUnitId && animation.targetDamage > 0) {
      this.showBloodSplatter({
        at: animation.to,
        direction: Math.atan2(animation.to.y - animation.from.y, animation.to.x - animation.from.x),
        hexSize: animation.hexSize,
        factionId: animation.targetFactionId,
        intensity: resolveBloodIntensity(animation.targetDamage),
        owner: animation.bloodSprites,
      });
    }

    // Импульс от стрелка: один толчок и обратно — не мигание
    if (animation.targetUnitId) {
      const direction = animation.to.x < animation.from.x ? -1 : 1;
      this.nudgeUnitSprites(
        animation.targetUnitId, animation.targetHexKey,
        direction * animation.hexSize * SHOT_ANIMATION.impact.shakeDistanceFactor[animation.kind],
        SHOT_ANIMATION.impact.shakeMs
      );
    }
  }

  /** Стрела воткнулась: мгновение дрожит на месте и гаснет */
  stickShotArrow(animation, item) {
    const {stuck} = ARROW_CAST;
    const {sprite, projectile} = item;
    const position = resolveShotProjectilePosition(projectile, 1);
    sprite.x = position.x;
    sprite.y = position.y;
    sprite.rotation = position.angleRad;
    sprite.setDepth(LAYER_DEPTH.SHOT_IMPACT);
    this.scene.tweens.add({
      targets: sprite,
      rotation: position.angleRad + stuck.wobbleRad,
      duration: stuck.wobbleMs,
      yoyo: true,
      repeat: stuck.wobbleRepeats,
      ease: 'Sine.easeInOut',
    });
    this.scene.tweens.add({
      targets: sprite,
      alpha: 0,
      delay: stuck.holdMs,
      duration: stuck.fadeMs,
      ease: Phaser.Math.Easing.Quadratic.In,
      onComplete: () => {
        this.destroyShotSprite(animation, sprite);
        this.finishShotAnimationIfDone(animation);
      },
    });
  }

  /**
   * Искра в точке попадания снаряда: у стрелы — маленькая, у валуна —
   * хлопок покрупнее (config — свой у каждого вида снаряда)
   */
  showShotSpark(animation, projectile, config = SHOT_ANIMATION.arrows.spark) {
    const endScale = animation.hexSize * config.sizeFactor / SHOT_TEXTURE_SIZES[SHOT_TEXTURE_KEYS.SPARK].width;
    const sprite = this.scene.add.sprite(projectile.endX, projectile.endY, SHOT_TEXTURE_KEYS.SPARK);
    sprite.setDepth(LAYER_DEPTH.SHOT_IMPACT);
    sprite.setOrigin(0.5, 0.5);
    sprite.setScale(endScale * config.startScaleMultiplier);
    sprite.setAlpha(1);
    animation.sprites.add(sprite);

    this.fadeOutShotSprite(animation, sprite, endScale, config.durationMs);
  }

  /**
   * Картинка для валуна: одна из загруженных, у каждого камня залпа своя.
   * Картинки могли не загрузиться — тогда валун рисуется запасной текстурой
   * из кода (см. shotProjectileTextures)
   *
   * @param {number} [variantSeed] — 0..1, какая из картинок
   * @returns {{key: string, width: number}}
   */
  resolveBoulderTexture(variantSeed = 0) {
    const keys = SHOT_PROJECTILE_IMAGES
      .map((image) => image.key)
      .filter((key) => this.scene.textures?.exists?.(key));
    if (keys.length === 0) {
      return {key: SHOT_TEXTURE_KEYS.BOULDER, width: SHOT_TEXTURE_SIZES[SHOT_TEXTURE_KEYS.BOULDER].width};
    }

    return {
      key: keys[Math.min(keys.length - 1, Math.floor(variantSeed * keys.length))],
      width: SHOT_PROJECTILE_IMAGE_SIZE,
    };
  }

  /**
   * Валун упал: камень раскалывается, осколки разлетаются от точки удара в
   * стороны, вдоль земли; из-под удара выбивает клуб пыли (состав —
   * buildShotDebris). Осколки и пыль числятся за залпом: его обрыв убирает и
   * их. Идут с обычной длительностью, даже когда сам залп сжат под шаг темпа
   */
  showShotDebris(animation, projectile) {
    const {shards, puffs} = buildShotDebris({
      at: {x: projectile.endX, y: projectile.endY},
      from: {x: projectile.startX, y: projectile.startY},
      hexSize: animation.hexSize,
      boulderCount: animation.projectileCount,
    });
    const isCurrent = () => this.shotAnimations.get(animation.id) === animation;

    // Светлый хлопок в точке удара: от него и летят осколки
    this.showShotSpark(animation, projectile, SHOT_ANIMATION.debris.flash);

    // Пыль лежит на земле, под отрядом; осколки летят поверх него
    const dustKeys = BUILDING_DUST_TEXTURES
      .map((texture) => texture.key)
      .filter((key) => this.scene.textures?.exists?.(key));
    if (dustKeys.length > 0) {
      puffs.forEach((puff) => {
        const textureKey = dustKeys[Math.min(dustKeys.length - 1, Math.floor(puff.textureSeed * dustKeys.length))];
        const sprite = this.scene.add.sprite(puff.x, puff.y, textureKey);
        sprite.setDepth(LAYER_DEPTH.BUILDING_DUST);
        sprite.setOrigin(0.5, 0.5);
        sprite.setScale(puff.startSize / DUST_TEXTURE_SIZE);
        sprite.setAlpha(puff.alpha);
        sprite.setAngle(puff.angleDeg);
        sprite.setFlipX(puff.flipX);
        // Песочный тон — как у пыли построек
        sprite.setTint?.(BUILDING_DUST_TINT);
        animation.sprites.add(sprite);

        this.fadeOutShotSprite(animation, sprite, puff.endSize / DUST_TEXTURE_SIZE, puff.duration);
      });
    }

    // Лист осколков мог не загрузиться — тогда остается одна пыль
    if (!this.scene.textures?.exists?.(SHOT_SHARDS_SHEET.key)) {
      return;
    }
    shards.forEach((shard) => {
      const baseScale = shard.size / SHOT_SHARDS_SHEET.frameWidth;
      const sprite = this.scene.add.sprite(shard.startX, shard.startY, SHOT_SHARDS_SHEET.key, shard.frame);
      sprite.setDepth(LAYER_DEPTH.SHOT_IMPACT);
      sprite.setOrigin(0.5, 0.5);
      sprite.setScale(baseScale);
      sprite.rotation = shard.startAngleRad;
      // До своего вылета осколка не видно
      sprite.setAlpha(0);
      animation.sprites.add(sprite);

      // Как и у снаряда, твин ведет счетчик хода: положение на дуге, поворот
      // и затухание считаются по нему
      const flight = {travel: 0};
      animation.flights.add(flight);
      const placeShard = () => {
        if (!isCurrent()) {
          return;
        }
        const position = resolveShotProjectilePosition(shard, flight.travel);
        const appearance = resolveShardAppearance(shard, flight.travel);
        sprite.x = position.x;
        sprite.y = position.y;
        sprite.rotation = appearance.rotation;
        sprite.setScale(baseScale * appearance.scaleMultiplier);
        sprite.setAlpha(appearance.alpha);
      };

      this.scene.tweens.add({
        targets: flight,
        travel: 1,
        delay: shard.delay,
        duration: shard.duration,
        // Осколок вылетает резко и тормозит: его разносит от удара в сторону
        ease: Phaser.Math.Easing.Quadratic.Out,
        onStart: placeShard,
        onUpdate: placeShard,
        onComplete: () => {
          animation.flights.delete(flight);
          this.destroyShotSprite(animation, sprite);
          this.finishShotAnimationIfDone(animation);
        },
      });
    });
  }

  /** Разовый спрайт эффекта: разрастается до endScale, гаснет и убирает себя */
  fadeOutShotSprite(animation, sprite, endScale, durationMs) {
    this.scene.tweens.add({
      targets: sprite,
      scale: endScale,
      // Затухание с разгоном: вспышка держит яркость и тает к концу
      alpha: {value: 0, ease: Phaser.Math.Easing.Quadratic.In},
      duration: durationMs,
      ease: Phaser.Math.Easing.Quadratic.Out,
      onComplete: () => {
        this.destroyShotSprite(animation, sprite);
        this.finishShotAnimationIfDone(animation);
      },
    });
  }

  /**
   * Короткий толчок отряда по горизонтали и обратно: отдача стрелка и
   * вздрагивание цели. Двигаются настоящие спрайты отряда, поэтому твин
   * регистрируется на ячейке — следующая перерисовка поля его снимет (спрайты
   * она все равно пересоздаст на своих местах). По вертикали не толкаем: там
   * у отряда свое покачивание, два твина на одном свойстве дрожали бы
   */
  nudgeUnitSprites(unitId, hexKey, offsetX, durationMs, delayMs = 0) {
    const hex = this.hexes[hexKey];
    if (!hex || !unitId || !offsetX) {
      return;
    }

    const sprites = this.getUnitMovableSprites(unitId, hex);
    if (sprites.length === 0) {
      return;
    }

    this.registerTweens(hex, this.scene.tweens.add({
      targets: sprites,
      x: `+=${offsetX}`,
      delay: delayMs,
      duration: durationMs,
      yoyo: true,
      ease: Phaser.Math.Easing.Quadratic.Out,
    }));
  }

  destroyShotSprite(animation, sprite) {
    if (!sprite) {
      return;
    }
    animation.sprites.delete(sprite);
    // Phaser не снимает твины с уничтоженного объекта
    this.scene.tweens.killTweensOf?.(sprite);
    sprite.destroy();
  }

  /** Залп отыграл целиком (попадание было, эффекты погасли) — забываем его */
  finishShotAnimationIfDone(animation) {
    if (animation.isLanded
      && animation.sprites.size === 0
      && animation.flights.size === 0
      && this.shotAnimations.get(animation.id) === animation
    ) {
      this.shotAnimations.delete(animation.id);
      markAwaitedAnimationFinished(`shot:${animation.id}`);
    }
  }

  /** Оборвать залп: убрать снаряды и эффекты. Итог выстрела после этого виден */
  destroyShotAnimation(actionId) {
    const animation = this.shotAnimations?.get(actionId);
    if (!animation) {
      return;
    }

    this.shotAnimations.delete(actionId);
    // Оборванный залп итог больше не прячет — ни на поле, ни в футере — и
    // запись матча больше не задерживает
    markShotLanded(actionId);
    markAwaitedAnimationFinished(`shot:${actionId}`);
    animation.flights.forEach((flight) => this.scene.tweens.killTweensOf?.(flight));
    animation.flights.clear();
    [...animation.sprites].forEach((sprite) => this.destroyShotSprite(animation, sprite));
    animation.bloodSprites?.forEach((sprite) => this.destroyBloodSprite(sprite));
    animation.bloodSprites?.clear();
  }

  /**
   * Оборвать залпы, которым на этом поле больше не место. Зовется на каждой
   * перерисовке поля ДО отбора выстрелов для отрисовки.
   *  - Зависший залп: вкладка была скрыта, кадры не шли, а итог выстрела все
   *    это время прятался.
   *  - Залп из «будущего»: состояние переписали на более раннее (запись
   *    матча перемотали к началу) — раунд на поле меньше раунда выстрела. В
   *    живой партии раунды назад не идут, так что законный залп под это
   *    правило не попадает.
   *
   * Залп, чьего выстрела в состоянии уже нет (этап завершили, урон
   * применили), здесь НЕ обрывается: снаряд долетает и бьет в цель (см.
   * showShotAnimation)
   *
   * @param {number} [round] — раунд на поле сейчас
   */
  dropStaleShotAnimations(round) {
    // Реестра может не быть: часть тестов поднимает рендерер без конструктора
    if (!this.shotAnimations?.size) {
      return;
    }

    const now = Date.now();
    [...this.shotAnimations.values()].forEach((animation) => {
      const isStale = !animation.isLanded && now > animation.staleAt;
      const isFromRewoundState = Number.isFinite(round) && Number.isFinite(animation.round) && round < animation.round;
      if (isStale || isFromRewoundState) {
        this.destroyShotAnimation(animation.id);
      }
    });
  }

  /**
   * Во сколько раз сжат по времени идущий залп выстрела (1 — не сжат или
   * залпа нет): по этому множителю идет и звук выстрела (см.
   * scenes/playShotAnimation.getShotTimeFactor)
   */
  getShotTimeFactor(actionId) {
    return this.shotAnimations?.get(actionId)?.timeFactor ?? 1;
  }

  /** Летит ли еще снаряд этого выстрела (до попадания его итог не показываем) */
  checkIfShotInFlight(actionId) {
    const animation = this.shotAnimations?.get(actionId);
    return Boolean(animation && !animation.isLanded);
  }

  /**
   * Выстрелы, чей итог уже виден на поле: все отложенные, кроме тех, чей
   * снаряд еще летит. По этому списку рисуются урон на цели, поврежденные
   * строения и линии выстрелов; правила (кто уже стрелял, доступные цели)
   * считаются по полному списку
   */
  filterLandedShootingActions(pendingShootingActions) {
    if (!this.shotAnimations?.size || !pendingShootingActions?.length) {
      return pendingShootingActions;
    }
    return pendingShootingActions.filter((action) => !this.checkIfShotInFlight(action.id));
  }

  /**
   * Анимация ударов ближнего боя: отряд делает рывок к цели и бьет слешем —
   * оружие его фракции проходит дугой, оставляя световой след; на цели —
   * вспышка, искры и всплывающее «−N», цель краснеет и отшатывается. Все —
   * цветом игрока-владельца бьющего отряда.
   *
   * ИТОГ БОЯ ПОЯВЛЯЕТСЯ ПОСЛЕ УДАРОВ. Расчет боя меняет состояние сразу, и
   * поле по нему уже перерисовано: у отрядов стоят значки мечей, урон на
   * плашках, по ячейкам идут волны. Поэтому отряды анимации (heldUnitIds) до
   * конца ударов рисуются без итога боя (см. checkIfMeleeResultHeld), а когда
   * удары отыграли, поле перерисовывается — и итог появляется разом.
   *
   * ЧАСЫ У ВСЕХ УДАРОВ ОБЩИЕ. Расписание (кто, когда и по кому бьет) приходит
   * готовым (см. utils/effects/buildMeleeTimeline); здесь один твин ведет часы
   * анимации, а каждый удар рисуется по своему времени на этих часах. Твин
   * живет в реестре рендерера, а не на ячейках: перерисовка поля его не
   * снимает. Толчки и красная вспышка идут на настоящих спрайтах отрядов —
   * перерисовка посреди удара их оборвет, но отряд при этом просто встанет на
   * место.
   *
   * @param {Object} plan
   * @param {string} plan.id — id анимации
   * @param {Array} plan.strikes — удары расписания; у каждого attackerUnitId,
   *   targetUnitId, startAtMs, isLungeLeader, isMirrored, color (цвет игрока
   *   числом), weaponFrame (кадр оружия) и sizeLoss (число над целью; 0 — без
   *   числа)
   * @param {number} plan.totalMs — когда отыграл последний удар
   * @param {number} plan.revealAtMs — когда показывать итог боя
   * @param {number} [plan.timeFactor] — множитель длительностей: меньше 1 —
   *   удары сжаты под шаг чужого темпа
   * @param {string[]} [plan.heldUnitIds] — отряды, чей итог боя придержан
   * @param {Array} [plan.units] — отряды партии
   * @param {number} [plan.delayMs] — пауза до первого удара (переброс костей:
   *   сначала звучат кости)
   * @param {number} [plan.round] — раунд боя: по нему снимается анимация,
   *   оставшаяся от состояния, которое переписали на более раннее
   * @param {boolean} [plan.isFullBattle] — бой рассчитан целиком (вход в
   *   этап): прежние анимации к нему уже не относятся. Иначе это повтор
   *   ударов отдельных отрядов (приоритетная цель, переброс костей)
   * @param {Function} [plan.onStrikesStart] — зовется, когда начинаются удары
   *   (разом — все удары волны): по нему звучат взмахи
   * @returns {boolean} пошла ли анимация
   */
  showMeleeAnimation({
    id, strikes = [], totalMs, revealAtMs, timeFactor = 1, heldUnitIds = [], units = [], delayMs = 0, round,
    isFullBattle = false, onStrikesStart,
  } = {}) {
    if (!id || !this.meleeAnimations || !this.scene?.tweens?.add || !(totalMs > 0)) {
      return false;
    }
    if (!ensureMeleeTextures(this.scene, {linearFilter: Phaser.Textures?.FilterMode?.LINEAR})) {
      return false;
    }

    const prepared = strikes.map((strike) => this.prepareMeleeStrike(strike, units)).filter(Boolean);
    if (prepared.length === 0) {
      return false;
    }
    // Пауза до первого удара в настоящем времени
    let startDelayMs = Math.max(0, delayMs);
    if (isFullBattle) {
      [...this.meleeAnimations.keys()].forEach((key) => this.destroyMeleeAnimation(key));
    } else {
      // Еще не начатые удары этих отрядов из прежних анимаций — по старому
      // расчету: их заменяют новые
      const attackerIds = new Set(prepared.map((strike) => strike.attackerUnitId));
      this.meleeAnimations.forEach((other) => other.strikes.forEach((strike) => {
        if (!strike.isStarted && attackerIds.has(strike.attackerUnitId)) {
          strike.isCancelled = true;
        }
      }));
      // ПОВТОР ЖДЕТ СВОЕЙ ОЧЕРЕДИ. Бот-защитник и соперник по сети выбирают
      // цель и перебрасывают кости, пока бой ходящего еще идет: без очереди
      // их отряд ударил бы поверх ударов стороны, которая бьет раньше, а то
      // и поверх собственного удара из прежней анимации
      startDelayMs = Math.max(startDelayMs, this.getMeleeQueueMs());
    }

    const animation = {
      id,
      strikes: prepared,
      heldUnitIds: new Set(heldUnitIds),
      isRevealed: false,
      isClockDone: false,
      totalMs,
      revealAtMs: Math.min(revealAtMs ?? totalMs, totalMs),
      timeFactor,
      round,
      onStrikesStart,
      // Часы анимации, мс расписания: их ведет один твин
      clock: {elapsed: 0},
      // Когда часы пойдут (Date.now): по нему считается очередь повторов
      // (см. getMeleeQueueMs)
      clockStartAt: Date.now() + startDelayMs,
      // Все, что надо убрать при обрыве: спрайты и объекты-счетчики эффектов
      sprites: new Set(),
      proxies: new Set(),
      // Брызги крови от ударов: конца анимации они не держат (живут дольше
      // нее и гаснут сами), но обрыв убирает и их
      bloodSprites: new Set(),
      // Твины идут по часам сцены, а в скрытой вкладке она стоит: анимация,
      // не отыгравшая к этому сроку, считается зависшей
      staleAt: Date.now() + startDelayMs + totalMs + MELEE_ANIMATION.staleMarginMs,
    };
    this.meleeAnimations.set(id, animation);
    // Интерфейсу тоже нужно знать, что итог боя придержан: панель с расчетом
    // урона в футере открывается после ударов
    holdMeleeResult(id, heldUnitIds);
    // Проигрыватель записи матча ждет, пока анимация не уйдет с поля — но не
    // дольше срока, после которого она считается зависшей
    markAwaitedAnimationPlaying(`melee:${id}`, animation.staleAt);
    // Волна по ячейке, пущенная значком боя до ударов, живет в своем реестре
    // и доиграла бы поверх них — снимаем ее у отрядов анимации
    units.filter((unit) => animation.heldUnitIds.has(unit.id)).forEach((unit) => {
      this.destroyRecruitmentPulse(unit.hexKey);
    });

    try {
      this.scene.tweens.add({
        targets: animation.clock,
        elapsed: totalMs,
        delay: Math.round(startDelayMs),
        duration: Math.max(1, Math.round(totalMs * timeFactor)),
        ease: 'Linear',
        onUpdate: () => this.advanceMeleeAnimation(animation),
        onComplete: () => this.completeMeleeAnimation(animation),
      });
    } catch (error) {
      // Анимация не запустилась: без уборки итог боя остался бы придержанным
      this.destroyMeleeAnimation(id);
      throw error;
    }

    // Поле уже нарисовано с итогом боя — перерисовываем, чтобы спрятать его
    // до конца ударов. Кадр между двумя перерисовками не успевает выйти
    this.requestFieldRedraw();

    return true;
  }

  /**
   * Сколько еще идти ударам уже запущенных анимаций, мс настоящего времени
   * (0 — ударов нет). Повтор ударов отряда ждет их конца (см.
   * showMeleeAnimation); тот, кто повтор планирует, по этому же числу решает,
   * уложится ли повтор в шаг чужого темпа (см. playMeleeAnimation).
   *
   * СЧЕТ — ПО ЧАСАМ САМОЙ АНИМАЦИИ, а не по настоящему времени от ее запуска:
   * твины Phaser после долгого кадра (вкладку скрыли и вернули, сборка
   * мусора) идут с отставанием, и оценка по Date.now оказалась бы занижена
   * на весь провал — повтор ударил бы поверх ударов, которых ждал.
   *
   * @param {string[]} [replacedAttackerUnitIds] — отряды будущего повтора: их
   *   еще не начатые удары в прежних анимациях он заменит, ждать их не надо
   * @param {Function} [checkIfStrikeStays] — (удар) => состоится ли не
   *   начатый удар после пересчета боя: удар, чей урон обнулился, сверка
   *   снимет (см. updatePendingMeleeStrikes), и ждать его незачем; удар,
   *   которому урон вернулся, она вернет — его ждать надо
   * @returns {number}
   */
  getMeleeQueueMs(replacedAttackerUnitIds = [], checkIfStrikeStays = null) {
    if (!this.meleeAnimations?.size) {
      return 0;
    }

    const now = Date.now();
    let queueMs = 0;
    this.meleeAnimations.forEach((animation) => {
      // Когда отыграет последний из ударов, которые еще состоятся, мс
      // расписания. Отыгравшие удары — в прошлом и на ответ не влияют
      const lastStrikeEndMs = animation.strikes
        .filter((strike) => {
          if (strike.isCancelled) {
            return false;
          }
          if (strike.isStarted) {
            return true;
          }
          if (replacedAttackerUnitIds.includes(strike.attackerUnitId)) {
            return false;
          }
          // Состоится ли не начатый удар, знает новый расчет: он и снимает
          // удар, и возвращает снятый прежней сверкой. Без него — как есть
          return checkIfStrikeStays ? Boolean(checkIfStrikeStays(strike)) : !strike.isSkipped;
        })
        .reduce((latest, strike) => Math.max(latest, strike.startAtMs + MELEE_ANIMATION.strike.durationMs), 0);
      if (lastStrikeEndMs > 0) {
        // Часы еще не пошли (пауза переброса, своя очередь) — ждать и паузу
        const untilClockStartMs = Math.max(0, animation.clockStartAt - now);
        queueMs = Math.max(
          queueMs, untilClockStartMs + (lastStrikeEndMs - animation.clock.elapsed) * animation.timeFactor
        );
      }
    });
    return Math.round(queueMs);
  }

  /**
   * Еще не начатые удары идущих анимаций — по новому расчету боя. Выбор
   * приоритетной цели и переброс костей пересчитывают бой целиком, и урон
   * меняется не только у отряда, который выбирал: общий остаток цели делится
   * между всеми бьющими заново. Удар, который еще не начался, должен звучать
   * и показывать «−N» уже по новому расчету — иначе число над целью разошлось
   * бы с плашкой; удар, от которого урона не осталось, снимается.
   *
   * Снятие сверкой обратимо (isSkipped): следующий пересчет может вернуть
   * удару урон — тогда он состоится. Не возвращается (isCancelled) удар,
   * который заменил повтор (см. showMeleeAnimation), и снятый удар, чье
   * время уже прошло (см. advanceMeleeAnimation).
   *
   * Какой урон и какое число у удара теперь — знает тот, кто видит записи боя
   * (см. playMeleeAnimation); рендерер только отдает ему свои удары в порядке,
   * в котором они пойдут.
   *
   * @param {Function} resolveUpdates — (удары [{attackerUnitId, targetUnitId,
   *   damage, sizeLoss}]) => массив той же длины с {damage, sizeLoss}; урона
   *   нет — удар снят
   */
  updatePendingMeleeStrikes(resolveUpdates) {
    if (!this.meleeAnimations?.size || typeof resolveUpdates !== 'function') {
      return;
    }

    // Анимации идут в порядке запуска (повтор ждет конца прежних ударов),
    // удары внутри анимации — по своему времени
    const pendingStrikes = [...this.meleeAnimations.values()].flatMap((animation) => (
      animation.strikes
        .filter((strike) => !strike.isStarted && !strike.isCancelled)
        .sort((a, b) => a.startAtMs - b.startAtMs)
    ));
    if (pendingStrikes.length === 0) {
      return;
    }

    const updates = resolveUpdates(pendingStrikes.map(({attackerUnitId, targetUnitId, damage, sizeLoss}) => (
      {attackerUnitId, targetUnitId, damage, sizeLoss}
    ))) || [];
    pendingStrikes.forEach((strike, index) => {
      const update = updates[index];
      strike.isSkipped = !update || !(update.damage > 0);
      if (strike.isSkipped) {
        return;
      }
      strike.damage = update.damage;
      strike.sizeLoss = update.sizeLoss > 0 ? update.sizeLoss : 0;
    });
  }

  /** Удар с геометрией по текущим местам отрядов; null — бить некому или не по кому */
  prepareMeleeStrike(strike, units) {
    const attacker = units.find((unit) => unit.id === strike.attackerUnitId);
    const target = units.find((unit) => unit.id === strike.targetUnitId);
    const attackerHex = attacker && this.hexes[attacker.hexKey];
    const targetHex = target && this.hexes[target.hexKey];
    if (!attackerHex || !targetHex) {
      return null;
    }

    return {
      ...strike,
      attackerHexKey: attacker.hexKey,
      targetHexKey: target.hexKey,
      targetFactionId: target.factionId,
      geometry: buildMeleeStrikeGeometry({
        from: {x: attackerHex.centerX, y: attackerHex.centerY + this.getUnitShiftY(attackerHex)},
        to: {x: targetHex.centerX, y: targetHex.centerY + this.getUnitShiftY(targetHex)},
        hexSize: attackerHex.size,
        isMirrored: strike.isMirrored,
      }),
      isStarted: false,
      isImpacted: false,
      isDone: false,
      // Удар не состоится: isCancelled — насовсем (его заменил повтор ударов
      // отряда либо его время прошло, пока он был снят), isSkipped — по
      // новому расчету урона по цели нет (до следующего пересчета)
      isCancelled: false,
      isSkipped: false,
      trail: null,
      weaponSprites: [],
    };
  }

  /** Шаг часов анимации: начать удары, которым пора, продвинуть идущие, показать итог */
  advanceMeleeAnimation(animation) {
    // Колбэк твина: исключение здесь остановило бы цикл кадров Phaser насовсем
    try {
      if (this.meleeAnimations.get(animation.id) !== animation) {
        return;
      }
      const {elapsed} = animation.clock;
      const startedStrikes = [];
      animation.strikes.forEach((strike) => {
        if (strike.isCancelled || strike.isDone || elapsed < strike.startAtMs) {
          return;
        }
        // Снятый сверкой удар свое время пропустил — насовсем: верни ему
        // урон следующий пересчет, он начался бы с середины или с конца —
        // попадание и число без взмаха
        if (strike.isSkipped) {
          strike.isCancelled = true;
          return;
        }
        // Удар — украшение: его сбой не должен оставить итог боя спрятанным
        try {
          if (!strike.isStarted) {
            strike.isStarted = true;
            startedStrikes.push(strike);
            this.startMeleeStrike(animation, strike);
          }
          this.updateMeleeStrike(animation, strike, elapsed - strike.startAtMs);
        } catch (error) {
          console.warn('[Melee] не удалось показать удар', error);
          this.finishMeleeStrike(animation, strike);
        }
      });
      if (startedStrikes.length > 0 && animation.onStrikesStart) {
        // Звук взмахов — тоже украшение
        try {
          animation.onStrikesStart(startedStrikes);
        } catch (error) {
          console.warn('[Melee] не удалось озвучить удары', error);
        }
      }
      if (!animation.isRevealed && elapsed >= animation.revealAtMs) {
        this.revealMeleeResult(animation);
      }
    } catch (error) {
      console.warn('[Melee] не удалось продолжить анимацию боя', error);
    }
  }

  /** Часы дошли до конца: удары убраны, итог показан */
  completeMeleeAnimation(animation) {
    try {
      this.advanceMeleeAnimation(animation);
      if (this.meleeAnimations.get(animation.id) !== animation) {
        return;
      }
      animation.isClockDone = true;
      animation.strikes.forEach((strike) => this.finishMeleeStrike(animation, strike));
      if (!animation.isRevealed) {
        this.revealMeleeResult(animation);
      }
      this.finishMeleeAnimationIfDone(animation);
    } catch (error) {
      console.warn('[Melee] не удалось завершить анимацию боя', error);
    }
  }

  /** Удары отыграли: итог боя появляется на плашках отрядов */
  revealMeleeResult(animation) {
    animation.isRevealed = true;
    releaseMeleeResult(animation.id);
    // Запись матча дает итогу побыть на поле (см. ReplayRunner)
    markAwaitedAnimationResultShown();
    this.requestFieldRedraw();
  }

  /** Свет удара: идет своим планом поверх механики (см. configs/combatCastAnimation) */
  playMeleeCast(buildPlan, timeScale = 1) {
    try {
      const plan = buildPlan();
      if (plan) {
        this.abilityCastAnimation?.play(plan, {timeScale});
      }
    } catch (error) {
      console.warn('[Melee] не удалось показать свет удара', error);
    }
  }

  /** Начало удара: оружие и световой след заведены, отряд делает рывок к цели */
  startMeleeStrike(animation, strike) {
    const {geometry} = strike;
    const depth = LAYER_DEPTH.MELEE_EFFECT;

    const trail = this.scene.add.graphics?.();
    if (trail) {
      trail.setDepth(depth);
      animation.sprites.add(trail);
      strike.trail = trail;
    }

    // Листа оружия могло не оказаться (битый файл, сеть): удар все равно идет
    // — следом и вспышкой
    if (this.scene.textures?.exists?.(MELEE_WEAPONS_SHEET.key)) {
      const scale = geometry.weaponLength / (MELEE_WEAPONS_SHEET.frameHeight - MELEE_WEAPONS_SHEET.framePadding * 2);
      // Под цветным оружием — такое же белое, чуть крупнее: светлая кромка, с
      // которой оружие темного цвета читается и на темной подложке поля
      strike.weaponSprites = [
        {tint: 0xffffff, scaleMultiplier: MELEE_ANIMATION.strike.slash.rimScale, depth: depth + 0.001},
        {tint: strike.color, scaleMultiplier: 1, depth: depth + 0.002},
      ].map((layer) => {
        const sprite = this.scene.add.sprite(
          geometry.pivot.x, geometry.pivot.y, MELEE_WEAPONS_SHEET.key, strike.weaponFrame
        );
        sprite.setOrigin(0.5, 0.5);
        sprite.setScale(scale * layer.scaleMultiplier);
        // В кадре лезвие смотрит по ходу обычного взмаха; встречный взмах
        // (вторая цель отряда) без разворота бил бы обухом вперед
        sprite.setFlipX(Boolean(strike.isMirrored));
        sprite.setTint(layer.tint);
        sprite.setDepth(layer.depth);
        // До начала взмаха оружия не видно
        sprite.setAlpha(0);
        animation.sprites.add(sprite);
        return sprite;
      });
    }

    this.playMeleeCast(() => buildMeleeSlashCastPlan({geometry, color: strike.color}), animation.timeFactor);

    if (strike.isLungeLeader) {
      const {lunge} = MELEE_ANIMATION.strike;
      this.pushUnitSprites(strike.attackerUnitId, strike.attackerHexKey, {
        offsetX: geometry.lunge.x,
        offsetY: geometry.lunge.y,
        outMs: lunge.outMs * animation.timeFactor,
        holdMs: lunge.holdMs * animation.timeFactor,
      });
    }
  }

  /**
   * Удар в заданный момент своего времени: оружие на дуге, след за ним, в
   * середине взмаха — попадание, после взмаха оружие и след гаснут
   */
  updateMeleeStrike(animation, strike, localMs) {
    const {slash} = MELEE_ANIMATION.strike;
    const swingMs = localMs - slash.startAtMs;
    if (swingMs < 0) {
      return;
    }

    const swing = swingMs / slash.durationMs;
    // После взмаха оружие и след тают
    const fade = Math.min(1, Math.max(0, 1 - (swingMs - slash.durationMs) / slash.fadeMs));
    const state = resolveMeleeSlashState(strike.geometry, swing);
    const {geometry} = strike;
    const weaponRadius = geometry.handleRadius + geometry.weaponLength / 2;
    // Оружие проявляется в начале замаха за шестую часть взмаха
    const weaponAlpha = swing <= 1 ? Math.min(1, swing * 6) : fade;
    strike.weaponSprites.forEach((sprite) => {
      sprite.x = geometry.pivot.x + Math.cos(state.angle) * weaponRadius;
      sprite.y = geometry.pivot.y + Math.sin(state.angle) * weaponRadius;
      sprite.rotation = state.rotation;
      sprite.setAlpha(weaponAlpha);
    });
    this.drawMeleeTrail(strike, state, fade);

    if (!strike.isImpacted && swing >= 0.5) {
      strike.isImpacted = true;
      this.showMeleeImpact(animation, strike);
    }
    if (swingMs >= slash.durationMs + slash.fadeMs) {
      this.finishMeleeStrike(animation, strike);
    }
  }

  /**
   * Световой след за оружием — полумесяц по пройденной части дуги: толстый в
   * середине дуги и сходящий на нет к ее краям, ярче у самого оружия. Три
   * слоя: мягкое свечение цветом игрока, сам след и белое ядро потоньше
   */
  drawMeleeTrail(strike, state, fade) {
    const {trail, geometry} = strike;
    if (!trail) {
      return;
    }
    trail.clear();
    const fullSpan = geometry.endAngle - geometry.startAngle;
    if (state.sweptShare <= 0 || fade <= 0) {
      return;
    }

    const {trailSegments, coreWidthShare, glowWidthShare, glowAlpha} = MELEE_ANIMATION.strike.slash;
    const {trailTailAlpha} = MELEE_SLASH_CAST;
    // Край полумесяца на заданной доле ПОЛНОЙ дуги: по ней его толщина
    const edgePoint = (share, widthShare, side) => {
      const angle = geometry.startAngle + fullSpan * share;
      const radius = geometry.trailRadius
        + side * geometry.trailWidth * widthShare * Math.sin(Math.PI * share) / 2;
      return {x: geometry.pivot.x + Math.cos(angle) * radius, y: geometry.pivot.y + Math.sin(angle) * radius};
    };
    // Комета: у оружия след яркий, к хвосту тает. Каждый слой — полоса соседних кусков без
    // наложений: дуга из толстых отрезков шла бы полосами, на стыках прозрачность складывается
    [
      {color: strike.color, widthShare: glowWidthShare, alpha: glowAlpha},
      {color: strike.color, widthShare: 1, alpha: 0.92},
      {color: 0xffffff, widthShare: coreWidthShare, alpha: 0.95},
    ].forEach((layer) => {
      for (let index = 0; index < trailSegments; index += 1) {
        const from = state.sweptShare * index / trailSegments;
        const to = state.sweptShare * (index + 1) / trailSegments;
        const headShare = (index + 1) / trailSegments;
        const points = [
          edgePoint(from, layer.widthShare, 1), edgePoint(to, layer.widthShare, 1),
          edgePoint(to, layer.widthShare, -1), edgePoint(from, layer.widthShare, -1),
        ];
        trail.fillStyle(layer.color, layer.alpha * fade * (trailTailAlpha + (1 - trailTailAlpha) * headShare * headShare));
        trail.beginPath();
        trail.moveTo(points[0].x, points[0].y);
        points.slice(1).forEach((point) => trail.lineTo(point.x, point.y));
        trail.closePath();
        trail.fillPath();
      }
    });
  }

  /**
   * Попадание: вспышка и искры цветом игрока, всплывающее «−N», цель кратко
   * краснеет и отшатывается от удара
   */
  showMeleeImpact(animation, strike) {
    const {geometry, color} = strike;
    const {hexSize} = geometry;

    // Вспышка, разрез, кольца, искры и дрожь — по силе удара
    this.playMeleeCast(() => buildMeleeImpactCastPlan({
      at: geometry.impact,
      direction: geometry.direction,
      hexSize,
      color,
      tier: resolveMeleeImpactTier(strike.sizeLoss),
      hexRotation: this.getHexRotation(this.hexes[strike.targetHexKey]),
    }));

    if (strike.sizeLoss > 0) {
      this.showMeleeDamageNumber(animation, strike);
      // Оружие прошло по отряду — брызги крови по ходу удара
      this.showBloodSplatter({
        at: geometry.impact, direction: geometry.direction, hexSize,
        factionId: strike.targetFactionId, intensity: resolveBloodIntensity(strike.sizeLoss),
        owner: animation.bloodSprites,
      });
    }

    const {recoil, flashColor, flashMs} = MELEE_ANIMATION.impact;
    this.flashUnitSprites(strike.targetUnitId, strike.targetHexKey, flashColor, flashMs);
    this.pushUnitSprites(strike.targetUnitId, strike.targetHexKey, {
      offsetX: Math.cos(geometry.direction) * hexSize * recoil.distanceFactor,
      offsetY: Math.sin(geometry.direction) * hexSize * recoil.distanceFactor,
      outMs: recoil.ms * animation.timeFactor,
    });
  }

  /**
   * Брызги крови от попадания (см. configs/bloodSplatter): капли летят дугой
   * от точки удара и падают, на месте самых крупных ненадолго остаются пятна.
   * Живут сами по себе — ни удар, ни залп их не ждут; сбой эффекта бой не
   * задевает
   *
   * @param {Object} params
   * @param {{x: number, y: number}} params.at
   * @param {number} params.direction — радианы
   * @param {number} params.hexSize
   * @param {string} [params.factionId] — фракция отряда-цели: цвет брызг
   * @param {number} params.intensity — 0..1
   */
  showBloodSplatter({at, direction, hexSize, factionId, intensity, owner}) {
    try {
      if (!this.bloodSprites || !this.scene?.tweens?.add) {
        return;
      }
      if (!ensureMeleeTextures(this.scene, {linearFilter: Phaser.Textures?.FilterMode?.LINEAR})) {
        return;
      }
      const {droplets, stains} = buildBloodSplatter({at, direction, hexSize, intensity});
      const colors = resolveBloodColors(factionId);
      const dropWidth = MELEE_TEXTURE_SIZES[MELEE_TEXTURE_KEYS.BLOOD_DROP].width;
      const stainWidth = MELEE_TEXTURE_SIZES[MELEE_TEXTURE_KEYS.BLOOD_STAIN].width;
      const addSprite = (x, y, textureKey, depth, tint) => {
        const sprite = this.scene.add.sprite(x, y, textureKey);
        sprite.setOrigin(0.5, 0.5);
        sprite.setDepth(depth);
        sprite.setTint(tint);
        sprite.setAlpha(0);
        this.bloodSprites.add(sprite);
        owner?.add(sprite);
        return sprite;
      };
      const release = (sprite) => {
        owner?.delete(sprite);
        this.destroyBloodSprite(sprite);
      };

      droplets.forEach((droplet) => {
        const sprite = addSprite(droplet.startX, droplet.startY, MELEE_TEXTURE_KEYS.BLOOD_DROP, LAYER_DEPTH.BLOOD_DROP, colors.drop);
        sprite.setScale(droplet.size / dropWidth);
        const flight = {progress: 0};
        sprite.bloodProxy = flight;
        this.scene.tweens.add({
          targets: flight,
          progress: 1,
          delay: droplet.delayMs,
          duration: droplet.durationMs,
          ease: 'Linear',
          onUpdate: () => {
            const position = resolveBloodDropletPosition(droplet, flight.progress);
            sprite.x = position.x;
            sprite.y = position.y;
            sprite.setAlpha(1);
            // Капля к концу полета мельчает: брызги оседают на землю
            sprite.setScale(droplet.size / dropWidth * (1 - 0.45 * flight.progress));
          },
          onComplete: () => release(sprite),
        });
      });

      // Пятно: проявляется, когда упала капля, держится и гаснет — одним твином
      const {holdMs, fadeMs} = BLOOD_SPLATTER.stains;
      const appearMs = 120;
      stains.forEach((stain) => {
        const sprite = addSprite(stain.x, stain.y, MELEE_TEXTURE_KEYS.BLOOD_STAIN, LAYER_DEPTH.BLOOD_STAIN, colors.stain);
        sprite.rotation = stain.rotation;
        sprite.setScale(stain.size / stainWidth * 0.4);
        const life = {ms: 0};
        sprite.bloodProxy = life;
        const totalMs = appearMs + holdMs + fadeMs;
        this.scene.tweens.add({
          targets: life,
          ms: totalMs,
          delay: stain.appearAtMs,
          duration: totalMs,
          ease: 'Linear',
          onUpdate: () => {
            const grow = Math.min(1, life.ms / appearMs);
            sprite.setScale(stain.size / stainWidth * (0.4 + 0.6 * (1 - (1 - grow) * (1 - grow))));
            const fade = Math.max(0, (life.ms - appearMs - holdMs) / fadeMs);
            sprite.setAlpha(stain.alpha * grow * (1 - fade));
          },
          onComplete: () => release(sprite),
        });
      });
    } catch (error) {
      console.warn('[Blood] не удалось показать брызги', error);
    }
  }

  /** Убрать спрайт брызг вместе с его твином */
  destroyBloodSprite(sprite) {
    this.bloodSprites?.delete(sprite);
    if (sprite.bloodProxy) {
      this.scene?.tweens?.killTweensOf?.(sprite.bloodProxy);
    }
    sprite.destroy();
  }

  /** Убрать все брызги крови (разбор поля) */
  destroyBloodSplatters() {
    if (!this.bloodSprites) {
      return;
    }
    [...this.bloodSprites].forEach((sprite) => this.destroyBloodSprite(sprite));
  }

  /**
   * Всплывающее «−N» над целью: красное в белом контуре, выпрыгивает,
   * поднимается и гаснет
   */
  showMeleeDamageNumber(animation, strike) {
    const config = MELEE_ANIMATION.number;
    const {geometry} = strike;
    const {hexSize} = geometry;
    // Текст рисуется вдвое крупнее и уменьшается вдвое — как подписи отрядов:
    // при приближении камеры он остается четким
    const fontPx = Math.round(hexSize * config.fontSizeFactor * 2);
    // Два отряда бьют одну цель одновременно — их числа встают рядом, а не
    // друг на друге. Соседи считаются в момент попадания, а не при запуске:
    // к этому времени удар соседа мог быть снят или потерять число (повтор,
    // сверка с новым расчетом), и одиночное число должно всплыть по центру
    const neighbours = animation.strikes.filter((other) => (
      other.targetUnitId === strike.targetUnitId && other.startAtMs === strike.startAtMs
      && other.sizeLoss > 0 && !other.isCancelled && !other.isSkipped
    ));
    const numberShiftX = (neighbours.indexOf(strike) - (neighbours.length - 1) / 2)
      * hexSize * config.sideBySideFactor;
    const startX = geometry.impact.x + numberShiftX;
    const startY = geometry.impact.y - hexSize * config.startLiftFactor;
    const text = this.scene.add.text(startX, startY, `${MINUS_SIGN}${strike.sizeLoss}`, {
      fontFamily: UNIT_LABEL_DAMAGE_STYLE.fontFamily,
      fontSize: `${fontPx}px`,
      fontStyle: 'bold',
      color: config.color,
      stroke: config.strokeColor,
      strokeThickness: Math.round(fontPx * config.strokeWidthShare),
    });
    text.setOrigin(0.5, 0.5);
    text.setDepth(LAYER_DEPTH.MELEE_NUMBER);
    text.setScale(0.5 * config.popFromScale);
    animation.sprites.add(text);

    const flight = {travel: 0};
    animation.proxies.add(flight);
    const isCurrent = () => this.meleeAnimations.get(animation.id) === animation && animation.sprites.has(text);
    const placeNumber = () => {
      if (!isCurrent()) {
        return;
      }
      const pop = Math.min(1, flight.travel * config.durationMs / config.popMs);
      text.y = startY - hexSize * config.riseFactor * (1 - (1 - flight.travel) * (1 - flight.travel));
      text.setScale(0.5 * (config.popFromScale + (1 - config.popFromScale) * (1 - (1 - pop) * (1 - pop))));
      text.setAlpha(flight.travel <= config.fadeFromProgress
        ? 1
        : 1 - (flight.travel - config.fadeFromProgress) / (1 - config.fadeFromProgress));
    };

    this.scene.tweens.add({
      targets: flight,
      travel: 1,
      // В сжатом чужом бою волны идут чаще — число поднимается быстрее, чтобы
      // «−N» следующей волны над той же целью на него не наехало; но не
      // быстрее предела, иначе его не прочесть
      duration: Math.round(config.durationMs * Math.min(1, Math.max(animation.timeFactor, config.minTimeFactor))),
      ease: 'Linear',
      onUpdate: placeNumber,
      onComplete: () => {
        animation.proxies.delete(flight);
        this.destroyMeleeSprite(animation, text);
        this.finishMeleeAnimationIfDone(animation);
      },
    });
  }

  /** Удар отыграл: оружие и след убраны */
  finishMeleeStrike(animation, strike) {
    if (strike.isDone) {
      return;
    }
    strike.isDone = true;
    strike.weaponSprites.forEach((sprite) => this.destroyMeleeSprite(animation, sprite));
    strike.weaponSprites = [];
    this.destroyMeleeSprite(animation, strike.trail);
    strike.trail = null;
  }

  destroyMeleeSprite(animation, sprite) {
    if (!sprite) {
      return;
    }
    animation.sprites.delete(sprite);
    // Phaser не снимает твины с уничтоженного объекта
    this.scene.tweens.killTweensOf?.(sprite);
    sprite.destroy();
  }

  /** Анимация отыграла целиком (часы дошли, эффекты погасли) — забываем ее */
  finishMeleeAnimationIfDone(animation) {
    // Всплывающее число — тоже спрайт анимации: пока оно в воздухе, она жива
    if (animation.isClockDone
      && animation.sprites.size === 0
      && this.meleeAnimations.get(animation.id) === animation
    ) {
      this.meleeAnimations.delete(animation.id);
      markAwaitedAnimationFinished(`melee:${animation.id}`);
    }
  }

  /** Оборвать анимацию ударов: убрать оружие и эффекты. Итог боя после этого виден */
  destroyMeleeAnimation(animationId) {
    const animation = this.meleeAnimations?.get(animationId);
    if (!animation) {
      return;
    }

    this.meleeAnimations.delete(animationId);
    // Оборванная анимация итог больше не прячет — ни на поле, ни в футере —
    // и запись матча больше не задерживает
    releaseMeleeResult(animationId);
    markAwaitedAnimationFinished(`melee:${animationId}`);
    this.scene.tweens.killTweensOf?.(animation.clock);
    animation.proxies.forEach((proxy) => this.scene.tweens.killTweensOf?.(proxy));
    animation.proxies.clear();
    [...animation.sprites].forEach((sprite) => this.destroyMeleeSprite(animation, sprite));
    animation.bloodSprites?.forEach((sprite) => this.destroyBloodSprite(sprite));
    animation.bloodSprites?.clear();
  }

  /**
   * Оборвать анимации ударов, которым на этом поле больше не место. Зовется на
   * каждой перерисовке поля ДО отрисовки отрядов.
   *  - Боя в состоянии уже нет: этап завершили и урон применили (или состояние
   *    переписали) — погибшие отряды сняты с поля, бить некому и не по кому.
   *  - Зависшая анимация: вкладка была скрыта, кадры не шли, а итог боя все
   *    это время прятался.
   *  - Анимация из «будущего»: запись матча перемотали к началу — раунд на
   *    поле меньше раунда боя.
   *
   * @param {Object} [state]
   * @param {number} [round] — раунд на поле сейчас
   */
  dropStaleMeleeAnimations(state, round) {
    // Реестра может не быть: часть тестов поднимает рендерер без конструктора
    if (!this.meleeAnimations?.size) {
      return;
    }

    const isBattleGone = Boolean(state) && !(
      selectPhase(state) === PHASES.WAR
      && selectWarPhaseStage(state) === WAR_PHASE_STAGES.COMBAT
      && (selectPendingMeleeDamage(state) || []).length > 0
    );
    const now = Date.now();
    [...this.meleeAnimations.values()].forEach((animation) => {
      const isStale = !animation.isRevealed && now > animation.staleAt;
      const isFromRewoundState = Number.isFinite(round) && Number.isFinite(animation.round) && round < animation.round;
      if (isBattleGone || isStale || isFromRewoundState) {
        this.destroyMeleeAnimation(animation.id);
      }
    });
  }

  /**
   * Придержан ли итог ближнего боя этого отряда: его удары (или удары по
   * нему) еще идут. Пока придержан, на поле у отряда нет ни значка мечей с
   * нанесенным уроном, ни полученного урона, ни мигания обреченного
   */
  checkIfMeleeResultHeld(unitId) {
    if (!this.meleeAnimations?.size || !unitId) {
      return false;
    }
    for (const animation of this.meleeAnimations.values()) {
      if (!animation.isRevealed && animation.heldUnitIds.has(unitId)) {
        return true;
      }
    }
    return false;
  }

  /** Спрайты отряда, которые двигаются вместе с ним: иконка, контур, тень, плашка, число */
  getUnitMovableSprites(unitId, hex) {
    this.ensureFieldCurrent();
    return [
      ...Object.keys(this.unitsSprites || {})
        .filter((spriteId) => spriteId === unitId || spriteId.startsWith(`${unitId}_`))
        .flatMap((spriteId) => this.unitsSprites[spriteId]),
      ...(hex.unitSizeLabel || []),
    ].filter((sprite) => sprite && typeof sprite.x === 'number');
  }

  /**
   * Толчок отряда в любую сторону и обратно: рывок к цели, отшатывание от
   * удара. В отличие от nudgeUnitSprites, двигает и по вертикали — соседи на
   * поле бывают строго сверху и снизу. Покачивание отряда идет по той же оси,
   * поэтому на время толчка оно ставится на паузу: два твина на одном
   * свойстве дрожали бы.
   *
   * Твин числится за ячейкой: следующая перерисовка поля его снимет (спрайты
   * она все равно пересоздаст на своих местах). Толчок один за раз: второй,
   * пришедший поверх первого, оставил бы отряд сдвинутым
   *
   * @param {string} unitId
   * @param {string} hexKey
   * @param {Object} params
   * @param {number} params.offsetX
   * @param {number} params.offsetY
   * @param {number} params.outMs — сколько длится движение в одну сторону
   * @param {number} [params.holdMs] — сколько отряд стоит в крайней точке
   */
  pushUnitSprites(unitId, hexKey, {offsetX = 0, offsetY = 0, outMs, holdMs = 0} = {}) {
    const hex = this.hexes[hexKey];
    if (!hex || !unitId || (!offsetX && !offsetY)) {
      return;
    }
    const now = Date.now();
    if ((this.unitPushUntil?.get(unitId) || 0) > now) {
      return;
    }
    const sprites = this.getUnitMovableSprites(unitId, hex);
    if (sprites.length === 0) {
      return;
    }

    const duration = Math.max(1, Math.round(outMs));
    const hold = Math.max(0, Math.round(holdMs));
    this.unitPushUntil?.set(unitId, now + duration * 2 + hold);
    const bobTween = hex.unitBobTween;
    bobTween?.pause?.();
    this.registerTweens(hex, this.scene.tweens.add({
      targets: sprites,
      x: `+=${offsetX}`,
      y: `+=${offsetY}`,
      duration,
      hold,
      yoyo: true,
      ease: Phaser.Math.Easing.Quadratic.Out,
      onComplete: () => {
        // Перерисовка могла пересоздать покачивание — тогда будить нечего
        if (hex.unitBobTween === bobTween) {
          bobTween?.resume?.();
        }
      },
    }));
  }

  /**
   * Отряд кратко краснеет (попадание в ближнем бою) и возвращает свой цвет.
   * Красятся иконка и половина переманенного отряда — у каждой свой цвет.
   * Перерисовка поля посреди вспышки пересоздаст спрайты уже своего цвета.
   *
   * ВСПЫШКА ВЕДЕТСЯ ПО СОСТОЯНИЮ СПРАЙТА, А НЕ ПО ВРЕМЕНИ. Вторая вспышка
   * может прийти, пока первая еще не вернула цвет (соседние волны сжатого
   * боя, просевший кадр): считанный со спрайта красный запомнился бы как
   * «свой», и отряд остался бы красным. Поэтому настоящий цвет хранится в
   * записи спрайта, новая вспышка его наследует и продлевает красный, а
   * возвращает цвет только та вспышка, что пришла последней
   */
  flashUnitSprites(unitId, hexKey, color, durationMs) {
    const hex = this.hexes[hexKey];
    if (!hex || !unitId) {
      return;
    }
    this.ensureFieldCurrent();
    const sprites = [this.unitsSprites?.[unitId], this.unitsSprites?.[`${unitId}_capturedFactionHalf`]]
      .flat()
      .filter((sprite) => sprite && typeof sprite.setTint === 'function');
    if (sprites.length === 0) {
      return;
    }

    // Реестра может не быть: часть тестов поднимает рендерер без конструктора
    if (!this.unitFlashStates) {
      this.unitFlashStates = new WeakMap();
    }
    const flashStates = this.unitFlashStates;
    const flash = {};
    sprites.forEach((sprite) => {
      const running = flashStates.get(sprite);
      flashStates.set(sprite, {tint: running ? running.tint : sprite.tintTopLeft, flash});
      sprite.setTint(color);
    });
    this.registerTweens(hex, this.scene.tweens.add({
      targets: {progress: 0},
      progress: 1,
      duration: Math.max(1, Math.round(durationMs)),
      onComplete: () => sprites.forEach((sprite) => {
        const state = flashStates.get(sprite);
        // Вспышку продлила следующая — цвет вернет она
        if (!state || state.flash !== flash) {
          return;
        }
        flashStates.delete(sprite);
        if (sprite.active === false) {
          return;
        }
        if (Number.isFinite(state.tint)) {
          sprite.setTint(state.tint);
        } else {
          sprite.clearTint?.();
        }
      }),
    }));
  }

  /**
   * Перерисовать поле по текущему состоянию. Порядок слоев и состав
   * перерисовки знает сцена (GameScene.handleStoreUpdate); без нее (тесты)
   * перерисовывать нечего
   */
  requestFieldRedraw() {
    this.scene?.handleStoreUpdate?.();
  }

  /**
   * Довести поле до текущего состояния стора, если перерисовка еще отложена
   * до кадра (см. GameScene.requestStoreUpdate). Зовут методы, которые отдают
   * или трогают построенное перерисовкой — спрайты отрядов и жетонов, высоту
   * накладок: их вызывают сразу после диспатча (эффекты сетевых действий,
   * клики), и без этого они нашли бы спрайты прошлого состояния. Во время
   * самой перерисовки и в тестах без сцены ничего не делает
   */
  ensureFieldCurrent() {
    this.scene?.flushStoreUpdate?.();
  }

  /**
   * Опорные точки ячейки для анимации способности (см.
   * utils/effects/buildAbilityCast): центр силуэта отряда (или строения) и
   * земля под ним, середина жетона — с подъемом над жетоном, как у самого отряда
   * @param {string} hexKey
   * @param {Object} [options]
   * @param {boolean} [options.isTrapOutline] — нужен и контур ловушки: его
   *   центр и вершины ячейки относительно центра
   * @returns {?{hexSize: number, center: {x, y}, body: {x, y}, ground: {x, y}, outlineCenter?: {x, y}, outlinePoints?: Array<{x, y}>}}
   */
  getAbilityCastAnchor(hexKey, {isTrapOutline = false} = {}) {
    this.ensureFieldCurrent();
    const hex = hexKey ? this.hexes[hexKey] : null;
    if (!hex) {
      return null;
    }
    const hexSize = hex.size;
    const unitY = hex.centerY + this.getUnitShiftY(hex);
    const anchor = {
      hexSize,
      hexRotation: this.getHexRotation(hex),
      center: {x: hex.centerX, y: unitY},
      body: {x: hex.centerX, y: unitY - hexSize * ABILITY_CAST_UNIT_BODY_LIFT_FACTOR},
      ground: {x: hex.centerX, y: unitY + hexSize * 0.22},
    };
    if (isTrapOutline) {
      anchor.outlineCenter = {x: hex.centerX, y: hex.centerY + this.resolveGoblinsTrapShiftY(this.unitHexState, hexKey)};
      anchor.outlinePoints = (hex.points || []).map((point) => ({x: point.x - hex.centerX, y: point.y - hex.centerY}));
    }
    return anchor;
  }

  /**
   * Рамка ячеек, доступных выбранному отряду на этапе (подсвеченных на поле:
   * перемещение, манёвр, цели стрельбы, соблазнения и способности Гоблинов),
   * вместе с ячейкой самого отряда — ее вписывает в кадр автовыбор отрядов
   * (GameScene.frameAutoSelectedUnit). Ячейки берутся с поля, а не считаются
   * заново: в кадр попадает ровно то, что игрок видит подсвеченным
   * @param {string} unitHexKey — ячейка отряда
   * @returns {?{minX: number, maxX: number, minY: number, maxY: number}} мир;
   *   null — ячейки отряда нет на поле
   */
  getUnitActionCellsBounds(unitHexKey) {
    this.ensureFieldCurrent();
    if (!this.hexes?.[unitHexKey]) {
      return null;
    }
    // Без вершин (заглушки) — квадрат по радиусу ячейки
    const radius = this.gridConfig?.hexSize || 60;
    const bounds = {minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity};
    const addPoint = ({x, y}) => {
      bounds.minX = Math.min(bounds.minX, x);
      bounds.maxX = Math.max(bounds.maxX, x);
      bounds.minY = Math.min(bounds.minY, y);
      bounds.maxY = Math.max(bounds.maxY, y);
    };
    Object.values(this.hexes).forEach((hex) => {
      const isActionCell = hex.key === unitHexKey
        || UNIT_ACTION_CELL_MARKER_LISTS.some((list) => hex[list]?.length > 0);
      if (!isActionCell) {
        return;
      }
      // Вершины ячейки — чтобы в кадр вошла вся ячейка, а не только ее центр
      if (hex.points?.length) {
        hex.points.forEach(addPoint);
      } else {
        addPoint({x: hex.centerX - radius, y: hex.centerY - radius});
        addPoint({x: hex.centerX + radius, y: hex.centerY + radius});
      }
    });
    return bounds;
  }

  /**
   * Прилет жетона сверху: двойник жетона падает на ячейку с высоты в несколько
   * размеров гекса, по дороге проявляясь (альфа 0 -> 1) и уменьшаясь до
   * обычного размера. В момент приземления зовется onLand — там звук постройки
   * и клубы пыли.
   *
   * Настоящий жетон на время полета спрятан, а летит ДВОЙНИК на своем слое,
   * выше пыли. Две причины: пыль должна выбиваться из-под жетона (и при этом
   * накрывать соседние жетоны поля), а обновление стора посреди полета
   * пересоздает настоящие спрайты и оборвало бы анимацию на них.
   *
   * Двойник держится над полем, пока не осядет пыль (BUILDING_DROP_COVER_MS).
   *
   * @param {string} hexKey
   * @param {Object} params
   * @param {string} [params.tokenType] — обычный жетон
   * @param {string} [params.specialBuildingType] — спецстроение
   * @param {Object} [params.state] — состояние: по нему выбирается картинка
   * @param {Function} [params.onLand] — приземление жетона
   * @param {{kind: string, palette: Object}} [params.impact] — какой удар у постройки
   *   (см. configs/buildingConstructionAnimation): с ним падение идет со шлейфом и
   *   меткой на земле, а жетон на ударе вспыхивает и пружинит
   * @returns {boolean} удалось ли запустить полет (иначе садить жетон сразу)
   */
  showBuildingDropAnimation(hexKey, {tokenType, specialBuildingType, isStartingQuarter, isPlanned = false, state, onLand, impact} = {}) {
    const hex = this.hexes[hexKey];
    if (!hex) {
      return false;
    }

    const layers = this.buildBuildingDropLayers(hexKey, tokenType, specialBuildingType, state, {isPlanned})
      .filter((layer) => layer && this.scene.textures?.exists?.(layer.textureKey));
    if (layers.length === 0) {
      return false;
    }

    // Повторное размещение на той же ячейке начинает полет заново
    this.destroyBuildingDropAnimation(hexKey);
    // Прячем ТОЛЬКО те слои, которые двойник несет с собой: спецстроение летит
    // одно, а жетон под ним никуда не девался и должен остаться видимым
    const kind = resolveDropKind({specialBuildingType, isStartingQuarter});
    // Запланированный жетон прячет только сам себя: настоящее на ячейке
    // (например, дорога под запланированным кварталом) стоит где стояло
    this.droppingHexKeys.set(hexKey, {
      kind,
      isPlanned,
      hidden: this.hideHexTokenSprites(hexKey, kind, {plannedOnly: isPlanned}),
      // До какой прозрачности тень жетона уже проявилась: с нею рисуется тень,
      // пересозданная перерисовкой посреди полета
      shadowAlpha: 0,
    });

    const startShiftY = -hex.size * BUILDING_DROP.heightFactor;
    const sprites = layers.map((layer) => {
      const sprite = this.scene.add.sprite(
        hex.centerX, hex.centerY + layer.shiftY + startShiftY, layer.textureKey
      );
      sprite.setOrigin(0.5, 0.5);
      sprite.setDepth(LAYER_DEPTH.BUILDING_DROP + (layer.depthOffset || 0));
      sprite.setScale(TOKEN_SCALE * BUILDING_DROP.startScaleFactor);
      sprite.setAlpha(BUILDING_DROP.startAlpha);
      if (layer.tint) {
        sprite.setTint?.(layer.tint);
      }
      sprite.setPipeline?.('TextureTintPipeline');
      return sprite;
    });
    const durationMs = BUILDING_IMPACTS[impact?.kind]?.flightMs ?? BUILDING_DROP.durationMs;
    const coverMs = BUILDING_DROP_COVER_MS + resolveBuildingDropCoverExtraMs(impact?.kind);
    this.markPlacementAnimation(hexKey, durationMs + coverMs);
    const flight = this.attachDropFlight({
      registry: this.buildingDropSprites,
      key: hexKey,
      sprites,
      targets: layers.map((layer) => ({y: hex.centerY + layer.shiftY, scale: TOKEN_SCALE})),
      durationMs,
      coverMs,
      reveal: () => this.revealHexTokenSprites(hexKey),
      destroy: () => this.destroyBuildingDropAnimation(hexKey),
      onLand: () => {
        this.playDropTokenImpact(hexKey, impact);
        onLand?.();
      },
      onProgress: (progress) => this.updateBuildingDropShadows(hexKey, progress),
    });
    if (impact) {
      const [firstLayer] = layers;
      this.showBuildingApproach(hexKey, impact, {
        textureKey: firstLayer.textureKey,
        sizePx: (this.scene.textures.get?.(firstLayer.textureKey)?.getSourceImage?.()?.width || 0) * TOKEN_SCALE,
        shiftY: firstLayer.shiftY,
      });
    }
    // По ним видно, что именно везет полет: снятую постройку он не довозит
    // (см. dropCancelledBuildingFlights)
    flight.tokenType = tokenType;
    flight.specialBuildingType = specialBuildingType;
    flight.isPlanned = isPlanned;
    flight.isStartingQuarter = kind === DROP_KINDS.STARTING_QUARTER;

    return true;
  }

  /**
   * Придержать жетон ячейки до своего полета: он уже есть на поле, но падать
   * начнет позже (см. playStartingQuartersRevealEffects). Перерисовка в это время
   * его не покажет: правило то же, что и у полета
   */
  holdBuildingDrop(hexKey) {
    this.droppingHexKeys.set(hexKey, {
      kind: DROP_KINDS.TOKEN,
      isPlanned: false,
      hidden: this.hideHexTokenSprites(hexKey, DROP_KINDS.TOKEN),
      shadowAlpha: 0,
    });
  }

  /**
   * Полет двойника: падение с высоты с проявлением и уменьшением до обычного
   * размера, приземление и «прикрытие» поля, пока идет эффект удара (пыль у
   * строений, волна у найма). Общий для прилета жетона и прилета отряда.
   *
   * Приземление одно на весь полет, чем бы он ни кончился: слои летят
   * одинаково, а оборванный полет (новое размещение, разбор сетки) обязан
   * отдать звук и эффект, а не проглотить их
   */
  attachDropFlight({registry, key, sprites, targets, durationMs = BUILDING_DROP.durationMs, coverMs, reveal, destroy, onLand, onProgress}) {
    let landed = false;
    const flight = {
      sprites,
      land: () => {
        if (landed) {
          return;
        }
        landed = true;
        reveal?.();
        // Сцену уже разбирают (вышли из партии, пока жетон летел): рисовать негде, а
        // ошибка здесь оборвала бы всю уборку сцены
        if (this.scene.add?.displayList !== null) {
          onLand?.();
        }
      },
    };
    registry.set(key, flight);

    sprites.forEach((sprite, index) => {
      this.scene.tweens.add({
        targets: sprite,
        y: targets[index].y,
        scale: targets[index].scale,
        // Проявление идет быстрее падения: к трети пути жетон уже непрозрачный
        alpha: {
          value: 1,
          duration: Math.max(1, Math.round(durationMs * BUILDING_DROP.alphaFadeInShare)),
        },
        duration: durationMs,
        // Разгон к земле: жетон падает, а не опускается
        ease: Phaser.Math.Easing.Quadratic.In,
        // Ход полета по времени (0..1, без разгона): по нему проявляется то,
        // что ждет жетон на земле, — его тень. Слои летят одинаково, поэтому
        // сообщает о ходе только первый
        ...(onProgress && index === 0 ? {onUpdate: (tween) => onProgress(tween?.progress)} : {}),
        onComplete: () => {
          if (index !== 0) {
            return;
          }
          flight.land();
          if (coverMs > 0) {
            this.holdDropCover({registry, key, flight, coverMs, destroy});
            return;
          }
          // Прикрытия нет — двойник свое отыграл
          destroy();
        },
      });
    });

    return flight;
  }

  /**
   * Пока жетон летит: метка на земле, шлейф и призраки жетона (см.
   * buildBuildingApproachCastPlan). Украшение: не вышло — жетон просто падает
   */
  showBuildingApproach(hexKey, {kind, palette}, ghost) {
    const hex = this.hexes[hexKey];
    if (!hex || !ensureBuildingImpactTextures(this.scene, {linearFilter: Phaser.Textures?.FilterMode?.LINEAR})) {
      return;
    }
    this.playFieldCast(() => buildBuildingApproachCastPlan({
      center: {x: hex.centerX, y: hex.centerY},
      hexSize: hex.size,
      kind,
      palette,
      ghost: ghost.sizePx > 0 ? ghost : null,
    }));
  }

  /**
   * Удар жетона о землю (см. buildBuildingImpactCastPlan). Пачка построек из плана не
   * должна заливать поле: сверх предела удары идут без осколков, искр и лучей
   */
  showBuildingImpact(hexKey, {kind, palette}) {
    const hex = this.hexes[hexKey];
    if (!hex || !ensureBuildingImpactTextures(this.scene, {linearFilter: Phaser.Textures?.FilterMode?.LINEAR})) {
      return;
    }
    const now = Date.now();
    this.buildingImpactEndTimes = this.buildingImpactEndTimes.filter((endTime) => endTime > now);
    const isReduced = this.buildingImpactEndTimes.length >= BUILDING_IMPACT_LIMITS.maxFullImpacts;
    this.playFieldCast(() => {
      const plan = buildBuildingImpactCastPlan({
        center: {x: hex.centerX, y: hex.centerY}, hexSize: hex.size, kind, palette, isReduced,
      });
      this.buildingImpactEndTimes.push(now + plan.totalMs);
      return plan;
    });
  }

  /**
   * Жетон коснулся земли: вспыхивает заливкой и пружинит, сплющенный ударом
   */
  playDropTokenImpact(hexKey, impact) {
    const flight = this.buildingDropSprites.get(hexKey);
    if (!flight || !impact || (typeof document !== 'undefined' && document.hidden)) {
      return;
    }
    const {flashAlpha, flashMs, squash, settleMs} = BUILDING_IMPACTS[impact.kind].token;
    const settle = (sprite) => {
      sprite.setScale(TOKEN_SCALE * (1 + squash), TOKEN_SCALE * (1 - squash));
      this.scene.tweens.add({
        targets: sprite,
        scaleX: TOKEN_SCALE,
        scaleY: TOKEN_SCALE,
        duration: settleMs,
        ease: 'Back.easeOut',
      });
    };
    flight.flashes = flight.sprites.map((sprite) => {
      const flash = this.scene.add.sprite(sprite.x, sprite.y, sprite.texture.key);
      flash.setDepth(sprite.depth + 0.001);
      flash.setTintFill(impact.palette.light);
      flash.setAlpha(flashAlpha);
      settle(flash);
      this.scene.tweens.add({
        targets: flash,
        alpha: 0,
        duration: flashMs,
        ease: 'Quad.easeIn',
        onComplete: () => flash.destroy(),
      });
      return flash;
    });
    flight.sprites.forEach(settle);
  }

  /**
   * Занять ячейки зоны кварталов на время полета жетона: штриховка на них появится
   * вспышкой на ударе (см. scenes/QuarterZoneBurn). Чем дальше ячейка от квартала,
   * тем позже: до нее докатывается волна
   *
   * @param {string} placedHexKey
   * @param {Array<{cellKey: string, spriteKey: string, isOnRoad: boolean}>} cells
   * @param {function(): Object} getState
   * @returns {?Object} см. QuarterZoneBurn.hold
   */
  holdQuarterZoneBurn(placedHexKey, cells, getState) {
    const placedHex = this.hexes[placedHexKey];
    if (!placedHex || (typeof document !== 'undefined' && document.hidden)) {
      return null;
    }
    const cellStepPx = placedHex.size * Math.sqrt(3);
    return this.quarterZoneBurn.hold(
      cells.filter(({cellKey}) => this.hexes[cellKey]).map((cell) => {
        const hex = this.hexes[cell.cellKey];
        const steps = Math.hypot(hex.centerX - placedHex.centerX, hex.centerY - placedHex.centerY) / cellStepPx;
        return {...cell, delayMs: Math.round(steps * QUARTER_ZONE_BURN.stepDelayMs)};
      }),
      getState,
    );
  }

  /**
   * Двойник остается над полем, пока идет эффект удара: это он не дает пыли
   * (или волне) накрыть сам жетон. Собственной анимации у этого времени нет —
   * держим твином-таймером
   */
  holdDropCover({registry, key, flight, coverMs, destroy}) {
    if (registry.get(key) !== flight) {
      return;
    }

    this.scene.tweens.add({
      targets: flight.sprites,
      alpha: 1,
      duration: coverMs,
      onComplete: () => {
        if (registry.get(key) === flight) {
          destroy();
        }
      },
    });
  }

  /**
   * Из чего собрать двойника: те же картинки, что рисует renderToken (и
   * renderSpecialBuilding), чтобы в полете жетон выглядел ровно так же, как он
   * встанет на поле
   */
  buildBuildingDropLayers(hexKey, tokenType, specialBuildingType, state, {isPlanned = false} = {}) {
    if (specialBuildingType) {
      return [{textureKey: specialBuildingType, shiftY: SPECIAL_BUILDING_SHIFT_Y}];
    }
    if (!tokenType) {
      return [];
    }

    const shiftY = tokenType === TERRAIN_TOKENS.MOUNTAIN ? MOUNTAIN_SHIFT_Y : OTHER_TOKEN_SHIFT_Y;
    if (checkIfCentralQuarter(tokenType)) {
      return this.buildCentralQuarterDropLayers(tokenType, shiftY, state);
    }

    // У плана картинку выбирает своя функция (renderPlannedBuildings берет ее
    // же): двойник обязан лететь той же картинкой, какой жетон встанет на поле,
    // иначе на приземлении она скачком менялась бы на другую
    const textureKey = isPlanned
      ? calculatePlannedTokenImage(hexKey, tokenType, state)
      : calculateTokenImage(hexKey, tokenType, state, {groundBackgroundId: this.scene.groundBackgroundId});
    return [{textureKey, shiftY}];
  }

  /**
   * Центральный квартал — три слоя, как в renderToken: подложка, обводка и
   * флаг: неподвижный, а живым он становится, когда жетон приземлится
   */
  buildCentralQuarterDropLayers(tokenType, shiftY, state) {
    const color = getPlayer0xColor(tokenType.replace(CENTRAL_QUARTER_PREFIX, ''));
    return [
      {textureKey: 'quarter_base', shiftY},
      ...this.fieldFlags.getRestLayers().map(({textureKey, isTinted}, index) => ({
        textureKey,
        shiftY,
        depthOffset: 0.01 * (index + 1),
        ...(isTinted ? {tint: color} : {}),
      })),
    ];
  }

  /**
   * Спрятать настоящий жетон ячейки на время полета двойника: сам жетон, его
   * обводку, флаг и спецстроение — все, что лежит на слоях жетонов
   */
  hideHexTokenSprites(hexKey, kind = DROP_KINDS.TOKEN, {plannedOnly = false} = {}) {
    this.ensureFieldCurrent();
    const hex = this.hexes[hexKey];
    const hidden = [];
    const hide = (sprite) => {
      sprite.setAlpha?.(0);
      hidden.push(sprite);
    };

    // Прилет запланированного жетона: прячем только спрайты плана этого вида
    // (drawPlannedBuildingSprite), настоящие жетоны ячейки остаются видны
    if (plannedOnly) {
      (hex?.markers || []).forEach((marker) => {
        if (marker?.plannedDropKind === kind) {
          hide(marker);
        }
      });
      return hidden;
    }

    // Спецстроение летит одно и лежит на самом гексе, а не в маркерах
    // (renderSpecialBuilding). Жетон под ним остается на месте: он никуда не
    // прилетает, и прятать его нельзя — иначе ячейка на время полета пустеет
    if (kind === DROP_KINDS.SPECIAL) {
      if (hex?.specialBuildingSprite) {
        hide(hex.specialBuildingSprite);
      }
      return hidden;
    }

    // Стартовый квартал в фазе setup рисуется ПОВЕРХ флага своей ячейки:
    // летит только он, флаг под ним никуда не девается
    if (kind === DROP_KINDS.STARTING_QUARTER) {
      (hex?.markers || []).forEach((marker) => {
        if (marker?.isStartingQuarterOverlay) {
          hide(marker);
        }
      });
      return hidden;
    }

    (hex?.markers || []).forEach((marker) => {
      if (checkIfHexTokenSprite(marker) && !marker.isStartingQuarterOverlay) {
        hide(marker);
      }
    });
    return hidden;
  }

  /** Вернуть видимость настоящему жетону: он приземлился */
  revealHexTokenSprites(hexKey) {
    const entry = this.droppingHexKeys.get(hexKey);
    this.droppingHexKeys.delete(hexKey);
    (entry?.hidden || []).forEach((sprite) => sprite?.setAlpha?.(1));
  }

  /**
   * Жетон ячейки рисуется, пока по ней идет полет двойника: прячем его сразу
   * при создании, иначе обновление стора посреди полета показало бы жетон на
   * месте раньше времени
   */
  hideSpriteWhileDropping(hexKey, sprite, kind = DROP_KINDS.TOKEN, {isPlannedSprite = false} = {}) {
    const entry = this.droppingHexKeys?.get(hexKey);
    // Прячем только те слои, которые летят: спецстроение не скрывает жетон под
    // собой, прилетающий жетон не скрывает стоящее на ячейке спецстроение, а
    // стартовый квартал не скрывает флаг, поверх которого он встает
    if (!entry || !sprite || entry.kind !== kind) {
      return;
    }
    // Летит запланированный жетон — настоящие жетоны ячейки не трогаем
    if (entry.isPlanned && !isPlannedSprite) {
      return;
    }

    // Тень рисуется с той прозрачностью, до которой полет уже дошел: иначе
    // перерисовка посреди полета показала бы ее сразу целиком или погасила
    sprite.setAlpha?.(sprite.isTokenShadow ? entry.shadowAlpha : 0);
    entry.hidden.push(sprite);
  }

  /**
   * Спрятан ли сейчас настоящий жетон ячейки: на нее летит его двойник.
   * Правило то же, что в hideSpriteWhileDropping: спецстроение и стартовый
   * квартал сам жетон не прячут, запланированный жетон — тоже
   */
  checkIfTokenHiddenByDrop(hexKey) {
    const entry = this.droppingHexKeys.get(hexKey);
    return Boolean(entry && entry.kind === DROP_KINDS.TOKEN && !entry.isPlanned);
  }

  /**
   * Тень жетона, который летит на ячейку: пока жетон падает, проявляется от
   * нуля до половинной густоты (BUILDING_DROP.shadowFlightAlpha), а в момент
   * удара о землю разом становится полной (revealHexTokenSprites). Сам жетон до
   * приземления спрятан (летит его двойник), а тень лежит на земле под ним и
   * густеет по мере падения — без этого она либо появлялась только в момент
   * удара, либо (после перерисовки посреди полета) стояла готовой под еще не
   * прилетевшим жетоном.
   *
   * Меняется прозрачность НАСТОЯЩЕЙ тени ячейки, а не отдельного двойника: она
   * остается на своем месте в порядке отрисовки. Перерисовка поля пересоздает
   * спрайты — новая тень подхватывает ту же прозрачность (см.
   * hideSpriteWhileDropping).
   *
   * @param {string} hexKey
   * @param {number} progress — ход полета по времени, от 0 до 1
   */
  updateBuildingDropShadows(hexKey, progress) {
    const entry = this.droppingHexKeys.get(hexKey);
    if (!entry || !Number.isFinite(progress)) {
      return;
    }

    entry.shadowAlpha = Math.min(1, Math.max(0, progress)) * BUILDING_DROP.shadowFlightAlpha;
    entry.hidden.forEach((sprite) => {
      if (sprite?.isTokenShadow) {
        sprite.setAlpha?.(entry.shadowAlpha);
      }
    });
  }

  /**
   * Пометить, что на ячейке идет анимация размещения и сколько ей осталось.
   * Берем максимум: полет и пыль идут подряд, и иконка действия ждет их обоих
   */
  markPlacementAnimation(hexKey, durationMs) {
    const endAt = Date.now() + durationMs;
    this.placementAnimationEndAt.set(hexKey, Math.max(endAt, this.placementAnimationEndAt.get(hexKey) || 0));
    // Иконку действия могли нарисовать раньше, чем началась анимация
    this.deferPendingBuildingMarkers(hexKey);
  }

  /** Сколько ячейке осталось «строиться»: 0 — анимация уже кончилась */
  getPlacementAnimationRemainingMs(hexKey) {
    const endAt = this.placementAnimationEndAt.get(hexKey);
    if (!endAt) {
      return 0;
    }

    const remaining = endAt - Date.now();
    if (remaining <= 0) {
      this.placementAnimationEndAt.delete(hexKey);
      return 0;
    }
    return remaining;
  }

  /**
   * Летит ли сейчас на ячейку стартовый квартал именно с этим жетоном (флаг
   * той же ячейки — другой жетон, он остается на виду)
   */
  checkIfStartingQuarterIsLanding(hexKey, tokenType) {
    const flight = this.buildingDropSprites?.get(hexKey);
    return Boolean(flight?.isStartingQuarter) && flight.tokenType === tokenType;
  }

  /**
   * Накладка стартового квартала в фазе расстановки: какой жетон и на какой
   * ячейке рисуется поверх флага. Свой выбор игрок видит сразу, а чужой (и
   * выбор бота) скрыт до конца расстановки — как чужие размещения в сетевой
   * игре. В hexes такого квартала нет: там до конца расстановки стоит флаг,
   * выбор лежит в startingQuarters.
   *
   * Один ответ на два вопроса — что рисовать (updateHexes) и жив ли полет
   * этого квартала (dropCancelledBuildingFlights): разойдись они, квартал
   * либо летел бы на ячейку без накладки, либо обрывался бы на ровном месте
   *
   * @param {Object} state
   * @returns {{hexKey: string, tokenType: string}|null}
   */
  getStartingQuarterOverlay(state) {
    return resolveOwnStartingQuarter(state);
  }

  /**
   * Оборвать полеты жетонов, которых на поле больше нет: постройку отменили
   * (двойной клик по ячейке, кнопка отмены), пока жетон летел
   *
   * @param {Object} hexes
   * @param {Object} specialBuildings
   * @param {Array} [planEntries] - план строительства: в нем живут
   *   запланированные жетоны
   * @param {{hexKey: string, tokenType: string}|null} [startingQuarterOverlay] -
   *   накладка стартового квартала (getStartingQuarterOverlay)
   */
  dropCancelledBuildingFlights(hexes, specialBuildings, planEntries = [], startingQuarterOverlay = null) {
    [...this.buildingDropSprites.entries()].forEach(([hexKey, flight]) => {
      // Запланированного жетона в hexes нет — он живет в плане: снят двойным
      // кликом, пока летел, — из плана пропал и он
      const flightToken = flight.specialBuildingType || flight.tokenType;
      let isGone;
      if (flight.isPlanned) {
        isGone = !(planEntries || []).some((entry) => entry.hexKey === hexKey && entry.token === flightToken);
      } else if (flight.isStartingQuarter) {
        // Стартового квартала в hexes тоже нет: до конца расстановки там флаг,
        // а квартал рисуется накладкой. Сверка с hexes обрывала его полет на
        // первой же перерисовке — от любого обновления стора. Полет жив, пока
        // накладка этого квартала стоит на этой ячейке; расстановка кончилась
        // посреди полета — квартал уже настоящий жетон ячейки
        const isOverlayDrawn = startingQuarterOverlay?.hexKey === hexKey
          && startingQuarterOverlay.tokenType === flight.tokenType;
        isGone = !isOverlayDrawn && hexes?.[hexKey] !== flight.tokenType;
      } else {
        isGone = flight.specialBuildingType
          ? specialBuildings?.[hexKey] !== flight.specialBuildingType
          : hexes?.[hexKey] !== flight.tokenType;
      }
      if (isGone) {
        this.destroyBuildingDropAnimation(hexKey);
        this.placementAnimationEndAt.delete(hexKey);
        // Дохода снятой постройки показывать уже некому
        clearTimeout(this.hexes[hexKey]?.deferredIncomeRevealTimer);
      }
    });
  }

  /**
   * Световая волна по ячейке (найм, докупка размера, бой): шестиугольник цветом
   * игрока расходится из-под ячейки — плотный фронт со шлейфом и светлая линия
   * фронта (см. configs/unitRecruitmentAnimation).
   *
   * Волна лежит поверх всего поля, поэтому внутри своей ячейки обрезана маской.
   * Спрайты живут в своем реестре (не рвутся при обновлении стора) и убирают себя сами
   *
   * @param {string} hexKey
   * @param {number} color0x — цвет игрока
   * @param {Object} [state]
   * @param {Object} [options]
   * @param {number} [options.count] — сколько волн идет подряд: следующая идет почти
   *   вплотную за предыдущей (у найма их число зависит от типа отряда)
   * @param {number} [options.depth] — слой волны; по умолчанию поверх всего
   *   поля, боевая волна (см. buildCombatCellPulse) идет ниже отрядов
   * @param {number} [options.endScaleFactor] — радиус фронта к концу, в радиусах ячейки
   * @param {number} [options.durationMs] — сколько волна идет; боевая идет
   *   дольше (см. COMBAT_CELL_PULSE.durationMultiplier)
   * @param {number} [options.shiftY] — подъем волны над центром ячейки: на
   *   ячейке с жетоном она едет вместе с ним (см. getTokenShiftY)
   */
  showUnitRecruitmentPulse(hexKey, color0x, state, {
    count = RECRUITMENT_PULSE_COUNT.SIZE,
    depth = LAYER_DEPTH.RECRUITMENT_PULSE,
    endScaleFactor = RECRUITMENT_PULSE.endScaleFactor,
    durationMs = RECRUITMENT_PULSE.durationMs,
    shiftY = 0,
  } = {}) {
    const hex = this.hexes[hexKey];
    if (!hex || !ensureHexWaveTextures(this.scene, {linearFilter: Phaser.Textures?.FilterMode?.LINEAR})) {
      return;
    }

    // Повторный найм на той же ячейке начинает волну заново
    this.destroyRecruitmentPulse(hexKey);

    const maskShape = this.buildHexMaskShape(hex);
    // Маска едет вместе с волной: иначе «окно» ячейки осталось бы на месте, и
    // поднятая волна вылезла бы из-под жетона снизу
    if (shiftY) {
      maskShape?.setY?.(shiftY);
    }
    const rotation = this.getHexRotation(hex);
    const startScale = resolveHexWaveScale(hex.size * RECRUITMENT_PULSE.startScaleFactor);
    const endScale = resolveHexWaveScale(hex.size * endScaleFactor);
    const coreTint = mixColor0x(color0x, 0xFFFFFF, RECRUITMENT_PULSE.coreLightShare);
    const waves = [];
    for (let index = 0; index < Math.max(1, count); index += 1) {
      // Тело волны цветом игрока и светлая линия ее фронта
      const layers = [
        {textureKey: RECRUITMENT_PULSE.textureKey, tint: color0x},
        {textureKey: RECRUITMENT_PULSE.coreTextureKey, tint: coreTint},
      ].map(({textureKey, tint}, layerIndex) => {
        const layer = this.scene.add.sprite(hex.centerX, hex.centerY + shiftY, textureKey);
        layer.setOrigin(0.5, 0.5);
        layer.setDepth(depth + layerIndex * 0.001);
        layer.setScale(startScale);
        layer.setRotation?.(rotation);
        layer.setAlpha(RECRUITMENT_PULSE.startAlpha);
        // Следующая волна ждет своей очереди СКРЫТОЙ, а не с нулевой
        // прозрачностью: прозрачностью распоряжается твин, и задавать ее еще и
        // формой {from, to} нельзя — ровно на этом не отображалась прежняя волна
        if (index > 0) {
          layer.setVisible?.(false);
        }
        layer.setTint?.(tint);
        // Внутри своей ячейки волна не рисуется: так она выходит ИЗ-ПОД казармы,
        // оставаясь при этом поверх всего остального поля
        applyInvertedMask(layer, maskShape);
        return layer;
      });
      waves.push(layers);
    }

    const sprites = waves.flat();
    this.recruitmentPulseSprites.set(hexKey, {sprites, maskShape});

    waves.forEach((layers, index) => {
      const delay = Math.round(index * durationMs * RECRUITMENT_PULSE.repeatDelayShare);
      const fadeDelay = Math.round(durationMs * RECRUITMENT_PULSE.alphaFadeStartShare);
      layers.forEach((layer, layerIndex) => {
        this.scene.tweens.add({
          targets: layer,
          scale: endScale,
          delay,
          duration: durationMs,
          // Резкий разбег с торможением: импульс от ячейки
          ease: Phaser.Math.Easing.Quadratic.Out,
          onStart: () => layer.setVisible?.(true),
          onComplete: () => {
            // Ячейку освобождает последняя волна: первая еще не отыграла всё
            if (index === waves.length - 1 && layerIndex === 0 && this.recruitmentPulseSprites.get(hexKey)?.sprites === sprites) {
              this.destroyRecruitmentPulse(hexKey);
            }
          },
        });

        // Затухание — отдельным твином со своей задержкой: до середины пути
        // волна идет во весь цвет и только потом гаснет
        this.scene.tweens.add({
          targets: layer,
          alpha: 0,
          delay: delay + fadeDelay,
          duration: durationMs - fadeDelay,
          ease: Phaser.Math.Easing.Quadratic.Out,
        });
      });
    });
  }

  /** Поворот картинки шестиугольника (вершина на оси x) под вершину ячейки */
  getHexRotation(hex) {
    const [firstPoint] = hex?.points || [];
    return firstPoint ? Math.atan2(firstPoint.y - hex.centerY, firstPoint.x - hex.centerX) : 0;
  }

  /**
   * Заготовка маски по контуру ячейки: ею подсветка найма обрезается изнутри
   * (см. applyInvertedMask). Рисуется не на поле, а в память
   */
  buildHexMaskShape(hex) {
    const graphics = this.scene.make?.graphics?.({add: false}) || this.scene.add?.graphics?.();
    if (!graphics?.fillPoints) {
      return null;
    }

    graphics.fillStyle(0xFFFFFF, 1);
    graphics.fillPoints(hex.points, true);
    return graphics;
  }

  destroyRecruitmentPulse(hexKey) {
    const entry = this.recruitmentPulseSprites.get(hexKey);
    if (!entry) {
      return;
    }

    entry.sprites.forEach((sprite) => {
      this.scene.tweens.killTweensOf(sprite);
      sprite.clearMask?.(false);
      sprite.destroy();
    });
    entry.maskShape?.destroy?.();
    this.recruitmentPulseSprites.delete(hexKey);
  }

  /**
   * Потраченные монеты: над ячейкой (над нанятым отрядом, над поставленным
   * строением) всплывает «−N» с иконкой монет — выпрыгивает, поднимается и
   * гаснет, как число урона ближнего боя (см. showMeleeDamageNumber). Число
   * живет в своем реестре, а не в hex.markers: перерисовка поля его не рвет.
   *
   * Над одной ячейкой числа могут идти подряд (размер отряда докупают с
   * нескольких кварталов, на квартал ставят спецстроение): прежние поднимаются
   * выше и уступают место новому
   *
   * @param {string} hexKey — ячейка, над которой всплывает число
   * @param {number} amount — сколько монет потрачено
   * @returns {boolean} показано ли число
   */
  showCoinsSpentNumber(hexKey, amount, {isGain = false} = {}) {
    const hex = this.hexes[hexKey];
    const coins = resolveCoinsSpentAmount(amount);
    if (!hex || coins === null) {
      return false;
    }

    const config = COINS_SPENT_NUMBER;
    const hexSize = hex.size;
    const sizes = resolveCoinsSpentSizes(hexSize);
    const centerX = hex.centerX;
    const startY = hex.centerY + this.getUnitShiftY(hex) - hexSize * config.startLiftFactor;

    const text = this.scene.add.text(centerX, startY, formatCoinsSpentText(coins, {isGain}), {
      fontFamily: UNIT_LABEL_DAMAGE_STYLE.fontFamily,
      fontSize: `${sizes.fontPx}px`,
      fontStyle: 'bold',
      color: config.color,
      stroke: config.strokeColor,
      strokeThickness: sizes.strokePx,
    });
    text.setOrigin(0.5, 0.5);
    text.setDepth(LAYER_DEPTH.COINS_SPENT_NUMBER);

    // Иконки могло не оказаться (битый файл, сеть): число всплывает одно. Под
    // иконкой — ее обводка, тем же цветом и той же толщины, что контур числа
    const hasIcon = Boolean(this.scene.textures?.exists?.(config.icon.textureKey));
    const iconOutlineKey = hasIcon ? this.resolveCoinsSpentIconOutlineKey() : null;
    const iconLayers = [
      {textureKey: iconOutlineKey, scale: sizes.iconOutlineScale},
      {textureKey: hasIcon ? config.icon.textureKey : null, scale: sizes.iconScale},
    ].filter((layer) => layer.textureKey).map((layer, index) => {
      const sprite = this.scene.add.sprite(centerX, startY, layer.textureKey);
      sprite.setOrigin(0.5, 0.5);
      // Обводка — под самой иконкой
      sprite.setDepth(LAYER_DEPTH.COINS_SPENT_NUMBER + index * 0.001);
      return {sprite, scale: layer.scale};
    });

    const layout = resolveCoinsSpentLayout({
      hexSize,
      textWidth: text.width * COINS_SPENT_TEXT_SCALE,
      hasIcon,
    });

    this.liftCoinsSpentNumbers(hexKey, hexSize);
    // travel ведет твин; push — последний подъем над новым числом (см.
    // liftCoinsSpentNumbers)
    const number = {hexKey, travel: 0, push: null, objects: [text, ...iconLayers.map((layer) => layer.sprite)]};
    this.coinsSpentNumbers.add(number);

    number.place = () => {
      if (!this.coinsSpentNumbers.has(number)) {
        return;
      }
      const pose = resolveCoinsSpentPose(number.travel, hexSize);
      const y = startY - pose.rise - resolveCoinsSpentLift(number.travel, number.push);
      // Пара растет из своей середины: смещения от нее растут вместе с масштабом
      text.setPosition(centerX + layout.textOffsetX * pose.scale, y);
      text.setScale(COINS_SPENT_TEXT_SCALE * pose.scale);
      text.setAlpha(pose.alpha);
      iconLayers.forEach(({sprite, scale}) => {
        sprite.setPosition(centerX + layout.iconOffsetX * pose.scale, y + sizes.iconShiftY * pose.scale);
        sprite.setScale(scale * pose.scale);
        sprite.setAlpha(pose.alpha);
      });
    };
    number.place();

    this.scene.tweens.add({
      targets: number,
      travel: 1,
      duration: config.durationMs,
      ease: 'Linear',
      onUpdate: number.place,
      onComplete: () => this.destroyCoinsSpentNumber(number),
    });

    return true;
  }

  /**
   * Обводка иконки монет: считается один раз по ее силуэту и дальше лежит в
   * кэше текстур. Не получилось (источник не прочитался) — иконка идет без
   * обводки, а следующее число попробует снова
   *
   * @returns {string|null} ключ текстуры обводки
   */
  resolveCoinsSpentIconOutlineKey() {
    const {icon, strokeColor0x} = COINS_SPENT_NUMBER;
    try {
      return generateOutlineTexture(
        this.scene, icon.textureKey, icon.outlineTextureKey, resolveCoinsSpentIconOutlineThickness(), strokeColor0x,
        {sourceScale: icon.outlineSourceScale}
      );
    } catch (error) {
      console.warn('[CoinsSpent] не удалось построить обводку иконки монет', error);
      return null;
    }
  }

  /**
   * Новое число над ячейкой: прежние числа над ней поднимаются все вместе —
   * между ними самими место уже есть, не хватает его только последнему из них
   * над новым (см. resolveCoinsSpentStackPush)
   */
  liftCoinsSpentNumbers(hexKey, hexSize) {
    const numbers = [...this.coinsSpentNumbers].filter((number) => number.hexKey === hexKey);
    const previous = numbers[numbers.length - 1];
    if (!previous) {
      return;
    }

    const extraLift = resolveCoinsSpentStackPush({previousTravel: previous.travel, hexSize});
    if (extraLift <= 0) {
      return;
    }

    numbers.forEach((number) => {
      number.push = {
        from: resolveCoinsSpentLift(number.travel, number.push),
        to: (number.push?.to ?? 0) + extraLift,
        atTravel: number.travel,
      };
    });
  }

  destroyCoinsSpentNumber(number) {
    if (!this.coinsSpentNumbers.delete(number)) {
      return;
    }

    // Phaser не снимает твины с уничтоженного объекта
    this.scene.tweens.killTweensOf?.(number);
    number.objects.forEach((object) => object.destroy());
  }

  /**
   * Двойник нанятого отряда для анимации найма: его картинка, контур и размеры такие
   * же, как у настоящей иконки на поле
   *
   * @param {Object} unit
   * @param {Object} [params]
   * @param {Object[]} [params.players]
   * @param {Object} [params.state] — по нему выбирается цвет контура
   * @returns {?{image: {textureKey, sizePx}, outline: ?{textureKey, sizePx}, tint: number, hexRotation: number}}
   */
  getUnitRecruitmentArt(unit, {players, state} = {}) {
    const hex = this.hexes[unit?.hexKey];
    if (!hex) {
      return null;
    }
    this.ensureFieldCurrent();

    const {unitImageKey} = this.resolveUnitImageKey(unit, players) || {};
    // Масштаб отряда считается при его отрисовке и кладется в кэш по текстуре
    const scale = this.unitSpriteScales?.[unitImageKey];
    if (!unitImageKey || !scale || !this.scene.textures?.exists?.(unitImageKey)) {
      return null;
    }

    const resolveLayer = (textureKey) => ({
      textureKey,
      sizePx: scale * (this.scene.textures.get?.(textureKey)?.get?.()?.width || 0),
    });
    const outlineKey = this.resolveUnitDropOutlineKey(unit, unitImageKey, state);
    const player = (players || []).find((item) => item.id === unit.ownerId);
    return {
      image: resolveLayer(unitImageKey),
      outline: outlineKey ? resolveLayer(outlineKey) : null,
      tint: player ? getPlayer0xColor(player.color) : 0xFFFFFF,
      hexRotation: this.getHexRotation(hex),
    };
  }

  /** Спрятать нанятый отряд, пока он проявляется из света (см. revealUnitSprites) */
  hideRecruitingUnit(unit) {
    const hex = this.hexes[unit?.hexKey];
    if (hex) {
      this.droppingUnitIds.set(unit.id, this.hideUnitSprites(unit.id, hex));
    }
  }

  /**
   * Плавный проход отряда по пути, когда состояние УЖЕ изменилось: ход
   * соперника по сети и воспроизведение записи матча (свой ход анимирует
   * animateUnitMovement — там, наоборот, состояние меняется после полета).
   *
   * Настоящий отряд к этому моменту уже нарисован на целевой ячейке, поэтому
   * он прячется, а по пути идет его двойник — тот же прием, что и у
   * нанятого отряда (hideRecruitingUnit). Двойник живет в
   * своем реестре, а не в hex.markers, и перерисовка стора его не рвет;
   * настоящий отряд перепрячет renderUnit (hideUnitSpritesWhileDropping).
   *
   * @param {Object} unit — отряд уже на целевой ячейке
   * @param {Array} path — путь {q, r} от исходной ячейки до целевой
   * @param {Object} [params]
   * @param {Array} [params.players]
   * @param {Object} [params.state] — по нему выбирается цвет контура
   * @param {number} [params.durationMs] — на весь путь, как у своего хода
   * @param {number} [params.timeScale] — сжатие анимации полета на крыльях под
   *   скорость записи матча (0.5 — вдвое короче)
   * @returns {boolean} удалось ли запустить полет (иначе отряд остается на
   *   целевой ячейке — то есть перемещается мгновенно, как раньше)
   */
  showUnitMovementFlight(unit, path, {players, state, durationMs = UNIT_MOVEMENT_DURATION_MS, timeScale = 1} = {}) {
    const pathHexes = (path || []).map((step) => this.hexes[coordsToKey(step.q, step.r)]);
    if (!unit || pathHexes.length < 2 || pathHexes.some((hex) => !hex)) {
      return false;
    }

    // Летающий отряд идет по прямой от центра ячейки к центру цели
    const isFlying = checkIfUnitTypeIsFlyingForFaction(unit.type, unit.factionId);
    const steps = isFlying ? [pathHexes[0], pathHexes[pathHexes.length - 1]] : pathHexes;
    const targetHex = steps[steps.length - 1];
    // Этот отряд только что прошел этот путь по-настоящему (свой ход, ход бота
    // на этом клиенте, обучение) — пришедший следом экшен лишь фиксирует итог
    if (this.checkIfUnitMovementAlreadyAnimated(unit.id, targetHex.key)) {
      return false;
    }

    const {unitImageKey} = this.resolveUnitImageKey(unit, players) || {};
    // Масштаб кладется в кэш при первой отрисовке отряда: идущий отряд на поле
    // уже стоял, но у чужого клиента мог и не успеть отрисоваться
    const scale = this.unitSpriteScales?.[unitImageKey];
    if (!unitImageKey || !scale || !this.scene.textures?.exists?.(unitImageKey)) {
      return false;
    }

    // Новый ход того же отряда начинает полет заново
    this.destroyUnitMovementFlight(unit.id);

    const positions = steps.map((hex) => ({x: hex.centerX, y: hex.centerY + this.getUnitShiftY(hex)}));
    const [start] = positions;

    const sprites = [];
    let shadowSprite = null;
    // Тень — такая же, как под настоящим отрядом: без нее отряд на время
    // анимации идет по полю «без опоры» (настоящая тень спрятана вместе с ним)
    const shadow = this.resolveUnitShadow(
      unit, this.checkIfUnitHasWhiteOutline(unit, state, state && selectGameResults(state))
    );
    if (this.scene.textures?.exists?.(shadow.textureKey)) {
      shadowSprite = this.scene.add.sprite(start.x, start.y, shadow.textureKey);
      shadowSprite.setOrigin(0.5, 0.5);
      shadowSprite.setDepth(LAYER_DEPTH.UNIT_MOVEMENT_FLIGHT - 0.2);
      shadowSprite.setScale(scale * shadow.scaleMultiplier);
      sprites.push(shadowSprite);
    }

    // Контур — такой же, как у настоящей иконки на поле (см. renderUnit)
    const outlineKey = this.resolveUnitDropOutlineKey(unit, unitImageKey, state);
    if (outlineKey) {
      const outlineSprite = this.scene.add.sprite(start.x, start.y, outlineKey);
      outlineSprite.setOrigin(0.5, 0.5);
      outlineSprite.setDepth(LAYER_DEPTH.UNIT_MOVEMENT_FLIGHT - 0.1);
      this.addUnitDoubleOutlineTint(outlineSprite, outlineKey);
      outlineSprite.setScale(scale);
      sprites.push(outlineSprite);
    }

    const sprite = this.scene.add.sprite(start.x, start.y, unitImageKey);
    sprite.setOrigin(0.5, 0.5);
    sprite.setDepth(LAYER_DEPTH.UNIT_MOVEMENT_FLIGHT);
    sprite.setScale(scale);
    const player = (players || []).find((item) => item.id === unit.ownerId);
    if (player) {
      sprite.setTint?.(getPlayer0xColor(player.color));
    }
    sprites.push(sprite);

    // Настоящий отряд уже стоит на целевой ячейке — прячем его на время пути
    this.droppingUnitIds.set(unit.id, this.hideUnitSprites(unit.id, targetHex));

    const flight = {unitId: unit.id, toHexKey: targetHex.key, sprites, castId: null};
    this.unitMovementFlights.set(unit.id, flight);
    if (isFlying && this.startWingedUnitMovementFlight(flight, steps[0].key, {shadowSprite, timeScale})) {
      return true;
    }

    // Летающий отряд сюда попадает, только если картинок крыльев нет: следов у него нет
    if (!isFlying) {
      this.startUnitFootprints(unit, positions, sprite, {hexSize: steps[0].size, players, timeScale});
    }

    // Проигрыватель записи матча ждет, пока отряд дойдет (на x4–x5 переход
    // длиннее шага записи), — но не дольше срока с запасом на зависший кадр
    markAwaitedAnimationPlaying(`move:${unit.id}`, Date.now() + durationMs + UNIT_MOVEMENT_WAIT_MARGIN_MS);

    const segments = positions.slice(1);
    const segmentDurationMs = Math.max(1, Math.round(durationMs / segments.length));
    let segmentIndex = 0;

    const moveToNextHex = () => {
      // Полет оборвали (отмена соперника, новый ход этого же отряда, разбор
      // сетки) — его спрайтов уже нет
      if (this.unitMovementFlights.get(unit.id) !== flight) {
        return;
      }
      if (segmentIndex >= segments.length) {
        this.destroyUnitMovementFlight(unit.id);
        return;
      }

      const target = segments[segmentIndex];
      const ease = resolveMovementSegmentEase(segmentIndex, segments.length);
      segmentIndex += 1;

      let completedCount = 0;
      sprites.forEach((spriteToMove) => {
        this.scene.tweens.add({
          targets: spriteToMove,
          x: target.x,
          y: target.y,
          duration: segmentDurationMs,
          ease,
          onComplete: () => {
            completedCount += 1;
            if (completedCount === sprites.length) {
              moveToNextHex();
            }
          },
        });
      });
    };

    moveToNextHex();

    return true;
  }

  /**
   * След за идущим отрядом: его вид решает облик отряда, как и картинку на поле
   * (см. resolveUnitImageKey), а время ведет спрайт отряда (см. UnitFootprints)
   *
   * @param {Object} unit
   * @param {Array<{x: number, y: number}>} points — путь от старта до цели
   * @param {Object} follow — спрайт идущего отряда
   * @param {Object} params
   * @param {number} params.hexSize
   * @param {Array} [params.players]
   * @param {number} [params.timeScale] — сжатие под скорость записи матча
   */
  startUnitFootprints(unit, points, follow, {hexSize, players, timeScale = 1}) {
    const {useFactionUnitSkin, isGiantsUnitIcon} = this.resolveUnitImageKey(unit, players) || {};
    this.unitFootprints?.start({
      unitId: unit.id,
      follow,
      points,
      hexSize,
      unitTypeId: unit.type,
      unitSize: unit.size,
      factionId: unit.factionId,
      hasFactionSkin: Boolean(useFactionUnitSkin || isGiantsUnitIcon),
      timeScale,
    });
  }

  /**
   * План полета на крыльях между двумя ячейками. null — показать нельзя (картинки
   * крыльев не загрузились, вкладка скрыта и кадры не идут): отряд просто проходит
   * по прямой
   */
  buildWingedFlightPlan(fromHexKey, toHexKey) {
    if (
      (typeof document !== 'undefined' && document.hidden)
      || !Object.values(WINGED_FLIGHT_TEXTURE_KEYS).every((key) => this.scene.textures?.exists?.(key))
    ) {
      return null;
    }
    try {
      const from = this.getAbilityCastAnchor(fromHexKey);
      const to = this.getAbilityCastAnchor(toHexKey);
      return from && to
        ? buildWingedFlightCastPlan({from, to, hexSize: from.hexSize, hexRotation: from.hexRotation})
        : null;
    } catch (error) {
      console.warn('[WingedFlight] не удалось построить анимацию полета', error);
      return null;
    }
  }

  /**
   * Чужой ход летающего отряда: двойник идет за планом полета, а на посадке
   * уступает место настоящему отряду. Крылья и волна досвечивают уже поверх него
   *
   * @returns {boolean} пошла ли анимация (иначе двойник просто идет по прямой)
   */
  startWingedUnitMovementFlight(flight, fromHexKey, {shadowSprite, timeScale}) {
    const plan = this.buildWingedFlightPlan(fromHexKey, flight.toHexKey);
    if (!plan) {
      return false;
    }

    const {unitId, sprites} = flight;
    const checkIfCurrent = () => this.unitMovementFlights.get(unitId) === flight;
    sprites.forEach((sprite) => {
      if (sprite !== shadowSprite) {
        sprite.setDepth(sprite.depth + WINGED_UNIT_DEPTH_RAISE);
      }
    });
    markAwaitedAnimationPlaying(`move:${unitId}`, Date.now() + plan.impactMs * timeScale + UNIT_MOVEMENT_WAIT_MARGIN_MS);

    const castId = this.abilityCastAnimation.play(plan, {
      timeScale,
      onFrame: (elapsedMs) => {
        if (!checkIfCurrent()) {
          return;
        }
        const pose = plan.resolveUnitPose(elapsedMs);
        sprites.forEach((sprite) => {
          sprite.x = pose.x;
          sprite.y = sprite === shadowSprite ? pose.y : pose.y - pose.lift;
        });
      },
      onImpact: () => {
        if (checkIfCurrent()) {
          flight.castId = null;
          this.destroyUnitMovementFlight(unitId);
        }
      },
    });
    if (castId === null) {
      return !checkIfCurrent();
    }

    flight.castId = castId;
    return true;
  }

  /**
   * Свой ход летающего отряда: настоящие спрайты отряда идут за планом полета, а
   * на посадке ход фиксируется в состоянии. Как и обычное перемещение, его добивает
   * skipUnitMovementAnimation
   *
   * @returns {boolean} пошла ли анимация (иначе отряд просто идет по прямой)
   */
  startWingedUnitMovement({unitId, sprite, relatedSprites, fromHexKey, toHexKey, onLanded}) {
    const plan = this.buildWingedFlightPlan(fromHexKey, toHexKey);
    if (!plan) {
      return false;
    }

    const shadowSprites = this.unitsSprites[`${unitId}_shadowSprite`] || [];
    const carried = relatedSprites.map((item) => ({
      item,
      offsetX: item.x - sprite.x,
      offsetY: item.y - sprite.y,
      isShadow: shadowSprites.includes(item),
      depth: item.depth,
    }));
    const raiseDepths = (raise) => carried.forEach(({item, isShadow, depth}) => {
      if (!isShadow && item.active !== false) {
        item.setDepth?.(depth + raise);
      }
    });
    raiseDepths(WINGED_UNIT_DEPTH_RAISE);

    const run = {castId: null, restoreDepths: () => raiseDepths(0)};
    this.wingedMovement = run;
    const castId = this.abilityCastAnimation.play(plan, {
      onFrame: (elapsedMs) => {
        if (this.wingedMovement !== run) {
          return;
        }
        const pose = plan.resolveUnitPose(elapsedMs);
        carried.forEach(({item, offsetX, offsetY, isShadow}) => {
          if (item.active !== false) {
            item.x = pose.x + offsetX;
            item.y = pose.y + offsetY - (isShadow ? 0 : pose.lift);
          }
        });
      },
      onImpact: () => {
        if (this.wingedMovement === run) {
          this.wingedMovement = null;
          run.restoreDepths();
          onLanded();
        }
      },
    });
    if (castId === null) {
      if (this.wingedMovement !== run) {
        return true;
      }
      this.wingedMovement = null;
      run.restoreDepths();
      return false;
    }

    run.castId = castId;
    return true;
  }

  /**
   * Убрать двойника и вернуть настоящему отряду видимость — там, где он сейчас
   * по состоянию. Так же обрывается и недошедший полет: отряд появляется на
   * месте мгновенно
   */
  destroyUnitMovementFlight(unitId) {
    const flight = this.unitMovementFlights.get(unitId);
    if (!flight) {
      return;
    }

    this.unitMovementFlights.delete(unitId);
    this.unitFootprints?.finish(unitId);
    if (flight.castId !== null && flight.castId !== undefined) {
      this.abilityCastAnimation.finish(flight.castId);
    }
    markAwaitedAnimationFinished(`move:${unitId}`);
    flight.sprites.forEach((sprite) => {
      // Phaser не снимает твины с уничтожаемого объекта сам
      this.scene.tweens.killTweensOf(sprite);
      sprite.destroy();
    });
    this.revealUnitSprites(unitId);
  }

  /**
   * Оборвать полеты отрядов, которых на их целевой ячейке в состоянии больше
   * нет: соперник отменил свое перемещение, отряд погиб, состояние переписали
   * целиком (переподключение, перемотка записи). Отряд при этом возвращается
   * мгновенно — туда, где он по состоянию
   */
  dropCancelledUnitMovementFlights(units) {
    if (this.unitMovementFlights.size === 0) {
      return;
    }

    const hexKeyByUnitId = new Map((units || []).map((unit) => [unit.id, unit.hexKey]));
    [...this.unitMovementFlights.entries()].forEach(([unitId, flight]) => {
      if (hexKeyByUnitId.get(unitId) !== flight.toHexKey) {
        // Хода не было: его след не должен дописаться до цели
        this.unitFootprints?.cancel(unitId);
        this.destroyUnitMovementFlight(unitId);
      }
    });
  }

  /**
   * Запомнить перемещение, которое рендерер отыграл сам: MOVE_UNIT уходит в
   * стор из колбэка анимации, и эффект этого же экшена (ход бота на этом
   * клиенте, обучение) не должен провести отряд по пути второй раз
   */
  markLocalUnitMovement(unitId, hexKey) {
    if (!unitId || !hexKey) {
      return;
    }
    this.lastLocalUnitMovement = {unitId, hexKey, at: Date.now()};
  }

  /**
   * Отметка одноразовая: снимается первой же проверкой, чтобы не погасить
   * следующее перемещение того же отряда на ту же ячейку
   */
  checkIfUnitMovementAlreadyAnimated(unitId, hexKey) {
    const last = this.lastLocalUnitMovement;
    if (!last || last.unitId !== unitId || last.hexKey !== hexKey) {
      return false;
    }

    this.lastLocalUnitMovement = null;
    return Date.now() - last.at <= LOCAL_UNIT_MOVEMENT_ECHO_MS;
  }

  /**
   * Белый ли контур у отряда: белым выделены отряды игрока, чья сейчас фаза
   * (по ним на поле видно, кто ходит), и все отряды игроков после конца партии;
   * у остальных контур черный, у нейтральных его нет вовсе.
   *
   * Общая точка правды для отряда на поле (renderUnit) и для его двойника в
   * анимациях: от этого же признака зависит светлая тень (resolveUnitShadow).
   * Без состояния (боты и обучение зовут анимации без него) считаем контур
   * белым — как было в анимациях до этого резолвера
   */
  checkIfUnitHasWhiteOutline(unit, state, gameResults) {
    if (unit.ownerId === NEUTRAL_PLAYER_ID) {
      return false;
    }
    if (!state || gameResults) {
      return true;
    }

    // Чья сейчас фаза — см. getActiveUnitOwnerIds (в одновременной фазе
    // снабжения это не текущий игрок-якорь)
    return getActiveUnitOwnerIds(state).includes(unit.ownerId);
  }

  /**
   * Тень отряда: текстура и множитель масштаба. Общая точка правды для отряда
   * на поле (renderUnit) и для его двойника в перемещении
   * (showUnitMovementFlight) — двойник обязан нести тень с собой, иначе на
   * время анимации отряд идет по полю без тени (найдено пользователем при
   * просмотре записи матча).
   *
   * Светлая тень — у нейтральных по ИСХОДНОЙ принадлежности отрядов, пока у
   * них нет белого контура: обычная темная тень на темных гексах сливалась бы
   * с фоном
   */
  resolveUnitShadow(unit, hasWhiteOutline) {
    const usesNeutralIcon = (unit.capturedFromOwnerId ?? unit.ownerId) === NEUTRAL_PLAYER_ID;

    return {
      textureKey: (usesNeutralIcon && !hasWhiteOutline) ? 'unit_shadow_neutral' : 'unit_shadow',
      scaleMultiplier: usesNeutralIcon ? NEUTRAL_UNIT_SHADOW_SCALE_MULTIPLIER : 1,
    };
  }

  /**
   * Контур падающего отряда: белый у отрядов, чья сейчас фаза (как и на поле),
   * черный у остальных, у нейтральных его нет вовсе — см. renderUnit
   */
  applyActiveContourTint(outlineSprite) {
    const {contourTop, contourBottom} = ACTIVE_GLOW;
    outlineSprite.setTint?.(contourTop, contourTop, contourBottom, contourBottom);
  }

  /** Контур двойника: светлый красится так же, как контур самого отряда */
  addUnitDoubleOutlineTint(outlineSprite, outlineKey) {
    if (!outlineKey.endsWith('_black')) {
      this.applyActiveContourTint(outlineSprite);
    }
  }

  resolveUnitDropOutlineKey(unit, unitImageKey, state) {
    if (unit.ownerId === NEUTRAL_PLAYER_ID) {
      return null;
    }

    const hasWhiteOutline = this.checkIfUnitHasWhiteOutline(unit, state, state && selectGameResults(state));
    return generateOutlineTexture(
      this.scene, unitImageKey, `${unitImageKey}_outline${hasWhiteOutline ? '' : '_black'}`, 20,
      hasWhiteOutline ? 0xFFFFFF : 0x000000
    );
  }

  /**
   * Спрятать отряд на время полета его двойника: сам спрайт, тень, контур и
   * подписи — все, что нарисовано для него на этой ячейке
   */
  hideUnitSprites(unitId, hex) {
    this.ensureFieldCurrent();
    const hidden = [];
    const hide = (sprite) => {
      if (sprite?.setAlpha) {
        sprite.setAlpha(0);
        hidden.push(sprite);
      }
    };

    Object.keys(this.unitsSprites).forEach((spriteId) => {
      if (spriteId !== unitId && !spriteId.startsWith(`${unitId}_`)) {
        return;
      }
      const stored = this.unitsSprites[spriteId];
      (Array.isArray(stored) ? stored : [stored]).forEach(hide);
    });
    (hex?.unitSizeLabel || []).forEach(hide);
    (hex?.markers || []).forEach((marker) => {
      if (checkIfUnitLayerSprite(marker)) {
        hide(marker);
      }
    });

    return hidden;
  }

  /** Вернуть отряду видимость: он приземлился */
  revealUnitSprites(unitId) {
    const hidden = this.droppingUnitIds.get(unitId);
    this.droppingUnitIds.delete(unitId);
    (hidden || []).forEach((sprite) => sprite?.setAlpha?.(1));
  }

  /**
   * Отряд перерисовали, пока летит его двойник (обновление стора) — прячем
   * заново, иначе отряд появится на месте раньше, чем долетит
   */
  hideUnitSpritesWhileDropping(unit, hex) {
    // Реестра может не быть: часть тестов поднимает рендерер без конструктора
    if (!unit || !this.droppingUnitIds?.has(unit.id)) {
      return;
    }

    this.droppingUnitIds.set(unit.id, [
      ...(this.droppingUnitIds.get(unit.id) || []),
      ...this.hideUnitSprites(unit.id, hex),
    ]);
  }

  destroyBuildingDropAnimation(hexKey) {
    const flight = this.buildingDropSprites.get(hexKey);
    if (!flight) {
      this.revealHexTokenSprites(hexKey);
      return;
    }

    // Недолетевший жетон все равно садится: звук постройки и пыль висят на
    // приземлении, и терять их нельзя
    flight.land();
    [...flight.sprites, ...(flight.flashes || [])].forEach((sprite) => {
      this.scene.tweens.killTweensOf(sprite);
      sprite.destroy();
    });
    this.buildingDropSprites.delete(hexKey);
  }

  destroyCityGrowthMarkers(trackingKey) {
    const markers = this.cityGrowthAnimationMarkers.get(trackingKey);
    if (!markers) {
      return;
    }

    markers.forEach((marker) => {
      this.scene.tweens.killTweensOf(marker);
      marker.destroy();
    });
    this.cityGrowthAnimationMarkers.delete(trackingKey);
  }

  /**
   * Разовая анимация пути снабжения — светящаяся нить, как пути влияния гильдий
   * и храмов (renderInfluencePaths + fade in/hold/fade out), но цветом
   * снабжения. Используется:
   *  - в фазе снабжения при ручной раздаче (весь путь валиден);
   *  - на этапе перемещения фазы войны как подсказка «отряд вне зоны
   *    снабжения»: валидная часть пути (докуда ферма реально дотянулась бы —
   *    validCellsCount ячеек, включая ферму) светится с точкой на границе
   *    покрытия, там нить рвется искрами, а остаток до отряда тянется погасшим.
   *
   * @param {string[]} pathHexKeys - путь [ферма, ..., целевая ячейка]
   * @param {number} validCellsCount - сколько первых ячеек пути валидны
   *   (по умолчанию весь путь)
   * @param intermediateFarmHexKeys
   */
  showSupplyPathAnimation(pathHexKeys, validCellsCount = pathHexKeys.length, {intermediateFarmHexKeys = []} = {}) {
    if (!pathHexKeys || pathHexKeys.length < 2) return;

    // Повторная раздача до затухания предыдущей анимации — старую убираем
    if (this.supplyPathAnimationGraphics) {
      this.supplyPathAnimationGraphics.forEach((graphics) => {
        this.scene.tweens.killTweensOf(graphics);
        graphics.destroy();
      });
    }
    this.supplyPathAnimationGraphics = [];

    const startHex = this.hexes[pathHexKeys[0]];
    if (!startHex) return;

    // Валидному сегменту нужны хотя бы 2 ячейки (линия от фермы); если ферма
    // не дотягивается даже до первой ячейки пути (перекрыто противником) —
    // весь путь рисуется погасшим
    const validPart = validCellsCount >= 2
      ? pathHexKeys.slice(0, Math.min(validCellsCount, pathHexKeys.length))
      : null;
    // Погасший хвост начинается с последней валидной ячейки — линия непрерывна
    const invalidPart = !validPart
      ? pathHexKeys
      : (pathHexKeys.length > validPart.length ? pathHexKeys.slice(validPart.length - 1) : null);

    // Выше подсветки ячеек этапа и ее значков (UNIT - 0.5), но ниже слоев отряда (тень —
    // UNIT - 0.2). Погасшая (невалидная) часть — глубиной НИЖЕ светящейся, чтобы не
    // накрывать точку на границе покрытия
    const validDepth = LAYER_DEPTH.UNIT - 0.3;
    const invalidDepth = LAYER_DEPTH.UNIT - 0.4;

    const resolveRevealMs = (path) => resolveThreadRevealMs(
      buildThreadGeometry(this.getPathPoints(path)).length, startHex.size,
    );
    const validRevealMs = validPart ? resolveRevealMs(validPart) : 0;
    const invalidRevealMs = invalidPart ? resolveRevealMs(invalidPart) : 0;

    const graphicsToAnimate = [];
    if (validPart) {
      graphicsToAnimate.push(...this.renderInfluencePaths(startHex, [validPart], GLOW_THREAD_PALETTES.SUPPLY, {
        alpha: 0,
        register: false,
        depth: validDepth,
        revealMs: validRevealMs,
        // Путь обрывается на этой точке: дальше снабжению не пройти
        endNodeOverlays: invalidPart ? SUPPLY_BLOCKED_NODE_OVERLAYS : undefined,
      }));
    }
    if (invalidPart) {
      // Погасшая нить тянется дальше с того места, где оборвалась светящаяся
      graphicsToAnimate.push(...this.renderInfluencePaths(startHex, [invalidPart], GLOW_THREAD_PALETTES.BROKEN, {
        alpha: 0,
        register: false,
        depth: invalidDepth,
        revealMs: invalidRevealMs,
        revealDelayMs: validRevealMs,
      }));
      const [breakPoint] = this.getPathPoints(invalidPart);
      if (breakPoint) {
        this.playFieldCast(() => buildSupplyBreakCastPlan({point: breakPoint, hexSize: startHex.size, delayMs: validRevealMs}));
      }
    }

    // Маленькая точка на стартовой ячейке пути (ферма-источник), чтобы ломаная
    // красиво "стартовала", а не обрывалась голым концом
    const startNodeGraphics = this.glowingThreads.createNodes({
      points: this.getPathPoints([pathHexKeys[0]]),
      palette: GLOW_THREAD_PALETTES.SUPPLY,
      hexSize: startHex.size,
      depth: validDepth,
      alpha: 0,
      // Ферма не дотягивается даже до первой ячейки: обрыв на ней самой
      isSmall: Boolean(validPart),
      overlays: validPart ? undefined : SUPPLY_BLOCKED_NODE_OVERLAYS,
    });
    if (startNodeGraphics) {
      graphicsToAnimate.push(startNodeGraphics);
    }

    // Точки на промежуточных фермах пути (цепочка ферм в фазе снабжения) — того
    // же размера, что точка конца пути. Стрелка на точке говорит, что снабжение идет
    // здесь транзитом, и смотрит на следующую ячейку маршрута
    const farmNodesGraphics = this.renderSupplyNodes(
      intermediateFarmHexKeys,
      validDepth,
      intermediateFarmHexKeys.map((hexKey) => this.calcPathDirectionAngle(pathHexKeys, hexKey)),
    );
    if (farmNodesGraphics) {
      graphicsToAnimate.push(farmNodesGraphics);
    }

    graphicsToAnimate.forEach((graphics) => {
      this.scene.tweens.add({
        targets: graphics,
        alpha: 1,
        duration: SUPPLY_ANIMATION_DURATION_MS,
        // Нить успевает дотянуться и еще побыть целиком
        hold: SUPPLY_ANIMATION_HOLD_MS + validRevealMs + invalidRevealMs,
        ease: Phaser.Math.Easing.Sine.InOut,
        yoyo: true,
        repeat: 0,
        onComplete: () => {
          graphics.destroy();
        },
      });
    });

    this.supplyPathAnimationGraphics = graphicsToAnimate;
  }

  /**
   * Передача снабжения от фермы отряду: шар идет по нити пути (см.
   * showSupplyPathAnimation) и вспыхивает на отряде
   * @param {string[]} pathHexKeys - путь [ферма, ..., ячейка отряда]
   */
  showSupplyTransfer(pathHexKeys) {
    const hexSize = this.hexes[pathHexKeys?.[0]]?.size;
    if (!hexSize) {
      return;
    }
    this.playFieldCast(() => buildSupplyTransferCastPlan({points: this.getPathPoints(pathHexKeys), hexSize}));
  }

  /**
   * Разовая анимация поля по плану (см. buildAbilityCast). Украшение: в скрытой
   * вкладке кадры стоят и незачем копить, а сбой не должен мешать самому действию
   * @param {function(): ?Object} buildPlan
   */
  playFieldCast(buildPlan) {
    if (typeof document !== 'undefined' && document.hidden) {
      return;
    }
    try {
      const plan = buildPlan();
      if (plan) {
        this.abilityCastAnimation?.play(plan);
      }
    } catch (error) {
      console.warn('[FieldCast] не удалось показать анимацию', error);
    }
  }

  /**
   * Разовый показ дохода: медальон выпрыгивает, от него расходится кольцо и
   * поднимаются искры цвета ресурса
   * @param {Object} hex
   * @param {string} incomeType - INCOME_TYPE
   * @param {Object[]} markers - элементы плашки (см. renderIncome)
   */
  playIncomeReveal(hex, incomeType, markers) {
    if (typeof document !== 'undefined' && document.hidden) {
      return;
    }
    (markers || []).forEach((marker) => {
      const {scaleX, scaleY} = marker;
      marker.setScale(scaleX * INCOME_MARKER.popFromScale, scaleY * INCOME_MARKER.popFromScale);
      this.scene.tweens.add({
        targets: marker,
        scaleX,
        scaleY,
        duration: INCOME_MARKER.popMs,
        ease: 'Back.easeOut',
      });
    });
    this.playFieldCast(() => buildIncomeRevealCastPlan({
      center: {x: hex.centerX, y: hex.centerY + MARKER_SHIFT_Y},
      hexSize: hex.size,
      isSilver: incomeType === INCOME_TYPE.WIN_POINTS,
    }));
  }

  /**
   * Оверлей принадлежности на ячейках переданных городов — заливка того же
   * вида (цвет владельца, прозрачность, сдвиг, слой), что у постоянного слоя
   * принадлежности (addCityOwnershipMarkers, иконка bust в VisualToggles).
   * Отбора здесь, в отличие от него, нет: красится все переданное, включая
   * нейтральные города, — что красить, решает вызывающий код. Маркеры
   * возвращаются, а не регистрируются на гексах: вызывающий код анимирует и
   * уничтожает их сам.
   * @param {Array<{playerColor: string, hexKeys: string[]}>} cities - города с цветом владельца
   * @param {Object} options
   * @param {number} options.alpha - стартовая прозрачность объекта (для анимации)
   * @returns {Array<Phaser.GameObjects.Graphics>}
   */
  renderCityOwnershipOverlay(cities, {alpha = 1} = {}) {
    const graphicsObjects = [];

    cities.forEach(({playerColor, hexKeys}) => {
      const playerColor0x = getPlayer0xColor(playerColor);

      hexKeys.forEach(hexKey => {
        const hex = this.hexes[hexKey];
        if (!hex) {
          return;
        }

        const graphics = this.scene.add.graphics();
        graphics.fillStyle(playerColor0x, CITY_OWNERSHIP_FILL_ALPHA);
        this.fillHexShape(graphics, hex, CITY_OWNERSHIP_SHIFT_Y);
        graphics.setDepth(LAYER_DEPTH.CITY_OWNERSHIP);
        graphics.setAlpha(alpha);

        graphicsObjects.push(graphics);
      });
    });

    return graphicsObjects;
  }

  /** Точки ломаной пути: центры его ячеек на высоте жетонов */
  getPathPoints(pathHexKeys) {
    return pathHexKeys
      .map((hexKey) => this.hexes[hexKey])
      .filter(Boolean)
      .map((hex) => ({x: hex.centerX, y: hex.centerY + PATH_SHIFT_Y}));
  }

  /**
   * Точки на фермах пути и сети снабжения
   * @returns {?Phaser.GameObjects.Container} null — ферм нет
   */
  renderSupplyNodes(hexKeys, depth, arrowAngles) {
    const points = this.getPathPoints(hexKeys);
    return this.glowingThreads.createNodes({
      points,
      palette: GLOW_THREAD_PALETTES.SUPPLY,
      hexSize: this.hexes[hexKeys[0]]?.size,
      depth,
      alpha: 0,
      arrowAngles,
    });
  }

  /**
   * Куда идет маршрут из его ячейки
   * @param {string[]} pathHexKeys
   * @param {string} hexKey - ячейка маршрута, кроме последней
   * @returns {number} угол на следующую ячейку, радианы
   */
  calcPathDirectionAngle(pathHexKeys, hexKey) {
    const index = pathHexKeys.indexOf(hexKey);
    const [from, to] = this.getPathPoints(pathHexKeys.slice(index, index + 2));
    return from && to ? Math.atan2(to.y - from.y, to.x - from.x) : 0;
  }

  /**
   * Светящиеся нити путей (см. scenes/GlowingThreads)
   * @param {Object} startHex - ячейка, на которой регистрируются постоянные нити
   * @param {string[][]} paths
   * @param {Object} palette - GLOW_THREAD_PALETTES
   * @param {Object} [options]
   * @param {number} [options.alpha]
   * @param {boolean} [options.register] - постоянная нить: убирается с перерисовкой ячейки
   * @param {number} [options.depth]
   * @param {number} [options.revealMs] - за сколько нить дотянется до конца (0 — сразу)
   * @param {number} [options.revealDelayMs]
   * @param {boolean} [options.isRevealed] - тянуть нить за время по ее длине
   * @param {boolean} [options.hasEndNode] - точка на конце нити
   * @returns {Phaser.GameObjects.Container[]}
   */
  renderInfluencePaths(startHex, paths, palette, options = {}) {
    const {
      alpha = 1,
      register = true,
      depth = LAYER_DEPTH.INFO - 0.1,
      revealMs = 0,
      revealDelayMs = 0,
      isRevealed = false,
      hasEndNode = true,
      endNodeOverlays,
    } = options;

    return paths.map((path) => {
      const points = this.getPathPoints(path);
      const thread = this.glowingThreads.createThread({
        points,
        palette,
        hexSize: startHex.size,
        depth,
        alpha,
        revealMs: isRevealed ? resolveThreadRevealMs(buildThreadGeometry(points).length, startHex.size) : revealMs,
        revealDelayMs,
        hasEndNode,
        endNodeOverlays,
      });
      if (register) {
        this.registerMarker(startHex, thread);
      }
      return thread;
    });
  }

  renderIncome(key, hex, incomeValue, incomeType, isBlocked, options = {}) {
    const {
      isAnyPlayerOwner = true,
      alpha,
      register = true,
      depth = LAYER_DEPTH.INFO,
      isAnimated = false,
      isAnimationReversed = false,
      textStyle = {}
    } = options;
    const {elements: markers, halo} = createIncomeMarker(this.scene, {
      x: hex.centerX,
      y: hex.centerY + MARKER_SHIFT_Y,
      hexSize: hex.size,
      value: incomeValue,
      incomeType,
      isBlocked,
      hasHalo: isAnyPlayerOwner,
      depth,
      textStyle,
      linearFilter: Phaser.Textures?.FilterMode?.LINEAR,
    });

    // Ничья точка заметно бледнее, но число на медальоне должно читаться
    const finalAlpha = alpha !== undefined ? alpha : (isAnyPlayerOwner ? 1 : UNOWNED_INCOME_MARKER_ALPHA);
    markers.forEach((marker) => marker.setAlpha(finalAlpha));

    if (isAnimated) {
      markers.forEach((marker) => this.addMarkerFadingTween(hex, marker, isAnimationReversed));
    }

    if (register) {
      this.registerMarker(hex, ...markers);
      // Постоянная плашка дышит общим пульсом (см. конец updateHexes). У мигающей
      // плашки прозрачностью управляет ее собственный твин
      if (halo && !isAnimated) {
        this.incomeMarkerHalos.push(halo);
      }
    }

    return markers;
  }

  /**
   * Ячейки, на которые нажать нельзя (см. checkIfHexClickable), не выдают себя
   * за кликабельные: курсор-рука над ними не появляется. Интерактивность ячеек
   * сетке нужна только ради курсора — сам клик сцена находит по координатам
   * (GameScene.handleClick), и там такие ячейки считаются кликом мимо поля
   */
  updateHexClickability(state) {
    if (this.isHexClickabilityUnused) {
      return;
    }
    let hexUnderPointer;
    Object.values(this.hexes).forEach((hex) => {
      const isClickable = checkIfHexClickable(state, hex.key);
      if (hex.isClickable === isClickable) {
        return;
      }
      hex.isClickable = isClickable;
      if (isClickable) {
        hex.setInteractive();
        return;
      }
      if (hexUnderPointer === undefined) {
        hexUnderPointer = this.getHexUnderPointer();
      }
      // Курсор сбрасывается, только если указатель стоит над самой
      // отключаемой ячейкой: сброс в Phaser общий на весь холст и снял бы
      // руку и с нажимаемой ячейки под указателем
      hex.disableInteractive(hex === hexUnderPointer);
    });
  }

  // Ячейка под указателем мыши (по его последней позиции) или null
  getHexUnderPointer() {
    const pointer = this.scene.input?.activePointer;
    const camera = this.scene.cameras?.main;
    if (!pointer || !camera) {
      return null;
    }
    const worldPoint = camera.getWorldPoint(pointer.x, pointer.y);
    return this.getHexAt(worldPoint.x, worldPoint.y);
  }

  /**
   * Отметка пропуска хода «zzZ» над правым плечом отряда: три буквы от
   * маленькой к большой, каждая правее и выше предыдущей (UNIT_SKIP_MARK_LETTERS).
   * Стоит там же, где искры способности (ABILITY_AVAILABLE_ICON_SHIFT_X/Y), — но
   * вместе их не бывает: спящему отряду искр не рисуют.
   *
   * Покачивается вместе с отрядом — своим твином, как значок способности
   * (addAbilityAvailableIndicator): те же duration/ease/yoyo/repeat и
   * старт в том же вызове renderUnit, поэтому идет синхронно с отрядом.
   *
   * Буквы мерцают в такт качанию (configs/unitSkipMarkAnimation): на каждом
   * кадре того же твина прозрачность считается по тому, насколько значок сейчас
   * поднят, — в верхней точке все буквы видны, в нижней прозрачны. Сразу при
   * создании значок внизу, и буквы прозрачны, как и в нижней точке качания
   * @param {number} duration - тот же duration, что у твина покачивания отряда
   */
  addUnitSkipMark(hex, unitId, unitShiftY, duration) {
    const baseX = hex.centerX + UNIT_SKIP_MARK_SHIFT_X;
    const baseY = hex.centerY + unitShiftY + UNIT_SKIP_MARK_SHIFT_Y;
    const letters = UNIT_SKIP_MARK_LETTERS.map(({char, fontSize, dx, dy}, index) => {
      const text = this.scene.add.text(baseX + dx, baseY + dy, char, {...UNIT_SKIP_MARK_STYLE, fontSize: `${fontSize}px`});
      text.setOrigin(0, 1);
      text.setResolution(2);
      text.setDepth(LAYER_DEPTH.UNIT + 1);
      this.registerUnitMarker(unitId, `skipMark${index}`, text);
      return text;
    });

    // Подъем — по первой букве: твин качает все буквы одинаково. Направление
    // меняется, только когда подъем сдвинулся: onUpdate зовется на каждую
    // букву за кадр, и повторный вызов с тем же подъемом не должен считаться
    // разворотом
    const restY = letters[0].y;
    let previousLift = 0;
    let isRising = true;
    const applyBlink = () => {
      const lift = Math.min(Math.max((restY - letters[0].y) / UNIT_BOB_AMPLITUDE_PX, 0), 1);
      if (lift !== previousLift) {
        isRising = lift > previousLift;
        previousLift = lift;
      }
      getUnitSkipMarkAlphas(lift, isRising, letters.length)
        .forEach((alpha, index) => letters[index].setAlpha(alpha));
    };
    applyBlink();

    const tween = this.scene.tweens.add({
      targets: letters,
      y: `-=${UNIT_BOB_AMPLITUDE_PX}`,
      duration,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
      onUpdate: applyBlink,
    });
    this.registerTweens(hex, tween);
  }

  /**
   * Отряд, пропустивший ход, начал перемещение (или манёвр): «zzZ» и
   * приглушение силуэта снимаются сразу. Сами они ушли бы только с
   * перерисовкой после анимации — перемещение засчитывается по ее концу, а
   * во время нее отряды не перерисовываются, — и бегущий отряд выглядел бы
   * спящим
   * @param {string} unitId
   */
  wakeUpSkippedUnit(unitId) {
    const markPrefix = `${unitId}_skipMark`;
    let hasMark = false;
    Object.keys(this.unitsSprites).forEach((key) => {
      if (!key.startsWith(markPrefix)) {
        return;
      }
      hasMark = true;
      const letters = this.unitsSprites[key];
      this.scene.tweens?.killTweensOf?.(letters);
      letters.forEach((letter) => letter.destroy());
      delete this.unitsSprites[key];
    });
    if (hasMark) {
      this.unitsSprites[unitId]?.setAlpha?.(1);
    }
  }

  /**
   * Идет ли на поле анимация действия отряда — перемещения (в том числе манёвра)
   * или залпа. Автовыбор отрядов (UnitAutoSelector) ждет ее конца: иначе камера
   * уехала бы к следующему отряду посреди полета
   * @returns {boolean}
   */
  isFieldActionAnimating() {
    return Boolean(this.isAnimatingMovement) || checkIfAnyShotInFlight();
  }

  /**
   * Получить ближайший гексагон по координатам клика
   * @param {number} clickX - X координата клика
   * @param {number} clickY - Y координата клика
   * @returns {Phaser.GameObjects.Graphics|null} Ближайший гексагон или null
   */
  getHexAt(clickX, clickY) {
    let closestHex = null;
    let minDist = Infinity;

    Object.values(this.hexes).forEach(hex => {
      const dx = clickX - hex.centerX;
      const dy = clickY - hex.centerY;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < hex.size * 1.2 && dist < minDist) {
        minDist = dist;
        closestHex = hex;
      }
    });

    return closestHex;
  }

  /**
   * Обновить видимость координат на гексах
   * @param {boolean} visible - Видимость координат
   * @param hexes
   */
  updateHexCoordinatesVisibility(visible, hexes) {
    if (visible === this.prevHexCoordinatesVisibility) {
      return;
    }
    this.prevHexCoordinatesVisibility = visible;

    Object.entries(this.hexes).forEach(([key, hex]) => {
      if (hexes[key] === TOKENS.SEA) {
        return;
      }
      // Скрывать нечего, если подпись еще не создавали
      const labels = visible ? this.ensureHexCoordinateLabel(hex) : [hex.coordBg, hex.coordText];
      labels.forEach((label) => label?.setVisible(visible));
    });
  }

  /**
   * Очистить все гексагоны
   */
  clear() {
    this.hexKeysTopToBottom = null;
    // Недоигравшие разовые маркеры роста города живут вне hex.markers —
    // при разборе сетки их надо убрать явно
    [...this.cityGrowthAnimationMarkers.keys()].forEach((key) => this.destroyCityGrowthMarkers(key));
    [...this.supplyNetworkAnimationGraphics.keys()].forEach((key) => this.destroySupplyNetworkAnimation(key));
    this.glowingThreads.clear();
    this.liveFieldMarkers.clear();
    this.fieldCellOutlines.clear();
    this.fieldRangeZone.clear();
    this.fieldFlags.clear();
    this.activeUnitGlows.clear();
    [...this.buildingDustSprites.keys()].forEach((key) => this.destroyBuildingPlacementDust(key));
    [...this.buildingDropSprites.keys()].forEach((key) => this.destroyBuildingDropAnimation(key));
    this.quarterZoneBurn?.clear();
    // Поле разбирается: свой полет не должен фиксировать ход из обрыва анимации
    this.wingedMovement = null;
    // Раньше волн и чисел: оборванная анимация все равно показывает итог — найм
    // отряда ставит на нем волну и число потраченных монет
    this.abilityCastAnimation.clear();
    this.unitFootprints?.clear();
    [...this.recruitmentPulseSprites.keys()].forEach((key) => this.destroyRecruitmentPulse(key));
    [...this.coinsSpentNumbers].forEach((number) => this.destroyCoinsSpentNumber(number));
    [...this.unitMovementFlights.keys()].forEach((key) => this.destroyUnitMovementFlight(key));
    [...this.shotAnimations.keys()].forEach((key) => this.destroyShotAnimation(key));
    [...this.meleeAnimations.keys()].forEach((key) => this.destroyMeleeAnimation(key));
    this.destroyBloodSplatters();
    this.placementAnimationEndAt.clear();
    this.tokenGlints.clear();
    this.ruinsIncomeGlow.clear();
    this.destroyUnitSizeLabelPool();

    Object.values(this.hexes).forEach(hex => {
      hex.emptyHexSprite?.destroy();
      hex.emptyHexSprite = null;
      // Удаляем подложку и текст координат перед уничтожением гекса
      if (hex.coordBg) {
        hex.coordBg.destroy();
        hex.coordBg = null;
      }
      if (hex.coordText) {
        hex.coordText.destroy();
        hex.coordText = null;
      }
      // Ячейки вне списка отрисовки: сцена их сама не уничтожает, и при уничтожении игры
      // сюда приходим, когда система ввода уже разобрана. Отписывать их от нее незачем
      if (!this.scene.input?.manager) {
        hex.input = null;
      }
      hex.destroy();
    });
    this.hexes = {};
  }

  /**
   * Ячейки строительства и зона кварталов — по одному и тому же состоянию.
   * При планировании в чужой ход это состояние плана (ячейки самого игрока
   * для выбранного в план строения, зона — с учетом запланированных
   * кварталов), иначе подсветка и зона расходились бы; в остальное время —
   * состояние как есть (см. getConstructionMarkersState)
   */
  updateConstructionAvailabilityMarkers(state) {
    const constructionState = getConstructionMarkersState(state);
    this.updateAvailableForConstructionMarkers(constructionState);
    this.updateQuarterZoneOverlay(constructionState);
  }

  /**
   * Обновить отрисовку маркеров доступности для строительства
   */
  updateAvailableForConstructionMarkers(state) {
    // Очищаем предыдущие маркеры
    this.clearAccessibilityMarkers();

    const selectedToken = selectSelectedToken(state);
    const sharedGameState = selectSharedGameState(state);

    // Проверяем, нужно ли отображать маркеры
    if (!isConstructionMarkersShown(selectedToken, sharedGameState)) {
      return;
    }

    const markerColor = getHexOutlineMarkerColor();

    const {availableCells, blockedCells} = calculateConstructionCells(state);

    // Ферма/казарма технически доступны для строительства через любой связный
    // путь построек (см. calculateConstructionCells), но реально заработают,
    // только если смежны уже размещенному на поле кварталу (базовому или
    // центральному) — иначе validatePendingBuildings отклонит их при
    // подтверждении фазы. Базовый квартал по той же причине должен быть смежен
    // именно Центральному кварталу (просто любой квартал недостаточен). Ячейки,
    // не удовлетворяющие своему условию, помечаем пунктиром вместо сплошной
    // обводки, чтобы это было видно сразу при выборе ячейки
    const isFarmOrBarracksSelected = selectedToken === TOKENS.FARM || selectedToken === TOKENS.BARRACKS;
    const isBaseQuarterSelected = selectedToken === TOKENS.QUARTER;
    const requiresQuarterAdjacencyCheck = isFarmOrBarracksSelected || isBaseQuarterSelected;
    const hexes = requiresQuarterAdjacencyCheck ? selectHexes(state) : null;

    availableCells.forEach(cellKey => {
      const hex = this.hexes[cellKey];
      if (hex && !blockedCells.has(cellKey)) {
        let isDashed = false;
        if (isFarmOrBarracksSelected) {
          isDashed = !checkIfAdjacentToAnyQuarter(cellKey, hexes, this.hexMath);
        } else if (isBaseQuarterSelected) {
          isDashed = !checkIfAdjacentToCentralQuarter(cellKey, hexes, this.hexMath);
        }
        // На ячейках с мигающей подсказкой квартала (flag/settlement) иконка
        // молотка не рисуется — они бы наложились друг на друга; контур
        // доступности остается
        const hammerIcon = this.quarterHintCellKeys?.has(cellKey) ? undefined : 'hammer_white';
        this.drawHexOutlineMarker(
          hex, markerColor, 'accessibilityMarkers', hammerIcon,
          LAYER_DEPTH.HIGHLIGHT, LAYER_DEPTH.HIGHLIGHT, false, 0.08, isDashed
        );
      }
    });
    blockedCells.forEach(cellKey => {
      const hex = this.hexes[cellKey];
      if (hex) {
        this.drawHexOutlineMarker(hex, WAR_COLOR_0x, 'accessibilityMarkers', 'hammer_red', LAYER_DEPTH.HIGHLIGHT - 0.5);
      }
    });
  }

  // Подсветка ячейки, выбранной для просмотра информации во время чужого хода
  // (см. handleInspectionClick в GameScene): белый контур, перерисовывается на
  // каждом обновлении стора, пока ячейка выбрана. Пути влияния и доход
  // гильдии/храма показываются разовой анимацией в момент выбора — той же
  // showSpecialBuildingIncomeAnimation, что и при клике в фазе строительства
  /**
   * Стрелка-указатель обучения на ячейку текущего шага (arrow_down_big.png).
   * Читает конфиг активной главы напрямую: маркеры перерисовываются на каждом
   * обновлении стора, регистрация через registerMarker чистит их штатно
   */

  /**
   * Ячейки для обучающих стрелок с учетом ПРОМЕЖУТОЧНЫХ действий двухкликовых
   * задач: снабжение (ферма -> отряд), перемещение/маневр (отряд -> ячейка),
   * докупка размера (отряд -> стрелки на ВСЕ кварталы, с которых докупка
   * доступна). Без промежуточного состояния — task.arrowHexKey из конфига.
   * Возвращает массив hexKey (обычно один, у докупки размера — несколько)
   */
  /**
   * Ферма для обучающей стрелки шага снабжения: ферма из конфига, пока у нее
   * остались единицы снабжения, иначе — первая своя ферма с остатком
   */
  resolveTutorialSupplyFarmHexKey(state, configFarmHexKey) {
    const currentPlayer = selectCurrentPlayer(state);
    if (!currentPlayer) {
      return configFarmHexKey || null;
    }
    const hexes = selectHexes(state);
    const specialBuildings = selectSpecialBuildings(state);
    const players = selectPlayers(state);
    const units = selectUnits(state);
    const citiesCache = selectCitiesCache(state);
    const pendingSupply = selectPendingSupply(state) || [];
    const buildingsDamage = selectBuildingsDamage(state);
    const selectedFarmsForConversion = selectSelectedFarmsForConversion(state) || [];
    const getRemainingSupply = (farmHexKey) => calcFarmRemainingSupply(
      farmHexKey, selectedFarmsForConversion, hexes, specialBuildings, players, units,
      citiesCache, currentPlayer, pendingSupply, buildingsDamage,
    );

    // task.arrowHexKey шага может указывать не на ферму, а на ЦЕЛЬ снабжения
    // (отряд) — тогда конфигная ячейка для стрелки «выбери ферму» не годится
    // и ферма ищется по городам игрока
    const isConfigHexFarm = Boolean(configFarmHexKey)
      && hexes[configFarmHexKey] === COMMON_BUILDING_TOKENS.FARM;
    if (isConfigHexFarm && getRemainingSupply(configFarmHexKey) > 0) {
      return configFarmHexKey;
    }
    const playerFarmHexKeys = (citiesCache[currentPlayer.color] || [])
      .flatMap((city) => city[COMMON_BUILDING_TOKENS.FARM] || []);
    return playerFarmHexKeys.find((farmHexKey) => getRemainingSupply(farmHexKey) > 0)
      || (isConfigHexFarm ? configFarmHexKey : null);
  }

  resolveTutorialArrowHexKeys(state, activeStep) {
    const allowDeclarations = activeStep.allow || [];
    const configArrowHexKeys = activeStep.task?.arrowHexKey ? [activeStep.task.arrowHexKey] : [];

    // Двухэтапные шаги («заверши найм и перемести...»): стрелка на ячейку —
    // только на своем этапе войны, до него указывает DOM-стрелка на кнопку
    // завершения (task.arrowUiSelector, см. TutorialUiArrow)
    const arrowHexKeyStage = activeStep.task?.arrowHexKeyStage;
    if (arrowHexKeyStage && selectWarPhaseStage(state) !== arrowHexKeyStage) {
      return [];
    }

    // «Изучи каждый отряд»: стрелки на отряды перечисленных ячеек, которые
    // игрок еще не выбирал на этом шаге (выборы копятся в inspectedUnitIds)
    const inspectUnitsHexKeys = activeStep.task?.inspectUnitsHexKeys;
    if (inspectUnitsHexKeys?.length) {
      const inspectedUnitIds = state.private?.tutorial?.inspectedUnitIds || [];
      const units = selectUnits(state) || [];
      return inspectUnitsHexKeys.filter((hexKey) => {
        const unit = units.find((u) => u.hexKey === hexKey);
        return unit && !inspectedUnitIds.includes(unit.id);
      });
    }

    // Снабжение: ферма не выбрана -> стрелка на ферму (конфиг); выбрана ->
    // на первый еще не снабженный отряд из предиката шага
    const supplyDeclaration = allowDeclarations.find((declaration) => declaration.action === 'supply');
    if (supplyDeclaration) {
      const selectedFarmHexKey = selectSelectedFarmHexKey(state);
      if (!selectedFarmHexKey) {
        // Ферма из конфига уже раздала все снабжение — стрелка на другую
        // свою ферму, у которой снабжение еще осталось
        const farmHexKey = this.resolveTutorialSupplyFarmHexKey(state, activeStep.task?.arrowHexKey);
        return farmHexKey ? [farmHexKey] : [];
      }
      const pendingSupply = selectPendingSupply(state) || [];
      const units = selectUnits(state) || [];
      const targetChecks = [];
      const collectSupplyTargets = (declaration) => {
        if (!declaration) return;
        if (declaration.check === 'unitSuppliedAt') targetChecks.push(declaration);
        if (declaration.check === 'allOf') (declaration.checks || []).forEach(collectSupplyTargets);
      };
      collectSupplyTargets(activeStep.complete);
      const nextTarget = targetChecks.find((check) => {
        const unit = units.find((u) => u.hexKey === check.hexKey);
        if (!unit) return false;
        const suppliedCount = pendingSupply.filter((supplyAction) => supplyAction.unitId === unit.id).length;
        return suppliedCount < (check.count || 1);
      });
      return nextTarget ? [nextTarget.hexKey] : configArrowHexKeys;
    }

    // Перемещение/маневр: отряд не выбран -> стрелка на сам отряд; выбран ->
    // на целевую ячейку
    const moveDeclaration = allowDeclarations.find((declaration) =>
      (declaration.action === 'moveUnit' || declaration.action === 'maneuverUnit')
      && declaration.fromHexKey && declaration.toHexKey);
    if (moveDeclaration) {
      const selectedUnitId = selectSelectedUnitId(state);
      const units = selectUnits(state) || [];
      const selectedUnit = selectedUnitId ? units.find((u) => u.id === selectedUnitId) : null;
      const isRequiredUnitSelected = Boolean(selectedUnit && selectedUnit.hexKey === moveDeclaration.fromHexKey);
      return [isRequiredUnitSelected ? moveDeclaration.toHexKey : moveDeclaration.fromHexKey];
    }

    // Докупка размера: отряд не выбран -> стрелка на отряд (конфиг); выбран ->
    // стрелки на все кварталы, с которых докупка доступна (та же выборка, что
    // у штатных маркеров цены). Выбран «не тот» отряд (не на рабочей казарме)
    // -> контекста нет, стрелка возвращается на отряд из конфига
    const sizeRecruitmentDeclaration = allowDeclarations.find(
      (declaration) => declaration.action === 'sizeRecruitment');
    if (sizeRecruitmentDeclaration && selectSelectedUnitId(state)) {
      const recruitmentContext = this.resolveUnitSizeRecruitmentContext(state);
      return recruitmentContext ? recruitmentContext.availableCells : configArrowHexKeys;
    }

    // Ближний бой (переброс): пока сражающийся отряд шага не выбран —
    // стрелка на него; выбран — ячеечных стрелок нет, подсказки продолжаются
    // DOM-стрелками на костях в SelectedUnitMelee (см. TutorialUiArrow)
    const meleeUnitHexKey = activeStep.task?.meleeUnitHexKey;
    if (meleeUnitHexKey) {
      const meleeUnits = selectUnits(state) || [];
      const meleeUnit = meleeUnits.find((unit) => unit.hexKey === meleeUnitHexKey);
      if (!meleeUnit || selectSelectedUnitId(state) === meleeUnit.id) {
        return [];
      }
      return [meleeUnitHexKey];
    }

    // Стрельба: катапульта не выбрана -> стрелка на нее; выбрана -> на цель
    // выстрела; выстрел уже сделан (pending) -> ячеечной стрелки нет
    // (DOM-стрелка возвращается на кнопку завершения этапа, см. TutorialUiArrow)
    const shootDeclaration = allowDeclarations.find((declaration) =>
      declaration.action === 'shoot' && declaration.targetHexKey && declaration.fromHexKey);
    if (shootDeclaration) {
      const units = selectUnits(state) || [];
      const shooterUnit = units.find((unit) => unit.hexKey === shootDeclaration.fromHexKey);
      if (!shooterUnit) {
        return configArrowHexKeys;
      }
      const pendingShootingActions = selectPendingShootingActions(state) || [];
      if (pendingShootingActions.some((action) => action.shooterUnitId === shooterUnit.id)) {
        return [];
      }
      return [selectSelectedUnitId(state) === shooterUnit.id
        ? shootDeclaration.targetHexKey
        : shootDeclaration.fromHexKey];
    }

    return configArrowHexKeys;
  }

  updateTutorialMarkers(state) {
    const tutorialState = state.private?.tutorial;
    if (!tutorialState?.chapterId || tutorialState.isChapterCompleted) {
      return;
    }
    const chapter = getTutorialChapter(tutorialState.chapterId);
    const activeStep = chapter?.steps[tutorialState.stepIndex];
    if (!activeStep || !this.scene?.add) {
      return;
    }
    // Модалка шага еще НЕ показана (например, ждет modalDelayMs после
    // предыдущей стройки) — подсказки нового задания (стрелки на ячейки и
    // жетон-призрак) не рисуем: игрок еще не прочитал, что от него хотят
    if (activeStep.modalId && !tutorialState.isStepModalShown) {
      return;
    }

    const arrowHexKeys = this.resolveTutorialArrowHexKeys(state, activeStep);
    arrowHexKeys.forEach((arrowHexKey) => {
      const arrowHex = this.hexes[arrowHexKey];
      if (!arrowHex) {
        return;
      }
      const arrowSprite = this.scene.add.sprite(arrowHex.centerX, arrowHex.centerY - 140, 'tutorial_arrow');
      arrowSprite.setScale(0.25);
      arrowSprite.setDepth(LAYER_DEPTH.TUTORIAL_ARROW);
      this.registerMarker(arrowHex, arrowSprite);

      // Покачивание вверх-вниз; твин регистрируется, чтобы штатная чистка
      // остановила его вместе со спрайтом
      const tween = this.scene.tweens.add({
        targets: arrowSprite,
        y: arrowHex.centerY - 100,
        duration: 450,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      this.registerTweens(arrowHex, tween);
    });

    // Жетон-призрак строения, которое шаг просит построить: мигает на целевой
    // ячейке (как молоток действия на отложенном строении, но альфа до 0.8).
    // Исчезает вместе с шагом — предикаты построек выполняются сразу при
    // размещении
    const buildDeclaration = (activeStep.allow || []).find((declaration) =>
      ((declaration.action === 'build' || declaration.action === 'buildSpecial')
        && declaration.token && declaration.hexKey)
      || (declaration.action === 'placeStartingQuarter' && declaration.hexKey));
    if (!buildDeclaration) {
      return;
    }
    // Стартовый квартал рисуется как quarter_base + флаг с тинтом цвета
    // игрока (в обучении игрок всегда синий) — отдельной текстуры у него нет
    const isCentralQuarterGhost = buildDeclaration.action === 'placeStartingQuarter';
    const ghostTexture = isCentralQuarterGhost ? 'quarter_base' : buildDeclaration.token;
    const ghostHex = this.hexes[buildDeclaration.hexKey];
    if (!ghostHex || !this.scene.textures.exists(ghostTexture)) {
      return;
    }

    const ghostSprites = [this.scene.add.sprite(ghostHex.centerX, ghostHex.centerY, ghostTexture)];
    if (isCentralQuarterGhost) {
      this.fieldFlags.getRestLayers().forEach(({textureKey, isTinted}) => {
        const flagSprite = this.scene.add.sprite(ghostHex.centerX, ghostHex.centerY, textureKey);
        if (isTinted) {
          flagSprite.setTint(getPlayer0xColor('blue'));
        }
        ghostSprites.push(flagSprite);
      });
    }

    ghostSprites.forEach((sprite, index) => {
      sprite.texture.setFilter(Phaser.Textures.FilterMode.LINEAR);
      sprite.setScale(TOKEN_SCALE);
      sprite.setAlpha(0);
      sprite.setDepth(LAYER_DEPTH.MARKER + 0.1 + index * 0.01);
      this.registerMarker(ghostHex, sprite);
    });

    const ghostTween = this.scene.tweens.add({
      targets: ghostSprites,
      alpha: 1,
      duration: MARKER_FADING_DURATION_MS,
      hold: MARKER_FADING_HOLD_MS,
      ease: Phaser.Math.Easing.Sine.InOut,
      yoyo: true,
      repeat: -1,
    });
    this.registerTweens(ghostHex, ghostTween);
  }

  updateInspectedHexMarker(state) {
    const inspectedHexKey = selectInspectedHexKey(state);
    // Обводка держится и после конца партии: итоги разбирают по полю, и осмотр
    // ячеек там работает как обычно (см. ветку gameResults в GameScene.handleClick)
    if (!inspectedHexKey) {
      return;
    }

    const hex = this.hexes[inspectedHexKey];
    if (!hex) {
      return;
    }

    this.drawSelectedHexOutlineMarker(hex, 'inspectedMarkers');
  }

  /**
   * Обводка ВЫБРАННОЙ игроком ячейки — то же, что маркер доступности, но втрое
   * толще (см. SELECTED_HEX_OUTLINE_MARKER_LINE_WIDTH). Отдельный метод, чтобы
   * заполнители необязательных аргументов drawHexOutlineMarker жили в одном месте
   * @param {Phaser.GameObjects.Graphics} hex - Объект гексагона
   * @param {string} markerKey - Ключ для хранения маркеров
   */
  drawSelectedHexOutlineMarker(hex, markerKey) {
    const {outlineMarker} = this.drawHexOutlineMarker(
      hex, getHexOutlineMarkerColor(), markerKey,
      undefined, undefined, undefined, undefined, undefined, undefined,
      SELECTED_HEX_OUTLINE_MARKER_LINE_WIDTH
    ) || {};

    // Жирная обводка выделенной ячейки едет вместе с жетоном — на пустой
    // ячейке подниматься нечему (см. getTokenShiftY). Остальные обводки
    // (доступность хода, снабжение) остаются по контуру самой ячейки
    outlineMarker?.setY?.(hex.centerY + this.getTokenShiftY(hex, COMBAT_TOKEN_SHIFT_Y));
  }

  /**
   * Нарисовать маркер доступности (контур гекса) на гексе
   * @param {Phaser.GameObjects.Graphics} hex - Объект гексагона
   * @param {number} color - Цвет маркера в формате 0xRRGGBB
   * @param {string} markerKey - Ключ для хранения маркеров (по умолчанию 'accessibilityMarkers')
   * @param {string | undefined} icon - Ключ изображения для отрисовки по центру ячейки
   * @param outlineDepth
   * @param {number | undefined} iconDepth - Глубина иконки
   * @param isAnimated
   * @param iconScale
   * @param isDashed - пунктирная обводка вместо сплошной
   * @param lineWidth - толщина обводки
   */
  drawHexOutlineMarker(
    hex,
    color,
    markerKey,
    icon,
    outlineDepth = LAYER_DEPTH.HIGHLIGHT,
    iconDepth = LAYER_DEPTH.HIGHLIGHT,
    isAnimated = false,
    iconScale = 0.08,
    isDashed = false,
    lineWidth = HEX_OUTLINE_MARKER_LINE_WIDTH
  ) {
    let iconMarker;
    if (icon) {
      iconMarker = this.scene.add.sprite(
        hex.centerX,
        hex.centerY,
        icon
      );
      iconMarker.setScale(iconScale);
      iconMarker.setDepth(iconDepth); // Выше маркеров доступности
    }

    let outlineMarker;
    if (color) {
      // Выше гексагона, но ниже жетона
      outlineMarker = this.createCellOutline(hex, color, outlineDepth, {isDashed, lineWidth});
    }

    // Анимируем флаги игрока только во время фазы setup, если стартовый квартал еще не установлен
    if (isAnimated) {
      if (outlineMarker) {
        this.addMarkerFadingTween(hex, outlineMarker);
      }
      if (iconMarker) {
        this.addMarkerFadingTween(hex, iconMarker);
      }
    }

    this.registerMarker(hex, outlineMarker, iconMarker)

    return {outlineMarker, iconMarker};
  }

  /**
   * Светящаяся обводка ячейки (см. scenes/FieldCellOutlines). Цвет выбирает
   * палитру: красная — там, где ячейка недоступна из-за противника, толстая
   * линия — выбранная ячейка
   * @returns {Phaser.GameObjects.Container}
   */
  createCellOutline(hex, color, depth, {isDashed = false, lineWidth = HEX_OUTLINE_MARKER_LINE_WIDTH} = {}) {
    const isSelected = lineWidth >= SELECTED_HEX_OUTLINE_MARKER_LINE_WIDTH;
    let palette = CELL_OUTLINE_PALETTES.DEFAULT;
    if (color === WAR_COLOR_0x) {
      palette = CELL_OUTLINE_PALETTES.BLOCKED;
    } else if (isSelected) {
      palette = CELL_OUTLINE_PALETTES.SELECTED;
    }
    return this.fieldCellOutlines.createOutline({
      hex, palette, depth, isDashed, isSelected, waveOrigin: this.outlineWaveOrigin,
    });
  }

  /**
   * Очистить все маркеры доступности
   */
  clearAccessibilityMarkers() {
    Object.values(this.hexes).forEach(hex => {
      if (hex.accessibilityMarkers) {
        hex.accessibilityMarkers.forEach(marker => marker.destroy());
        hex.accessibilityMarkers = [];
      }
    });
  }

  /**
   * Обновить отрисовку оверлея зоны влияния кварталов
   */
  updateQuarterZoneOverlay(state) {
    const quarterZoneVisible = selectQuarterZoneVisible(state);
    if (!quarterZoneVisible) {
      return;
    }

    const quarterZoneCells = calculateQuarterZoneCells(state);
    this.quarterZoneBurn?.syncZone(quarterZoneCells);
    // Ячейки, которые сейчас прожигаются, штрихует само прожигание
    this.drawQuarterZoneMarkers(
      [...quarterZoneCells].filter((cellKey) => !this.quarterZoneBurn?.checkIfCellOwned(cellKey)),
      state,
    );
  }

  /** Вернуть слою зоны ячейки после прожигания, если они еще в зоне и слой показан */
  drawQuarterZoneCells(cellKeys, state) {
    const zoneState = state && getConstructionMarkersState(state);
    if (!zoneState || !selectQuarterZoneVisible(zoneState)) {
      return;
    }
    const quarterZoneCells = calculateQuarterZoneCells(zoneState);
    this.drawQuarterZoneMarkers(cellKeys.filter((cellKey) => quarterZoneCells.has(cellKey)), zoneState);
  }

  drawQuarterZoneMarkers(cellKeys, state) {
    const baseQuarterAllowedCells = calculateBaseQuarterAllowedCells(state);
    const hexes = selectHexes(state);

    cellKeys.forEach((cellKey) => {
      const hex = this.hexes[cellKey];
      if (hex) {
        const spriteKey = baseQuarterAllowedCells.has(cellKey)
          ? 'base_quarter_allowed'
          : 'quarter_zone';
        this.drawQuarterZoneMarker(
          hex, spriteKey, hexes[cellKey] === COMMON_BUILDING_TOKENS.ROAD
        );
      }
    });
  }

  /**
   * @param hex
   * @param {string} [spriteKey]
   * @param {boolean} [isOnRoad] - под оверлеем лежит жетон дороги (иначе
   *   ячейка пустая): от этого зависят и смещение, и слой отрисовки
   */
  drawQuarterZoneMarker(hex, spriteKey = 'quarter_zone', isOnRoad = false) {
    this.registerMarker(hex, this.createQuarterZoneSprite(hex, spriteKey, isOnRoad));
  }

  /** Спрайт штриховки зоны: его же берет прожигание (см. scenes/QuarterZoneBurn) */
  createQuarterZoneSprite(hex, spriteKey = 'quarter_zone', isOnRoad = false) {
    const sprite = this.scene.add.sprite(
      hex.centerX,
      hex.centerY + (isOnRoad ? QUARTER_ZONE_ON_ROAD_SHIFT_Y : 0),
      spriteKey
    );
    sprite.setScale(TOKEN_SCALE);
    // На дороге оверлей поверх ее жетона (как и был), на пустой ячейке — под
    // строениями, чтобы не перекрывать краешком соседей снизу
    sprite.setDepth(isOnRoad ? LAYER_DEPTH.HIGHLIGHT - 0.1 : QUARTER_ZONE_EMPTY_CELL_DEPTH);
    // На темных подложках иконка плотнее — иначе сливается с фоном
    sprite.setAlpha(getQuarterZoneMarkerAlpha(this.scene.groundBackgroundId));
    // Где темно-коричневые полосы теряются на ячейке, они заливаются светлее
    const fillColor = getQuarterZoneMarkerFillColor(this.scene.groundBackgroundId);
    if (fillColor !== null) {
      sprite.setTintFill(fillColor);
    }

    return sprite;
  }

  /**
   * Обновить маркеры доступности казарм для найма отрядов
   */
  updateNewUnitRecruitmentMarkers(state) {
    const selectedToken = selectSelectedToken(state);
    const sharedGameState = selectSharedGameState(state);
    const markerColor = getHexOutlineMarkerColor();

    // Обратный порядок действий на этапе найма: казарма уже выбрана, а тип
    // отряда еще нет (см. GameScene). Подсвечиваем саму казарму — иначе
    // игрок не видит, на какую из них уйдет найм по клику в футере.
    // Отдельно от блока ниже: тот включается только при выбранном типе отряда
    const selectedBarracksHexKey = selectSelectedBarracksHexKey(state);
    const selectedBarracksHex = selectedBarracksHexKey ? this.hexes[selectedBarracksHexKey] : null;
    if (selectedBarracksHex) {
      this.drawCellOutlineMarker(selectedBarracksHex, markerColor, false, SELECTED_HEX_OUTLINE_MARKER_LINE_WIDTH);
    }

    const showMarkers = isNewUnitRecruitmentMarkersShown(selectedToken, sharedGameState);
    if (!showMarkers) {
      return;
    }

    const currentPlayer = selectCurrentPlayer(state);
    if (!currentPlayer) return;

    const recruitmentCells = calculateAvailableRecruitmentCells(state);

    // Отрисовываем маркеры на доступных казармах
    try {
      recruitmentCells.forEach(cellKey => {
        const hex = this.hexes[cellKey];
        if (hex) {
          this.drawCellOutlineMarker(hex, markerColor);
        }
      });
    } catch (e) {
      console.error('Error drawing recruitment markers:', e);
    }
  }

  drawCombatTargetMarker(hex) {
    const markerColor = getHexOutlineMarkerColor();

    this.drawCellOutlineMarker(hex, markerColor, true);

    const targetSprite = this.scene.add.sprite(
      hex.centerX,
      hex.centerY + this.getUnitShiftY(hex) + UNIT_COMBAT_INDICATOR_SHIFT_Y,
      'priority_target_outline'
    );
    targetSprite.setScale(0.1);
    targetSprite.setDepth(LAYER_DEPTH.MARKER);
    this.registerMarker(hex, targetSprite);

    this.addMarkerFadingTween(hex, targetSprite);
  }

  /**
   * Нарисовать маркер доступности найма (контур гекса) на гексе
   * @param {Phaser.GameObjects.Graphics} hex - Объект гексагона
   * @param {number} color - Цвет маркера в формате 0xRRGGBB
   * @param isAnimated
   * @param lineWidth - толщина обводки
   */
  drawCellOutlineMarker(hex, color, isAnimated = false, lineWidth = HEX_OUTLINE_MARKER_LINE_WIDTH) {
    // Выше гексагона, но ниже жетона
    const marker = this.createCellOutline(hex, color, 2, {lineWidth});

    this.registerMarker(hex, marker);

    if (isAnimated) {
      this.addMarkerFadingTween(hex, marker);
    }
  }

  /**
   * Обновить маркеры перемещения отрядов в фазе найма
   * Рисует контур на ячейках, куда может переместиться выбранный отряд
   */
  updateUnitMovementMarkers(state) {
    // Очищаем предыдущие маркеры
    this.clearMovementMarkers();

    if (!isUnitMovementMarkersShown(state)) {
      return;
    }

    const hexes = selectHexes(state);
    const units = selectUnits(state);
    const selectedUnit = selectSelectedUnit(state);
    const players = selectPlayers(state);

    const {cells: availableMovementCells, additionalSupplyCells} = calculateAvailableMovementCells(
      this, hexes, units, selectedUnit, players, selectGoblinsTrapDeathHexKeys(state)
    );

    const markerColor = getHexOutlineMarkerColor();
    // Летающему отряду (селестиалы) — иконка полета; на ячейках за доп.
    // снабжение иконка общая для всех (ниже)
    const isFlyingUnit = checkIfUnitTypeIsFlyingForFaction(selectedUnit.type, selectedUnit.factionId);
    const movementIcon = isFlyingUnit ? 'speed_fly_white_shadow' : 'speed_white_shadow';
    const movementIconScale = isFlyingUnit ? FLYING_MOVEMENT_ICON_SCALE : MOVEMENT_ICON_SCALE;

    availableMovementCells.forEach(cellKey => {
      const hex = this.hexes[cellKey];
      if (hex) {
        const icon = additionalSupplyCells.some(i => i === cellKey) ?
          undefined : movementIcon;
        this.drawHexOutlineMarker(
          hex, markerColor, 'movementMarkers', icon, undefined, LAYER_DEPTH.UNIT - 0.5, false, movementIconScale
        );
      }
    });

    // Рисуем иконку supply для ячеек, достижимых только за счет дополнительного снабжения
    additionalSupplyCells.forEach(cellKey => {
      const hex = this.hexes[cellKey];
      if (hex) {
        this.drawMovementAdditionalSupplyMarker(hex);
      }
    });
  }

  /**
   * Очистить все маркеры перемещения
   */
  clearMovementMarkers() {
    // Используем отдельный массив для маркеров перемещения
    Object.values(this.hexes).forEach(hex => {
      if (hex.movementMarkers) {
        hex.movementMarkers.forEach(marker => marker.destroy());
        hex.movementMarkers = [];
      }
    });
    this.clearMovementSupplyMarkers();
  }

  /**
   * Очистить все маркеры снабжения перемещения
   */
  clearMovementSupplyMarkers() {
    Object.values(this.hexes).forEach(hex => {
      if (hex.movementSupplyMarkers) {
        hex.movementSupplyMarkers.forEach(marker => marker.destroy());
        hex.movementSupplyMarkers = [];
      }
    });
  }

  /**
   * Обновить маркеры маневра в фазе войны
   * Подсвечивает смежные ячейки доступные для маневра
   * @param {Object} state - Состояние store
   */
  updateUnitManeuverMarkers(state) {
    this.clearManeuverMarkers();

    if (!isUnitManeuverMarkersShown(state)) {
      return;
    }

    const selectedUnit = selectSelectedUnit(state);
    if (!selectedUnit) {
      return;
    }

    const hexes = selectHexes(state);
    const units = selectUnits(state);

    // Выбор отряда мог «протечь» с прошлых этапов (смена этапа войны не
    // сбрасывает selectedUnitId): чужой отряд, выбранный для просмотра
    // результатов стрельбы/боя, или свой, не имеющий права на маневр, — для
    // них клетки маневра подсвечивать нельзя. Подсветка строго повторяет
    // условия исполнения маневра в GameScene.handleManeuverPhaseClick:
    // свой отряд, еще не маневрировал, участвовал в бою/стрельбе и с
    // доп. снабжением (checkIfUnitCouldManeuverFromHex)
    const actingPlayer = getActingPlayerFromState(state);
    if (!actingPlayer || selectedUnit.ownerId !== actingPlayer.id) {
      return;
    }
    // Маневрируют только отряды игрока, чей этап маневра идет: свой
    // отряд-защитник (дрался в чужом бою, право на маневр формально есть)
    // в ЧУЖОЙ этап маневра подсвечивать нельзя — исполнить его сейчас нельзя
    if (actingPlayer.id !== selectCurrentPlayer(state)?.id) {
      return;
    }
    const pendingManeuverActions = selectPendingManeuverActions(state);
    if (pendingManeuverActions.some(action => action.unitId === selectedUnit.id)) {
      return;
    }
    if (!checkIfUnitCouldManeuverFromHex(actingPlayer, selectedUnit, hexes)) {
      return;
    }

    const availableManeuverCells = calculateAvailableManeuverCells(
      this, hexes, units, selectedUnit, selectGoblinsTrapDeathHexKeys(state)
    );
    const markerColor = getHexOutlineMarkerColor();

    availableManeuverCells.forEach(cellKey => {
      const hex = this.hexes[cellKey];
      if (hex) {
        this.drawHexOutlineMarker(hex, markerColor, 'maneuverMarkers', 'maneuver_shadow', undefined, LAYER_DEPTH.UNIT - 0.5, false, 0.1);
      }
    });
  }

  /**
   * Очистить все маркеры маневра
   */
  clearManeuverMarkers() {
    Object.values(this.hexes).forEach(hex => {
      if (hex.maneuverMarkers) {
        hex.maneuverMarkers.forEach(marker => marker.destroy());
        hex.maneuverMarkers = [];
      }
    });
  }

  /**
   * Обновить маркеры стреляющих отрядов в фазе войны
   * Подсвечивает вражеские отряды в радиусе стрельбы отряда
   * @param {Object} state - Состояние store
   */
  updateShootingMarkers(state) {
    // Очищаем предыдущие маркеры
    this.clearShootingMarkers();

    const phase = selectPhase(state);
    const warPhaseStage = selectWarPhaseStage(state);

    // Проверяем, находимся ли мы в этапе стрельбы фазы войны
    if (phase !== PHASES.WAR || warPhaseStage !== WAR_PHASE_STAGES.SHOOTING) {
      return;
    }

    const hexes = selectHexes(state);
    const units = selectUnits(state);
    const pendingShootingActions = selectPendingShootingActions(state);
    const citiesCache = selectCitiesCache(state);
    const currentPlayer = selectCurrentPlayer(state);
    const specialBuildings = selectSpecialBuildings(state);
    const buildingsDamage = selectBuildingsDamage(state);
    const fortressesUnderConstruction = selectFortressesUnderConstruction(state);

    // Линия и значок урона — только у выстрелов, чей снаряд уже долетел
    this.addTrajectoryMarkers(this.filterLandedShootingActions(pendingShootingActions), units);

    const selectedUnit = selectSelectedUnit(state);
    if (!selectedUnit) {
      return;
    }

    let availableShootingCells;

    const isUnitMeleeElf = checkIfUnitMeleeElf(selectedUnit);

    // Вычисляем доступные для стрельбы ячейки
    if (isUnitMeleeElf) {
      availableShootingCells = calculateAvailableShootingCellsForElves(hexes, units, selectedUnit, pendingShootingActions, specialBuildings, buildingsDamage, fortressesUnderConstruction);
    } else {
      availableShootingCells = calculateAvailableShootingCells(
        hexes,
        units,
        selectedUnit,
        pendingShootingActions,
        citiesCache,
        currentPlayer,
        specialBuildings,
        buildingsDamage,
        fortressesUnderConstruction,
      );
    }

    this.rangeZoneTargetHexKeys = availableShootingCells;
    const markerColor = getHexOutlineMarkerColor();

    availableShootingCells.forEach(hexKey => {
      const hex = this.hexes[hexKey];
      if (hex) {
        this.drawHexOutlineMarker(hex, markerColor, 'shootingMarkers', undefined, undefined, LAYER_DEPTH.UNIT - 0.5);
      }
    });
  }

  /**
   * Обновить маркеры доступных целей соблазнения Суккубов в этапе способностей
   * Подсвечивает отряды, не принадлежащие текущему игроку, в радиусе
   * SUCCUBES_SEDUCTION_RANGE гексов прямой видимости (см. calculateAvailableSeductionCells)
   * @param {Object} state - Состояние store
   */
  updateSeductionMarkers(state) {
    this.clearSeductionMarkers();

    const phase = selectPhase(state);
    const warPhaseStage = selectWarPhaseStage(state);

    if (phase !== PHASES.WAR || warPhaseStage !== WAR_PHASE_STAGES.ABILITIES) {
      return;
    }

    const selectedUnit = selectSelectedUnit(state);
    if (!selectedUnit) {
      return;
    }

    const units = selectUnits(state);
    const hexes = selectHexes(state);
    const specialBuildings = selectSpecialBuildings(state);
    const buildingsDamage = selectBuildingsDamage(state);
    const fortressesUnderConstruction = selectFortressesUnderConstruction(state);
    const availableSeductionCells = calculateAvailableSeductionCells(
      hexes, units, selectedUnit, specialBuildings, buildingsDamage, fortressesUnderConstruction
    );

    this.rangeZoneTargetHexKeys = availableSeductionCells;
    const markerColor = getHexOutlineMarkerColor();

    availableSeductionCells.forEach(hexKey => {
      const hex = this.hexes[hexKey];
      if (hex) {
        this.drawHexOutlineMarker(hex, markerColor, 'seductionMarkers', undefined, undefined, LAYER_DEPTH.UNIT - 0.5);
      }
    });
  }

  /**
   * Обновить маркеры доступных целей текущего режима способности Гоблинов
   * (установка/снятие ловушки, повреждение строения) на этапе способностей.
   * Показывается только после выбора конкретного режима через
   * GoblinsAbilitiesToolbar (GOBLINS_ABILITY_MODES) - до выбора режима
   * неясно, какую из трёх calculateAvailable*Cells применять. По аналогии с
   * updateSeductionMarkers
   * @param {Object} state - Состояние store
   */
  updateGoblinsAbilityMarkers(state) {
    const phase = selectPhase(state);
    const warPhaseStage = selectWarPhaseStage(state);
    if (phase !== PHASES.WAR || warPhaseStage !== WAR_PHASE_STAGES.ABILITIES) {
      return;
    }

    const selectedUnit = selectSelectedUnit(state);
    if (!selectedUnit || !isUnitFaction(selectedUnit, FACTIONS.GOBLINS)) {
      return;
    }

    const abilityMode = selectGoblinsAbilityMode(state);
    if (!abilityMode) {
      return;
    }

    const units = selectUnits(state);
    const hexes = selectHexes(state);
    const goblinsTraps = selectGoblinsTraps(state);
    const specialBuildings = selectSpecialBuildings(state);
    const buildingsDamage = selectBuildingsDamage(state);
    const fortressesUnderConstruction = selectFortressesUnderConstruction(state);

    let targetHexKeys = [];
    if (abilityMode === GOBLINS_ABILITY_MODES.SET_TRAP) {
      targetHexKeys = calculateAvailableTrapSetupCells(
        hexes, units, goblinsTraps, selectedUnit, specialBuildings, buildingsDamage, fortressesUnderConstruction
      );
    } else if (abilityMode === GOBLINS_ABILITY_MODES.REMOVE_TRAP) {
      targetHexKeys = calculateAvailableTrapRemovalCells(
        units, goblinsTraps, selectedUnit, hexes, specialBuildings, buildingsDamage, fortressesUnderConstruction
      );
    } else if (abilityMode === GOBLINS_ABILITY_MODES.DAMAGE_BUILDING) {
      const citiesCache = selectCitiesCache(state);
      const currentPlayer = selectCurrentPlayer(state);
      targetHexKeys = calculateAvailableBuildingDamageCells(
        hexes, citiesCache, specialBuildings, buildingsDamage, units, selectedUnit, currentPlayer,
        fortressesUnderConstruction
      );
    }

    this.rangeZoneTargetHexKeys = targetHexKeys;
    const markerColor = getHexOutlineMarkerColor();

    targetHexKeys.forEach(hexKey => {
      const hex = this.hexes[hexKey];
      if (hex) {
        this.drawHexOutlineMarker(hex, markerColor, 'goblinsAbilityMarkers', undefined, undefined, LAYER_DEPTH.UNIT - 0.5);
      }
    });
  }

  /**
   * Зона действия отряда на поле (см. scenes/FieldRangeZone): светящийся контур,
   * свет внутри, тень снаружи и штриховка ячеек за препятствиями. Это чистая
   * геометрия дальности и траектории: есть ли на ячейке цель, здесь не важно.
   * Вызывать после маркеров целей: они оставляют здесь ячейки, которые вздрогнут при волне
   * @param {Object} state - Состояние store
   */
  updateRangeZone(state) {
    const targetHexKeys = this.rangeZoneTargetHexKeys;
    this.rangeZoneTargetHexKeys = [];

    const source = this.resolveRangeZoneSource(state);
    const unitHex = source && this.hexes[source.unit.hexKey];
    if (!unitHex) {
      this.fieldRangeZone.hide();
      return;
    }

    const {unit, range, kind, pendingShootingActions, isQuiet} = source;
    const hexes = selectHexes(state);
    const reachableCells = calculateShootingReachableCells(
      unit.hexKey,
      range,
      hexes,
      selectSpecialBuildings(state),
      pendingShootingActions,
      selectBuildingsDamage(state),
      selectFortressesUnderConstruction(state),
    );

    const cells = [];
    const blockedHexes = [];
    const outsideHexes = [];
    Object.entries(this.hexes).forEach(([hexKey, hex]) => {
      // Море в зону не входит и в тени не лежит: своей заливки у таких ячеек нет, сквозь них
      // виден фон сцены — затемнялись бы только они, а фон за краем поля нет
      if (hexes[hexKey] === TERRAIN_TOKENS.SEA && hexKey !== unit.hexKey) {
        return;
      }
      const distance = getHexDistance(unit.hexKey, hexKey);
      if (reachableCells.has(hexKey)) {
        cells.push({hex, distance});
        return;
      }
      outsideHexes.push(hex);
      if (distance <= range) {
        blockedHexes.push(hex);
      }
    });

    this.fieldRangeZone.show({
      key: `${kind}:${unit.id}:${unit.hexKey}`,
      isQuiet,
      range,
      cells,
      blockedHexes,
      outsideHexes,
      targets: [...targetHexKeys]
        .filter((hexKey) => this.hexes[hexKey])
        .map((hexKey) => ({hex: this.hexes[hexKey], distance: getHexDistance(unit.hexKey, hexKey)})),
      depth: {ground: LAYER_DEPTH.RANGE_ZONE_GROUND, contour: LAYER_DEPTH.RANGE_ZONE_CONTOUR},
    });
  }

  /**
   * Чью зону показать и какой дальности. Выбранная способность приоритетнее стрельбы: у катапульты
   * гоблинов с выбранным режимом важен его радиус, а не дальность выстрела
   * @param {Object} state - Состояние store
   * @returns {?{unit: Object, range: number, kind: string, pendingShootingActions: Array, isQuiet: boolean}}
   */
  resolveRangeZoneSource(state) {
    // После окончания партии поле показывается «как есть» (см. renderUnit) —
    // подсказок там уже нет
    if (selectGameResults(state)) {
      return null;
    }

    const selectedUnit = selectSelectedUnit(state);
    const isAbilitiesStage = selectPhase(state) === PHASES.WAR
      && selectWarPhaseStage(state) === WAR_PHASE_STAGES.ABILITIES;
    if (selectedUnit && isAbilitiesStage) {
      const goblinsAbilityMode = isUnitFaction(selectedUnit, FACTIONS.GOBLINS) && selectGoblinsAbilityMode(state);
      if (goblinsAbilityMode) {
        return {
          unit: selectedUnit,
          range: GOBLINS_ABILITY_RADIUS,
          kind: `goblins_${goblinsAbilityMode}`,
          pendingShootingActions: [],
          isQuiet: false,
        };
      }
      // Катапульты (D4) не соблазняют (см. calculateAvailableSeductionCells): у катапульты суккубов
      // зона — по радиусу стрельбы
      if (isUnitFaction(selectedUnit, FACTIONS.SUCCUBES) && selectedUnit.type !== UNIT_TYPES.D4.id) {
        return {
          unit: selectedUnit,
          range: SUCCUBES_SEDUCTION_RANGE,
          kind: 'seduction',
          pendingShootingActions: [],
          isQuiet: false,
        };
      }
    }

    const shooterUnit = this.resolveShootingZoneUnit(state);
    if (!shooterUnit) {
      return null;
    }
    return {
      unit: shooterUnit,
      // Катапульта бьет на 5 в любой фракции, включая эльфийскую; остальные
      // отряды эльфов — на 4
      range: shooterUnit.type === UNIT_TYPES.D4.id ? UNIT_D4_MAX_SHOOTING_RANGE : ELVES_SHOOTING_RANGE,
      kind: 'shooting',
      pendingShootingActions: selectPendingShootingActions(state),
      // Зона показывается и для осмотренного отряда, в том числе вражеского: это просмотр, а не прицеливание
      isQuiet: !selectedUnit,
    };
  }

  /**
   * Отряд, зону обстрела которого нужно показать: выбранный отряд, а если
   * выбора нет — отряд на инспектируемой ячейке. Выбор приоритетнее инспекции:
   * inspectedHexKey не сбрасывается при выборе отряда и может остаться от
   * предыдущего клика.
   * @param {Object} state - Состояние store
   * @returns {Object|null} катапульта или отряд эльфов, иначе null
   */
  resolveShootingZoneUnit(state) {
    let unit = selectSelectedUnit(state);

    if (!unit) {
      const inspectedHexKey = selectInspectedHexKey(state);
      if (!inspectedHexKey) {
        return null;
      }
      unit = selectUnits(state).find(u => u.hexKey === inspectedHexKey);
    }

    if (!unit) {
      return null;
    }

    const isShooter = unit.type === UNIT_TYPES.D4.id || isUnitFaction(unit, FACTIONS.ELVES);

    return isShooter ? unit : null;
  }

  /**
   * Линии уже сделанных выстрелов. Раньше полоса и значок урона над целью
   * циклично мигали и читались как тревога; сам выстрел теперь показывает
   * анимация залпа (showShotAnimation), а до конца этапа от него остается
   * спокойный пунктир от стрелка к цели.
   *
   * ЗНАЧКА УРОНА НАД ЦЕЛЬЮ НЕТ — ни над отрядом, ни над строением: он
   * закрывал бы цель, а итог виден и без него — «−N» у численности отряда,
   * поврежденное строение
   */
  addTrajectoryMarkers(pendingShootingActions, units) {
    if (pendingShootingActions && pendingShootingActions.length > 0) {
      // Один графический объект на все линии
      const shootingLinesGraphics = this.scene.add.graphics();
      shootingLinesGraphics.setDepth(LAYER_DEPTH.SHOOTING_TRAJECTORY);
      shootingLinesGraphics.lineStyle(SHOT_TRAJECTORY.lineWidth, WAR_COLOR_0x, SHOT_TRAJECTORY.alpha);
      // Ячейка, на которой числится общий объект линий: уничтожается вместе с
      // ее маркерами на следующей перерисовке
      let linesOwnerHex = null;

      pendingShootingActions.forEach(action => {
        const shooterUnit = units.find(u => u.id === action.shooterUnitId);
        const targetUnit = action.targetUnitId ? units.find(u => u.id === action.targetUnitId) : null;
        const targetHexKey = targetUnit ? targetUnit.hexKey : action.targetHexKey;

        if (shooterUnit && targetHexKey) {
          const shooterHex = this.hexes[shooterUnit.hexKey];
          const targetHex = this.hexes[targetHexKey];

          if (shooterHex && targetHex) {
            // Концы линии — на высоте отрядов: стрелок, стоящий на жетоне,
            // поднят вместе с ним (см. getUnitShiftY), а цель — отряд или само
            // здание — поднята тоже. Без этого линия упиралась бы под них
            this.strokeDashedLine(
              shootingLinesGraphics,
              shooterHex.centerX, shooterHex.centerY + this.getUnitShiftY(shooterHex),
              targetHex.centerX, targetHex.centerY + this.getUnitShiftY(targetHex)
            );
            linesOwnerHex = linesOwnerHex || shooterHex;
          }
        }
      });

      if (linesOwnerHex) {
        this.registerMarker(linesOwnerHex, shootingLinesGraphics);
      } else {
        // Ни одной линии не вышло (нет стрелка или ячеек) — объект не нужен
        shootingLinesGraphics.destroy?.();
      }
    }
  }

  /** Пунктирный отрезок: штрихи заданной длины с промежутками (стиль линии уже задан) */
  strokeDashedLine(graphics, fromX, fromY, toX, toY) {
    const length = Math.hypot(toX - fromX, toY - fromY);
    if (length <= 0) {
      return;
    }

    const stepX = (toX - fromX) / length;
    const stepY = (toY - fromY) / length;
    const period = SHOT_TRAJECTORY.dashLength + SHOT_TRAJECTORY.gapLength;
    for (let start = 0; start < length; start += period) {
      const end = Math.min(length, start + SHOT_TRAJECTORY.dashLength);
      graphics.beginPath();
      graphics.moveTo(fromX + stepX * start, fromY + stepY * start);
      graphics.lineTo(fromX + stepX * end, fromY + stepY * end);
      graphics.strokePath();
    }
  }

  /**
   * Очистить все маркеры стрельбы
   */
  clearShootingMarkers() {
    // Очищаем маркеры на гексах
    Object.values(this.hexes).forEach(hex => {
      if (hex.shootingMarkers) {
        hex.shootingMarkers.forEach(marker => marker.destroy());
        hex.shootingMarkers = [];
      }
    });
  }

  /**
   * Очистить все маркеры доступных целей соблазнения
   */
  clearSeductionMarkers() {
    Object.values(this.hexes).forEach(hex => {
      if (hex.seductionMarkers) {
        hex.seductionMarkers.forEach(marker => marker.destroy());
        hex.seductionMarkers = [];
      }
    });
  }

  /**
   * Нарисовать маркер снабжения на ячейке перемещения
   * @param {Phaser.GameObjects.Graphics} hex - Объект гексагона
   */
  drawMovementAdditionalSupplyMarker(hex) {
    // Добавляем иконку снабжения
    const iconX = hex.centerX;
    const iconY = hex.centerY;

    const scale = 0.08;

    const additionalIconSprite = this.scene.add.sprite(iconX, iconY, 'supply_outline_shadow');
    additionalIconSprite.setScale(scale);
    additionalIconSprite.setDepth(LAYER_DEPTH.HIGHLIGHT);

    this.registerMarker(hex, additionalIconSprite);
  }

  /**
   * Обновить маркеры улучшения отрядов
   * Рисует контур на выбранном отряде и прямоугольники +1 X на кварталах города
   * @param {Object} state - Состояние игры из Redux
   */
  updateSelectedUnitMarkers(state) {
    const selectedUnit = selectSelectedUnit(state);
    this.outlineWaveOrigin = undefined;
    if (!selectedUnit) return;

    const unitHexKey = selectedUnit.hexKey;
    const unitHex = this.hexes[unitHexKey];
    if (!unitHex) return;

    // Волна света по доступным ячейкам расходится от выбранного отряда
    this.outlineWaveOrigin = {x: unitHex.centerX, y: unitHex.centerY};

    // Рисуем контур на гексе с отрядом: отряд выбрал сам игрок, поэтому
    // обводка толстая — рядом ячейки его хода помечены тонкой
    const markerColor = getHexOutlineMarkerColor();
    this.drawCellOutlineMarker(unitHex, markerColor, false, SELECTED_HEX_OUTLINE_MARKER_LINE_WIDTH);

    const showMeleeTargets = selectShowMeleeTargets(state);
    if (showMeleeTargets) {
      const units = selectUnits(state);
      const neighborEnemyUnits = getNeighborEnemyUnits(selectedUnit.ownerId, selectedUnit.hexKey, units);

      neighborEnemyUnits.forEach(otherUnit => {
        const otherUnitHex = this.hexes[otherUnit.hexKey];
        this.drawCombatTargetMarker(otherUnitHex);
      });
    }
  }

  /**
   * Обновить маркеры улучшения отрядов
   * Рисует контур на выбранном отряде и прямоугольники +1 X на кварталах города
   * @param {Object} state - Состояние игры из Redux
   */
  /**
   * Контекст найма размера для выбранного отряда: город, на казарме которого
   * он стоит, и кварталы, с которых докупка еще доступна (без кварталов с уже
   * отложенной докупкой и заблокированных противником). null — маркеры не
   * показываются (не тот этап, отряд не выбран или не на рабочей казарме).
   * Общая база штатных маркеров цены и обучающих стрелок на кварталы
   */
  resolveUnitSizeRecruitmentContext(state) {
    const phase = selectPhase(state);
    const warPhaseStage = selectWarPhaseStage(state);
    const selectedUnitId = selectSelectedUnitId(state);
    const hexes = selectHexes(state);
    const currentPlayer = selectCurrentPlayer(state);
    const citiesCache = selectCitiesCache(state);
    const units = selectUnits(state);
    const buildingsDamage = selectBuildingsDamage(state);
    const specialBuildings = selectSpecialBuildings(state)

    const showMarkers = isUnitSizeRecruitmentMarkersShown(
      phase,
      warPhaseStage,
      selectedUnitId,
      hexes,
      specialBuildings,
      currentPlayer,
      citiesCache,
      units,
      buildingsDamage,
    );
    if (!showMarkers) {
      return null;
    }

    const pendingRecruitmentActions = selectPendingRecruitmentActions(state);

    // Находим выбранный отряд
    const selectedUnit = selectSelectedUnit(state);
    if (!selectedUnit) return null;

    const unitHexKey = selectedUnit.hexKey;

    const playerCities = citiesCache[currentPlayer.color] || [];
    let cityWhereUnitStands = null;

    // Находим город, на казарме которого находится выбранный отряд
    for (const city of playerCities) {
      const cityBarracks = city[COMMON_BUILDING_TOKENS.BARRACKS];
      if (cityBarracks && cityBarracks.includes(unitHexKey)) {
        cityWhereUnitStands = city;
        break;
      }
    }

    if (!cityWhereUnitStands) return null;

    const allQuarters = getAllQuartersOfCity(cityWhereUnitStands);

    // Фильтруем кварталы, с которых еще не производился найм размера для отрядов,
    // и которые не заблокированы вражескими отрядоми.
    const availableCells = allQuarters.filter(quarterHexKey => {
      const isUnitSizeAlreadyRecruited = pendingRecruitmentActions.some(
        q => q.quarterHexKey === quarterHexKey
      );
      if (isUnitSizeAlreadyRecruited) {
        return false;
      }

      // Проверяем заблокированные кварталы - передаем базовые кварталы,
      // так как для центрального квартала правила блокировки идентичны.
      const isBlocked = checkIfHexBlockedByEnemyUnit(quarterHexKey, TOKENS.QUARTER, currentPlayer.id, units, hexes, specialBuildings, buildingsDamage);
      return !isBlocked;
    });

    return {selectedUnit, cityWhereUnitStands, availableCells};
  }

  updateUnitSizeRecruitmentMarkers(state) {
    // Очищаем предыдущие маркеры
    this.clearUnitSizeRecruitmentMarkers();

    const recruitmentContext = this.resolveUnitSizeRecruitmentContext(state);
    if (!recruitmentContext) {
      this.unitSizeRecruitmentMarkerPulse.setHalos([]);
      return;
    }
    const {selectedUnit, cityWhereUnitStands, availableCells} = recruitmentContext;

    const hexes = selectHexes(state);
    const currentPlayer = selectCurrentPlayer(state);
    const units = selectUnits(state);
    const buildingsDamage = selectBuildingsDamage(state);
    const specialBuildings = selectSpecialBuildings(state);
    const pendingRecruitmentActions = selectPendingRecruitmentActions(state);

    const sizeToAdd = calcCoinsRecruitmentSize(cityWhereUnitStands, currentPlayer.id, units, selectedUnit, pendingRecruitmentActions, hexes, specialBuildings, buildingsDamage)

    // Получаем цену улучшения
    const unitConfig = getUnitTypeConfig(selectedUnit.type);
    const {sizePrice} = getSizeRecruitmentPrice(unitConfig, currentPlayer);

    const halos = availableCells
      .map(hexKey => this.drawUnitSizeRecruitmentMarker(this.hexes[hexKey], sizePrice, hexKey, sizeToAdd))
      .filter(Boolean);
    this.unitSizeRecruitmentMarkerPulse.setHalos(halos);
  }

  /**
   * Нарисовать маркер улучшения на квартале
   * @param {Object} hex - Объект гекса
   * @param {number} price - Цена улучшения
   * @param {string} quarterHexKey - Ключ квартала
   * @param {number} sizeToAdd - Количество размера для найма (+1 или +2 с арсеналом)
   * @returns {?Object} ореол маркера (у потухшего маркера его нет)
   */
  drawUnitSizeRecruitmentMarker(hex, price, quarterHexKey, sizeToAdd = 1) {
    // Размер уже максимальный (+0) — найм не сработает: маркер остается
    // кликабельным (тост с причиной), но показывается потухшим.
    // Медальон лежит на верхней грани жетона — там же стоят отряды
    const {elements: markerElements, halo} = createUnitSizeRecruitmentMarker(this.scene, {
      x: hex.centerX,
      y: hex.centerY + this.getUnitShiftY(hex),
      hexSize: hex.size,
      price,
      sizeToAdd,
      linearFilter: Phaser.Textures?.FilterMode?.LINEAR,
    });

    // Сохраняем маркер для последующего удаления
    if (!hex.unitSizeRecruitmentMarkers) {
      hex.unitSizeRecruitmentMarkers = [];
    }
    // Все элементы теперь в markerElements (включая фон)
    hex.unitSizeRecruitmentMarkers.push({
      graphics: null,
      texts: markerElements,
      quarterHexKey
    });
    return halo;
  }

  /**
   * Очистить все маркеры улучшения
   */
  clearUnitSizeRecruitmentMarkers() {
    Object.values(this.hexes).forEach(hex => {
      if (hex.unitSizeRecruitmentMarkers) {
        hex.unitSizeRecruitmentMarkers.forEach(marker => {
          if (marker.graphics) marker.graphics.destroy();
          // Удаляем старый формат (одиночный text)
          if (marker.text) marker.text.destroy();
          // Удаляем новый формат (массив texts - содержит и тексты, и иконки)
          if (marker.texts && Array.isArray(marker.texts)) {
            marker.texts.forEach(el => {
              if (el) el.destroy();
            });
          }
        });
        hex.unitSizeRecruitmentMarkers = [];
      }
    });
  }

  // ==================== Многослойная система отрисовки: Отряды ====================

  /**
   * На сколько поднять отряд и все, что к нему привязано, над центром ячейки:
   * на жетоне — на UNIT_ON_BUILDING_SHIFT_Y, на пустой ячейке — никак.
   *
   * Токены ячеек берутся из this.unitHexState. Первым его в каждой перерисовке
   * записывает updateHexes — он идет раньше всех маркеров, и метки на отрядах
   * (цель, линии стрельбы) видят уже свежие ячейки. Анимацию перемещения
   * запускают и боты, и обучение, без состояния на руках: им хватает
   * последнего записанного, ячейки за время одного перемещения не меняются
   */
  getUnitShiftY(hex) {
    return this.getTokenShiftY(hex, UNIT_ON_BUILDING_SHIFT_Y);
  }

  /**
   * Подъем накладки над ячейкой на заданную высоту: жетоны строений и ключевых
   * точек нарисованы в псевдо-3D и приподняты, поэтому все, что ложится
   * поверх них, поднимается вместе с ними. На ПУСТОЙ ячейке поднимать нечего,
   * и накладка остается по центру (см. checkIfUnitStandsOnBuilding).
   *
   * Тем же правилом живут боевые иконки, волна по ячейке и жирная обводка
   * выделенной ячейки — высота у них своя, а условие общее
   *
   * @param {Object} hex
   * @param {number} shiftY — подъем на ячейке с жетоном
   * @returns {number} shiftY или 0
   */
  getTokenShiftY(hex, shiftY) {
    this.ensureFieldCurrent();
    const token = hex ? this.unitHexState?.[hex.key] : undefined;
    return checkIfUnitStandsOnBuilding(token) ? shiftY : 0;
  }

  /**
   * Обновить отрисовку отрядов
   * @param state
   */
  updateUnits(state) {
    // Для getUnitShiftY: поднимать ли отряд, решает жетон под ним
    this.unitHexState = selectHexes(state);
    // Очищаем все спрайты отрядов
    this.clearAllUnits();

    const unitsVisible = selectUnitsVisible(state);

    if (!unitsVisible) return;

    const units = selectUnits(state);

    units.forEach(unit => {
      // Новобранец крысолюдов за снабжение (размер 0) до итога фазы снабжения
      // виден только владельцу
      if (checkIfSupplyRecruitHiddenFromViewer(unit, state)) return;
      this.renderUnit(unit, state);
    });
  }

  /**
   * Анимировать перемещение отряда по пути
   * @param {string} unitId - ID отряда
   * @param {Array} path - Массив объектов {q, r} - путь перемещения
   * @param {Function} onComplete - колбэк по завершении анимации
   */
  animateUnitMovement(unitId, path, onComplete) {
    this.ensureFieldCurrent();
    const sprite = this.unitsSprites[unitId];
    if (!sprite || !path || path.length === 0) {
      if (onComplete) onComplete();
      return;
    }

    // Если путь содержит только стартовую позицию, ничего не делаем
    if (path.length === 1) {
      if (onComplete) onComplete();
      return;
    }

    // Сохраняем данные текущей анимации для возможности пропуска
    this.currentMovementUnitId = unitId;
    this.currentMovementTargetHexKey = coordsToKey(path[path.length - 1].q, path[path.length - 1].r);
    this.currentMovementOnComplete = onComplete;

    // Устанавливаем флаг анимации
    this.isAnimatingMovement = true;

    // Летающий отряд идет по прямой от центра ячейки к центру цели, а маршрут по
    // ячейкам важен только правилам
    const movingUnit = this.scene.store
      ? (selectUnits(this.scene.store.getState()) || []).find((unit) => unit.id === unitId)
      : null;
    const isFlying = Boolean(movingUnit) && checkIfUnitTypeIsFlyingForFaction(movingUnit.type, movingUnit.factionId);

    // Создаём последовательность анимаций для каждого сегмента пути
    // Пропускаем первый элемент - это стартовая позиция
    const movementPath = isFlying ? path.slice(-1) : path.slice(1);

    // Создаём массив координат для tween
    const tweenTargets = [];
    movementPath.forEach((step) => {
      const hexKey = coordsToKey(step.q, step.r);
      const hex = this.hexes[hexKey];
      if (hex) {
        tweenTargets.push({
          x: hex.centerX,
          y: hex.centerY + this.getUnitShiftY(hex),
          hexKey: hexKey
        });
      }
    });

    if (tweenTargets.length === 0) {
      // Флаг уже поднят — сбрасываем, иначе перерисовка отрядов (GameScene
      // пропускает updateUnits во время анимации) замрет до клика по полю
      this.isAnimatingMovement = false;
      this.currentMovementUnitId = null;
      this.currentMovementTargetHexKey = null;
      this.currentMovementOnComplete = null;
      if (onComplete) onComplete();
      return;
    }

    // Отряд, пропустивший ход, проснулся — отметка снимается сразу, а не по
    // приходу (до него поле отряды не перерисовывает)
    this.wakeUpSkippedUnit(unitId);

    // Находим все связанные спрайты (индикаторы размера, снабжения и т.д.)
    const relatedSprites = [];

    // Собираем все спрайты из unitsSprites, начинающиеся с ID отряда
    Object.keys(this.unitsSprites).forEach(key => {
      if (key.startsWith(unitId) || key === unitId) {
        const spriteObj = this.unitsSprites[key];
        if (spriteObj) {
          if (Array.isArray(spriteObj)) {
            // Массив спрайтов (индикаторы)
            spriteObj.forEach(s => {
              if (s && s.active) relatedSprites.push(s);
            });
          } else {
            // Одиночный спрайт
            relatedSprites.push(spriteObj);
          }
        }
      }
    });

    // Также собираем спрайты размера отряда, которые хранятся на гексе
    // Первый гекс - это стартовая позиция
    const startHexKey = coordsToKey(path[0].q, path[0].r);
    const startHex = this.hexes[startHexKey];
    if (startHex && startHex.unitSizeLabel) {
      startHex.unitSizeLabel.forEach(labelSprite => {
        if (labelSprite && labelSprite.active) {
          relatedSprites.push(labelSprite);
        }
      });
    }

    const finishMovement = () => {
      // Анимация завершена
      this.unitFootprints?.finish(unitId);
      this.markLocalUnitMovement(unitId, this.currentMovementTargetHexKey);
      this.isAnimatingMovement = false;
      this.currentMovementUnitId = null;
      this.currentMovementTargetHexKey = null;
      this.currentMovementOnComplete = null;
      this.movementTweens = [];
      if (onComplete) onComplete();
    };

    if (isFlying && this.startWingedUnitMovement({
      unitId,
      sprite,
      relatedSprites,
      fromHexKey: startHexKey,
      toHexKey: this.currentMovementTargetHexKey,
      onLanded: finishMovement,
    })) {
      return;
    }

    if (movingUnit && !isFlying) {
      this.startUnitFootprints(movingUnit, [{x: sprite.x, y: sprite.y}, ...tweenTargets], sprite, {
        hexSize: startHex?.size,
        players: selectPlayers(this.scene.store.getState()),
      });
    }

    // Выполняем последовательность tween-ов
    let currentIndex = 0;

    const moveToNextHex = () => {
      if (currentIndex >= tweenTargets.length) {
        finishMovement();
        return;
      }

      const target = tweenTargets[currentIndex];

      const easeType = resolveMovementSegmentEase(currentIndex, tweenTargets.length);

      // Анимируем все связанные спрайты
      let completedCount = 0;
      const totalSprites = relatedSprites.length;

      relatedSprites.forEach(spriteToMove => {
        if (spriteToMove && spriteToMove.active) {
          // Вычисляем смещение относительно основного спрайта
          const offsetX = spriteToMove.x - sprite.x;
          const offsetY = spriteToMove.y - sprite.y;

          const tween = this.scene.tweens.add({
            targets: spriteToMove,
            x: target.x + offsetX,
            y: target.y + offsetY,
            duration: UNIT_MOVEMENT_DURATION_MS / tweenTargets.length,
            ease: easeType,
            onComplete: () => {
              completedCount++;
              if (completedCount === totalSprites) {
                currentIndex++;
                moveToNextHex();
              }
            }
          });
          this.movementTweens.push(tween);
        } else {
          completedCount++;
          if (completedCount === totalSprites) {
            currentIndex++;
            moveToNextHex();
          }
        }
      });

      // Если нет спрайтов для анимации
      if (totalSprites === 0) {
        currentIndex++;
        moveToNextHex();
      }
    };

    moveToNextHex();
  }

  /**
   * Ключ текстуры отряда с учётом фракционного скина/дефолтной иконки
   * великанов/нейтральной иконки - единая точка правды для renderUnit И
   * renderGoblinsTrapGhostUnit (найдено пользователем в ручном тестировании:
   * "призрак" погибшего от ловушки отряда терял купленный фракционный скин и
   * откатывался на общий силуэт `${unit.type}_white`, т.к. дублировал только
   * эту одну, самую простую ветку резолвинга вместо вызова сюда). Не зависит
   * от текущего игрока/хода/фазы - чистая функция от самого юнита и списка
   * игроков (для findUnitSkinOwnerPlayer)
   * @param {Object} unit
   * @param {Array} players
   * @returns {{unitImageKey: string, usesNeutralIcon: boolean, useFactionUnitSkin: boolean, isGiantsUnitIcon: boolean}}
   */
  resolveUnitImageKey(unit, players) {
    // Игрок, чья покупка скина решает вид отряда - ИСХОДНЫЙ владелец, а не
    // текущий: иначе захваченный отряд терял бы уже открытый скин исходного
    // владельца, если у самого суккуба скин не куплен (или наоборот -
    // получал бы чужой не открытый скин). Ищется по ID, а не по совпадению
    // фракции: за одну фракцию в матче могут играть несколько игроков, и
    // поиск первого игрока нужной фракции отдавал чужой ответ
    // (см. findUnitSkinOwnerPlayer)
    const originalOwnerId = unit.capturedFromOwnerId ?? unit.ownerId;
    const usesNeutralIcon = originalOwnerId === NEUTRAL_PLAYER_ID;

    // Кому образ положен — см. checkIfUnitHasFactionSkin: по тому же правилу
    // выбирается оружие в анимации удара ближнего боя. Витрина главного меню
    // показывает образы у всех отрядов игроков
    const useFactionUnitSkin = checkIfUnitHasFactionSkin(unit, players, {isEverySkinShown: this.scene.isDemoMode});
    // Ключ текстуры строится по factionId ЮНИТА, а не его текущего владельца -
    // соблазненный Суккубом Великан продолжает выглядеть Великаном, а не
    // получает скин фракции нового владельца (см. unit-scoped-faction-abilities).
    // Ключ общий на фракцию, и это верно даже когда за нее играют несколько
    // игроков: арт скина отрядов у всех цветовых скинов фракции один и тот же
    // (см. getFactionUnitSkinIconPath - путь не зависит от selectedSkin),
    // игроки различаются только тоном. Кому этот скин ПОЛОЖЕН, решает
    // отдельно useFactionUnitSkin выше
    const factionUnitSkinKey = unit.factionId ? `${unit.type}_${unit.factionId}` : null;
    // Нейтральные отряды (в т.ч. соблазнённые/захваченные, но исходно
    // нейтральные - usesNeutralIcon) всегда используют собственный набор
    // иконок (по одной на тип отряда, /img/factions/unit_icons/unit_dX_neutral.png)
    // вместо общего силуэта unit_dX_white — во всех режимах и видах партий.
    // Масштаб — как у обычных иконок (useFactionUnitSkin остается false для
    // нейтралов): пропорции неутральных иконок близки к unit_dX_white, а не
    // к более крупным фракционным скинам
    // Великаны без купленного/применённого фракционного скина получают
    // собственную дефолтную иконку вместо общего силуэта unit_dX_white
    const isGiantsPlayerUnit = !usesNeutralIcon && unit.factionId === FACTIONS.GIANTS;
    const giantsDefaultImageKey = 'unit_d20_giants_default';

    const unitImageKey = usesNeutralIcon
      ? `${unit.type}_${NEUTRAL_PLAYER_COLOR}`
      : (useFactionUnitSkin && factionUnitSkinKey && this.scene.textures.exists(factionUnitSkinKey)
        ? factionUnitSkinKey
        : (isGiantsPlayerUnit && this.scene.textures.exists(giantsDefaultImageKey)
          ? giantsDefaultImageKey
          : `${unit.type}_white`));

    // Иконка великана (и фракционный скин, и дефолтная) крупнее обычной на
    // GIANTS_UNIT_ICON_SCALE_MULTIPLIER. Проверяем именно ключ текстуры, а не
    // фракцию игрока: кэш масштабов ниже общий по ключу, и на общих силуэтах
    // unit_dX_white прибавка утекла бы на отряды других фракций
    const isGiantsUnitIcon = unitImageKey === giantsDefaultImageKey
      || (isGiantsPlayerUnit && unitImageKey === factionUnitSkinKey);

    return {unitImageKey, usesNeutralIcon, useFactionUnitSkin, isGiantsUnitIcon};
  }

  /**
   * Отрисовать отряд на гексе
   * @param {Object} unit - Объект отряда
   * @param state
   */
  renderUnit(unit, state) {
    const hex = this.hexes[unit.hexKey];

    // Пока на поле показано срабатывание ловушки Гоблинов (см.
    // updateGoblinsTrapTriggerDisplay, design.md Decision 22) и этот отряд
    // выжил, но уже помечен в state.units уменьшенным размером - численность
    // ПОКАЗЫВАЕМ старую (до урона): урон уже отражён отдельным индикатором
    // "-N" (addUnitSizeUpdateIndicator), и вычитать его из уже уменьшенного
    // числа было бы для игрока вдвойне. Реальное уменьшение появится, когда
    // индикаторы уйдут - на завершении этапа перемещения/маневра
    const trapStageTriggers = selectGoblinsTrapStageTriggers(state);
    const survivedTrapTrigger = trapStageTriggers.find(
      trigger => !trigger.died && trigger.unit && trigger.unit.id === unit.id
    );
    const displaySize = survivedTrapTrigger ? survivedTrapTrigger.unit.size : unit.size;

    const gameResults = selectGameResults(state);
    const phase = selectPhase(state);
    const warStage = selectWarPhaseStage(state);
    const players = selectPlayers(state);
    const currentPlayer = selectCurrentPlayer(state);
    const specialBuildings = selectSpecialBuildings(state);
    const buildingsDamage = selectBuildingsDamage(state);
    const pendingSupply = selectPendingSupply(state)
    const pendingMovementActions = selectPendingMovementActions(state)
    const pendingRecruitmentActions = selectPendingRecruitmentActions(state)
    const pendingShootingActions = selectPendingShootingActions(state)
    const pendingManeuverActions = selectPendingManeuverActions(state)
    const pendingMeleeDamage = selectPendingMeleeDamage(state)
    const showMeleeTargets = selectShowMeleeTargets(state);
    const fortressesUnderConstruction = selectFortressesUnderConstruction(state);
    // Для checkIfGoblinsUnitCouldUseAbility (индикатор доступности способности
    // на этапе ABILITIES, см. ниже)
    const units = selectUnits(state);
    const citiesCache = selectCitiesCache(state);
    const goblinsTraps = selectGoblinsTraps(state);
    const hexes = selectHexes(state);
    // Отряд на жетоне поднимается вместе с псевдо-3D жетоном под ним (на
    // пустой ячейке — нет). Считается один раз: от этой высоты строятся сам
    // отряд, тень, контур и покачивание; помощники считают ее сами по hex
    this.unitHexState = hexes;
    const unitShiftY = this.getUnitShiftY(hex);

    // Получаем цвет игрока-владельца
    const player = players.find(p => p.id === unit.ownerId);
    let playerColor = player?.color || getNeutralPlayerMock().color;

    const isNeutralUnit = unit.ownerId === NEUTRAL_PLAYER_ID;

    // Иконка/скин юнита выбираются по его ИСХОДНОЙ принадлежности, а не по
    // текущему owner'у: соблазнённый Суккубом отряд должен сохранять внешний
    // вид (скин фракции или нейтральную иконку), который был у него до
    // захвата, а не переключаться на скин игрока-суккуба (см.
    // unit-scoped-faction-abilities). Контур ниже по-прежнему завязан на
    // isNeutralUnit (текущее владение) — захваченный отряд теперь чей-то, ему
    // нужен обычный контур хода
    const originalOwnerId = unit.capturedFromOwnerId ?? unit.ownerId;
    const usesNeutralIcon = originalOwnerId === NEUTRAL_PLAYER_ID;

    // Белый контур — у отряда, чья сейчас фаза (или у всех отрядов игроков
    // после конца партии, см. ниже). Считаем это ДО тени: захваченному
    // Суккубом нейтральному отряду (usesNeutralIcon, но уже не isNeutralUnit)
    // на чужом ходу нужна светлая тень так же, как истинно нейтральным —
    // на чёрном контуре (чужой ход) тёмная тень сливалась бы с тёмной
    // (нейтральной) половиной его силуэта. На своём ходу контур белый и уже
    // выделяет отряд — тень можно оставить обычную, как у остальных отрядов
    // игрока
    // Чья сейчас фаза — см. getActiveUnitOwnerIds (в одновременной фазе
    // снабжения это не текущий игрок-якорь)
    const hasWhiteOutline = this.checkIfUnitHasWhiteOutline(unit, state, gameResults);

    // Текстура тени (общая для всех типов отрядов) и ее масштаб — тем же
    // резолвером, что и у двойника идущего отряда (см. resolveUnitShadow)
    const {
      textureKey: unitShadowImageKey,
      scaleMultiplier: unitShadowScaleMultiplier,
    } = this.resolveUnitShadow(unit, hasWhiteOutline);

    // Ключ текстуры (фракционный скин/дефолтная иконка великанов/нейтральная
    // иконка) — общая точка правды с renderGoblinsTrapGhostUnit, см. её
    // резолвер
    const {unitImageKey, useFactionUnitSkin, isGiantsUnitIcon} = this.resolveUnitImageKey(unit, players);

    // Создаём спрайт
    const sprite = this.scene.add.sprite(hex.centerX, hex.centerY + unitShiftY, unitImageKey);

    // Включаем линейную фильтрацию для более гладкого отображения при зуме
    sprite.texture.setFilter(Phaser.Textures.FilterMode.LINEAR);

    let scale = this.unitSpriteScales[unitImageKey];
    if (!scale) {
      const bounds = this.hexMath.getHexBounds(hex.points);
      const spriteWidth = sprite.width;
      const spriteHeight = sprite.height;
      const baseScaleMultiplier = useFactionUnitSkin ? FACTION_UNIT_SCALE_MULTIPLIER : UNIT_SCALE_MULTIPLIER;
      // Прибавки за великанов и за нейтралов взаимоисключающие: у нейтралов
      // свои иконки (unit_dX_neutral), фракционных среди них не бывает.
      // Кэш масштабов общий по ключу текстуры, а ключи у них разные — прибавка
      // не утечет на отряды игроков
      let scaleMultiplier = baseScaleMultiplier;
      if (isGiantsUnitIcon) {
        scaleMultiplier *= GIANTS_UNIT_ICON_SCALE_MULTIPLIER;
      }
      if (usesNeutralIcon) {
        scaleMultiplier *= NEUTRAL_UNIT_ICON_SCALE_MULTIPLIER;
      }
      scale = this.hexMath.calculateSpriteScale(bounds, {width: spriteWidth, height: spriteHeight})
        * scaleMultiplier;
      this.unitSpriteScales[unitImageKey] = scale;
    }
    sprite.setScale(scale);
    sprite.setDepth(LAYER_DEPTH.UNIT); // Отряды рендерятся выше токенов
    sprite.setTint(getPlayer0xColor(playerColor));
    // Пропустивший ход отряд (change unit-auto-selection): силуэт чуть тусклее,
    // «zzZ» над правым плечом — правее значка способности над головой. Значок
    // рисуется ниже, когда известен темп покачивания отряда
    const isTurnSkipShown = checkIfUnitTurnSkipShown(state, unit);
    if (isTurnSkipShown) {
      sprite.setAlpha(UNIT_SKIP_SPRITE_ALPHA);
    }

    // Соблазнённый Суккубом юнит: правая половина силуэта остаётся цвета
    // исходного владельца, чтобы игрок-суккуб видел, откуда юнит (см.
    // unit-scoped-faction-abilities/design.md Decision 14). Основной sprite
    // остается на месте (от него зависят твин покачивания и
    // unitsSprites[unit.id], используемый анимацией перемещения) — ТОЛЬКО для
    // захваченных юнитов рядом с ним добавляется второй спрайт той же
    // текстуры, обрезанный по правой половине кадра (setCrop не зависит от
    // setScale). См. getCapturedUnitOriginalColor для того, почему «юнит
    // захвачен» определяется по capturedFromOwnerId, а не по сравнению factionId
    let capturedHalfSprite = null;
    const capturedOriginalColor = getCapturedUnitOriginalColor(unit, players);
    if (capturedOriginalColor != null) {
      capturedHalfSprite = this.scene.add.sprite(hex.centerX, hex.centerY + unitShiftY, unitImageKey);
      capturedHalfSprite.texture.setFilter(Phaser.Textures.FilterMode.LINEAR);
      capturedHalfSprite.setScale(scale);
      capturedHalfSprite.setDepth(LAYER_DEPTH.UNIT + 0.01);
      capturedHalfSprite.setTint(getPlayer0xColor(capturedOriginalColor));
      capturedHalfSprite.setCrop(sprite.width / 2, 0, sprite.width / 2, sprite.height);
      // Основной спрайт обрезается до левой половины, чтобы половины не
      // перекрывались: когда отряд мигает (addUnitFadingTween), полупрозрачные
      // слои сложились бы, и правая половина гасла бы слабее левой, с примесью
      // цвета нового владельца
      sprite.setCrop(0, 0, sprite.width / 2, sprite.height);
      this.registerUnitMarker(unit.id, 'capturedFactionHalf', capturedHalfSprite);
    }

    const shadowSprite = this.scene.add.sprite(hex.centerX, hex.centerY + unitShiftY, unitShadowImageKey);
    shadowSprite.texture.setFilter(Phaser.Textures.FilterMode.LINEAR);
    shadowSprite.setScale(scale * unitShadowScaleMultiplier);
    shadowSprite.setDepth(LAYER_DEPTH.UNIT - 0.2); // Отряды рендерятся выше токенов
    this.registerUnitMarker(unit.id, 'shadowSprite', shadowSprite);

    // Контур вокруг отряда (по форме непрозрачной части спрайта). Белым
    // выделены только отряды игрока, чья сейчас фаза — по ним на поле сразу
    // видно, кто ходит; у остальных игроков контур черный. После конца партии
    // ходов больше нет и подсвечивать некого — белый контур у всех отрядов
    // игроков, чтобы итоговая расстановка читалась целиком.
    // Нейтральным отрядам контур не рисуется вовсе: они ничьи, и обводка только
    // мешала бы отличать их от игроков
    let outlineSprite = null;
    if (!isNeutralUnit) {
      const outlineKey = generateOutlineTexture(
        this.scene, unitImageKey, `${unitImageKey}_outline${hasWhiteOutline ? '' : '_black'}`, 20,
        hasWhiteOutline ? 0xFFFFFF : 0x000000
      );
      // Источник не прочитался — обводку нарисует следующая перерисовка
      if (outlineKey) {
        outlineSprite = this.scene.add.sprite(hex.centerX, hex.centerY + unitShiftY, outlineKey);
        outlineSprite.setScale(scale);
        outlineSprite.setDepth(LAYER_DEPTH.UNIT - 0.1);
        this.registerUnitMarker(unit.id, 'outline', outlineSprite);
      }
    }

    // Активный игрок: контур яркий, белый сверху и золотистый снизу, а за ним дышит
    // золотое свечение — такое же, как у его флагов (configs/activeGlow). На итоговой
    // расстановке контур светлый у всех, и подсвечивать там некого
    let glowSprite = null;
    if (outlineSprite && hasWhiteOutline && !gameResults) {
      this.applyActiveContourTint(outlineSprite);
      const glowKey = generateGlowTexture(this.scene, unitImageKey, `${unitImageKey}_glow`);
      if (glowKey) {
        const glowScale = scale / UNIT_GLOW_TEXTURE.sourceScale;
        glowSprite = this.scene.add.sprite(hex.centerX, hex.centerY + unitShiftY, glowKey);
        glowSprite.setDepth(LAYER_DEPTH.UNIT - 0.15);
        this.registerUnitMarker(unit.id, 'outlineGlow', glowSprite);
        this.activeUnitGlows.add(glowSprite, outlineSprite, glowScale);
      }
    }

    // Анимация плавного подъёма и опускания иконки отряда
    let isAttacker = false;
    if (phase === PHASES.WAR) {
      if (warStage === WAR_PHASE_STAGES.COMBAT) {
        // Частое покачивание участника боя — тоже итог боя: до конца ударов
        // отряд качается как обычно
        isAttacker = !!pendingMeleeDamage.find(attacker => attacker.unitId === unit.id)
          && !this.checkIfMeleeResultHeld(unit.id)
      } else if (warStage === WAR_PHASE_STAGES.SHOOTING) {
        isAttacker = !!pendingShootingActions.find(attacker => attacker.shooterUnitId === unit.id)
      }
    }
    const duration = isAttacker ? 300 : 1200;
    const tween = this.scene.tweens.add({
      targets: [sprite, outlineSprite, glowSprite, shadowSprite, capturedHalfSprite].filter(Boolean),
      y: hex.centerY + unitShiftY - UNIT_BOB_AMPLITUDE_PX,
      duration,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1
    });
    this.registerTweens(hex, tween);
    // Толчок отряда (рывок, отшатывание в ближнем бою) идет и по вертикали —
    // на его время покачивание ставится на паузу (см. pushUnitSprites)
    hex.unitBobTween = tween;
    if (isTurnSkipShown) {
      this.addUnitSkipMark(hex, unit.id, unitShiftY, duration);
    }

    // Сохраняем ссылку для последующего удаления
    this.unitsSprites[unit.id] = sprite;

    // Добавляем отображение размера отряда (текст) - displaySize, а не
    // unit.size напрямую, см. комментарий выше про ловушки Гоблинов
    this.addUnitSizeLabel(hex, displaySize);

    if (gameResults) {
      this.addUnitStatBackground(hex, unit.id);
      this.hideUnitSpritesWhileDropping(unit, hex);
      return;
    }

    let leftStatMode;
    let rightStatMode;

    // В фазе снабжения показываем изменение размера отряда ТОЛЬКО для отрядов текущего игрока.
    // В одновременной фазе — для отрядов самого пользователя (и пока он
    // раздает, и после), а зрителю и записи матча — для отрядов всех игроков
    // (см. getSupplyPreviewPlayers); размер считается по политике владельца
    // Во время облета захваченных городов снабжения на отрядах не видно, хотя
    // оно уже могло раздаться автоматически (checkIfSupplyShownOnField)
    const supplyPreviewOwner = checkIfSupplyShownOnField(state)
      ? getSupplyPreviewPlayers(state).find((player) => player.id === unit.ownerId)
      : null;
    if (supplyPreviewOwner) {
      const unitUpdate = calcUnitUpdateBasedOnPendingSupply(unit, supplyPreviewOwner, pendingSupply);
      if (unitUpdate.sizeUpdate !== 0) {
        this.addUnitSizeUpdateIndicator(hex, unitUpdate.sizeUpdate, false, unit.size);
      }

      // Проверяем, получил ли отряд снабжение и сколько единиц снабжения (количество ферм)
      const hasSupply = pendingSupply.some(action => action.unitId === unit.id);

      if (hasSupply) {
        const hasPendingAdditionalSupply = checkIfUnitHasPendingAdditionalSupply(unit.id, pendingSupply);
        this.addSupplyIndicator(hex, unit, hasPendingAdditionalSupply, true);
        leftStatMode = hasPendingAdditionalSupply ? STAT_BACKGROUND_MODES.LEFT : STAT_BACKGROUND_MODES.LEFT_SHORT;
      }
    } else {
      if (unit.additionalSupply) {
        const usedAdditionalSupplyForMovement = pendingMovementActions.some(
          action => action.unitId === unit.id && action.additionalSupply === true
        );
        const usedAdditionalSupplyForManeuver = pendingManeuverActions.some(
          action => action.unitId === unit.id
        );
        if (!usedAdditionalSupplyForMovement && !usedAdditionalSupplyForManeuver) {
          this.addSupplyIndicator(hex, unit, false, false);
          leftStatMode = STAT_BACKGROUND_MODES.LEFT_SHORT;
        }
      }
    }

    // В фазе войны на этапе найма показываем изменение размера отряда для отрядов текущего игрока
    if (phase === PHASES.WAR) {
      if (warStage === WAR_PHASE_STAGES.RECRUITMENT) {
        if (unit.ownerId === currentPlayer.id) {
          // Докупка, чей шар еще летит к отряду, на бейдже появится с его прилетом
          const sizeUpdate = calcUnitUpdateBasedOnPendingRecruitment(unit, filterArrivedRecruitmentActions(pendingRecruitmentActions));
          if (sizeUpdate !== 0) {
            this.addUnitSizeUpdateIndicator(hex, sizeUpdate, true, unit.size);
          }
        }
      }

      if (warStage === WAR_PHASE_STAGES.MOVEMENT) {
        // Проверяем, перемещался ли отряд в текущей фазе
        const hasMoved = pendingMovementActions.some(action => action.unitId === unit.id);
        if (hasMoved) {
          this.addMovementIndicator(hex, unit.id, true);
          rightStatMode = STAT_BACKGROUND_MODES.RIGHT_SHORT;
        }

        if (isUnitFaction(unit, FACTIONS.LEPRECHAUNS)) {
          const leprechaunsStealCoinsActions = selectLeprechaunsStealCoinsActions(state);

          const hasStolen = leprechaunsStealCoinsActions.some(action => action.unitId === unit.id);
          if (hasStolen) {
            this.addLeprechaunsStealIndicator(hex, unit.id);
            rightStatMode = STAT_BACKGROUND_MODES.RIGHT_SHORT;
          }
        }
      }

      if (warStage === WAR_PHASE_STAGES.ABILITIES) {
        // Отряд, уже пытавшийся соблазнить в этом этапе, - иконка + итоговый
        // результат попытки (см. design.md Decision 12). Фильтр по ownerId
        // текущего игрока обязателен: hasUsedAbilityThisWarPhase - это
        // персистентное булево поле юнита (не очищаемый на каждом ходу pending-
        // массив), поэтому без фильтра при переходе хода к другому игроку-
        // суккубу индикаторы предыдущего игрока на этапе способностей ошибочно
        // "воскресали бы" - см. тот же паттерн у hasFoughtThisWarPhase выше.
        // Явная проверка фракции обязательна: флаг общий для всех фракций со
        // способностями (см. configs/units.js) - без неё эта ветка ошибочно
        // сработала бы и на гоблине, применившем СВОЮ способность
        if (isUnitFaction(unit, FACTIONS.SUCCUBES) && unit.ownerId === currentPlayer.id
          && unit.hasUsedAbilityThisWarPhase) {
          const succubesSeductionActions = selectSuccubesSeductionActions(state);
          const seductionActions = succubesSeductionActions.filter(action => action.unitId === unit.id);
          const lastSeductionAction = seductionActions[seductionActions.length - 1];
          if (lastSeductionAction) {
            const result = lastSeductionAction.roll + lastSeductionAction.attackerSize - lastSeductionAction.targetSize;
            this.addSuccubesSeductionIndicator(hex, unit.id, result);
            rightStatMode = Math.abs(result) >= 10 ? STAT_BACKGROUND_MODES.RIGHT_LONG : STAT_BACKGROUND_MODES.RIGHT;
          }
        }

        // Гоблины: см. design.md (пункты 5/6 ручного тестирования) - над
        // отрядом, который ещё МОЖЕТ применить способность в этом этапе,
        // висит покачивающаяся иконка способности; как только отряд
        // применяет одну из трёх способностей (hasUsedAbilityThisWarPhase),
        // иконка сменяется результатом попытки справа от численности - тем
        // же способом, что и у addSuccubesSeductionIndicator выше. Фильтр по
        // ownerId текущего игрока по той же причине, что и у суккубов:
        // hasUsedAbilityThisWarPhase - персистентный флаг юнита, без фильтра
        // индикаторы предыдущего игрока-гоблина "воскресали" бы при переходе
        // хода
        if (isUnitFaction(unit, FACTIONS.GOBLINS) && unit.ownerId === currentPlayer.id) {
          if (unit.hasUsedAbilityThisWarPhase) {
            const goblinsAbilityActions = selectGoblinsAbilityActions(state);
            const unitAbilityActions = goblinsAbilityActions.filter(action => action.unitId === unit.id);
            const lastAbilityAction = unitAbilityActions[unitAbilityActions.length - 1];

            if (lastAbilityAction) {
              const playerColor0x = getPlayer0xColor(currentPlayer.color);

              if (lastAbilityAction.type === 'remove_trap') {
                // Снятие ловушки не требует броска - только сама иконка
                // способности, цветом текущего игрока (в игре может быть
                // несколько игроков-гоблинов одновременно)
                this.addGoblinsAbilityResultIndicator(hex, unit.id, {
                  iconKey: 'ability_white', tintColor: playerColor0x
                });
                rightStatMode = STAT_BACKGROUND_MODES.RIGHT_SHORT;
              } else if (lastAbilityAction.type === 'damage_building' && lastAbilityAction.isFortressTarget) {
                // Крепость - итоговый урон способности и нейтральная иконка
                // урона, по аналогии со стрельбой катапульт (не иконка
                // способности - там уже показан её результат числом)
                const damage = lastAbilityAction.damage || 0;
                this.addGoblinsAbilityResultIndicator(hex, unit.id, {
                  numberText: damage.toString(), numberStyle: UNIT_LABEL_DAMAGE_STYLE, iconKey: 'damage'
                });
                rightStatMode = STAT_BACKGROUND_MODES.RIGHT;
              } else {
                // Установка ловушки или попытка повредить обычное строение -
                // тот же бросок д6, что решал успех попытки, плюс иконка
                // способности цветом игрока
                this.addGoblinsAbilityResultIndicator(hex, unit.id, {
                  numberText: lastAbilityAction.roll.toString(), numberStyle: UNIT_LABEL_TEXT_STYLE,
                  iconKey: 'ability_white', tintColor: playerColor0x
                });
                rightStatMode = STAT_BACKGROUND_MODES.RIGHT;
              }
            }
          } else if (checkIfGoblinsUnitCouldUseAbility(
            unit, units, currentPlayer.coins, hexes, citiesCache, specialBuildings, buildingsDamage, goblinsTraps,
            currentPlayer, fortressesUnderConstruction
          )) {
            // Раньше здесь проверялся только размер отряда (>=
            // GOBLINS_MIN_UNIT_SIZE_FOR_ABILITY) - иконка ошибочно висела и
            // над отрядом, смежным вражескому (способности нельзя применять
            // смежно врагу, как и у всех остальных фракций), и над отрядом
            // без единой доступной цели/монет вообще. checkIfGoblinsUnitCouldUseAbility -
            // та же функция, что уже решает, показывать ли саму панель
            // GoblinsAbilitiesToolbar при выборе юнита (TokensToolbar.js) -
            // индикатор теперь по определению не может разойтись с панелью
            // (найдено пользователем в ручном тестировании). Спящему отряду
            // (пропустил ход) искр нет — над ним «zzZ»
            if (!isTurnSkipShown) {
              this.addAbilityAvailableIndicator(hex, unit.id, duration, currentPlayer.color);
            }
          }
        }

        // Суккубы: те же искры над отрядом, который еще может соблазнить (та
        // же проверка, что решает, показывать ли панель соблазнения при выборе
        // отряда). Фильтр по ownerId и спящий отряд — как у Гоблинов выше
        if (isUnitFaction(unit, FACTIONS.SUCCUBES) && unit.ownerId === currentPlayer.id && !isTurnSkipShown
          && checkIfSuccubesUnitCouldSeduce(
            unit, units, currentPlayer.coins, hexes, specialBuildings, buildingsDamage, fortressesUnderConstruction
          )) {
          this.addAbilityAvailableIndicator(hex, unit.id, duration, currentPlayer.color);
        }
      }

      if (warStage === WAR_PHASE_STAGES.SHOOTING) {
        const isUnitMeleeElf = checkIfUnitMeleeElf(unit);
        const isUnitD4 = unit.type === UNIT_TYPES.D4.id;

        if (isUnitD4 || isUnitMeleeElf) {
          // Проверяем, перемещался ли отряд в текущей фазе
          const hasMoved = pendingMovementActions.some(action => action.unitId === unit.id);
          if (hasMoved && isUnitD4) {
            this.addMovementIndicator(hex, unit.id, false);
            rightStatMode = STAT_BACKGROUND_MODES.RIGHT_SHORT;
          }

          const hasShot = pendingShootingActions.some(action => action.shooterUnitId === unit.id);
          if (hasShot) {
            this.addShootingIndicator(hex, this.buildCombatCellPulse(unit, players, state));
          }
        }

        // Отображаем индикатор урона от выстрелов отрядов. Пока снаряд летит,
        // его урона на цели еще нет (см. showShotAnimation)
        const landedShootingActions = this.filterLandedShootingActions(pendingShootingActions);
        if (landedShootingActions && landedShootingActions.length > 0) {
          // Суммируем весь урон по данному отряду
          let totalDamage = landedShootingActions
            .filter(action => action.targetUnitId === unit.id)
            .reduce((sum, action) => sum + action.damage, 0);

          let sizeLoss = totalDamage;
          if (isUnitFaction(unit, FACTIONS.ORCS)) {
            sizeLoss = calculateOrcLoss(totalDamage);
          }

          if (sizeLoss > 0) {
            // Отображаем индикатор уменьшения размера отряда (отрицательное значение)
            this.addUnitSizeUpdateIndicator(hex, Math.max(-sizeLoss, -unit.size), false, unit.size);
          }
          this.addDoomedUnitFadingTween(hex, unit, sizeLoss);
        }
      }

      // ИТОГ БЛИЖНЕГО БОЯ — ПОСЛЕ УДАРОВ. Пока идет анимация ударов (см.
      // showMeleeAnimation), у ее отрядов нет ни значка мечей с нанесенным
      // уроном и волной по ячейке, ни полученного урона под численностью, ни
      // мигания обреченного: все это появляется разом, когда удары отыграли
      if (warStage === WAR_PHASE_STAGES.COMBAT && !this.checkIfMeleeResultHeld(unit.id)) {
        if (!showMeleeTargets) {
          const attacker = pendingMeleeDamage.find(attacker => attacker.unitId === unit.id);
          if (attacker) {
            const damage = attacker.damage;
            rightStatMode = damage >= 10 ? STAT_BACKGROUND_MODES.RIGHT_LONG : STAT_BACKGROUND_MODES.RIGHT;
            this.addMeleeIndicator(hex, damage, this.buildCombatCellPulse(unit, players, state));
          }
        }

        const {sizeLoss} = calcUnitMeleeDamageWithFortress(unit, pendingMeleeDamage, specialBuildings, buildingsDamage, fortressesUnderConstruction);
        if (sizeLoss > 0) {
          this.addUnitSizeUpdateIndicator(hex, Math.max(-sizeLoss, -unit.size), false, unit.size);
        }
        this.addDoomedUnitFadingTween(hex, unit, sizeLoss);
      }

      if (warStage === WAR_PHASE_STAGES.MANEUVER) {
        // Проверяем, маневрировал ли отряд в текущей фазе
        const hasMoved = pendingManeuverActions.some(action => action.unitId === unit.id);
        if (hasMoved) {
          this.addMovementIndicator(hex, unit.id, true, 'maneuver');
          rightStatMode = STAT_BACKGROUND_MODES.RIGHT_SHORT;
        } else if (unit.ownerId === currentPlayer.id && checkIfUnitCouldManeuverFromHex(currentPlayer, unit, hexes)) {
          // Индикатор возможности маневра: ближний бой приоритетнее стрельбы,
          // а способность (соблазнение/ловушки и т.п. — ни то ни другое из
          // предыдущих двух) - запасной вариант, той же иконкой, что и у
          // индикатора применённой способности на этапе ABILITIES
          const eligibilityIcon = unit.hasFoughtThisWarPhase
            ? 'barracks_white'
            : (unit.hasShotThisWarPhase ? 'shooting_white' : 'ability_white');
          this.addManeuverEligibilityIndicator(hex, unit.id, eligibilityIcon);
          rightStatMode = STAT_BACKGROUND_MODES.RIGHT_SHORT;
        }
      }
    }

    this.addUnitStatBackground(hex, unit.id, leftStatMode, rightStatMode);
    // По ячейке летит двойник этого отряда — настоящий ждет приземления
    this.hideUnitSpritesWhileDropping(unit, hex);
  }

  /**
   * Индикатор выстрела над отрядом. Стоит слева от центра ячейки, на месте и
   * в размер жетона повреждения строения: в центре иконка закрывала отряд.
   * @param {Object} hex
   * @param {Function} [onFadeInStart] — волна по ячейке на каждое разгорание
   *   иконки (см. buildCombatCellPulse)
   */
  addShootingIndicator(hex, onFadeInStart = null) {
    const iconX = hex.centerX + BUILDING_DAMAGE_MARKER_SHIFT_X;
    const iconY = hex.centerY + this.getTokenShiftY(hex, COMBAT_TOKEN_SHIFT_Y);
    const shootingMarker = this.liveFieldMarkers.createCombatMarker({
      x: iconX, y: iconY, hexSize: hex.size, depth: BUILDING_STATE_MARKER_DEPTH, kind: COMBAT_MARKER_KINDS.SHOOTING,
    });

    this.applyCombatIndicatorFading(hex, shootingMarker, onFadeInStart);
    this.registerMarker(hex, shootingMarker);
  }

  /**
   * Мигание боевой иконки по прозрачности — сейчас выключено флагом
   * COMBAT_INDICATOR_FADING_ENABLED: иконка висит ровно.
   *
   * Волна по ячейке от мигания не зависит: с миганием она идет на каждое
   * разгорание иконки, без него ритм задает отдельный твин
   * (см. addCombatCellPulseRhythm). Наложение двух волн на одной ячейке
   * гасит сам колбэк (см. buildCombatCellPulse)
   *
   * @param {Object} hex
   * @param {Object} marker — иконка ближнего боя или выстрела
   * @param {Function|null} onFadeInStart — волна по ячейке отряда
   */
  applyCombatIndicatorFading(hex, marker, onFadeInStart) {
    if (COMBAT_INDICATOR_FADING_ENABLED) {
      this.addMarkerFadingTween(hex, marker, false, MARKER_FADING_HOLD_MS, 0, 0, onFadeInStart);
      return;
    }

    marker.setAlpha(1);
    this.addCombatCellPulseRhythm(hex, marker, onFadeInStart);
  }

  /**
   * Ритм волны по ячейке, когда мигание иконок выключено: волна должна идти
   * ЗАЦИКЛЕННО, а не один раз на отрисовку поля.
   *
   * Тик дает твин-пустышка на самой иконке — прозрачность в нем не меняется
   * (1 -> 1), нужны только его onStart/onRepeat. Твин, а не таймер сцены,
   * чтобы ритм убирался ровно там же, где мигание: clearTweens на каждом
   * обновлении стора снимет его вместе с маркером, и новый пойдет с первой
   * волны — как раньше с миганием
   *
   * @param {Object} hex
   * @param {Object} marker — иконка ближнего боя или выстрела
   * @param {Function|null} onFadeInStart — волна по ячейке отряда
   */
  addCombatCellPulseRhythm(hex, marker, onFadeInStart) {
    if (!onFadeInStart) {
      return;
    }

    const tween = this.scene.tweens.add({
      targets: marker,
      alpha: {from: 1, to: 1},
      duration: COMBAT_CELL_PULSE.intervalMs,
      repeat: -1,
      onStart: onFadeInStart,
      onRepeat: onFadeInStart,
    });

    this.registerTweens(hex, tween);
  }

  /**
   * Волна по ячейке отряда в такт боевой иконке: та же подсветка, что у
   * докупки размера отряду (showUnitRecruitmentPulse, одна волна), цветом
   * ВЛАДЕЛЬЦА отряда — по ней видно, чьи отряды сцепились в бою и кто стреляет.
   *
   * Волна идет ПОД отрядами (LAYER_DEPTH.COMBAT_CELL_PULSE) и уже, чем у
   * найма (COMBAT_CELL_PULSE.endScaleDivider): бой не должен скрываться за
   * собственной подсветкой.
   *
   * У нейтрального отряда игрока нет — его волна черная (цвет нейтрала в
   * палитре, см. COLORS_CONFIG). Возвращает колбэк для addMarkerFadingTween
   * или null, если владелец не нашелся вовсе (тогда иконка просто мигает).
   *
   * Волна живет вне hex.markers, поэтому переживает перерисовку поля. Мигание
   * иконки перерисовку НЕ переживает: маркеры и твины пересоздаются на каждом
   * обновлении стора, и разгорание начинается заново — вместе с ним заново
   * идет и волна. Гейт ниже гасит только НАЛОЖЕНИЕ двух волн на одной ячейке
   * (пока спрайты предыдущей живы, новая не стартует); реестр волн общий с
   * подсветкой найма, так что на одной ячейке они не наложатся тоже
   *
   * @param {Object} unit — отряд, который бьется или стреляет
   * @param {Object[]} players — игроки партии
   * @param {Object} state — состояние (его ждет showUnitRecruitmentPulse)
   * @returns {Function|null}
   */
  buildCombatCellPulse(unit, players, state) {
    if (!unit?.hexKey) {
      return null;
    }

    const owner = (players || []).find((player) => player.id === unit.ownerId);
    const isNeutralUnit = !owner && unit.ownerId === NEUTRAL_PLAYER_ID;
    if (!owner && !isNeutralUnit) {
      return null;
    }

    const hexKey = unit.hexKey;
    const color0x = getPlayer0xColor(owner ? owner.color : NEUTRAL_PLAYER_COLOR);
    return () => {
      if (this.recruitmentPulseSprites.has(hexKey)) {
        return;
      }
      this.showUnitRecruitmentPulse(hexKey, color0x, state, {
        count: RECRUITMENT_PULSE_COUNT.SIZE,
        depth: LAYER_DEPTH.COMBAT_CELL_PULSE,
        endScaleFactor: RECRUITMENT_PULSE.endScaleFactor / COMBAT_CELL_PULSE.endScaleDivider,
        durationMs: Math.round(RECRUITMENT_PULSE.durationMs * COMBAT_CELL_PULSE.durationMultiplier),
        shiftY: this.getTokenShiftY(this.hexes[hexKey], COMBAT_TOKEN_SHIFT_Y),
      });
    };
  }

  /**
   * Индикатор участия в ближнем бою: иконка казармы слева от центра ячейки
   * (на месте и в размер жетона повреждения строения — в центре она закрывала
   * отряд) и урон отряда справа.
   * @param {Object} hex
   * @param {number} damage
   * @param {Function} [onFadeInStart] — волна по ячейке на каждое разгорание
   *   иконки (см. buildCombatCellPulse)
   */
  addMeleeIndicator(hex, damage, onFadeInStart = null) {
    const meleeMarkerX = hex.centerX + BUILDING_DAMAGE_MARKER_SHIFT_X;
    const meleeMarkerY = hex.centerY + this.getTokenShiftY(hex, COMBAT_TOKEN_SHIFT_Y);
    const meleeMarker = this.liveFieldMarkers.createCombatMarker({
      x: meleeMarkerX, y: meleeMarkerY, hexSize: hex.size, depth: BUILDING_STATE_MARKER_DEPTH, kind: COMBAT_MARKER_KINDS.MELEE,
    });
    this.applyCombatIndicatorFading(hex, meleeMarker, onFadeInStart);
    this.registerMarker(hex, meleeMarker);

    const damageLabelX = hex.centerX
      + (damage >= 10 ? 25 : 22);
    const damageLabelY = hex.centerY + this.getUnitShiftY(hex) + UNIT_LABEL_TEXT_SHIFT_Y;
    const damageLabel = this.scene.add.text(damageLabelX, damageLabelY, damage.toString(), UNIT_LABEL_DAMAGE_STYLE);
    damageLabel.setScale(0.5);
    damageLabel.setOrigin(0.5, 0.5);
    damageLabel.setDepth(LAYER_DEPTH.UNIT + 0.3);
    this.registerMarker(hex, damageLabel);

    const damageMarkerX = damageLabelX
      + (damage >= 10 ? 17 : 12);
    const damageMarker = this.scene.add.sprite(damageMarkerX, damageLabelY, 'damage');
    damageMarker.setScale(STAT_ICON_SCALE);
    damageMarker.setDepth(LAYER_DEPTH.UNIT + 1);
    this.registerMarker(hex, damageMarker);
  }

  /**
   * Добавить индикатор перемещения (иконка скорости) к отряду
   * @param {Object} hex - Объект гекса
   * @param {string} unitId - ID отряда
   * @param isAnimated
   * @param icon
   */
  addMovementIndicator(hex, unitId, isAnimated, icon = 'speed_white') {
    // Вычисляем позицию иконки (справа от центра гекса)
    const iconX = hex.centerX + UNIT_INDICATOR_SHIFT_X - 7;
    const iconY = hex.centerY + this.getUnitShiftY(hex) + UNIT_STAT_SHIFT_Y;

    // Создаём спрайт иконки
    const movementMarker = this.scene.add.sprite(iconX, iconY, icon);
    movementMarker.setScale(STAT_ICON_SCALE);
    movementMarker.setOrigin(0.5, 0.5);
    movementMarker.setDepth(LAYER_DEPTH.UNIT + 1);
    if (isAnimated) {
      this.addMarkerFadingTween(hex, movementMarker);
    }
    this.registerUnitMarker(unitId, 'movementMarker', movementMarker);
  }

  /**
   * Добавить индикатор возможности маневра к отряду
   * @param {Object} hex - Объект гекса
   * @param {string} unitId - ID отряда
   * @param {string} icon - Ключ текстуры иконки
   */
  addManeuverEligibilityIndicator(hex, unitId, icon) {
    // Вычисляем позицию иконки (справа от центра гекса)
    const iconX = hex.centerX + UNIT_INDICATOR_SHIFT_X - 7;
    const iconY = hex.centerY + this.getUnitShiftY(hex) + UNIT_STAT_SHIFT_Y;

    // Создаём спрайт иконки
    const movementMarker = this.scene.add.sprite(iconX, iconY, icon);
    movementMarker.setScale(STAT_ICON_SCALE);
    movementMarker.setOrigin(0.5, 0.5);
    movementMarker.setDepth(LAYER_DEPTH.UNIT + 1);
    // Золотом, под оправу шеврона: отряд уже бился или стрелял и может маневрировать
    movementMarker.setTint?.(UNIT_BADGE_GOLD_ICON_TINT);
    this.registerUnitMarker(unitId, 'maneuverEligibility', movementMarker);
  }

  addLeprechaunsStealIndicator(hex, unitId) {
    // Вычисляем позицию иконки (справа от центра гекса)
    const iconX = hex.centerX + UNIT_INDICATOR_SHIFT_X - 7;
    const iconY = hex.centerY + this.getUnitShiftY(hex) + UNIT_STAT_SHIFT_Y;

    // Создаём спрайт иконки
    const stealMarker = this.scene.add.sprite(iconX, iconY, 'steal_hand_white');
    stealMarker.setScale(STAT_ICON_SCALE);
    stealMarker.setOrigin(0.5, 0.5);
    stealMarker.setDepth(LAYER_DEPTH.UNIT + 1);
    this.registerUnitMarker(unitId, 'stealMarker', stealMarker);
  }

  /**
   * Добавить индикатор "уже пытался соблазнить" + итоговый результат попытки
   * (бросок + разница численности) к отряду Суккубов. Позиция иконки - как у
   * addMovementIndicator/addLeprechaunsStealIndicator (справа от size-бейджа);
   * число - по образцу addMeleeIndicator, но отдельным цветом
   * (UNIT_LABEL_SEDUCTION_STYLE), чтобы не путать с уроном ближнего боя.
   * Иконка - 'ability_white' (белая версия, как у остальных индикаторов рядом
   * с отрядом: 'speed_white', 'steal_hand_white', 'shooting_white' и т.д.)
   * @param {Object} hex - Объект гекса
   * @param {string} unitId - ID отряда
   * @param {number} result - итоговый результат (roll + sizeDiff)
   */
  addSuccubesSeductionIndicator(hex, unitId, result) {
    // Число - слева, иконка - справа от числа (как трейлинг-иконка урона у
    // addMeleeIndicator), а не наоборот: при иконке слева от числа они
    // визуально наезжали друг на друга
    const isNegative = result < 0;
    const absResult = Math.abs(result);
    const resultText = (isNegative ? MINUS_SIGN : '') + absResult;

    const resultLabelX = hex.centerX + UNIT_INDICATOR_SHIFT_X - 7;
    const resultLabelY = hex.centerY + this.getUnitShiftY(hex) + UNIT_LABEL_TEXT_SHIFT_Y;
    const resultLabel = this.scene.add.text(resultLabelX, resultLabelY, resultText, UNIT_LABEL_SEDUCTION_STYLE);
    resultLabel.setScale(0.5);
    resultLabel.setOrigin(0.5, 0.5);
    resultLabel.setDepth(LAYER_DEPTH.UNIT + 0.3);
    this.registerUnitMarker(unitId, 'seductionResultLabel', resultLabel);

    // Отступ иконки — по длине отображаемой строки (resultText.length), с
    // запасом побольше, чем в первой версии фикса (11/14/17 — тогда 14 для
    // положительного двузначного числа было буквально тем же значением, что
    // и в исходном коде до фикса, поэтому наезд не исчезал). Пробовали также
    // считать через resultLabel.displayWidth (фактическая отрисованная
    // ширина текста) — оказалось ЕЩЁ хуже: значение получалось меньше даже
    // для одной цифры, похоже на ненадёжные метрики Phaser Text для этого
    // жирного кастомного шрифта (Franklin Gothic Heavy). Захардкоженные
    // константы с заметным запасом — надёжнее
    const iconOffsetByTextLength = {1: 11, 2: 17, 3: 24};
    const iconX = resultLabelX + (iconOffsetByTextLength[resultText.length] || 28);
    const iconY = hex.centerY + this.getUnitShiftY(hex) + UNIT_STAT_SHIFT_Y;
    const seductionMarker = this.scene.add.sprite(iconX, iconY, 'ability_white');
    seductionMarker.setScale(STAT_ICON_SCALE);
    seductionMarker.setOrigin(0.5, 0.5);
    seductionMarker.setDepth(LAYER_DEPTH.UNIT + 1);
    this.registerUnitMarker(unitId, 'seductionMarker', seductionMarker);
  }

  /**
   * Искры способности над отрядом Гоблинов или Суккубов, который ещё МОЖЕТ
   * применить способность в этапе ABILITIES (hasUsedAbilityThisWarPhase ещё
   * false и checkIfGoblinsUnitCouldUseAbility / checkIfSuccubesUnitCouldSeduce -
   * см. renderUnit; в частности, это исключает отряд, смежный вражескому, как и
   * у всех остальных фракций). Три искры стоят как звезды иконки этапа
   * способностей и мягко мерцают по очереди (configs/abilitySparkleAnimation);
   * у каждой — белая обводка по силуэту, она мерцает вместе с искрой (без нее
   * цветная искра терялась на поле — найдено пользователем). Под искрами —
   * ровное белесое свечение.
   *
   * Искры тонированы цветом игрока (в игре может быть несколько игроков одной
   * фракции, тот же принцип, что и у addGoblinsAbilityResultIndicator/
   * playTrapTriggerAnimation), а не жёстко белые - иначе сливались бы с белой
   * обводкой ячейки/отряда (найдено пользователем в ручном тестировании).
   *
   * Покачивается своим отдельным твином, а не в targets основного твина
   * отряда (тот уже создан раньше в этой же функции) - но с теми же
   * duration/ease/yoyo/repeat и стартом в этом же вызове renderUnit, поэтому
   * идет синхронно с отрядом. Мерцание считается на каждом кадре того же твина
   * от общего времени сцены и сразу при создании — перерисовка поля его не сбивает
   * @param {Object} hex
   * @param {string} unitId
   * @param {number} duration - тот же duration, что у твина покачивания отряда
   * @param {string} playerColor - цвет текущего игрока
   */
  addAbilityAvailableIndicator(hex, unitId, duration, playerColor) {
    const iconX = hex.centerX + ABILITY_AVAILABLE_ICON_SHIFT_X;
    const iconY = hex.centerY + this.getUnitShiftY(hex) + ABILITY_AVAILABLE_ICON_SHIFT_Y;
    const {
      textureKey, textureStarSizePx, particles, glowSizeFactor, glowAlpha,
      outlineKey, outlineThicknessPx, outlineSourceScale,
    } = ABILITY_SPARKLES;
    const bobTargets = [];

    // Белесое свечение под искрами — мягкий круг эффектов боя (своей текстуры
    // не нужно). Нет текстуры — искры и без него
    if (ensureMeleeTextures(this.scene, {linearFilter: Phaser.Textures?.FilterMode?.LINEAR})) {
      // Поперечник — от иконки этапа целиком (сторона ее картинки ~512 px)
      const glowSize = ABILITY_ICON_SOURCE_SIZE_PX * ABILITY_AVAILABLE_ICON_SCALE * glowSizeFactor;
      const glow = this.scene.add.sprite(iconX, iconY, MELEE_TEXTURE_KEYS.GLOW);
      glow.setDisplaySize(glowSize, glowSize);
      glow.setAlpha(glowAlpha);
      glow.setDepth(LAYER_DEPTH.UNIT + 0.8);
      this.registerUnitMarker(unitId, 'abilityAvailableGlow', glow);
      bobTargets.push(glow);
    }

    // Белая обводка по силуэту искры — та же техника, что у контуров отрядов
    // (generateOutlineTexture идемпотентна: текстура считается раз на партию).
    // Посчитана по уменьшенной картинке — ее спрайту нужен масштаб искры,
    // деленный на outlineSourceScale. Не прочиталась картинка — без обводки
    const readyOutlineKey = generateOutlineTexture(
      this.scene, textureKey, outlineKey, outlineThicknessPx * outlineSourceScale, 0xFFFFFF,
      {sourceScale: outlineSourceScale}
    );

    const playerTint = getPlayer0xColor(playerColor);
    const sparkles = particles.map(({dx, dy, sizePx}, index) => {
      const x = iconX + dx * ABILITY_AVAILABLE_ICON_SCALE;
      const y = iconY + dy * ABILITY_AVAILABLE_ICON_SCALE;
      let outline = null;
      if (readyOutlineKey) {
        outline = this.scene.add.sprite(x, y, readyOutlineKey);
        outline.setOrigin(0.5, 0.5);
        outline.setDepth(LAYER_DEPTH.UNIT + 0.9);
        this.registerUnitMarker(unitId, `abilityAvailableSparkleOutline${index}`, outline);
      }
      const sparkle = this.scene.add.sprite(x, y, textureKey);
      sparkle.setOrigin(0.5, 0.5);
      sparkle.setDepth(LAYER_DEPTH.UNIT + 1);
      sparkle.setTint(playerTint);
      this.registerUnitMarker(unitId, `abilityAvailableSparkle${index}`, sparkle);
      return {sprite: sparkle, outline, baseScale: ABILITY_AVAILABLE_ICON_SCALE * (sizePx / textureStarSizePx)};
    });
    sparkles.forEach(({sprite, outline}) => bobTargets.push(...(outline ? [outline, sprite] : [sprite])));

    // Обводка мерцает и сжимается вместе со своей искрой
    const applySparkle = () => {
      getAbilitySparkleStates(this.scene.time?.now, sparkles.length).forEach(({alpha, scale}, index) => {
        const {sprite, outline, baseScale} = sparkles[index];
        sprite.setAlpha(alpha);
        sprite.setScale(baseScale * scale);
        if (outline) {
          outline.setAlpha(alpha);
          outline.setScale((baseScale * scale) / outlineSourceScale);
        }
      });
    };
    applySparkle();

    const tween = this.scene.tweens.add({
      targets: bobTargets,
      y: `-=${UNIT_BOB_AMPLITUDE_PX}`,
      duration,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
      onUpdate: applySparkle,
    });
    this.registerTweens(hex, tween);
  }

  /**
   * Индикатор "уже применил способность в этом этапе" для отряда Гоблинов -
   * позиция как у addSuccubesSeductionIndicator/addLeprechaunsStealIndicator
   * (справа от size-бейджа). В отличие от addSuccubesSeductionIndicator,
   * отступ иконки от числа фиксированный, без таблицы по длине текста: числа
   * этой способности всегда однозначные (бросок д6 - 1-6, урон крепости
   * roll - GOBLINS_FORTRESS_DAMAGE_PENALTY - 1-4)
   * @param {Object} hex
   * @param {string} unitId
   * @param {?string} numberText - null для снятия ловушки (там нет броска)
   * @param {?Object} numberStyle - обязателен, если задан numberText
   * @param {string} iconKey - 'ability_white' (способность) или 'damage' (крепость)
   * @param {?number} tintColor - 0x-цвет игрока для иконки способности; для
   *   индикатора урона по крепости не передаётся - иконка урона нейтральная,
   *   как у катапульт
   */
  addGoblinsAbilityResultIndicator(hex, unitId, {numberText = null, numberStyle, iconKey, tintColor = null}) {
    const baseX = hex.centerX + UNIT_INDICATOR_SHIFT_X - 7;
    const iconY = hex.centerY + this.getUnitShiftY(hex) + UNIT_STAT_SHIFT_Y;
    let iconX = baseX;

    if (numberText != null) {
      const labelY = hex.centerY + this.getUnitShiftY(hex) + UNIT_LABEL_TEXT_SHIFT_Y;
      const label = this.scene.add.text(baseX, labelY, numberText, numberStyle);
      label.setScale(0.5);
      label.setOrigin(0.5, 0.5);
      label.setDepth(LAYER_DEPTH.UNIT + 0.3);
      this.registerUnitMarker(unitId, 'goblinsAbilityResultLabel', label);
      iconX = baseX + 11;
    }

    const icon = this.scene.add.sprite(iconX, iconY, iconKey);
    icon.setScale(STAT_ICON_SCALE);
    icon.setOrigin(0.5, 0.5);
    icon.setDepth(LAYER_DEPTH.UNIT + 1);
    if (tintColor != null) {
      icon.setTint(tintColor);
    }
    this.registerUnitMarker(unitId, 'goblinsAbilityResultMarker', icon);
  }

  /**
   * Мигание иконки-маркера: прозрачность ходит туда-обратно без остановки.
   *
   * onFadeInStart зовется в начале КАЖДОГО разгорания (первый заход и каждый
   * повтор) — им боевые иконки тянут за собой волну по своей ячейке
   * (см. buildCombatCellPulse). При isReverse первый заход, наоборот, гасит
   * иконку, поэтому колбэк там не к месту.
   *
   * Колбэк рассчитан на твин с ОДНОЙ целью и одним свойством: Phaser шлет
   * onRepeat на каждую пару «цель × свойство», и на нескольких целях волна
   * ушла бы кратно чаще.
   *
   * minAlpha — до какой прозрачности иконка гаснет: по умолчанию до нуля. Ритм
   * от нее не зависит, поэтому иконки с разной minAlpha мигают в одной фазе
   */
  addMarkerFadingTween(hex, iconSprite, isReverse = false, holdMs = MARKER_FADING_HOLD_MS, delay = 0, repeatDelay = 0, onFadeInStart = null, minAlpha = 0) {
    iconSprite.setAlpha(isReverse ? 1 : minAlpha);
    const tween = this.scene.tweens.add({
      targets: iconSprite,
      alpha: isReverse ? minAlpha : 1,
      duration: MARKER_FADING_DURATION_MS,
      delay: delay,
      hold: holdMs,
      repeatDelay: repeatDelay,
      ease: Phaser.Math.Easing.Sine.InOut,
      yoyo: true,
      repeat: -1,
      ...(onFadeInStart && !isReverse ? {onStart: onFadeInStart, onRepeat: onFadeInStart} : {}),
    });

    this.registerTweens(hex, tween);
  }

  /**
   * Мигание самого отряда — то же, что у маркеров над ним
   * (addMarkerFadingTween), и в той же фазе.
   *
   * До полной прозрачности мигают иконка отряда (у соблазненного Суккубом —
   * обе ее половины) и тень под ней: тень без иконки висела бы над ячейкой сама
   * по себе. Контур мигает вместе с ними, но гаснет лишь наполовину
   * (UNIT_FADING_OUTLINE_MIN_ALPHA) — по нему видно, где стоит отряд, пока
   * иконка погашена. У нейтральных отрядов контура нет: на это время от них
   * остаются только численность и «-N».
   *
   * Слои берутся из реестра спрайтов отряда, поэтому отряд к этому моменту уже
   * должен быть отрисован (renderUnit). Список слоев один на оба случая:
   * обреченный в бою отряд (addDoomedUnitFadingTween) и отряд, переживший
   * ловушку Гоблинов (updateGoblinsTrapTriggerDisplay)
   *
   * @param {Object} hex
   * @param {string} unitId
   */
  addUnitFadingTween(hex, unitId) {
    const sprites = [
      this.unitsSprites[unitId],
      ...(this.unitsSprites[`${unitId}_capturedFactionHalf`] || []),
      ...(this.unitsSprites[`${unitId}_shadowSprite`] || []),
    ].filter(Boolean);

    // Свой твин на каждый слой, как у остальных маркеров: параметры одни и
    // заводятся они в одном кадре, поэтому слои идут в одной фазе
    sprites.forEach((sprite) => this.addMarkerFadingTween(hex, sprite));
    (this.unitsSprites[`${unitId}_outline`] || []).forEach((outlineSprite) => {
      this.addMarkerFadingTween(
        hex, outlineSprite, false, MARKER_FADING_HOLD_MS, 0, 0, null, UNIT_FADING_OUTLINE_MIN_ALPHA
      );
    });
  }

  /**
   * Отряд, которому нанесенного урона уже хватает на уничтожение, мигает до
   * полной прозрачности (см. addUnitFadingTween) — ВМЕСТЕ с маркерами, которые
   * мигают рядом с ним: красной плашкой гибели под его численностью
   * (addUnitSizeUpdateIndicator). Они гаснут и проявляются разом, а не по
   * очереди. Так видно, что отряд не переживет этап. Боевые иконки ближнего
   * боя при этом не мигают (COMBAT_INDICATOR_FADING_ENABLED). Значка урона
   * над целью выстрела нет вовсе (см. addTrajectoryMarkers).
   *
   * Численность и «-N» рядом с отрядом не гаснут: по ним игрок и видит, что
   * урон дотянул до размера отряда
   *
   * @param {Object} hex
   * @param {Object} unit
   * @param {number} sizeLoss — урон, переведенный в потерю размера
   */
  addDoomedUnitFadingTween(hex, unit, sizeLoss) {
    if (!sizeLoss || sizeLoss < unit.size) {
      return;
    }

    this.addUnitFadingTween(hex, unit.id);
  }

  addUnitStatBackground(hex, unitId, leftStatMode, rightStatMode) {
    // Вычисляем позицию иконки (слева от центра гекса, напротив movement indicator)

    let statMode = STAT_BACKGROUND_MODES.NONE;
    if (leftStatMode && rightStatMode) {
      // both: left side is always short, right could be short, standard or long
      switch (rightStatMode) {
        case STAT_BACKGROUND_MODES.RIGHT_SHORT:
          statMode = STAT_BACKGROUND_MODES.BOTH_SHORT;
          break;
        case STAT_BACKGROUND_MODES.RIGHT:
          statMode = STAT_BACKGROUND_MODES.BOTH;
          break;
        case STAT_BACKGROUND_MODES.RIGHT_LONG:
          statMode = STAT_BACKGROUND_MODES.BOTH_LONG;
          break;
        default:
          console.warn(`[addUnitStatBackground] Unexpected addUnitStatBackground value: ${rightStatMode}`)
          statMode = STAT_BACKGROUND_MODES.RIGHT_LONG;
      }
    } else if (leftStatMode || rightStatMode) {
      statMode = leftStatMode || rightStatMode;
    }

    // Шеврон под отрядом в золотой оправе (см. configs/unitBadges): границы те же,
    // что у прежних картинок, на них стоят иконки снабжения, хода и боя
    ensureUnitBadgeTexture(this.scene, statMode, {linearFilter: Phaser.Textures?.FilterMode?.LINEAR});
    const {centerX: xShift} = resolveUnitBadgeLayout(statMode);

    const iconY = hex.centerY + this.getUnitShiftY(hex) + UNIT_STAT_SHIFT_Y;
    const statBackgroundMarker = this.scene.add.sprite(hex.centerX + xShift, iconY, resolveUnitBadgeTextureKey(statMode));
    statBackgroundMarker.setScale(1 / UNIT_BADGE_PIXELS_PER_UNIT);
    statBackgroundMarker.setOrigin(0.5, 0.5);
    statBackgroundMarker.setDepth(LAYER_DEPTH.UNIT + 0.1);
    this.registerUnitMarker(unitId, 'statBackgroundMarker', statBackgroundMarker);
  }

  /**
   * Добавить индикатор снабжения (иконка supply) к отряду
   * @param {Object} hex - Объект гекса
   * @param unit
   * @param doubleSupply
   * @param isAnimated
   */
  addSupplyIndicator(hex, unit, doubleSupply, isAnimated) {
    // Вычисляем позицию иконки (слева от центра гекса, напротив movement indicator)
    const iconY = hex.centerY + this.getUnitShiftY(hex) + UNIT_STAT_SHIFT_Y;
    const iconImage = 'supply';

    const iconX = doubleSupply ? hex.centerX - 23 : hex.centerX - 24;

    const supplyMarker = this.scene.add.sprite(iconX, iconY, iconImage);
    supplyMarker.setScale(STAT_ICON_SCALE);
    supplyMarker.setOrigin(0.5, 0.5);
    supplyMarker.setDepth(LAYER_DEPTH.UNIT + 1);
    if (isAnimated && !isUnitInsectoid(unit)) {
      this.addMarkerFadingTween(hex, supplyMarker);
    }
    this.registerUnitMarker(unit.id, 'supplyMarker', supplyMarker);

    if (doubleSupply) {
      const additionalSupplyMarker = this.scene.add.sprite(iconX - 11, iconY, iconImage);
      additionalSupplyMarker.setScale(STAT_ICON_SCALE);
      additionalSupplyMarker.setOrigin(0.5, 0.5);
      additionalSupplyMarker.setDepth(LAYER_DEPTH.UNIT + 1);
      if (isAnimated) {
        this.addMarkerFadingTween(hex, additionalSupplyMarker);
      }
      this.registerUnitMarker(unit.id, 'additionalSupplyMarker', additionalSupplyMarker);
    }
  }

  /**
   * Добавить текстовую метку с размером отряда
   */
  addUnitSizeLabel(hex, size) {
    const x = hex.centerX;
    const y = hex.centerY + this.getUnitShiftY(hex) + UNIT_LABEL_TEXT_SHIFT_Y;
    // Число берется из запаса прошлых перерисовок (см. clearAllUnits): у
    // каждого текста Phaser свой canvas и своя текстура, и создавать их
    // заново на каждой перерисовке поля дорого. Текст перерисовывается,
    // только если число другое
    const text = size.toString();
    const label = this.takeUnitSizeLabelFromPool(text)
      || this.scene.add.text(x, y, text, UNIT_LABEL_TEXT_STYLE);
    label.setText(text);
    label.setPosition(x, y);
    label.setAlpha(1);
    label.setVisible(true);
    label.setScale(0.5);
    label.setOrigin(0.5, 0.5);
    label.setDepth(LAYER_DEPTH.UNIT + 0.3);

    // Сохраняем для очистки
    if (!hex.unitSizeLabel) {
      hex.unitSizeLabel = [];
    }
    hex.unitSizeLabel.push(label);
  }

  /**
   * Число размера отряда из запаса, отложенного прошлой перерисовкой, — с
   * остановленными твинами: его могли двигать и гасить анимации отряда
   * (перемещение, толчок), а теперь оно стоит под другим отрядом. Сначала ищется число
   * с тем же значением: иначе тексты перемешиваются, и на каждой перерисовке поля
   * canvas и текстура каждого перерисовываются заново
   * @param {string} text
   * @returns {Phaser.GameObjects.Text|null}
   */
  takeUnitSizeLabelFromPool(text) {
    const pool = this.unitSizeLabelPool || [];
    const sameIndex = pool.findIndex((label) => label?.text === text && label?.active !== false && label?.scene);
    if (sameIndex >= 0) {
      const [label] = pool.splice(sameIndex, 1);
      this.scene.tweens?.killTweensOf?.(label);
      return label;
    }
    while (pool.length > 0) {
      const label = pool.pop();
      if (label?.active !== false && label?.scene) {
        this.scene.tweens?.killTweensOf?.(label);
        return label;
      }
    }
    return null;
  }

  /** Убрать запас чисел размера отрядов (разбор сетки) */
  destroyUnitSizeLabelPool() {
    (this.unitSizeLabelPool || []).forEach((label) => label?.destroy?.());
    this.unitSizeLabelPool = [];
  }

  /**
   * Добавить индикатор изменения размера отряда (прямоугольник с + или -)
   * @param {Object} hex - Объект гекса
   * @param {number} sizeUpdate - Изменение размера (положительное - увеличение, отрицательное - уменьшение)
   * @param isRecruitmentWithCoins
   * @param unitSize
   */
  addUnitSizeUpdateIndicator(hex, sizeUpdate, isRecruitmentWithCoins = false, unitSize) {
    const isKilled = sizeUpdate < 0 && unitSize <= Math.abs(sizeUpdate)

    // Выбираем плашку в зависимости от знака изменения
    let deltaKind = UNIT_SIZE_DELTA_KINDS.DECREASE;
    if (sizeUpdate > 0) {
      deltaKind = isRecruitmentWithCoins ? UNIT_SIZE_DELTA_KINDS.INCREASE_COINS : UNIT_SIZE_DELTA_KINDS.INCREASE;
    }
    const linearFilter = Phaser.Textures?.FilterMode?.LINEAR;
    ensureUnitSizeDeltaTexture(this.scene, deltaKind, {linearFilter});

    // Создаём спрайт под прямоугольником с размером отряда
    const sizeUpdateBackground = this.scene.add.sprite(
      hex.centerX,
      hex.centerY + this.getUnitShiftY(hex) + UNIT_STAT_SHIFT_Y + UNIT_SIZE_DELTA_BADGE.shiftY, // Ниже основного размера
      resolveUnitSizeDeltaTextureKey(deltaKind)
    );
    sizeUpdateBackground.setScale(1 / UNIT_BADGE_PIXELS_PER_UNIT);
    sizeUpdateBackground.setDepth(LAYER_DEPTH.UNIT + 0.2);

    // Создаём текст с изменением размера (со знаком)
    const sign = sizeUpdate > 0 ? '+' : MINUS_SIGN;
    const absUpdate = Math.abs(sizeUpdate);

    let textStyle = {...UNIT_LABEL_TEXT_STYLE};
    if (absUpdate >= 10) {
      textStyle = {
        ...textStyle,
        ...UNIT_LABEL_TEN_AND_MORE_TEXT_STYLE,
      }
    }
    if (sizeUpdate > 0) {
      textStyle = {
        ...textStyle,
        color: '#000000'
      }
    }

    const label = this.scene.add.text(
      hex.centerX,
      hex.centerY + this.getUnitShiftY(hex) + UNIT_STAT_SHIFT_Y + 17.5,
      `${sign}${absUpdate}`,
      textStyle
    );
    label.setScale(0.5);
    label.setOrigin(0.5, 0.5);
    label.setDepth(LAYER_DEPTH.UNIT + 0.3);

    this.registerMarker(hex, sizeUpdateBackground, label);

    if (isKilled) {
      ensureUnitSizeDeltaTexture(this.scene, UNIT_SIZE_DELTA_KINDS.KILLED, {linearFilter});
      const sizeKilledBackground = this.scene.add.sprite(
        hex.centerX,
        hex.centerY + this.getUnitShiftY(hex) + UNIT_STAT_SHIFT_Y + UNIT_SIZE_DELTA_BADGE.shiftY, // Ниже основного размера
        resolveUnitSizeDeltaTextureKey(UNIT_SIZE_DELTA_KINDS.KILLED)
      );
      sizeKilledBackground.setScale(1 / UNIT_BADGE_PIXELS_PER_UNIT);
      sizeKilledBackground.setDepth(LAYER_DEPTH.UNIT + 0.25);
      this.registerMarker(hex, sizeKilledBackground);

      this.addMarkerFadingTween(hex, sizeKilledBackground);
    }
  }

  /**
   * Очистить все спрайты отрядов
   */
  clearAllUnits() {
    // Удаляем все спрайты
    Object.values(this.unitsSprites).forEach(sprite => {
      if (Array.isArray(sprite)) {
        // Если это массив спрайтов (например, индикаторы движения)
        sprite.forEach(s => s.destroy());
      } else {
        sprite.destroy();
      }
    });
    this.unitsSprites = {};

    // Очищаем подсветки и метки отрядов
    Object.values(this.hexes).forEach(hex => {
      if (hex.unitHighlight) {
        hex.unitHighlight.forEach(h => h.destroy());
        hex.unitHighlight = [];
      }
      // Числа размера не уничтожаются, а прячутся в запас — следующая
      // перерисовка возьмет их оттуда (см. addUnitSizeLabel)
      if (hex.unitSizeLabel) {
        if (!this.unitSizeLabelPool) {
          this.unitSizeLabelPool = [];
        }
        hex.unitSizeLabel.forEach((label) => {
          label.setVisible?.(false);
          this.unitSizeLabelPool.push(label);
        });
        hex.unitSizeLabel = [];
      }
    });
  }


  updatePendingBuildingMarkers(state) {
    const sharedGameState = selectSharedGameState(state);

    // Проверяем, нужно ли отображать маркеры
    if (sharedGameState.phase !== PHASES.CONSTRUCTION) {
      return;
    }

    const pendingBuildings = sharedGameState.pendingBuildings || [];
    if (pendingBuildings.length === 0) {
      return;
    }

    // Отрисовываем молотки на отложенных строениях
    try {
      const invalidHexKeys = sharedGameState.invalidPendingBuildings || [];

      pendingBuildings.forEach(building => {
        const hex = this.hexes[building.hexKey];
        if (hex) {
          const isInvalid = invalidHexKeys.includes(building.hexKey);
          this.drawPendingBuildingMarker(hex, isInvalid);
        }
      });
    } catch (e) {
      console.error('Error drawing pending building markers:', e);
    }
  }

  /**
   * Нарисовать маркер действия на гексе
   * @param {Phaser.GameObjects.Graphics} hex - Объект гексагона
   * @param {boolean} isInvalid - Является ли строение невалидным (для красного молотка)
   * @param {boolean} isPlanned - Строение только запланировано, а не строится в этом ходу
   */
  drawPendingBuildingMarker(hex, isInvalid = false, isPlanned = false) {
    // Молоток — действие строительства этого хода, у плана своя иконка:
    // план еще не стоит игроку действия и может не состояться
    const kind = isInvalid
      ? CONSTRUCTION_MARKER_KINDS.INVALID
      : (isPlanned ? CONSTRUCTION_MARKER_KINDS.PLAN : CONSTRUCTION_MARKER_KINDS.BUILD);

    // Живой маркер (см. LiveFieldMarkers): выше маркеров доступности
    const marker = this.liveFieldMarkers.createConstructionMarker({
      x: hex.centerX,
      y: hex.centerY,
      hexSize: hex.size,
      depth: LAYER_DEPTH.MARKER + 0.1,
      kind,
    });
    // По метке иконку находят, когда на ячейке начинается размещение: ее
    // проявление тогда откладывается (см. deferPendingBuildingMarkers)
    marker.isPendingBuildingMarker = true;

    this.registerMarker(hex, marker);
    this.deferPendingBuildingMarker(hex, marker);
  }

  /**
   * Пока на ячейке идет размещение (летит жетон, поднимается пыль), иконка действия не
   * показывается: она ждет конца анимации (см. markPlacementAnimation), а мигает потом
   * вместе со всеми, от часов сцены (см. createConstructionMarker)
   */
  deferPendingBuildingMarker(hex, marker) {
    const remainingMs = this.getPlacementAnimationRemainingMs(hex.key);
    // Без размещения значок сразу в общем такте: перерисовка поля не должна его гасить
    if (remainingMs <= 0) {
      return;
    }
    marker.setAlpha?.(0);
    marker.revealAt = (this.scene.time?.now ?? 0) + remainingMs;
  }

  /**
   * Отложить иконки действия строительства, уже нарисованные на ячейке.
   *
   * Без этого иконка успевает проявиться раньше анимации: жетон ставится
   * диспатчем, подписчик стора перерисовывает поле (и иконку) СРАЗУ, а полет
   * жетона начинается уже после — на момент отрисовки откладывать было нечего
   */
  deferPendingBuildingMarkers(hexKey) {
    const hex = this.hexes[hexKey];
    (hex?.markers || []).forEach((marker) => {
      if (!marker?.isPendingBuildingMarker) {
        return;
      }

      this.deferPendingBuildingMarker(hex, marker);
    });
  }

  // Маркеры на запланированных строениях: иконка плана, а не молоток
  // строительства (updatePendingBuildingMarkers) — план еще не потрачен и
  // может не состояться, поэтому и выглядит иначе. Не прошедшее проверку плана
  // (validateConstructionPlan) — красный молоток, как невалидное строение хода
  updatePlannedBuildingMarkers(state) {
    const invalidHexKeys = selectConstructionPlanInvalidHexKeys(state);
    this.getPlannedEntriesByHexKey(state).forEach((_, hexKey) => {
      const hex = this.hexes[hexKey];
      if (hex) {
        this.drawPendingBuildingMarker(hex, invalidHexKeys.includes(hexKey), true);
      }
    });
  }

  /**
   * Подсказки «рядом нужен квартал» в фазе строительства (только у самого
   * строящего игрока):
   *  - ферма/казарма без смежных кварталов (базовых или центральных): на
   *    смежных пустых/дорожных ячейках мигают попеременно флаг (центральный
   *    квартал) и поселение (базовый), а где центральный по зоне кварталов
   *    запрещен, но базовый возможен — только поселение;
   *  - базовый квартал без смежного центрального: аналогично, но только флаг.
   * Ячейки, где по зоне кварталов (см. updateQuarterZoneOverlay) нельзя ни
   * тот, ни другой, пропускаются, как и дороги, построенные в текущем ходу
   * (на них уже мигает иконка действия — молоток)
   */
  updateQuarterHintMarkers(state) {
    // Ячейки с подсказками — для updateAvailableForConstructionMarkers: на
    // них не рисуется иконка молотка доступности, чтобы не накладывалась на
    // мигающие flag/settlement (вызов подсказок идет ПЕРЕД доступностью)
    this.quarterHintCellKeys = new Set();

    const sharedGameState = selectSharedGameState(state);
    if (sharedGameState.phase !== PHASES.CONSTRUCTION) {
      return;
    }
    const pendingBuildings = sharedGameState.pendingBuildings || [];
    if (pendingBuildings.length === 0) {
      return;
    }

    // Подсказка адресована строящему — наблюдатели и противники ее не видят
    const currentPlayer = selectCurrentPlayer(state);
    const actingPlayer = getActingPlayerFromState(state);
    if (!currentPlayer || actingPlayer?.id !== currentPlayer.id) {
      return;
    }

    const hexes = selectHexes(state);
    const pendingHexKeysSet = new Set(pendingBuildings.map((building) => building.hexKey));

    // Зоны кварталов считаются лениво — только если нашлось строение,
    // которому нужна подсказка
    let quarterZoneCells = null;
    let baseQuarterAllowedCells = null;
    const ensureQuarterZones = () => {
      if (quarterZoneCells === null) {
        quarterZoneCells = calculateQuarterZoneCells(state);
        baseQuarterAllowedCells = calculateBaseQuarterAllowedCells(state);
      }
    };

    // cellKey -> Set('flag' | 'settlement') — ячейка может быть соседом
    // нескольких построенных зданий, иконки объединяются
    const hintIconsByCell = new Map();

    for (const building of pendingBuildings) {
      // ВАЖНО: у CONSTRUCTION-записей pendingBuildings нет playerId (его
      // кладут только записи ремонта) — фильтровать по нему нельзя. Свои ли
      // это строения, уже гарантирует гейт actingPlayer === currentPlayer
      // выше: строит всегда игрок текущего хода
      const isFarmOrBarracks = building.token === TOKENS.FARM || building.token === TOKENS.BARRACKS;
      const isBaseQuarter = building.token === TOKENS.QUARTER;
      if (!isFarmOrBarracks && !isBaseQuarter) continue;

      if (isFarmOrBarracks && checkIfAdjacentToAnyQuarter(building.hexKey, hexes, this.hexMath)) {
        continue;
      }
      if (isBaseQuarter && checkIfAdjacentToCentralQuarter(building.hexKey, hexes, this.hexMath)) {
        continue;
      }

      ensureQuarterZones();

      const [q, r] = building.hexKey.split(',').map(Number);
      for (const [nq, nr] of this.hexMath.getNeighborsFlatTop(q, r)) {
        const neighborKey = coordsToKey(nq, nr);
        if (!this.hexes[neighborKey]) continue;

        const neighborToken = hexes[neighborKey];
        const isEmpty = !neighborToken;
        const isRoad = neighborToken === TOKENS.ROAD;
        if (!isEmpty && !isRoad) continue;
        // Дорога, построенная в текущем ходу, — поверх нее уже мигает молоток
        if (isRoad && pendingHexKeysSet.has(neighborKey)) continue;

        // Вне зоны кварталов можно любой квартал (в т.ч. центральный нового
        // города); в зоне — только базовый, и то не везде
        const isCentralForbidden = quarterZoneCells.has(neighborKey);
        const isBaseAllowed = baseQuarterAllowedCells.has(neighborKey);

        let icons;
        if (isFarmOrBarracks) {
          if (!isCentralForbidden) {
            // Порядок серии (после молотка): базовый квартал, затем центральный
            icons = ['settlement', 'flag'];
          } else if (isBaseAllowed) {
            icons = ['settlement'];
          } else {
            continue;
          }
        } else {
          if (!isCentralForbidden) {
            icons = ['flag'];
          } else {
            continue;
          }
        }

        const cellIcons = hintIconsByCell.get(neighborKey) || new Set();
        icons.forEach((icon) => cellIcons.add(icon));
        hintIconsByCell.set(neighborKey, cellIcons);
      }
    }

    hintIconsByCell.forEach((iconsSet, cellKey) => {
      const hex = this.hexes[cellKey];
      if (hex) {
        this.quarterHintCellKeys.add(cellKey);
        this.drawQuarterHintMarker(hex, [...iconsSet]);
      }
    });
  }

  /**
   * Мигающая подсказка квартала на ячейке: серия иконок, попеременно
   * появляющихся и затухающих по альфе (каждая — в своем «окне» общего
   * периода, как молоток pending-строений). Серия всегда начинается с
   * молотка («здесь нужно построить»), затем иконки допустимых кварталов:
   * hammer + settlement + flag — можно и базовый, и центральный;
   * hammer + settlement — только базовый; hammer + flag — только центральный
   * @param {Object} hex
   * @param {string[]} iconKeys - 'flag' и/или 'settlement'
   */
  drawQuarterHintMarker(hex, iconKeys) {
    const QUARTER_HINT_TEXTURES = {
      hammer: 'hammer_white',
      flag: 'quarter_hint_flag',
      settlement: 'quarter_hint_settlement',
    };
    const seriesIconKeys = ['hammer', ...iconKeys];

    // Период одного полного цикла иконки: появление + пик + затухание
    const singleCyclePeriod = MARKER_FADING_DURATION_MS * 2 + MARKER_FADING_HOLD_MS;

    seriesIconKeys.forEach((iconKey, index) => {
      const texture = QUARTER_HINT_TEXTURES[iconKey];
      const marker = this.scene.add.sprite(hex.centerX, hex.centerY, texture);
      marker.setScale(0.1);
      marker.setAlpha(0);
      marker.setDepth(LAYER_DEPTH.MARKER + 0.1);

      const tween = this.scene.tweens.add({
        targets: marker,
        alpha: 1,
        duration: MARKER_FADING_DURATION_MS,
        hold: MARKER_FADING_HOLD_MS,
        ease: Phaser.Math.Easing.Sine.InOut,
        yoyo: true,
        repeat: -1,
        // Чередование: каждая иконка мигает в своем «окне» общего периода
        delay: index * singleCyclePeriod,
        repeatDelay: (seriesIconKeys.length - 1) * singleCyclePeriod,
      });

      this.registerMarker(hex, marker);
      this.registerTweens(hex, tween);
    });
  }

  registerMarker(hex, ...markers) {
    if (!hex.markers) {
      hex.markers = [];
    }
    hex.markers.push(...markers);
  }

  /**
   * Спрайт жетона ячейки (тень, сам жетон, обводка, флаг) — с пометкой
   * ячейки: по ней rerenderHexToken находит и заменяет жетон, не трогая
   * остальные маркеры ячейки
   */
  registerTokenPart(hex, hexKey, sprite) {
    if (sprite) {
      sprite.tokenPartHexKey = hexKey;
    }
    this.registerMarker(hex, sprite);
  }

  /**
   * Перерисовать на месте один жетон ячейки по текущему состоянию и подмене
   * облета (setCityCaptureOverrides), не пересобирая все поле: облет меняет
   * флаг центрального квартала посреди прожигания, и полная перерисовка
   * (тысячи объектов) давала рывок огня — на телефоне в сотни миллисекунд.
   *
   * Новые спрайты встают в списке отрисовки на места прежних той же глубины:
   * жетоны одной глубины перекрывают соседей по порядку отрисовки сверху вниз
   * (см. getHexKeysTopToBottom), и жетон, добавленный в конец, лег бы поверх
   * нижнего соседа. Вне этапа стрельбы урон жетон не меняет (см.
   * getBuildingRenderState), поэтому здесь его не учитываем
   * @param {string} hexKey
   * @param {Object} state
   * @returns {boolean} перерисован ли жетон
   */
  rerenderHexToken(hexKey, state) {
    const hex = this.hexes[hexKey];
    const hexState = selectHexes(state);
    const override = this.cityCaptureOverrides?.tokenByHexKey.get(hexKey);
    const tokenType = override && checkIfCentralQuarter(hexState?.[hexKey]) ? override : hexState?.[hexKey];
    if (!hex || !tokenType || tokenType === TERRAIN_TOKENS.SEA) {
      return false;
    }

    const oldParts = (hex.markers || [])
      .filter((marker) => marker?.tokenPartHexKey === hexKey && !marker.isStartingQuarterOverlay);
    const markersCountBefore = hex.markers?.length || 0;
    const glintTargetsCountBefore = this.tokenGlintTargets?.length;
    const ruinsGlowTargetsCountBefore = this.ruinsIncomeGlowTargets?.length;
    this.renderToken(hexKey, hex, tokenType, state, selectIsGameStarted(state), selectPhase(state) === PHASES.SETUP);
    // Место блика и свечения у жетона то же — цели от прошлой перерисовки остаются
    if (Number.isInteger(glintTargetsCountBefore)) {
      this.tokenGlintTargets.length = glintTargetsCountBefore;
    }
    if (Number.isInteger(ruinsGlowTargetsCountBefore)) {
      this.ruinsIncomeGlowTargets.length = ruinsGlowTargetsCountBefore;
    }

    const newParts = hex.markers.slice(markersCountBefore);
    const unmatchedOldParts = [...oldParts];
    newParts.forEach((part) => {
      const index = unmatchedOldParts.findIndex((oldPart) => oldPart.depth === part.depth);
      if (index >= 0) {
        part.setAbove?.(unmatchedOldParts[index]);
        unmatchedOldParts.splice(index, 1);
      }
    });

    const oldPartsSet = new Set(oldParts);
    hex.markers = hex.markers.filter((marker) => !oldPartsSet.has(marker));
    oldParts.forEach((part) => part.destroy?.());
    return true;
  }

  registerUnitMarker(unitId, markerName, marker) {
    const spriteId = unitId + '_' + markerName;
    if (!this.unitsSprites[spriteId]) {
      this.unitsSprites[spriteId] = [];
    }
    this.unitsSprites[spriteId].push(marker);
  }

  registerTweens(hex, ...animations) {
    if (!hex.animations) {
      hex.animations = [];
    }
    hex.animations.push(...animations);
  }

  /**
   * Очистить все маркеры отложенных строений
   */
  clearMarkers() {
    Object.values(this.hexes).forEach(hex => {
      if (hex.markers) {
        hex.markers.forEach(marker => {
          marker && marker.destroy && marker.destroy();
        });
        hex.markers = [];
      }
    });
  }

  skipUnitMovementAnimation() {
    if (!this.isAnimatingMovement) return;

    const onComplete = this.currentMovementOnComplete;
    this.unitFootprints?.finish(this.currentMovementUnitId, {isSkipped: true});
    this.markLocalUnitMovement(this.currentMovementUnitId, this.currentMovementTargetHexKey);

    this.stopMovementTweens();

    this.isAnimatingMovement = false;
    this.currentMovementUnitId = null;
    this.currentMovementTargetHexKey = null;
    this.currentMovementOnComplete = null;

    const wingedMovement = this.wingedMovement;
    this.wingedMovement = null;
    if (wingedMovement) {
      wingedMovement.restoreDepths();
      this.abilityCastAnimation.finish(wingedMovement.castId);
    }

    if (onComplete) onComplete();
  }

  /**
   * Жестко останавливает твины цепочки перемещения (без onComplete — вызывающий
   * код сам решает, завершать ли логически прерванное перемещение).
   */
  stopMovementTweens() {
    this.movementTweens.forEach(tween => {
      tween.stop();
      tween.remove();
    });
    this.movementTweens = [];
  }

  clearTweens() {
    Object.values(this.hexes).forEach(hex => {
      if (hex.animations) {
        hex.animations.forEach(tween => {
          tween.stop();
          tween.remove();
        });
        hex.animations = [];
      }
    });
  }
}
